import React, { useState, useEffect, useCallback, useRef } from "react";
import * as membersUserStoriesApi from "./api/membersUserStories";
import * as membersInvitationsApi from "./api/membersInvitations";
import * as membersMessagesApi from "./api/membersMessages";
import * as membersCustomersApi from "./api/membersCustomers";
import { useVoiceCall } from "./voice/useVoiceCall";
import { exchangeToUser as exchangeMemberToUser } from "./api/authExchange";
import { markAdminArea, markFrontendArea } from "./api/lastAreaCookie";
import MemberInviteRegister from "./MemberInviteRegister";
import {
  SERVICES as MOCK_AI_SERVICES,
  CONSUMERS as MOCK_AI_CONSUMERS,
  RUNS as MOCK_AI_RUNS,
  EVAL_SETS as MOCK_AI_EVALS,
  COSTS as MOCK_AI_COSTS,
  APPROVALS as MOCK_AI_APPROVALS,
  INCIDENTS as MOCK_AI_INCIDENTS,
  PLATFORM_OVERVIEW_ROWS as MOCK_AI_PLATFORM_OVERVIEW_ROWS,
  TOOLS as MOCK_AI_TOOLS,
  ACTIONS as MOCK_AI_ACTIONS,
  DATA_STORES as MOCK_AI_DATA_STORES,
} from "./mock/aiAgents";

function getApiBase() {
  const ws = process.env.REACT_APP_WS_URL || "wss://ws.aimafia.de";
  return ws.replace(/^wss:/, "https:").replace(/^ws:/, "http:");
}

const ADMIN_KONTO_PATH = "/konto";
const ADMIN_MEMBER_INVITE_PATH = "/member-einladung";

function adminPathnameIsMemberInvite() {
  if (typeof window === "undefined") return false;
  try {
    const p = window.location.pathname || "";
    return p === ADMIN_MEMBER_INVITE_PATH || p.endsWith(ADMIN_MEMBER_INVITE_PATH);
  } catch {
    return false;
  }
}

function adminPathnameIsKonto() {
  if (typeof window === "undefined") return false;
  try {
    const p = window.location.pathname || "";
    return p === ADMIN_KONTO_PATH || p.endsWith(ADMIN_KONTO_PATH);
  } catch {
    return false;
  }
}

function syncKontoUrl(open) {
  if (typeof window === "undefined") return;
  if (open) {
    if (!adminPathnameIsKonto()) {
      window.history.pushState({ admin: "konto" }, "", ADMIN_KONTO_PATH);
    }
  } else if (adminPathnameIsKonto()) {
    window.history.pushState({ admin: "app" }, "", "/");
  }
}

const PRODUCT_VIEWS = [
  { id: "kunden", label: "Nutzer" },
  { id: "kis", label: "KI's" },
  { id: "anrufe", label: "Anrufe" },
  { id: "error_logs", label: "Error Logs" },
  { id: "aicoworker", label: "Agenten" },
  { id: "module", label: "Module" },
  { id: "tests", label: "Tests" },
  { id: "product_userstories", label: "User Stories" },
];

const ADMIN_AREAS = [
  {
    id: "product",
    label: "Product",
    description: "Nutzer, KI’s, Anrufe und technische Stammdaten.",
  },
  {
    id: "marketing",
    label: "Marketing",
    description: "Posts und Sichtbarkeit (in Arbeit).",
  },
  {
    id: "sales",
    label: "Sales",
    description: "Kunden, Akquise, Schulung und Einnahmen (in Arbeit).",
  },
  {
    id: "admin",
    label: "Admin",
    description: "Agendas, KPIs und Personal (in Arbeit).",
  },
  {
    id: "people",
    label: "People",
    description: "People-Bereich und Teamthemen (in Arbeit).",
  },
  {
    id: "finance",
    label: "Finance",
    description: "Finanzkennzahlen, Buchhaltung und Reporting (Platzhalter).",
  },
  {
    id: "ai_agents",
    label: "AI Agents",
    description: "Agent-as-a-Service: Services, Konsumenten, Traces, Evals, Governance (Mockup).",
  },
  {
    id: "design",
    label: "Design",
    description: "Patterns und visuelle Bausteine (Platzhalter).",
  },
];

const VIEWS_BY_AREA = {
  product: PRODUCT_VIEWS,
  marketing: [
    { id: "marketing_posts", label: "Community Posts" },
    { id: "marketing_community_content", label: "Community Content" },
    { id: "marketing_channel_content", label: "Channel Content" },
    { id: "marketing_targetgroups", label: "Zielgruppen" },
    { id: "marketing_channels", label: "Kanäle" },
  ],
  people: [
    { id: "people_members", label: "Member" },
    { id: "people_schulungen", label: "Schulungen" },
  ],
  sales: [
    { id: "sales_akquise", label: "Akquise" },
    { id: "sales_service", label: "Service" },
    { id: "sales_karriere", label: "Karriere" },
  ],
  admin: [
    { id: "admin_agendas", label: "Markt" },
    { id: "admin_kpi_optimization", label: "Optimierung" },
  ],
  finance: [
    { id: "finance_overview", label: "Übersicht" },
  ],
  ai_agents: [
    { id: "ai_agents_overview", label: "Übersicht" },
    { id: "ai_agents_consumers", label: "Clients" },
    { id: "ai_agents_services", label: "Agents" },
    { id: "ai_agents_tools", label: "Tools" },
    { id: "ai_agents_actions", label: "Actions" },
    { id: "ai_agents_data", label: "Data" },
    { id: "ai_agents_runs", label: "Aufrufe" },
    { id: "ai_agents_evals", label: "Evals" },
    { id: "ai_agents_governance", label: "Governance" },
  ],
  design: [
    { id: "design_patterns", label: "Patterns" },
  ],
};

/** Mittlere Spalte: Titel + Kurztext; Desktop-Rechte Spalte: Detail-Header (Gerüst). */
const WORKSPACE_SHELL_META = {
  admin_kpi_optimization: {
    title: "Optimierung",
    desc: "Kennzahlen und Verbesserungszyklen (KPIs) – hier folgt später Inhalt.",
    detailTitle: "KPI-Detail",
    detailSub: "Drilldown und Verlauf folgen.",
  },
  finance_overview: {
    title: "Finance · Übersicht",
    desc: "Platzhalter für Finanz-Übersicht (Cashflow, Kosten, Reporting). Inhalt folgt.",
    detailTitle: "Finance-Detail",
    detailSub: "Drilldown auf einzelne Kennzahlen folgt.",
  },
};

function isWorkspaceShellTab(viewId) {
  return viewId != null && Boolean(WORKSPACE_SHELL_META[viewId]);
}

/** Platzhalter-Liste für den Design > Patterns Tab. */
const DESIGN_PATTERN_PLACEHOLDERS = [
  {
    id: "buttons",
    title: "Buttons",
    desc: "Platzhalter — Pattern-Beschreibung folgt.",
  },
];

function membersPostChannelLabel(ch) {
  switch (String(ch || "").toLowerCase()) {
    case "whatsapp":
      return "WhatsApp";
    case "instagram":
      return "Instagram";
    case "facebook":
      return "Facebook";
    case "tiktok":
      return "TikTok";
    default:
      return ch ? String(ch) : "–";
  }
}

function formatMembersPostTs(raw) {
  if (raw == null || String(raw).trim() === "") return "–";
  const s0 = String(raw).trim();
  const s = s0.includes("T") ? s0 : `${s0.slice(0, 19).replace(" ", "T")}`;
  const dt = new Date(s);
  if (Number.isNaN(dt.getTime())) return "–";
  return dt.toLocaleString("de-DE", {
    day: "2-digit",
    month: "2-digit",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  });
}

/**
 * WhatsApp-artige Zeitangabe (deutsch) fuer die letzte Nachricht in der Team-Liste.
 * - heute  -> "HH:MM"
 * - gestern -> "Gestern"
 * - innerhalb der letzten 7 Tage -> Wochentag-Kuerzel ("Mo", "Di", …)
 * - sonst -> "DD.MM.YYYY"
 *
 * Erwartet `unixTs` als Sekunden-Timestamp (so liefert das Backend `last_message_at`).
 */
function formatChatPreviewTime(unixTs) {
  if (unixTs == null) return "";
  const ts = Number(unixTs);
  if (!Number.isFinite(ts) || ts <= 0) return "";
  const d = new Date(ts * 1000);
  if (isNaN(d.getTime())) return "";
  const now = new Date();
  const sameDay =
    d.getFullYear() === now.getFullYear() &&
    d.getMonth() === now.getMonth() &&
    d.getDate() === now.getDate();
  if (sameDay) {
    return d.toLocaleTimeString("de-DE", { hour: "2-digit", minute: "2-digit" });
  }
  const yesterday = new Date(now);
  yesterday.setDate(now.getDate() - 1);
  const isYesterday =
    d.getFullYear() === yesterday.getFullYear() &&
    d.getMonth() === yesterday.getMonth() &&
    d.getDate() === yesterday.getDate();
  if (isYesterday) return "Gestern";
  const diffMs = now.getTime() - d.getTime();
  if (diffMs < 7 * 24 * 60 * 60 * 1000) {
    return d.toLocaleDateString("de-DE", { weekday: "short" });
  }
  return d.toLocaleDateString("de-DE", {
    day: "2-digit",
    month: "2-digit",
    year: "numeric",
  });
}

/** Tagesueberschrift im Chat-Verlauf ("Heute", "Gestern", "Mo, 12.04."). */
function formatChatDayLabel(unixTs) {
  if (unixTs == null) return "";
  const ts = Number(unixTs);
  if (!Number.isFinite(ts) || ts <= 0) return "";
  const d = new Date(ts * 1000);
  if (isNaN(d.getTime())) return "";
  const now = new Date();
  const startOf = (x) => new Date(x.getFullYear(), x.getMonth(), x.getDate()).getTime();
  const days = Math.round((startOf(now) - startOf(d)) / (24 * 60 * 60 * 1000));
  if (days === 0) return "Heute";
  if (days === 1) return "Gestern";
  if (days > 1 && days < 7) {
    return d.toLocaleDateString("de-DE", { weekday: "long" });
  }
  return d.toLocaleDateString("de-DE", {
    weekday: "short",
    day: "2-digit",
    month: "2-digit",
    year: "numeric",
  });
}

/** Uhrzeit fuer eine einzelne Chat-Bubble ("HH:MM"). */
function formatChatBubbleTime(unixTs) {
  if (unixTs == null) return "";
  const ts = Number(unixTs);
  if (!Number.isFinite(ts) || ts <= 0) return "";
  const d = new Date(ts * 1000);
  if (isNaN(d.getTime())) return "";
  return d.toLocaleTimeString("de-DE", { hour: "2-digit", minute: "2-digit" });
}

/** Zwei Buchstaben (gross) aus display_name oder email als Initialen-Fallback fuer Avatare. */
function memberInitials(member) {
  if (!member) return "?";
  const raw = String(member.display_name || member.email || "").trim();
  if (!raw) return "?";
  const parts = raw.split(/[\s@._-]+/).filter(Boolean);
  if (parts.length >= 2) {
    return (parts[0][0] + parts[1][0]).toUpperCase();
  }
  return raw.slice(0, 2).toUpperCase();
}

/** Liefert die URL fuer das Profilbild eines Members oder null, wenn keines hinterlegt ist. */
function memberProfilePicSrc(member, apiBase, adminToken, version) {
  if (!member || !member.id || !member.profile_picture_key) return null;
  if (!apiBase || !adminToken) return null;
  const v = version != null ? `&v=${encodeURIComponent(String(version))}` : "";
  return `${apiBase}/api/admin/members/${member.id}/profile-picture?token=${encodeURIComponent(adminToken)}${v}`;
}

/** Anzeige Mitarbeiter aus members_posts-JOIN (display_name / E-Mail / nur ID). */
function formatMembersPostActorLabel(row, idKey, displayKey, emailKey) {
  const id = row != null ? row[idKey] : null;
  const display = row != null && row[displayKey] != null ? String(row[displayKey]).trim() : "";
  const email = row != null && row[emailKey] != null ? String(row[emailKey]).trim() : "";
  if (display && email) return `${display} (${email})`;
  if (display) return display;
  if (email) return email;
  if (id != null && Number.isFinite(Number(id))) return `Mitarbeiter-ID ${id}`;
  return "–";
}

function areaAccessField(areaId) {
  if (areaId === "product") return "access_product";
  if (areaId === "marketing") return "access_marketing";
  if (areaId === "sales") return "access_sales";
  if (areaId === "admin") return "access_admin";
  if (areaId === "people") return "access_people";
  if (areaId === "ai_agents") return "access_ai_agents";
  if (areaId === "finance") return "access_finance";
  if (areaId === "design") return "access_design";
  return null;
}

/**
 * `users.plan` (Telephonio): free, basic, pro, business — nur Anzeige im Admin.
 * Mai 2026: Plan-Rename (siehe `backend/scripts/db-migration-rename-plan-keys.sql`).
 * Legacy-Keys `pro` -> Label "Basic" und `businessplus` -> Label "Business" gibt
 * es in der DB nach der Migration nicht mehr; die `normalize`-Funktion ersetzt
 * sie defensiv noch fuer evtl. alte Cache-/Log-Eintraege.
 */
function normalizeTelephonioPlanKey(raw) {
  const s = String(raw || "")
    .trim()
    .toLowerCase()
    .replace(/[\s_-]+/g, "");
  if (s === "free") return "free";
  if (s === "basic") return "basic";
  if (s === "pro") return "pro";
  if (s === "business") return "business";
  // Defensiver Legacy-Pfad (sollte nach der Migration nicht mehr vorkommen)
  if (s === "businessplus") return "business";
  return "";
}

function abbrevTelephonioPlan(raw) {
  const k = normalizeTelephonioPlanKey(raw);
  if (k === "free") return "FREE";
  if (k === "basic") return "BASIC";
  if (k === "pro") return "PRO";
  if (k === "business") return "BUS";
  return "";
}

function labelTelephonioPlan(raw) {
  const k = normalizeTelephonioPlanKey(raw);
  if (k === "free") return "Free";
  if (k === "basic") return "Basic";
  if (k === "pro") return "Pro";
  if (k === "business") return "Ultra";
  if (raw != null && String(raw).trim()) return String(raw).trim();
  return "—";
}

function planCloudColors(planRaw) {
  const k = normalizeTelephonioPlanKey(planRaw);
  if (!k) {
    return { background: "var(--admin-text-muted, #B0B0B0)", label: "" };
  }
  if (k === "free" || k === "basic") {
    return {
      background: "#FFB6C1",
      label: k === "free" ? "Free" : "Basic",
    };
  }
  if (k === "pro") return { background: "#428BFF", label: "Pro" };
  if (k === "business") {
    return {
      background: "linear-gradient(135deg, #FFD700 0%, #FFA500 100%)",
      label: "Ultra",
    };
  }
  return { background: "var(--admin-text-muted, #B0B0B0)", label: "" };
}

function formatHistoryDate(iso) {
  if (!iso) return "—";
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return "—";
  const dd = String(d.getDate()).padStart(2, "0");
  const mm = String(d.getMonth() + 1).padStart(2, "0");
  const hh = String(d.getHours()).padStart(2, "0");
  const mi = String(d.getMinutes()).padStart(2, "0");
  return `${dd}.${mm} - ${hh}:${mi}`;
}

function formatServiceTime(iso) {
  if (!iso) return "";
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return "";
  const hh = String(d.getHours()).padStart(2, "0");
  const mi = String(d.getMinutes()).padStart(2, "0");
  return `${hh}:${mi}`;
}

function PlanCloud({ plan, size = 32 }) {
  const colors = planCloudColors(plan);
  return (
    <span
      className="admin_plan_cloud"
      title={colors.label || "Lead"}
      style={{
        width: size,
        height: size,
        borderRadius: "50%",
        background: colors.background,
        display: "inline-flex",
        alignItems: "center",
        justifyContent: "center",
        flexShrink: 0,
      }}>
      <svg
        width={Math.round(size * 0.55)}
        height={Math.round(size * 0.55)}
        viewBox="0 0 24 24"
        fill="none"
        stroke="#fff"
        strokeWidth="2"
        strokeLinecap="round"
        strokeLinejoin="round">
        <path d="M18 10h-1.26A8 8 0 1 0 9 20h9a5 5 0 0 0 0-10z" />
      </svg>
    </span>
  );
}

const MOCK_CAREER_EVENTS = [
  {
    id: "mock-1",
    title: "Akquisegespräch",
    subtitle: "11:33 - Liebscher & Bracht",
    amount: "+5€",
    tag: "Sofort",
  },
  {
    id: "mock-2",
    title: "Onboarding",
    subtitle: "11:33 - Liebscher & Bracht",
    amount: "+30€",
    tag: "Sofort",
  },
  {
    id: "mock-3",
    title: "Abschluss",
    subtitle: "11:33 - Liebscher & Bracht",
    amount: "+300€",
    tag: "30€/Monat",
  },
];

/**
 * Rendert ein Test-Transcript (Manual/E2E) so, dass `tool_call` und das
 * passende `tool_result` in einer kombinierten Box landen — gematcht ueber
 * `tool_call_id`. Tool-Rows ohne passenden Call (defensiv) fallen auf den
 * Legacy-Result-Box-Look zurueck.
 *
 * Inline-Styles spiegeln den bestehenden Look des Tests-Tabs.
 */
function summarizeAdminToolResult(rawContent) {
  let parsed = rawContent;
  try { parsed = JSON.parse(rawContent); } catch { /* ignore */ }
  const summary = parsed && typeof parsed === "object" && parsed.results
    ? parsed.results
    : typeof parsed === "string"
      ? parsed
      : (parsed != null ? JSON.stringify(parsed) : "");
  if (!summary) return "";
  return summary.length > 600 ? summary.slice(0, 600) + "…" : summary;
}

function renderAdminTranscriptMerged(transcript) {
  const arr = Array.isArray(transcript) ? transcript : [];

  // Tool-Results nach tool_call_id indizieren + Indices vermerken, die durch
  // einen assistant.tool_calls-Eintrag konsumiert werden.
  const resultByCallId = {};
  arr.forEach((m, idx) => {
    if (m && m.role === "tool" && m.tool_call_id) {
      resultByCallId[m.tool_call_id] = { msg: m, idx };
    }
  });
  const consumed = new Set();
  arr.forEach((m) => {
    if (m && m.role === "assistant" && Array.isArray(m.tool_calls)) {
      m.tool_calls.forEach((tc) => {
        if (tc && tc.id && resultByCallId[tc.id]) {
          consumed.add(resultByCallId[tc.id].idx);
        }
      });
    }
  });

  return arr.map((msg, i) => {
    if (!msg) return null;
    if (msg.role === "user") {
      return (
        <div
          key={i}
          className="admin_anruf_bubble--user"
          style={{
            alignSelf: "flex-start",
            maxWidth: "85%",
            padding: "8px 12px",
            borderRadius: "12px 12px 12px 2px",
            border: "1px solid var(--admin-border, #ddd)",
            fontSize: "13px",
            whiteSpace: "pre-wrap",
          }}>
          {msg.content}
        </div>
      );
    }
    if (msg.role === "assistant") {
      return (
        <div
          key={i}
          style={{
            alignSelf: "flex-end",
            maxWidth: "90%",
            display: "flex",
            flexDirection: "column",
            alignItems: "flex-end",
            gap: "4px",
          }}>
          {msg.content && (
            <div
              className="admin_anruf_bubble--assistant"
              style={{
                padding: "8px 12px",
                borderRadius: "12px 12px 2px 12px",
                fontSize: "13px",
                whiteSpace: "pre-wrap",
              }}>
              {msg.content}
            </div>
          )}
          {Array.isArray(msg.tool_calls) &&
            msg.tool_calls.map((tc, j) => {
              const toolName =
                (tc && tc.tool_name) ||
                (tc && tc.function && tc.function.name) ||
                "";
              const argsRaw =
                (tc && tc.args_raw) ||
                (tc && tc.function && tc.function.arguments) ||
                "";
              const matched = tc && tc.id ? resultByCallId[tc.id] : null;
              const resultSummary = matched
                ? summarizeAdminToolResult(matched.msg.content)
                : "";
              return (
                <div
                  key={j}
                  style={{
                    padding: "8px 10px",
                    borderRadius: "8px",
                    background: "rgba(255,90,95,0.08)",
                    border: "1px dashed var(--admin-accent, #FF5A5F)",
                    color: "var(--admin-text, #222)",
                    fontSize: "11px",
                    fontFamily: "monospace",
                    display: "flex",
                    flexDirection: "column",
                    gap: "6px",
                  }}>
                  <div>
                    <span style={{ fontWeight: 700 }}>tool_call</span>{" "}
                    {toolName}({argsRaw})
                  </div>
                  {matched && (
                    <div
                      style={{
                        paddingTop: "6px",
                        borderTop:
                          "1px solid var(--cn-border, #ebebeb)",
                        color: "var(--admin-text-secondary, #717171)",
                        whiteSpace: "pre-wrap",
                      }}>
                      <span style={{ fontWeight: 700 }}>
                        → tool_result
                      </span>
                      {matched.msg.tool_name
                        ? " · " + matched.msg.tool_name
                        : ""}
                      {resultSummary ? " · " + resultSummary : ""}
                    </div>
                  )}
                </div>
              );
            })}
        </div>
      );
    }
    if (msg.role === "tool") {
      if (consumed.has(i)) return null;
      // Orphan: kein passender assistant.tool_call → Legacy-Result-Look.
      const summary = summarizeAdminToolResult(msg.content);
      return (
        <div
          key={i}
          style={{
            alignSelf: "flex-start",
            maxWidth: "90%",
            padding: "6px 10px",
            borderRadius: "8px",
            background: "var(--admin-bg-input, #fff)",
            border: "1px solid var(--cn-border, #ebebeb)",
            color: "var(--admin-text-secondary, #717171)",
            fontSize: "11px",
            fontFamily: "monospace",
            whiteSpace: "pre-wrap",
          }}>
          <div style={{ fontWeight: 700, marginBottom: "2px" }}>
            tool_result · {msg.tool_name || ""}
          </div>
          {summary}
        </div>
      );
    }
    return null;
  });
}

/** Einnahmen-Liste: Datum wie 02.5 oder 30.4 (Tag zweistellig, Monat ohne führende Null). */
function formatEarningsRowDate(raw) {
  if (raw == null || String(raw).trim() === "") return "–";
  const s0 = String(raw).trim();
  const s = s0.includes("T") ? s0 : `${s0.slice(0, 10)}T12:00:00`;
  const dt = new Date(s);
  if (Number.isNaN(dt.getTime())) return "–";
  const d = dt.getDate();
  const m = dt.getMonth() + 1;
  return `${String(d).padStart(2, "0")}.${m}`;
}

function formatEarningsAmountDisplay(amountCents, currency) {
  if (amountCents == null || !Number.isFinite(Number(amountCents))) return "–";
  const euros = Number(amountCents) / 100;
  const isWhole = Math.abs(euros - Math.round(euros)) < 1e-6;
  const num = isWhole
    ? String(Math.round(euros))
    : euros.toLocaleString("de-DE", {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
      });
  const c = currency ? String(currency).toUpperCase() : "EUR";
  return c === "EUR" || !currency ? `${num}€` : `${num} ${c}`;
}

function earningsRowCustomerLabel(notes) {
  const t = notes != null ? String(notes).trim() : "";
  if (!t) return "–";
  const line = t.split(/\r?\n/)[0].trim();
  return line || "–";
}

function earningsPostCardTitleFromNotes(notes) {
  const line = earningsRowCustomerLabel(notes);
  const m = line.match(/^Akquise-Post bestätigt\s*\((.+)\)\s*$/);
  if (m) return String(m[1]).trim() || "(ohne Titel)";
  return line;
}

function formatLocalDate(d) {
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, "0");
  const dd = String(d.getDate()).padStart(2, "0");
  return `${y}-${m}-${dd}`;
}

function computeCallsDateRange(preset) {
  if (!preset) return { from: "", to: "" };
  const today = new Date();
  if (preset === "today") {
    const f = formatLocalDate(today);
    return { from: f, to: f };
  }
  if (preset === "yesterday") {
    const y = new Date(today);
    y.setDate(y.getDate() - 1);
    const f = formatLocalDate(y);
    return { from: f, to: f };
  }
  if (preset === "7d") {
    const from = new Date(today);
    from.setDate(from.getDate() - 6);
    return { from: formatLocalDate(from), to: formatLocalDate(today) };
  }
  if (preset === "30d") {
    const from = new Date(today);
    from.setDate(from.getDate() - 29);
    return { from: formatLocalDate(from), to: formatLocalDate(today) };
  }
  if (preset.startsWith("month-")) {
    const [yStr, mStr] = preset.slice(6).split("-");
    const y = Number(yStr);
    const m = Number(mStr);
    if (!y || !m) return { from: "", to: "" };
    const first = new Date(y, m - 1, 1);
    const last = new Date(y, m, 0);
    return { from: formatLocalDate(first), to: formatLocalDate(last) };
  }
  if (preset === "lastyear") {
    const y = today.getFullYear() - 1;
    return { from: `${y}-01-01`, to: `${y}-12-31` };
  }
  return { from: "", to: "" };
}

function buildCallsMonthOptions(count = 12) {
  const out = [];
  const now = new Date();
  const cursor = new Date(now.getFullYear(), now.getMonth(), 1);
  for (let i = 0; i < count; i++) {
    const y = cursor.getFullYear();
    const m = cursor.getMonth() + 1;
    const value = `month-${y}-${String(m).padStart(2, "0")}`;
    const label = cursor.toLocaleDateString("de-DE", {
      month: "long",
      year: "numeric",
    });
    out.push({ value, label });
    cursor.setMonth(cursor.getMonth() - 1);
  }
  return out;
}

const AUTO_SAVE_MS = 3000;
function AutoSaveProgressRing({ durationMs = AUTO_SAVE_MS, size = 16 }) {
  const stroke = 2.5;
  const radius = (size - stroke) / 2;
  const circumference = 2 * Math.PI * radius;
  const center = size / 2;
  return (
    <svg
      className="admin_autosave_ring"
      width={size}
      height={size}
      viewBox={`0 0 ${size} ${size}`}
      aria-hidden="true">
      <circle
        className="admin_autosave_ring_track"
        cx={center}
        cy={center}
        r={radius}
      />
      <circle
        className="admin_autosave_ring_arc"
        cx={center}
        cy={center}
        r={radius}
        strokeDasharray={circumference}
        strokeDashoffset={circumference}
        transform={`rotate(-90 ${center} ${center})`}
        style={{ animationDuration: `${durationMs}ms` }}
      />
    </svg>
  );
}
const MODULE_ICONS = [
  {
    key: "calendar",
    paths: (
      <>
        <rect x="3" y="4" width="18" height="18" rx="2" ry="2" />
        <line x1="16" y1="2" x2="16" y2="6" />
        <line x1="8" y1="2" x2="8" y2="6" />
        <line x1="3" y1="10" x2="21" y2="10" />
      </>
    ),
  },
  {
    key: "clock",
    paths: (
      <>
        <circle cx="12" cy="12" r="10" />
        <polyline points="12 6 12 12 16 14" />
      </>
    ),
  },
  {
    key: "phone",
    paths: (
      <path d="M22 16.92v3a2 2 0 0 1-2.18 2 19.79 19.79 0 0 1-8.63-3.07 19.5 19.5 0 0 1-6-6 19.79 19.79 0 0 1-3.07-8.67A2 2 0 0 1 4.11 2h3a2 2 0 0 1 2 1.72 12.84 12.84 0 0 0 .7 2.81 2 2 0 0 1-.45 2.11L8.09 9.91a16 16 0 0 0 6 6l1.27-1.27a2 2 0 0 1 2.11-.45 12.84 12.84 0 0 0 2.81.7A2 2 0 0 1 22 16.92z" />
    ),
  },
  {
    key: "mail",
    paths: (
      <>
        <path d="M4 4h16c1.1 0 2 .9 2 2v12c0 1.1-.9 2-2 2H4c-1.1 0-2-.9-2-2V6c0-1.1.9-2 2-2z" />
        <polyline points="22,6 12,13 2,6" />
      </>
    ),
  },
  {
    key: "message",
    paths: (
      <path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z" />
    ),
  },
  {
    key: "users",
    paths: (
      <>
        <path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2" />
        <circle cx="9" cy="7" r="4" />
        <path d="M23 21v-2a4 4 0 0 0-3-3.87" />
        <path d="M16 3.13a4 4 0 0 1 0 7.75" />
      </>
    ),
  },
  {
    key: "user",
    paths: (
      <>
        <path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2" />
        <circle cx="12" cy="7" r="4" />
      </>
    ),
  },
  {
    key: "building",
    paths: (
      <>
        <rect x="4" y="2" width="16" height="20" rx="1" />
        <path d="M9 22v-4h6v4" />
        <line x1="9" y1="6" x2="9.01" y2="6" />
        <line x1="15" y1="6" x2="15.01" y2="6" />
        <line x1="9" y1="10" x2="9.01" y2="10" />
        <line x1="15" y1="10" x2="15.01" y2="10" />
        <line x1="9" y1="14" x2="9.01" y2="14" />
        <line x1="15" y1="14" x2="15.01" y2="14" />
      </>
    ),
  },
  {
    key: "home",
    paths: (
      <>
        <path d="M3 9l9-7 9 7v11a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2z" />
        <polyline points="9 22 9 12 15 12 15 22" />
      </>
    ),
  },
  {
    key: "car",
    paths: (
      <>
        <path d="M14 16H9m10 0h3v-3.15a1 1 0 0 0-.84-.99L16 11l-2.7-3.6a1 1 0 0 0-.8-.4H5.24a2 2 0 0 0-1.8 1.1l-.8 1.63A6 6 0 0 0 2 12.42V16h2" />
        <circle cx="6.5" cy="16.5" r="2.5" />
        <circle cx="16.5" cy="16.5" r="2.5" />
      </>
    ),
  },
  {
    key: "wrench",
    paths: (
      <path d="M14.7 6.3a1 1 0 0 0 0 1.4l1.6 1.6a1 1 0 0 0 1.4 0l3.77-3.77a6 6 0 0 1-7.94 7.94l-6.91 6.91a2.12 2.12 0 0 1-3-3l6.91-6.91a6 6 0 0 1 7.94-7.94l-3.76 3.76z" />
    ),
  },
  {
    key: "medical",
    paths: (
      <>
        <rect x="9" y="2" width="6" height="20" rx="1" />
        <rect x="2" y="9" width="20" height="6" rx="1" />
      </>
    ),
  },
  {
    key: "scales",
    paths: (
      <>
        <path d="M12 3v18" />
        <line x1="5" y1="21" x2="19" y2="21" />
        <line x1="6" y1="6" x2="18" y2="6" />
        <path d="M3 13l3-7 3 7" />
        <path d="M15 13l3-7 3 7" />
      </>
    ),
  },
  {
    key: "utensils",
    paths: (
      <>
        <path d="M3 2v7c0 1.1.9 2 2 2h4a2 2 0 0 0 2-2V2" />
        <path d="M7 2v20" />
        <path d="M21 15V2v0a5 5 0 0 0-5 5v6c0 1.1.9 2 2 2h3zm0 0v7" />
      </>
    ),
  },
  {
    key: "coffee",
    paths: (
      <>
        <path d="M18 8h1a4 4 0 0 1 0 8h-1" />
        <path d="M2 8h16v9a4 4 0 0 1-4 4H6a4 4 0 0 1-4-4V8z" />
        <line x1="6" y1="1" x2="6" y2="4" />
        <line x1="10" y1="1" x2="10" y2="4" />
        <line x1="14" y1="1" x2="14" y2="4" />
      </>
    ),
  },
  {
    key: "cart",
    paths: (
      <>
        <circle cx="9" cy="21" r="1" />
        <circle cx="20" cy="21" r="1" />
        <path d="M1 1h4l2.68 13.39a2 2 0 0 0 2 1.61h9.72a2 2 0 0 0 2-1.61L23 6H6" />
      </>
    ),
  },
  {
    key: "tag",
    paths: (
      <>
        <path d="M20.59 13.41l-7.17 7.17a2 2 0 0 1-2.83 0L2 12V2h10l8.59 8.59a2 2 0 0 1 0 2.82z" />
        <line x1="7" y1="7" x2="7.01" y2="7" />
      </>
    ),
  },
  {
    key: "card",
    paths: (
      <>
        <rect x="1" y="4" width="22" height="16" rx="2" ry="2" />
        <line x1="1" y1="10" x2="23" y2="10" />
      </>
    ),
  },
  {
    key: "file",
    paths: (
      <>
        <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" />
        <polyline points="14 2 14 8 20 8" />
      </>
    ),
  },
  {
    key: "folder",
    paths: (
      <path d="M22 19a2 2 0 0 1-2 2H4a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h5l2 3h9a2 2 0 0 1 2 2z" />
    ),
  },
  {
    key: "database",
    paths: (
      <>
        <ellipse cx="12" cy="5" rx="9" ry="3" />
        <path d="M21 12c0 1.66-4 3-9 3s-9-1.34-9-3" />
        <path d="M3 5v14c0 1.66 4 3 9 3s9-1.34 9-3V5" />
      </>
    ),
  },
  {
    key: "search",
    paths: (
      <>
        <circle cx="11" cy="11" r="8" />
        <line x1="21" y1="21" x2="16.65" y2="16.65" />
      </>
    ),
  },
  {
    key: "star",
    paths: (
      <polygon points="12 2 15.09 8.26 22 9.27 17 14.14 18.18 21.02 12 17.77 5.82 21.02 7 14.14 2 9.27 8.91 8.26 12 2" />
    ),
  },
  {
    key: "heart",
    paths: (
      <path d="M20.84 4.61a5.5 5.5 0 0 0-7.78 0L12 5.67l-1.06-1.06a5.5 5.5 0 0 0-7.78 7.78l1.06 1.06L12 21.23l7.78-7.78 1.06-1.06a5.5 5.5 0 0 0 0-7.78z" />
    ),
  },
  {
    key: "settings",
    paths: (
      <>
        <circle cx="12" cy="12" r="3" />
        <path d="M19.4 15a1.65 1.65 0 0 0 .33 1.82l.06.06a2 2 0 0 1-2.83 2.83l-.06-.06a1.65 1.65 0 0 0-1.82-.33 1.65 1.65 0 0 0-1 1.51V21a2 2 0 0 1-4 0v-.09A1.65 1.65 0 0 0 9 19.4a1.65 1.65 0 0 0-1.82.33l-.06.06a2 2 0 0 1-2.83-2.83l.06-.06A1.65 1.65 0 0 0 4.6 15a1.65 1.65 0 0 0-1.51-1H3a2 2 0 0 1 0-4h.09A1.65 1.65 0 0 0 4.6 9a1.65 1.65 0 0 0-.33-1.82l-.06-.06a2 2 0 0 1 2.83-2.83l.06.06A1.65 1.65 0 0 0 9 4.6a1.65 1.65 0 0 0 1-1.51V3a2 2 0 0 1 4 0v.09a1.65 1.65 0 0 0 1 1.51 1.65 1.65 0 0 0 1.82-.33l.06-.06a2 2 0 0 1 2.83 2.83l-.06.06A1.65 1.65 0 0 0 19.4 9a1.65 1.65 0 0 0 1.51 1H21a2 2 0 0 1 0 4h-.09a1.65 1.65 0 0 0-1.51 1z" />
      </>
    ),
  },
  {
    key: "package",
    paths: (
      <>
        <line x1="16.5" y1="9.4" x2="7.5" y2="4.21" />
        <path d="M21 16V8a2 2 0 0 0-1-1.73l-7-4a2 2 0 0 0-2 0l-7 4A2 2 0 0 0 3 8v8a2 2 0 0 0 1 1.73l7 4a2 2 0 0 0 2 0l7-4A2 2 0 0 0 21 16z" />
        <polyline points="3.27 6.96 12 12.01 20.73 6.96" />
        <line x1="12" y1="22.08" x2="12" y2="12" />
      </>
    ),
  },
  {
    key: "truck",
    paths: (
      <>
        <rect x="1" y="3" width="15" height="13" />
        <polygon points="16 8 20 8 23 11 23 16 16 16 16 8" />
        <circle cx="5.5" cy="18.5" r="2.5" />
        <circle cx="18.5" cy="18.5" r="2.5" />
      </>
    ),
  },
  {
    key: "globe",
    paths: (
      <>
        <circle cx="12" cy="12" r="10" />
        <line x1="2" y1="12" x2="22" y2="12" />
        <path d="M12 2a15.3 15.3 0 0 1 4 10 15.3 15.3 0 0 1-4 10 15.3 15.3 0 0 1-4-10 15.3 15.3 0 0 1 4-10z" />
      </>
    ),
  },
  {
    key: "pin",
    paths: (
      <>
        <path d="M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 0 1 18 0z" />
        <circle cx="12" cy="10" r="3" />
      </>
    ),
  },
  {
    key: "info",
    paths: (
      <>
        <circle cx="12" cy="12" r="10" />
        <line x1="12" y1="16" x2="12" y2="12" />
        <line x1="12" y1="8" x2="12.01" y2="8" />
      </>
    ),
  },
  {
    key: "briefcase",
    paths: (
      <>
        <rect x="2" y="7" width="20" height="14" rx="2" ry="2" />
        <path d="M16 21V5a2 2 0 0 0-2-2h-4a2 2 0 0 0-2 2v16" />
      </>
    ),
  },
  {
    key: "bell",
    paths: (
      <>
        <path d="M18 8a6 6 0 0 0-12 0c0 7-3 9-3 9h18s-3-2-3-9" />
        <path d="M13.73 21a2 2 0 0 1-3.46 0" />
      </>
    ),
  },
  {
    key: "camera",
    paths: (
      <>
        <path d="M23 19a2 2 0 0 1-2 2H3a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h4l2-3h6l2 3h4a2 2 0 0 1 2 2z" />
        <circle cx="12" cy="13" r="4" />
      </>
    ),
  },
  {
    key: "image",
    paths: (
      <>
        <rect x="3" y="3" width="18" height="18" rx="2" ry="2" />
        <circle cx="8.5" cy="8.5" r="1.5" />
        <polyline points="21 15 16 10 5 21" />
      </>
    ),
  },
  {
    key: "video",
    paths: (
      <>
        <polygon points="23 7 16 12 23 17 23 7" />
        <rect x="1" y="5" width="15" height="14" rx="2" ry="2" />
      </>
    ),
  },
  {
    key: "music",
    paths: (
      <>
        <path d="M9 18V5l12-2v13" />
        <circle cx="6" cy="18" r="3" />
        <circle cx="18" cy="16" r="3" />
      </>
    ),
  },
  {
    key: "mic",
    paths: (
      <>
        <rect x="9" y="2" width="6" height="12" rx="3" />
        <path d="M19 10v2a7 7 0 0 1-14 0v-2" />
        <line x1="12" y1="19" x2="12" y2="23" />
        <line x1="8" y1="23" x2="16" y2="23" />
      </>
    ),
  },
  {
    key: "lock",
    paths: (
      <>
        <rect x="3" y="11" width="18" height="11" rx="2" ry="2" />
        <path d="M7 11V7a5 5 0 0 1 10 0v4" />
      </>
    ),
  },
  {
    key: "key",
    paths: (
      <>
        <circle cx="7.5" cy="15.5" r="4.5" />
        <line x1="11" y1="12" x2="22" y2="2" />
        <line x1="18" y1="6" x2="22" y2="2" />
        <line x1="15" y1="9" x2="18" y2="6" />
      </>
    ),
  },
  {
    key: "eye",
    paths: (
      <>
        <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z" />
        <circle cx="12" cy="12" r="3" />
      </>
    ),
  },
  {
    key: "edit",
    paths: (
      <>
        <path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7" />
        <path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z" />
      </>
    ),
  },
  {
    key: "trash",
    paths: (
      <>
        <polyline points="3 6 5 6 21 6" />
        <path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2" />
      </>
    ),
  },
  {
    key: "download",
    paths: (
      <>
        <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" />
        <polyline points="7 10 12 15 17 10" />
        <line x1="12" y1="15" x2="12" y2="3" />
      </>
    ),
  },
  {
    key: "upload",
    paths: (
      <>
        <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" />
        <polyline points="17 8 12 3 7 8" />
        <line x1="12" y1="3" x2="12" y2="15" />
      </>
    ),
  },
  {
    key: "plus",
    paths: (
      <>
        <line x1="12" y1="5" x2="12" y2="19" />
        <line x1="5" y1="12" x2="19" y2="12" />
      </>
    ),
  },
  { key: "check", paths: <polyline points="20 6 9 17 4 12" /> },
  {
    key: "x",
    paths: (
      <>
        <line x1="18" y1="6" x2="6" y2="18" />
        <line x1="6" y1="6" x2="18" y2="18" />
      </>
    ),
  },
  {
    key: "euro",
    paths: (
      <>
        <path d="M19 5a7 7 0 1 0 0 14" />
        <line x1="3" y1="10" x2="14" y2="10" />
        <line x1="3" y1="14" x2="14" y2="14" />
      </>
    ),
  },
  {
    key: "gift",
    paths: (
      <>
        <polyline points="20 12 20 22 4 22 4 12" />
        <rect x="2" y="7" width="20" height="5" />
        <line x1="12" y1="22" x2="12" y2="7" />
        <path d="M12 7H7.5a2.5 2.5 0 0 1 0-5C11 2 12 7 12 7z" />
        <path d="M12 7h4.5a2.5 2.5 0 0 0 0-5C13 2 12 7 12 7z" />
      </>
    ),
  },
  {
    key: "send",
    paths: (
      <>
        <line x1="22" y1="2" x2="11" y2="13" />
        <polygon points="22 2 15 22 11 13 2 9 22 2" />
      </>
    ),
  },
  {
    key: "link",
    paths: (
      <>
        <path d="M10 13a5 5 0 0 0 7.54.54l3-3a5 5 0 0 0-7.07-7.07l-1.72 1.71" />
        <path d="M14 11a5 5 0 0 0-7.54-.54l-3 3a5 5 0 0 0 7.07 7.07l1.71-1.71" />
      </>
    ),
  },
  {
    key: "share",
    paths: (
      <>
        <circle cx="18" cy="5" r="3" />
        <circle cx="6" cy="12" r="3" />
        <circle cx="18" cy="19" r="3" />
        <line x1="8.59" y1="13.51" x2="15.42" y2="17.49" />
        <line x1="15.41" y1="6.51" x2="8.59" y2="10.49" />
      </>
    ),
  },
  {
    key: "bookmark",
    paths: <path d="M19 21l-7-5-7 5V5a2 2 0 0 1 2-2h10a2 2 0 0 1 2 2z" />,
  },
  {
    key: "flag",
    paths: (
      <>
        <path d="M4 15s1-1 4-1 5 2 8 2 4-1 4-1V3s-1 1-4 1-5-2-8-2-4 1-4 1z" />
        <line x1="4" y1="22" x2="4" y2="15" />
      </>
    ),
  },
  {
    key: "lightbulb",
    paths: (
      <>
        <path d="M9 18h6" />
        <path d="M10 22h4" />
        <path d="M12 2a7 7 0 0 0-4 12.74V18h8v-3.26A7 7 0 0 0 12 2z" />
      </>
    ),
  },
  {
    key: "shield",
    paths: <path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z" />,
  },
  {
    key: "cloud",
    paths: <path d="M18 10h-1.26A8 8 0 1 0 9 20h9a5 5 0 0 0 0-10z" />,
  },
  {
    key: "sun",
    paths: (
      <>
        <circle cx="12" cy="12" r="5" />
        <line x1="12" y1="1" x2="12" y2="3" />
        <line x1="12" y1="21" x2="12" y2="23" />
        <line x1="4.22" y1="4.22" x2="5.64" y2="5.64" />
        <line x1="18.36" y1="18.36" x2="19.78" y2="19.78" />
        <line x1="1" y1="12" x2="3" y2="12" />
        <line x1="21" y1="12" x2="23" y2="12" />
        <line x1="4.22" y1="19.78" x2="5.64" y2="18.36" />
        <line x1="18.36" y1="5.64" x2="19.78" y2="4.22" />
      </>
    ),
  },
  {
    key: "scissors",
    paths: (
      <>
        <circle cx="6" cy="6" r="3" />
        <circle cx="6" cy="18" r="3" />
        <line x1="20" y1="4" x2="8.12" y2="15.88" />
        <line x1="14.47" y1="14.48" x2="20" y2="20" />
        <line x1="8.12" y1="8.12" x2="12" y2="12" />
      </>
    ),
  },
  {
    key: "hammer",
    paths: (
      <>
        <path d="M15 12l-8.5 8.5a2.121 2.121 0 0 1-3-3L12 9" />
        <path d="M17.64 15L22 10.64" />
        <path d="M20.91 11.7l-1.25-1.25c-.6-.6-.93-1.4-.93-2.25v-.86L16.01 4.6a5.56 5.56 0 0 0-3.94-1.64H9l.92.82A6.18 6.18 0 0 1 12 8.4v1.56l2 2h2.47l2.26 1.91" />
      </>
    ),
  },
  {
    key: "book",
    paths: (
      <>
        <path d="M4 19.5A2.5 2.5 0 0 1 6.5 17H20" />
        <path d="M6.5 2H20v20H6.5A2.5 2.5 0 0 1 4 19.5v-15A2.5 2.5 0 0 1 6.5 2z" />
      </>
    ),
  },
  {
    key: "graduation",
    paths: (
      <>
        <path d="M22 10v6M2 10l10-5 10 5-10 5z" />
        <path d="M6 12v5c0 1.66 4 3 6 3s6-1.34 6-3v-5" />
      </>
    ),
  },
  {
    key: "trophy",
    paths: (
      <>
        <path d="M6 9H4.5a2.5 2.5 0 0 1 0-5H6" />
        <path d="M18 9h1.5a2.5 2.5 0 0 0 0-5H18" />
        <path d="M4 22h16" />
        <path d="M10 14.66V17c0 .55-.47.98-.97 1.21C7.85 18.75 7 20.24 7 22" />
        <path d="M14 14.66V17c0 .55.47.98.97 1.21C16.15 18.75 17 20.24 17 22" />
        <path d="M18 2H6v7a6 6 0 0 0 12 0V2z" />
      </>
    ),
  },
  {
    key: "smartphone",
    paths: (
      <>
        <rect x="5" y="2" width="14" height="20" rx="2" ry="2" />
        <line x1="12" y1="18" x2="12.01" y2="18" />
      </>
    ),
  },
  {
    key: "plane",
    paths: (
      <path d="M17.8 19.2 16 11l3.5-3.5C21 6 21.5 4 21 3c-1-.5-3 0-4.5 1.5L13 8 4.8 6.2c-.5-.1-.9.1-1.1.5l-.3.5c-.2.5-.1 1 .3 1.3L9 12l-2 3H4l-1 1 3 2 2 3 1-1v-3l3-2 3.5 5.3c.3.4.8.5 1.3.3l.5-.2c.4-.3.6-.7.5-1.2z" />
    ),
  },
  {
    key: "dumbbell",
    paths: (
      <>
        <path d="M6 5v14" />
        <path d="M18 5v14" />
        <path d="M3 8v8" />
        <path d="M21 8v8" />
        <line x1="6" y1="12" x2="18" y2="12" />
      </>
    ),
  },
  {
    key: "paw",
    paths: (
      <>
        <circle cx="11" cy="4" r="2" />
        <circle cx="18" cy="8" r="2" />
        <circle cx="20" cy="16" r="2" />
        <circle cx="4" cy="12" r="2" />
        <path d="M9 10a5 5 0 0 1 5 5v3.5a3.5 3.5 0 0 1-7 0v-3.5a5 5 0 0 1 2-4z" />
      </>
    ),
  },
  {
    key: "baby",
    paths: (
      <>
        <circle cx="12" cy="12" r="10" />
        <circle cx="9" cy="11" r="1" />
        <circle cx="15" cy="11" r="1" />
        <path d="M9 16c.5.5 1.5 1 3 1s2.5-.5 3-1" />
      </>
    ),
  },
  {
    key: "bus",
    paths: (
      <>
        <rect x="4" y="3" width="16" height="14" rx="2" ry="2" />
        <line x1="4" y1="11" x2="20" y2="11" />
        <circle cx="8" cy="19" r="1.5" />
        <circle cx="16" cy="19" r="1.5" />
        <line x1="6" y1="7" x2="10" y2="7" />
        <line x1="14" y1="7" x2="18" y2="7" />
      </>
    ),
  },
  {
    key: "bicycle",
    paths: (
      <>
        <circle cx="5.5" cy="17.5" r="3.5" />
        <circle cx="18.5" cy="17.5" r="3.5" />
        <path d="M15 6a1 1 0 1 0 0-2 1 1 0 0 0 0 2zm-3 11.5V14l-3-3 4-3 2 3h2" />
      </>
    ),
  },
  {
    key: "motorcycle",
    paths: (
      <>
        <circle cx="5.5" cy="17.5" r="3.5" />
        <circle cx="18.5" cy="17.5" r="3.5" />
        <path d="M15 6h2l3 8" />
        <path d="M5.5 17.5L9 10h6" />
      </>
    ),
  },
  {
    key: "train",
    paths: (
      <>
        <rect x="4" y="3" width="16" height="14" rx="3" ry="3" />
        <line x1="4" y1="11" x2="20" y2="11" />
        <circle cx="8.5" cy="14.5" r="1" />
        <circle cx="15.5" cy="14.5" r="1" />
        <line x1="9" y1="17" x2="7" y2="21" />
        <line x1="15" y1="17" x2="17" y2="21" />
      </>
    ),
  },
  {
    key: "ship",
    paths: (
      <>
        <path d="M2 16l1.5-5h17L22 16" />
        <path d="M3 16h18s-1 5-9 5-9-5-9-5z" />
        <path d="M12 11V3l5 4" />
      </>
    ),
  },
  {
    key: "pizza",
    paths: (
      <>
        <path d="M12 2L4 22h16z" />
        <circle cx="9.5" cy="14" r="1" />
        <circle cx="14" cy="13" r="1" />
        <circle cx="12" cy="18" r="1" />
      </>
    ),
  },
  {
    key: "burger",
    paths: (
      <>
        <path d="M3 8a6 6 0 0 1 6-6h6a6 6 0 0 1 6 6" />
        <line x1="3" y1="12" x2="21" y2="12" />
        <path d="M3 16a4 4 0 0 0 4 4h10a4 4 0 0 0 4-4" />
        <line x1="3" y1="20" x2="21" y2="20" />
      </>
    ),
  },
  {
    key: "wine",
    paths: (
      <>
        <path d="M8 2h8l-1 7a4 4 0 0 1-6 0z" />
        <line x1="12" y1="13" x2="12" y2="22" />
        <line x1="8" y1="22" x2="16" y2="22" />
      </>
    ),
  },
  {
    key: "beer",
    paths: (
      <>
        <rect x="6" y="3" width="11" height="18" rx="1" />
        <path d="M17 7h2a2 2 0 0 1 2 2v6a2 2 0 0 1-2 2h-2" />
        <line x1="9" y1="7" x2="9" y2="18" />
        <line x1="13" y1="7" x2="13" y2="18" />
      </>
    ),
  },
  {
    key: "cake",
    paths: (
      <>
        <line x1="3" y1="21" x2="21" y2="21" />
        <path d="M5 21V11a2 2 0 0 1 2-2h10a2 2 0 0 1 2 2v10" />
        <line x1="5" y1="14" x2="19" y2="14" />
        <line x1="12" y1="9" x2="12" y2="5" />
        <circle cx="12" cy="3" r="1" />
      </>
    ),
  },
  {
    key: "ice-cream",
    paths: (
      <>
        <path d="M12 2a4 4 0 0 0-4 4 4 4 0 0 0 1 2.65L12 22l3-13.35A4 4 0 0 0 16 6a4 4 0 0 0-4-4z" />
        <line x1="8" y1="10" x2="16" y2="10" />
      </>
    ),
  },
  {
    key: "football",
    paths: (
      <>
        <circle cx="12" cy="12" r="10" />
        <polygon points="12 7 16 10 14.5 14.5 9.5 14.5 8 10" />
      </>
    ),
  },
  {
    key: "basketball",
    paths: (
      <>
        <circle cx="12" cy="12" r="10" />
        <line x1="2" y1="12" x2="22" y2="12" />
        <path d="M12 2a14.5 14.5 0 0 1 0 20" />
        <path d="M12 2a14.5 14.5 0 0 0 0 20" />
      </>
    ),
  },
  {
    key: "drill",
    paths: (
      <>
        <path d="M14 4h6v6" />
        <path d="M14 10h6v4l-3 3-4-4" />
        <path d="M11 11l-7 7v3h3l7-7" />
      </>
    ),
  },
  {
    key: "paintbrush",
    paths: (
      <>
        <path d="M18 2l4 4-9 9-4-4z" />
        <path d="M9 11l-7 11 11-7" />
      </>
    ),
  },
  {
    key: "ruler",
    paths: (
      <>
        <path d="M21 3l-3-3-15 15 3 3z" />
        <line x1="6" y1="9" x2="9" y2="12" />
        <line x1="9" y1="6" x2="12" y2="9" />
        <line x1="12" y1="3" x2="15" y2="6" />
        <line x1="15" y1="12" x2="18" y2="15" />
      </>
    ),
  },
  {
    key: "stethoscope",
    paths: (
      <>
        <path d="M5 2v6a5 5 0 0 0 10 0V2" />
        <path d="M3 2h4" />
        <path d="M13 2h4" />
        <path d="M10 13v3a4 4 0 0 0 8 0v-1" />
        <circle cx="20" cy="13" r="2" />
      </>
    ),
  },
  {
    key: "pill",
    paths: (
      <>
        <path d="M10.5 20.5l10-10a4.95 4.95 0 1 0-7-7l-10 10a4.95 4.95 0 1 0 7 7z" />
        <line x1="8.5" y1="8.5" x2="15.5" y2="15.5" />
      </>
    ),
  },
  {
    key: "syringe",
    paths: (
      <>
        <path d="M18 2l4 4" />
        <path d="M21 5L11 15" />
        <path d="M11 15l-2-2" />
        <path d="M9 13l-7 7v2h2l7-7" />
        <path d="M13 11l3 3" />
      </>
    ),
  },
  {
    key: "tooth",
    paths: (
      <path d="M12 5.5c-1.5-1.5-3-2-4.5-2C5 3.5 3 5.5 3 8c0 2 1 3.5 1 5s-.5 4.5 0 6.5c.4 1.5 1 1.5 2 1.5s1.5-1.5 2-3 .5-4 2-4 1.5 2.5 2 4 1 3 2 3 1.6 0 2-1.5c.5-2 0-5 0-6.5s1-3 1-5c0-2.5-2-4.5-4.5-4.5C15 3.5 13.5 4 12 5.5z" />
    ),
  },
  {
    key: "chip",
    paths: (
      <>
        <rect x="4" y="4" width="16" height="16" rx="2" />
        <rect x="9" y="9" width="6" height="6" />
        <line x1="9" y1="2" x2="9" y2="4" />
        <line x1="15" y1="2" x2="15" y2="4" />
        <line x1="9" y1="20" x2="9" y2="22" />
        <line x1="15" y1="20" x2="15" y2="22" />
        <line x1="2" y1="9" x2="4" y2="9" />
        <line x1="2" y1="15" x2="4" y2="15" />
        <line x1="20" y1="9" x2="22" y2="9" />
        <line x1="20" y1="15" x2="22" y2="15" />
      </>
    ),
  },
  {
    key: "server",
    paths: (
      <>
        <rect x="2" y="2" width="20" height="8" rx="2" />
        <rect x="2" y="14" width="20" height="8" rx="2" />
        <line x1="6" y1="6" x2="6.01" y2="6" />
        <line x1="6" y1="18" x2="6.01" y2="18" />
      </>
    ),
  },
  {
    key: "code",
    paths: (
      <>
        <polyline points="16 18 22 12 16 6" />
        <polyline points="8 6 2 12 8 18" />
      </>
    ),
  },
  {
    key: "terminal",
    paths: (
      <>
        <polyline points="4 17 10 11 4 5" />
        <line x1="12" y1="19" x2="20" y2="19" />
      </>
    ),
  },
  {
    key: "bug",
    paths: (
      <>
        <rect x="8" y="6" width="8" height="14" rx="4" />
        <path d="M16 13h6" />
        <path d="M2 13h6" />
        <path d="M12 6V3" />
        <path d="M9 4l3 2 3-2" />
        <path d="M16 16l4 3" />
        <path d="M8 16l-4 3" />
        <path d="M16 9l4-3" />
        <path d="M8 9L4 6" />
      </>
    ),
  },
  {
    key: "voicemail",
    paths: (
      <>
        <circle cx="6" cy="12" r="4" />
        <circle cx="18" cy="12" r="4" />
        <line x1="6" y1="16" x2="18" y2="16" />
      </>
    ),
  },
  {
    key: "hourglass",
    paths: (
      <>
        <path d="M5 22h14" />
        <path d="M5 2h14" />
        <path d="M17 22v-4.17a2 2 0 0 0-.59-1.42L12 12l-4.41 4.41A2 2 0 0 0 7 17.83V22" />
        <path d="M7 2v4.17a2 2 0 0 0 .59 1.42L12 12l4.41-4.41A2 2 0 0 0 17 6.17V2" />
      </>
    ),
  },
  {
    key: "alarm",
    paths: (
      <>
        <circle cx="12" cy="13" r="8" />
        <path d="M12 9v4l2 2" />
        <path d="M5 3L2 6" />
        <path d="M22 6l-3-3" />
        <path d="M6 19l-2 2" />
        <path d="M18 19l2 2" />
      </>
    ),
  },
  {
    key: "stopwatch",
    paths: (
      <>
        <circle cx="12" cy="14" r="8" />
        <path d="M12 14V9" />
        <line x1="9" y1="2" x2="15" y2="2" />
      </>
    ),
  },
  {
    key: "snowflake",
    paths: (
      <>
        <line x1="2" y1="12" x2="22" y2="12" />
        <line x1="12" y1="2" x2="12" y2="22" />
        <path d="M20 16l-4-4 4-4" />
        <path d="M4 8l4 4-4 4" />
        <path d="M16 4l-4 4-4-4" />
        <path d="M8 20l4-4 4 4" />
      </>
    ),
  },
  {
    key: "thermometer",
    paths: <path d="M14 4v10.54a4 4 0 1 1-4 0V4a2 2 0 0 1 4 0z" />,
  },
  {
    key: "wallet",
    paths: (
      <>
        <path d="M21 12V7H5a2 2 0 0 1 0-4h14v4" />
        <path d="M3 5v14a2 2 0 0 0 2 2h16v-5" />
        <path d="M18 12a2 2 0 0 0 0 4h4v-4z" />
      </>
    ),
  },
  {
    key: "coins",
    paths: (
      <>
        <circle cx="8" cy="8" r="6" />
        <path d="M18.09 10.37A6 6 0 1 1 10.34 18" />
        <path d="M7 6h1v4" />
        <path d="M16.71 13.88l.7.71-2.82 2.82" />
      </>
    ),
  },
  {
    key: "receipt",
    paths: (
      <>
        <path d="M4 2v20l2-1 2 1 2-1 2 1 2-1 2 1 2-1 2 1V2l-2 1-2-1-2 1-2-1-2 1-2-1-2 1z" />
        <line x1="8" y1="8" x2="16" y2="8" />
        <line x1="8" y1="12" x2="16" y2="12" />
        <line x1="8" y1="16" x2="13" y2="16" />
      </>
    ),
  },
  {
    key: "calculator",
    paths: (
      <>
        <rect x="4" y="2" width="16" height="20" rx="2" />
        <line x1="8" y1="6" x2="16" y2="6" />
        <line x1="8" y1="11" x2="8.01" y2="11" />
        <line x1="12" y1="11" x2="12.01" y2="11" />
        <line x1="16" y1="11" x2="16.01" y2="11" />
        <line x1="8" y1="15" x2="8.01" y2="15" />
        <line x1="12" y1="15" x2="12.01" y2="15" />
        <line x1="16" y1="15" x2="16.01" y2="15" />
        <line x1="8" y1="19" x2="16" y2="19" />
      </>
    ),
  },
  {
    key: "bank",
    paths: (
      <>
        <line x1="3" y1="22" x2="21" y2="22" />
        <line x1="6" y1="18" x2="6" y2="11" />
        <line x1="10" y1="18" x2="10" y2="11" />
        <line x1="14" y1="18" x2="14" y2="11" />
        <line x1="18" y1="18" x2="18" y2="11" />
        <polygon points="12 2 20 7 4 7" />
      </>
    ),
  },
  {
    key: "clipboard",
    paths: (
      <>
        <rect x="8" y="2" width="8" height="4" rx="1" ry="1" />
        <path d="M16 4h2a2 2 0 0 1 2 2v14a2 2 0 0 1-2 2H6a2 2 0 0 1-2-2V6a2 2 0 0 1 2-2h2" />
      </>
    ),
  },
  {
    key: "paperclip",
    paths: (
      <path d="M21.44 11.05l-9.19 9.19a6 6 0 0 1-8.49-8.49l9.19-9.19a4 4 0 0 1 5.66 5.66l-9.2 9.19a2 2 0 0 1-2.83-2.83l8.49-8.48" />
    ),
  },
  {
    key: "warning",
    paths: (
      <>
        <path d="M10.29 3.86L1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0z" />
        <line x1="12" y1="9" x2="12" y2="13" />
        <line x1="12" y1="17" x2="12.01" y2="17" />
      </>
    ),
  },
  {
    key: "question",
    paths: (
      <>
        <circle cx="12" cy="12" r="10" />
        <path d="M9.09 9a3 3 0 0 1 5.83 1c0 2-3 3-3 3" />
        <line x1="12" y1="17" x2="12.01" y2="17" />
      </>
    ),
  },
  {
    key: "ban",
    paths: (
      <>
        <circle cx="12" cy="12" r="10" />
        <line x1="4.93" y1="4.93" x2="19.07" y2="19.07" />
      </>
    ),
  },
  { key: "tree", paths: <path d="M12 2L4 14h4v8h8v-8h4z" /> },
  { key: "mountain", paths: <path d="M8 3l4 8 5-5 5 15H2L8 3z" /> },
  { key: "droplet", paths: <path d="M12 2.69l5.66 5.66a8 8 0 1 1-11.31 0z" /> },
  {
    key: "ticket",
    paths: (
      <>
        <path d="M2 9a3 3 0 0 1 0 6v2a2 2 0 0 0 2 2h16a2 2 0 0 0 2-2v-2a3 3 0 0 1 0-6V7a2 2 0 0 0-2-2H4a2 2 0 0 0-2 2z" />
        <line x1="13" y1="5" x2="13" y2="7" />
        <line x1="13" y1="17" x2="13" y2="19" />
        <line x1="13" y1="11" x2="13" y2="13" />
      </>
    ),
  },
  {
    key: "qr-code",
    paths: (
      <>
        <rect x="3" y="3" width="7" height="7" />
        <rect x="14" y="3" width="7" height="7" />
        <rect x="3" y="14" width="7" height="7" />
        <line x1="14" y1="14" x2="14" y2="17" />
        <line x1="14" y1="21" x2="17" y2="21" />
        <line x1="17" y1="17" x2="17.01" y2="17" />
        <line x1="20" y1="14" x2="20" y2="21" />
        <line x1="14" y1="20" x2="14" y2="20.01" />
      </>
    ),
  },
  {
    key: "target",
    paths: (
      <>
        <circle cx="12" cy="12" r="10" />
        <circle cx="12" cy="12" r="6" />
        <circle cx="12" cy="12" r="2" />
      </>
    ),
  },
  {
    key: "compass",
    paths: (
      <>
        <circle cx="12" cy="12" r="10" />
        <polygon points="16.24 7.76 14.12 14.12 7.76 16.24 9.88 9.88 16.24 7.76" />
      </>
    ),
  },
  {
    key: "map",
    paths: (
      <>
        <polygon points="1 6 1 22 8 18 16 22 23 18 23 2 16 6 8 2 1 6" />
        <line x1="8" y1="2" x2="8" y2="18" />
        <line x1="16" y1="6" x2="16" y2="22" />
      </>
    ),
  },
  {
    key: "flame",
    paths: (
      <path d="M8.5 14.5A2.5 2.5 0 0 0 11 12c0-1.38-.5-2-1-3-1.072-2.143-.224-4.054 2-6 .5 2.5 2 4.9 4 6.5 2 1.6 3 3.5 3 5.5a7 7 0 1 1-14 0c0-1.153.433-2.294 1-3a2.5 2.5 0 0 0 2.5 2.5z" />
    ),
  },
  {
    key: "moon",
    paths: <path d="M21 12.79A9 9 0 1 1 11.21 3 7 7 0 0 0 21 12.79z" />,
  },
  {
    key: "leaf",
    paths: (
      <>
        <path d="M11 20A7 7 0 0 1 9.8 6.1C15.5 5 17 4.48 19.2 2.96c.5 6.5.25 10.04-1.5 13.04C16.5 18.7 13.4 20 11 20z" />
        <path d="M2 21c0-3 1.85-5.36 5.08-6" />
      </>
    ),
  },
  {
    key: "anchor",
    paths: (
      <>
        <circle cx="12" cy="5" r="3" />
        <line x1="12" y1="22" x2="12" y2="8" />
        <path d="M5 12H2a10 10 0 0 0 20 0h-3" />
      </>
    ),
  },
  {
    key: "headphones",
    paths: (
      <path d="M3 18v-6a9 9 0 0 1 18 0v6a3 3 0 0 1-3 3h-2v-6h5M3 18a3 3 0 0 0 3 3h2v-6H3" />
    ),
  },
  {
    key: "laptop",
    paths: (
      <>
        <rect x="3" y="4" width="18" height="12" rx="2" />
        <line x1="2" y1="20" x2="22" y2="20" />
      </>
    ),
  },
  {
    key: "monitor",
    paths: (
      <>
        <rect x="2" y="3" width="20" height="14" rx="2" ry="2" />
        <line x1="8" y1="21" x2="16" y2="21" />
        <line x1="12" y1="17" x2="12" y2="21" />
      </>
    ),
  },
  {
    key: "printer",
    paths: (
      <>
        <polyline points="6 9 6 2 18 2 18 9" />
        <path d="M6 18H4a2 2 0 0 1-2-2v-5a2 2 0 0 1 2-2h16a2 2 0 0 1 2 2v5a2 2 0 0 1-2 2h-2" />
        <rect x="6" y="14" width="12" height="8" />
      </>
    ),
  },
  {
    key: "chef-hat",
    paths: (
      <>
        <path d="M5 13a4 4 0 0 1 2.7-3.8A3.5 3.5 0 0 1 12 5a3.5 3.5 0 0 1 4.3 4.2A4 4 0 0 1 19 13v4H5z" />
        <line x1="5" y1="17" x2="19" y2="17" />
        <line x1="5" y1="20" x2="19" y2="20" />
      </>
    ),
  },
  {
    key: "dish",
    paths: (
      <>
        <ellipse cx="12" cy="15" rx="9" ry="2" />
        <path d="M3 15c0-3 4-5 9-5s9 2 9 5" />
        <path d="M9 6c0 1 1 1 1 2s-1 1-1 2" />
        <path d="M14 5c0 1 1 1 1 2s-1 1-1 2" />
      </>
    ),
  },
  {
    key: "bread",
    paths: (
      <>
        <path d="M4 14c0-4 4-6 8-6s8 2 8 6v4a2 2 0 0 1-2 2H6a2 2 0 0 1-2-2z" />
        <path d="M8 10v8" />
        <path d="M12 9v9" />
        <path d="M16 10v8" />
      </>
    ),
  },
  {
    key: "salad-bowl",
    paths: (
      <>
        <path d="M3 12h18a9 9 0 0 1-18 0z" />
        <path d="M9 8c1-2 4-2 5 0" />
        <path d="M14 6c1-1 3-1 3 1" />
        <path d="M7 8c-1-1-3-1-3 1" />
      </>
    ),
  },
  {
    key: "sushi",
    paths: (
      <>
        <circle cx="12" cy="12" r="5" />
        <circle cx="12" cy="12" r="2" />
        <line x1="3" y1="12" x2="7" y2="12" />
        <line x1="17" y1="12" x2="21" y2="12" />
      </>
    ),
  },
  {
    key: "noodles",
    paths: (
      <>
        <path d="M3 12h18a9 9 0 0 1-18 0z" />
        <path d="M5 8c2 1 4-1 6 0s4-1 6 0 1 0 2 0" />
        <path d="M5 11c2 1 4-1 6 0s4-1 6 0 1 0 2 0" />
      </>
    ),
  },
  {
    key: "bottle",
    paths: (
      <>
        <path d="M10 2h4v3a2 2 0 0 0 1 2c1 1 1 2 1 4v9a2 2 0 0 1-2 2h-4a2 2 0 0 1-2-2v-9c0-2 0-3 1-4a2 2 0 0 0 1-2z" />
        <line x1="10" y1="13" x2="14" y2="13" />
      </>
    ),
  },
  {
    key: "martini",
    paths: (
      <>
        <path d="M3 4h18l-9 10z" />
        <line x1="12" y1="14" x2="12" y2="22" />
        <line x1="8" y1="22" x2="16" y2="22" />
      </>
    ),
  },
  {
    key: "soda-cup",
    paths: (
      <>
        <path d="M6 7h12l-1 13a2 2 0 0 1-2 2H9a2 2 0 0 1-2-2z" />
        <line x1="14" y1="3" x2="12" y2="9" />
        <line x1="6" y1="11" x2="18" y2="11" />
      </>
    ),
  },
  {
    key: "egg",
    paths: <path d="M12 2c5 0 8 5 8 11 0 5-4 9-8 9s-8-4-8-9c0-6 3-11 8-11z" />,
  },
  {
    key: "chair",
    paths: (
      <>
        <path d="M7 5h10v8H7z" />
        <line x1="7" y1="13" x2="7" y2="22" />
        <line x1="17" y1="13" x2="17" y2="22" />
        <line x1="5" y1="13" x2="19" y2="13" />
      </>
    ),
  },
  {
    key: "table",
    paths: (
      <>
        <line x1="2" y1="9" x2="22" y2="9" />
        <line x1="2" y1="11" x2="22" y2="11" />
        <line x1="6" y1="11" x2="6" y2="20" />
        <line x1="18" y1="11" x2="18" y2="20" />
      </>
    ),
  },
  {
    key: "bar-stool",
    paths: (
      <>
        <ellipse cx="12" cy="6" rx="6" ry="1.5" />
        <line x1="7" y1="7" x2="9" y2="22" />
        <line x1="17" y1="7" x2="15" y2="22" />
        <line x1="8" y1="14" x2="16" y2="14" />
      </>
    ),
  },
  {
    key: "couch",
    paths: (
      <>
        <path d="M3 13a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2v4H3z" />
        <path d="M5 11V8a2 2 0 0 1 2-2h10a2 2 0 0 1 2 2v3" />
        <line x1="5" y1="17" x2="5" y2="20" />
        <line x1="19" y1="17" x2="19" y2="20" />
      </>
    ),
  },
];

const MODULE_ICON_MAP = MODULE_ICONS.reduce((acc, it) => {
  acc[it.key] = it.paths;
  return acc;
}, {});

function ModuleIconSvg({ name, size = 18, strokeWidth = 2 }) {
  const paths = name && MODULE_ICON_MAP[name];
  if (!paths) return null;
  return (
    <svg
      width={size}
      height={size}
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth={strokeWidth}
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true">
      {paths}
    </svg>
  );
}

/** Icons für Bereichswahl-Karten (Marketing, Sales, …). */
function AdminAreaNavIcon({ areaId, size = 24 }) {
  const svgProps = {
    width: size,
    height: size,
    viewBox: "0 0 24 24",
    fill: "none",
    stroke: "currentColor",
    strokeWidth: 2,
    strokeLinecap: "round",
    strokeLinejoin: "round",
    "aria-hidden": true,
  };
  if (areaId === "product") {
    return (
      <svg {...svgProps}>
        <path d="M21 16V8a2 2 0 0 0-1-1.73l-7-4a2 2 0 0 0-2 0l-7 4A2 2 0 0 0 3 8v8a2 2 0 0 0 1 1.73l7 4a2 2 0 0 0 2 0l7-4A2 2 0 0 0 21 16z" />
        <polyline points="3.27 6.96 12 12.01 20.73 6.96" />
        <line x1="12" y1="22.08" x2="12" y2="12" />
      </svg>
    );
  }
  if (areaId === "marketing") {
    return (
      <svg {...svgProps}>
        <path d="m3 11 18-5v12L3 13v-2z" />
        <path d="M11.6 16.8a3 3 0 1 1-5.8-1.6" />
      </svg>
    );
  }
  if (areaId === "sales") {
    return (
      <svg
        width={size}
        height={size}
        viewBox="0 0 24 24"
        fill="currentColor"
        aria-hidden="true">
        <text
          x="12"
          y="17.25"
          textAnchor="middle"
          fontSize="15"
          fontWeight="700"
          fontFamily='system-ui, "Segoe UI", Roboto, "Helvetica Neue", Arial, sans-serif'>
          €
        </text>
      </svg>
    );
  }
  if (areaId === "admin") {
    return (
      <svg {...svgProps}>
        <line x1="4" y1="21" x2="4" y2="14" />
        <line x1="4" y1="10" x2="4" y2="3" />
        <line x1="12" y1="21" x2="12" y2="12" />
        <line x1="12" y1="8" x2="12" y2="3" />
        <line x1="20" y1="21" x2="20" y2="16" />
        <line x1="20" y1="12" x2="20" y2="3" />
        <line x1="2" y1="14" x2="6" y2="14" />
        <line x1="10" y1="8" x2="14" y2="8" />
        <line x1="18" y1="16" x2="22" y2="16" />
      </svg>
    );
  }
  if (areaId === "people") {
    return (
      <svg {...svgProps}>
        <path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2" />
        <circle cx="9" cy="7" r="4" />
        <path d="M23 21v-2a4 4 0 0 0-3-3.87" />
        <path d="M16 3.13a4 4 0 0 1 0 7.75" />
      </svg>
    );
  }
  if (areaId === "ai_agents") {
    return (
      <svg {...svgProps}>
        <path d="M12 8a4 4 0 1 0 0 8 4 4 0 0 0 0-8Z" />
        <path d="M12 2v2M12 20v2M4.93 4.93l1.41 1.41M17.66 17.66l1.41 1.41M2 12h2M20 12h2M4.93 19.07l1.41-1.41M17.66 6.34l1.41-1.41" />
      </svg>
    );
  }
  if (areaId === "finance") {
    return (
      <svg {...svgProps}>
        <rect x="2" y="6" width="20" height="13" rx="2" ry="2" />
        <path d="M2 10h20" />
        <path d="M6 15h2" />
        <path d="M11 15h3" />
      </svg>
    );
  }
  if (areaId === "design") {
    return (
      <svg {...svgProps}>
        <path d="M12 2a10 10 0 1 0 10 10 4 4 0 0 1-4-4h-2a2 2 0 0 1 0-4 8 8 0 0 0-4-2z" />
        <circle cx="6.5" cy="11.5" r="1.5" />
        <circle cx="9.5" cy="7.5" r="1.5" />
        <circle cx="14.5" cy="7.5" r="1.5" />
        <circle cx="17.5" cy="11.5" r="1.5" />
      </svg>
    );
  }
  return (
    <svg {...svgProps}>
      <circle cx="12" cy="12" r="10" />
    </svg>
  );
}

function AdminKontoChevronIcon() {
  return (
    <svg
      xmlns="http://www.w3.org/2000/svg"
      width="20"
      height="20"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true">
      <polyline points="9 18 15 12 9 6" />
    </svg>
  );
}

/** Mobile: Titel links, optionale Mitte-Aktion, Konto rechts. */
function AdminMobileTabHeader({
  tabLabel,
  onOpenKonto,
  showNewAgenda,
  onNewAgenda,
  midActionLabel,
  onMidAction,
}) {
  const label = midActionLabel || (showNewAgenda ? "Neue Agenda" : "");
  const onClick = onMidAction || (showNewAgenda ? onNewAgenda : null);
  return (
    <header className="admin_mobile_tab_header">
      <div className="admin_mobile_tab_header_left">
        <h1 className="admin_mobile_tab_header_title">{tabLabel}</h1>
      </div>
      {label && onClick ? (
        <div className="admin_mobile_tab_header_mid">
          <button
            type="button"
            className="admin_mobile_tab_header_newagenda"
            onClick={onClick}>
            {label}
          </button>
        </div>
      ) : null}
      <div className="admin_mobile_tab_header_right">
        <button
          type="button"
          className="admin_mobile_tab_header_personbtn"
          onClick={onOpenKonto}
          aria-label="Konto">
          <span className="admin_mobile_tab_header_person" aria-hidden="true">
            <svg
              width="22"
              height="22"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round">
              <circle cx="12" cy="8" r="4" />
              <path d="M4 20a8 8 0 0 1 16 0" />
            </svg>
          </span>
        </button>
      </div>
    </header>
  );
}

function ViewNavIcon({ viewId, size = 22 }) {
  const svgProps = {
    width: size,
    height: size,
    viewBox: "0 0 24 24",
    fill: "none",
    stroke: "currentColor",
    strokeWidth: 2,
    strokeLinecap: "round",
    strokeLinejoin: "round",
    "aria-hidden": true,
  };
  if (viewId === "kalender") {
    return (
      <svg {...svgProps}>
        <rect x="3" y="4" width="18" height="18" rx="2" ry="2" />
        <line x1="16" y1="2" x2="16" y2="6" />
        <line x1="8" y1="2" x2="8" y2="6" />
        <line x1="3" y1="10" x2="21" y2="10" />
      </svg>
    );
  }
  if (viewId === "nachrichten") {
    return (
      <svg {...svgProps}>
        <path d="M4 4h16c1.1 0 2 .9 2 2v12c0 1.1-.9 2-2 2H4c-1.1 0-2-.9-2-2V6c0-1.1.9-2 2-2z" />
        <polyline points="22,6 12,13 2,6" />
      </svg>
    );
  }
  if (viewId === "anrufe") {
    return (
      <svg
        width={size}
        height={size}
        viewBox="0 0 24 24"
        fill="currentColor"
        aria-hidden="true">
        <path d="M20.01 15.38c-1.23 0-2.42-.2-3.53-.56a.977.977 0 0 0-1.01.24l-1.57 1.97c-2.83-1.35-5.48-3.9-6.89-6.83l1.95-1.66c.27-.28.35-.67.24-1.02-.37-1.11-.56-2.3-.56-3.53 0-.54-.45-.99-.99-.99H4.19C3.65 3 3 3.24 3 3.99 3 13.28 10.73 21 20.01 21c.71 0 .99-.63.99-1.18v-3.45c0-.54-.45-.99-.99-.99z" />
      </svg>
    );
  }
  if (viewId === "aicoworker") {
    return (
      <svg
        width={size}
        height={size}
        viewBox="0 0 512 512"
        fill="currentColor"
        aria-hidden="true">
        <path d="M464,448V267.85a104.76,104.76,0,0,1-33.56,6.58c-1.18,0-2.3.05-3.4.05a108,108,0,0,1-56.86-16,108,108,0,0,1-56.85,16,106.16,106.16,0,0,1-56.51-16.2,107.84,107.84,0,0,1-57.2,16.2,106.14,106.14,0,0,1-56.85-16.42,106.14,106.14,0,0,1-56.85,16.42c-1.09,0-2.19,0-3.37-.05h-.06A104.66,104.66,0,0,1,48,267.49V448H16v32H496V448ZM224,384H128V308a4,4,0,0,1,4-4h88a4,4,0,0,1,4,4Zm160,64H304V308a4,4,0,0,1,4-4h72a4,4,0,0,1,4,4Z" />
        <path d="M492.57,170.28,445.89,64C432,32,432,32,400,32H112c-32,0-32,0-45.94,32L19.38,170.28c-9,19.41,2.89,39.34,2.9,39.35l.41.66c.42.66,1.13,1.75,1.62,2.37.1.13.19.27.28.4l5.24,6.39,5.31,5.14.42.36A69.65,69.65,0,0,0,45,231.73v.05a74,74,0,0,0,36,10.67c.82,0,1.64,0,2.47,0a76.08,76.08,0,0,0,51.89-20.31,72.38,72.38,0,0,0,5.77-6,74.18,74.18,0,0,0,5.78,6,76.08,76.08,0,0,0,51.89,20.31c23.28,0,44.07-10,57.63-25.56a.11.11,0,0,1,.15,0l5.66,5.26a76.09,76.09,0,0,0,51.9,20.31c23.29,0,44.11-10,57.66-25.61,13.56,15.61,34.37,25.61,57.67,25.61l2.49,0a71.35,71.35,0,0,0,35-10.7v0c.95-.57,1.86-1.17,2.78-1.77A71.33,71.33,0,0,0,488,212.17l2-3C490.9,207.13,501.21,188.87,492.57,170.28Z" />
      </svg>
    );
  }
  if (viewId === "module") {
    return (
      <svg {...svgProps}>
        <path d="M11 12h2a2 2 0 1 0 0-4h-3c-.6 0-1.1.2-1.4.6L3 14" />
        <path d="m7 18 1.6-1.4c.3-.4.8-.6 1.4-.6h4c1.1 0 2.1-.4 2.8-1.2l4.6-4.4a2 2 0 0 0-2.75-2.91l-4.2 3.9" />
        <path d="m2 13 6 6" />
      </svg>
    );
  }
  if (viewId === "kis") {
    return (
      <svg {...svgProps}>
        <rect x="4" y="6" width="16" height="12" rx="2" />
        <circle cx="9" cy="12" r="1.4" />
        <circle cx="15" cy="12" r="1.4" />
        <line x1="12" y1="3" x2="12" y2="6" />
        <line x1="2" y1="12" x2="4" y2="12" />
        <line x1="20" y1="12" x2="22" y2="12" />
      </svg>
    );
  }
  if (viewId === "tests") {
    return (
      <svg {...svgProps}>
        <path d="M9 2v6l-5 8a3 3 0 0 0 2.6 4.5h10.8A3 3 0 0 0 20 16l-5-8V2" />
        <line x1="7" y1="2" x2="17" y2="2" />
        <line x1="9" y1="13" x2="15" y2="13" />
      </svg>
    );
  }
  if (viewId === "product_userstories") {
    return (
      <svg {...svgProps}>
        <path d="M4 19.5A2.5 2.5 0 0 1 6.5 17H20" />
        <path d="M4 4.5A2.5 2.5 0 0 0 6.5 7H20" />
        <path d="M6.5 7v10" />
        <path d="M9.5 7h6" />
        <path d="M9.5 11h4" />
        <path d="M9.5 15h5" />
      </svg>
    );
  }
  if (viewId === "marketing_posts") {
    return (
      <svg {...svgProps}>
        <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" />
        <path d="M14 2v6h6M16 13H8M16 17H8M10 9H8" />
      </svg>
    );
  }
  if (viewId === "marketing_community_content") {
    return (
      <svg {...svgProps}>
        <line x1="12" y1="5" x2="12" y2="19" />
        <line x1="5" y1="12" x2="19" y2="12" />
      </svg>
    );
  }
  if (viewId === "marketing_channel_content") {
    return (
      <svg {...svgProps}>
        <path d="M3 11l18-8-7 18-2-7-9-3z" />
      </svg>
    );
  }
  if (viewId === "marketing_targetgroups") {
    return (
      <svg {...svgProps}>
        <circle cx="12" cy="8" r="4" />
        <path d="M4 20a8 8 0 0 1 16 0" />
      </svg>
    );
  }
  if (viewId === "marketing_channels") {
    return (
      <svg {...svgProps}>
        <path d="M4 12h16M8 12a4 4 0 0 1 8 0M6 8a8 8 0 0 1 12 0M4 4a12 12 0 0 1 16 0" />
      </svg>
    );
  }
  if (viewId === "sales_home") {
    return (
      <svg {...svgProps}>
        <path d="M3 11l9-8 9 8" />
        <path d="M5 10v10h14V10" />
        <path d="M10 20v-6h4v6" />
      </svg>
    );
  }
  if (viewId === "sales_akquise") {
    return (
      <svg {...svgProps}>
        <circle cx="12" cy="8" r="4" />
        <path d="M4 20a8 8 0 0 1 16 0" />
      </svg>
    );
  }
  if (viewId === "sales_service") {
    return (
      <svg {...svgProps}>
        <circle cx="12" cy="12" r="10" />
        <path d="M9.09 9a3 3 0 0 1 5.83 1c0 2-3 3-3 3" />
        <line x1="12" y1="17" x2="12.01" y2="17" />
      </svg>
    );
  }
  if (viewId === "sales_karriere") {
    return (
      <svg {...svgProps}>
        <line x1="12" y1="1" x2="12" y2="23" />
        <path d="M17 5H9.5a3.5 3.5 0 0 0 0 7h5a3.5 3.5 0 0 1 0 7H6" />
      </svg>
    );
  }
  if (viewId === "sales_kunden") {
    return (
      <svg {...svgProps}>
        <path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2" />
        <circle cx="9" cy="7" r="4" />
        <path d="M23 21v-2a4 4 0 0 0-3-3.87" />
        <path d="M16 3.13a4 4 0 0 1 0 7.75" />
      </svg>
    );
  }
  if (viewId === "sales_acquisition_content") {
    return (
      <svg {...svgProps}>
        <line x1="12" y1="5" x2="12" y2="19" />
        <line x1="5" y1="12" x2="19" y2="12" />
      </svg>
    );
  }
  if (viewId === "sales_team") {
    return (
      <svg {...svgProps}>
        <circle cx="9" cy="8" r="3" />
        <circle cx="17" cy="10" r="2" />
        <path d="M3 20a6 6 0 0 1 12 0" />
        <path d="M14 20a4 4 0 0 1 7 0" />
      </svg>
    );
  }
  if (viewId === "sales_training" || viewId === "people_schulungen") {
    return (
      <svg {...svgProps}>
        <path d="M22 10v6M2 10l10-5 10 5-10 5z" />
        <path d="M6 12v5c0 2 3 4 6 4s6-2 6-4v-5" />
      </svg>
    );
  }
  if (viewId === "sales_revenue") {
    return (
      <svg {...svgProps}>
        <line x1="12" y1="1" x2="12" y2="23" />
        <path d="M17 5H9.5a3.5 3.5 0 0 0 0 7h5a3.5 3.5 0 0 1 0 7H6" />
      </svg>
    );
  }
  if (viewId === "admin_agendas") {
    return (
      <svg {...svgProps}>
        <rect x="3" y="4" width="18" height="18" rx="2" ry="2" />
        <line x1="16" y1="2" x2="16" y2="6" />
        <line x1="8" y1="2" x2="8" y2="6" />
        <line x1="3" y1="10" x2="21" y2="10" />
      </svg>
    );
  }
  if (viewId === "admin_kpi_optimization") {
    return (
      <svg {...svgProps}>
        <rect x="3" y="3" width="7" height="9" rx="1" />
        <rect x="14" y="3" width="7" height="5" rx="1" />
        <rect x="14" y="12" width="7" height="9" rx="1" />
        <rect x="3" y="16" width="7" height="5" rx="1" />
      </svg>
    );
  }
  if (viewId === "ai_agents_overview") {
    return (
      <svg {...svgProps}>
        <rect x="3" y="3" width="7" height="7" rx="1" />
        <rect x="14" y="3" width="7" height="7" rx="1" />
        <rect x="3" y="14" width="7" height="7" rx="1" />
        <rect x="14" y="14" width="7" height="7" rx="1" />
      </svg>
    );
  }
  if (viewId === "ai_agents_services") {
    return (
      <svg {...svgProps}>
        <polygon points="12 2 22 8 22 16 12 22 2 16 2 8 12 2" />
        <circle cx="12" cy="12" r="3" />
      </svg>
    );
  }
  if (viewId === "ai_agents_consumers") {
    return (
      <svg {...svgProps}>
        <path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2" />
        <circle cx="9" cy="7" r="4" />
        <path d="M23 21v-2a4 4 0 0 0-3-3.87" />
        <path d="M16 3.13a4 4 0 0 1 0 7.75" />
      </svg>
    );
  }
  if (viewId === "ai_agents_tools") {
    return (
      <svg {...svgProps}>
        <path d="M14.7 6.3a4 4 0 0 0-5.4 5.4L3 18l3 3 6.3-6.3a4 4 0 0 0 5.4-5.4l-2.5 2.5-2.1-2.1 2.5-2.5z" />
      </svg>
    );
  }
  if (viewId === "ai_agents_actions") {
    return (
      <svg {...svgProps}>
        <polygon points="13 2 3 14 12 14 11 22 21 10 12 10 13 2" />
      </svg>
    );
  }
  if (viewId === "ai_agents_data") {
    return (
      <svg {...svgProps}>
        <ellipse cx="12" cy="5" rx="9" ry="3" />
        <path d="M3 5v6c0 1.66 4.03 3 9 3s9-1.34 9-3V5" />
        <path d="M3 11v6c0 1.66 4.03 3 9 3s9-1.34 9-3v-6" />
      </svg>
    );
  }
  if (viewId === "ai_agents_runs") {
    return (
      <svg {...svgProps}>
        <polyline points="22 12 18 12 15 21 9 3 6 12 2 12" />
      </svg>
    );
  }
  if (viewId === "ai_agents_evals") {
    return (
      <svg {...svgProps}>
        <path d="M22 11.08V12a10 10 0 1 1-5.93-9.14" />
        <polyline points="22 4 12 14.01 9 11.01" />
      </svg>
    );
  }
  if (viewId === "ai_agents_governance") {
    return (
      <svg {...svgProps}>
        <path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z" />
      </svg>
    );
  }
  return (
    <svg {...svgProps}>
      <path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2" />
      <circle cx="9" cy="7" r="4" />
      <path d="M23 21v-2a4 4 0 0 0-3-3.87" />
      <path d="M16 3.13a4 4 0 0 1 0 7.75" />
    </svg>
  );
}

const AI_AGENT_VIEW_IDS = new Set([
  "ai_agents_services",
  "ai_agents_consumers",
  "ai_agents_tools",
  "ai_agents_actions",
  "ai_agents_data",
  "ai_agents_runs",
  "ai_agents_evals",
  "ai_agents_governance",
]);

/** Alle Unteransichten des Bereichs AI Agents (inkl. Übersicht ohne Detail-Spalte). */
const AI_AGENT_AREA_VIEW_IDS = new Set([
  ...AI_AGENT_VIEW_IDS,
  "ai_agents_overview",
]);

function agentMockCopyToClipboard(text) {
  if (typeof navigator !== "undefined" && navigator.clipboard?.writeText) {
    navigator.clipboard.writeText(String(text)).catch(() => {});
  }
}

function AgentMockHealthDots({ level }) {
  const n = Math.min(3, Math.max(0, Number(level) || 0));
  return (
    <span className="agent_health_dots" aria-hidden="true">
      {[0, 1, 2].map((i) => (
        <span
          key={i}
          className={
            "agent_health_dot" + (i < n ? " agent_health_dot--on" : "")
          }
        />
      ))}
    </span>
  );
}

function AgentMockStatusPill({ status }) {
  const s = String(status || "").toLowerCase();
  return (
    <span className={"agent_status_pill agent_status_pill--" + s}>{status}</span>
  );
}

function AgentMockRunIcon({ status }) {
  const st = String(status || "").toLowerCase();
  if (st === "success")
    return <span className="agent_run_status agent_run_status--success">✓</span>;
  if (st === "error")
    return <span className="agent_run_status agent_run_status--error">✗</span>;
  if (st === "pending")
    return <span className="agent_run_status agent_run_status--pending">⏸</span>;
  if (st === "refused")
    return <span className="agent_run_status agent_run_status--refused">⊘</span>;
  return <span className="agent_run_status">{status}</span>;
}

function AgentMockTraceSteps({ nodes, depth = 0 }) {
  if (!nodes || !nodes.length) return null;
  return (
    <>
      {nodes.map((node, i) => (
        <div
          key={i}
          className={
            "agent_trace_step agent_trace_step--" +
            String(node.type || "generic")
          }
          style={{ marginLeft: depth * 12 }}>
          <div className="agent_trace_step_title">{node.title}</div>
          {node.body != null && String(node.body).length > 0 ? (
            <pre className="agent_trace_step_body">{node.body}</pre>
          ) : null}
          {node.tokens != null ? (
            <div className="agent_trace_meta">{node.tokens} tok</div>
          ) : null}
          {node.children ? (
            <AgentMockTraceSteps nodes={node.children} depth={depth + 1} />
          ) : null}
        </div>
      ))}
    </>
  );
}

function AgentMockSparkline({ values }) {
  const arr = Array.isArray(values) ? values : [];
  if (arr.length === 0) return <span className="agent_sparkline">-</span>;
  const max = Math.max(...arr, 1);
  const min = Math.min(...arr, 0);
  const span = max - min || 1;
  return (
    <div className="agent_sparkline" aria-hidden="true">
      {arr.map((v, i) => {
        const h = 4 + (14 * (v - min)) / span;
        return (
          <span
            key={i}
            className="agent_sparkline_bar"
            style={{ height: `${h}px` }}
          />
        );
      })}
    </div>
  );
}

/** Published-Override auf Mock-Service (pro Service-ID, nach Promote). */
function mergeAiAgentPublished(mock, override) {
  if (!mock) return null;
  if (!override || typeof override !== "object") return mock;
  return {
    ...mock,
    ...override,
    brain:
      override.brain != null
        ? { ...mock.brain, ...override.brain }
        : mock.brain,
    contract:
      override.contract != null
        ? { ...mock.contract, ...override.contract }
        : mock.contract,
    tools: override.tools !== undefined ? override.tools : mock.tools,
    knowledge:
      override.knowledge !== undefined ? override.knowledge : mock.knowledge,
    versions:
      override.versions !== undefined ? override.versions : mock.versions,
    endpoints:
      override.endpoints != null
        ? { ...mock.endpoints, ...override.endpoints }
        : mock.endpoints,
    consumer_ids:
      override.consumer_ids !== undefined
        ? override.consumer_ids
        : mock.consumer_ids,
    stats:
      override.stats != null ? { ...mock.stats, ...override.stats } : mock.stats,
    recent_runs:
      override.recent_runs !== undefined
        ? override.recent_runs
        : mock.recent_runs,
  };
}

/** Draft-Slice auf published mergen (Anzeige + Formularwerte). */
function mergeAiAgentDraft(published, draft) {
  if (!draft) return published;
  return mergeAiAgentPublished(published, draft);
}

function bumpAgentServiceVersion(versionStr) {
  const s = String(versionStr || "v0.0");
  const m = s.match(/^v(\d+)\.(\d+)$/);
  if (m) return `v${m[1]}.${Number(m[2]) + 1}`;
  const m2 = s.match(/^v(\d+)\.(\d+)\.(\d+)$/);
  if (m2) return `v${m2[1]}.${m2[2]}.${Number(m2[3]) + 1}`;
  return `${s}-next`;
}

/** Einheitliche Onboarding-Timestamps aus Admin-API (snake_case oder camelCase). */
function normalizeAdminKunde(raw) {
  if (!raw || typeof raw !== "object") return raw;
  const completed =
    raw.onboarding_completed_at ?? raw.onboardingCompletedAt ?? null;
  const skipped =
    raw.onboarding_skipped_at ?? raw.onboardingSkippedAt ?? null;
  const cleanTs = (v) => {
    if (v == null || v === "") return null;
    const s = typeof v === "string" ? v : String(v);
    if (s.startsWith("0000-00-00")) return null;
    return v;
  };
  return {
    ...raw,
    onboarding_completed_at: cleanTs(completed),
    onboarding_skipped_at: cleanTs(skipped),
  };
}

/** Kurzlabel fuer Nutzer-Onboarding (Admin Nutzerliste / Detail). */
function productUserOnboardingLabel(u) {
  if (!u) {
    return { title: "", short: "?", color: "#717171" };
  }
  const n = normalizeAdminKunde(u);
  if (n.onboarding_completed_at) {
    return {
      title: "Einrichtung abgeschlossen",
      short: "OK",
      color: "var(--cn-semantic-success, #008a05)",
    };
  }
  if (n.onboarding_skipped_at) {
    return {
      title: "Einrichtung übersprungen",
      short: "Skip",
      color: "var(--admin-text-secondary, #717171)",
    };
  }
  return {
    title: "Einrichtung nicht begonnen",
    short: "Offen",
    color: "#b45309",
  };
}

function useIsMobile() {
  const [mobile, setMobile] = useState(window.innerWidth < 800);
  useEffect(() => {
    const handler = () => setMobile(window.innerWidth < 800);
    window.addEventListener("resize", handler);
    return () => window.removeEventListener("resize", handler);
  }, []);
  return mobile;
}

/**
 * Sub-Block im Module-Detail: vordefinierte E2E-Tests fuer das aktuelle Modul.
 * CRUD ueber `/api/admin/module/:id/tests` + `/api/admin/modules-tests/:id`.
 * scenario_json wird als Text-Editor gehalten (Live-JSON-Validator: turns-Pflicht).
 */
function validateModuleTestScenario(text) {
  if (!text || !text.trim()) return "Scenario-JSON ist Pflicht";
  let parsed;
  try { parsed = JSON.parse(text); }
  catch (e) { return "Kein gueltiges JSON: " + e.message; }
  if (!parsed || typeof parsed !== "object" || Array.isArray(parsed)) {
    return "scenario_json muss ein Object mit `turns` sein";
  }
  if (!Array.isArray(parsed.turns) || parsed.turns.length === 0) {
    return "`turns` muss ein nicht-leeres Array sein";
  }
  return null;
}

function ModuleTestRow({ test, isOpen, onToggle, onSave, onDelete }) {
  const [title, setTitle] = useState(test.title || "");
  const [scenarioText, setScenarioText] = useState(test.scenario_json || "");
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    setTitle(test.title || "");
    setScenarioText(test.scenario_json || "");
    setError(null);
  }, [test.id, test.title, test.scenario_json]);

  const validationError = validateModuleTestScenario(scenarioText);
  const titleTrimmed = title.trim();
  const canSave = !!titleTrimmed && !validationError && !saving;

  async function handleSave() {
    if (!canSave) return;
    setSaving(true);
    setError(null);
    try {
      await onSave({ title: titleTrimmed, scenario_json: scenarioText });
    } catch (e) {
      setError(e.message || "Speichern fehlgeschlagen");
    } finally {
      setSaving(false);
    }
  }

  return (
    <div
      style={{
        border: "1px solid var(--cn-border, #ebebeb)",
        borderRadius: "8px",
        background: "var(--admin-bg-card, #fff)",
        marginBottom: "8px",
        overflow: "hidden",
      }}>
      <button
        type="button"
        onClick={onToggle}
        style={{
          display: "flex",
          alignItems: "center",
          gap: "8px",
          width: "100%",
          padding: "10px 12px",
          background: "transparent",
          border: "none",
          cursor: "pointer",
          textAlign: "left",
        }}>
        <span style={{ fontSize: "12px", color: "var(--admin-text-secondary, #717171)" }}>
          {isOpen ? "▾" : "▸"}
        </span>
        <span style={{ fontWeight: 600, fontSize: "13px", color: "var(--admin-text, #222)" }}>
          {test.title || "(ohne Titel)"}
        </span>
      </button>
      {isOpen && (
        <div
          style={{
            display: "flex",
            flexDirection: "column",
            gap: "10px",
            padding: "0 12px 12px 12px",
          }}>
          <label style={{ display: "flex", flexDirection: "column", gap: "4px", fontSize: "12px" }}>
            <span style={{ fontWeight: 600, color: "var(--admin-text-secondary, #717171)" }}>Titel</span>
            <input
              type="text"
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              placeholder="z.B. Tisch reservieren am Freitag 19 Uhr"
              style={{
                padding: "6px 8px",
                border: "1px solid var(--cn-border, #ebebeb)",
                borderRadius: "6px",
                fontSize: "13px",
              }}
            />
          </label>
          <label style={{ display: "flex", flexDirection: "column", gap: "4px", fontSize: "12px" }}>
            <span style={{ fontWeight: 600, color: "var(--admin-text-secondary, #717171)" }}>
              Scenario-JSON
            </span>
            <textarea
              value={scenarioText}
              onChange={(e) => setScenarioText(e.target.value)}
              rows={Math.max(8, Math.min(24, scenarioText.split("\n").length + 2))}
              spellCheck={false}
              style={{
                padding: "8px 10px",
                border: "1px solid " + (validationError ? "#c13515" : "var(--cn-border, #ebebeb)"),
                borderRadius: "6px",
                fontSize: "12px",
                fontFamily: "ui-monospace, SFMono-Regular, Menlo, monospace",
                lineHeight: 1.45,
                resize: "vertical",
                minHeight: "120px",
                background: "var(--admin-bg-input, #fafafa)",
                color: "var(--admin-text, #222)",
              }}
            />
            <span
              style={{
                fontSize: "11px",
                color: validationError ? "#c13515" : "var(--admin-text-secondary, #717171)",
                minHeight: "14px",
              }}>
              {validationError
                || "OK — `turns` enthaelt " + (() => {
                  try { return JSON.parse(scenarioText).turns.length; } catch { return "?"; }
                })() + " Schritt(e)"}
            </span>
          </label>
          {error && (
            <div style={{ color: "#c13515", fontSize: "12px" }}>
              {error}
            </div>
          )}
          <div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
            <button
              type="button"
              onClick={handleSave}
              disabled={!canSave}
              style={{
                padding: "6px 14px",
                background: canSave ? "var(--admin-accent, #FF5A5F)" : "#ccc",
                color: "#fff",
                border: "none",
                borderRadius: "6px",
                cursor: canSave ? "pointer" : "not-allowed",
                fontWeight: 600,
                fontSize: "12px",
              }}>
              {saving ? "Speichere…" : "Speichern"}
            </button>
            <button
              type="button"
              onClick={() => {
                if (window.confirm('Test "' + (test.title || "ohne Titel") + '" wirklich loeschen?')) {
                  onDelete();
                }
              }}
              style={{
                marginLeft: "auto",
                padding: "6px 12px",
                background: "transparent",
                border: "1px solid #c13515",
                borderRadius: "6px",
                cursor: "pointer",
                fontSize: "12px",
                color: "#c13515",
              }}>
              Loeschen
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

function ModuleTestsBlock({
  moduleId,
  tests,
  loading,
  editId,
  setEditId,
  onCreate,
  onSave,
  onDelete,
}) {
  return (
    <div
      style={{
        borderTop: "1px solid var(--admin-border, #ddd)",
        paddingTop: "16px",
        marginBottom: "16px",
      }}>
      <div
        style={{
          display: "flex",
          alignItems: "center",
          marginBottom: "6px",
          gap: "8px",
        }}>
        <h3
          style={{
            margin: 0,
            fontSize: "14px",
            fontWeight: 700,
            color: "var(--admin-text, #222)",
          }}>
          Tests
        </h3>
        <button
          type="button"
          onClick={() => {
            const defaultScenario = JSON.stringify(
              { description: "", turns: [{ user: "" }] },
              null,
              2,
            );
            onCreate(moduleId, {
              title: "Neuer Test",
              scenario_json: defaultScenario,
            });
          }}
          style={{
            marginLeft: "auto",
            padding: "4px 10px",
            background: "var(--admin-accent, #FF5A5F)",
            color: "#fff",
            border: "none",
            borderRadius: "6px",
            cursor: "pointer",
            fontSize: "12px",
            fontWeight: 600,
          }}>
          + Test
        </button>
      </div>
      <p
        style={{
          margin: "0 0 10px 0",
          fontSize: "12px",
          color: "var(--admin-text-secondary, #717171)",
        }}>
        Vordefinierte E2E-Tests, die der User aus dem <em>Deine KI</em>-Tab im
        Kunden-Login starten kann. Format wie <code>backend/tests/e2e/scenarios/*.json</code>
        — mind. ein nicht-leeres <code>turns</code>-Array.
      </p>
      {loading && (
        <div style={{ fontSize: "12px", color: "var(--admin-text-secondary, #717171)" }}>
          Lade…
        </div>
      )}
      {!loading && tests.length === 0 && (
        <div
          style={{
            fontSize: "12px",
            color: "var(--admin-text-secondary, #717171)",
            padding: "8px 0",
          }}>
          Noch keine Tests fuer dieses Modul.
        </div>
      )}
      {tests.map((test) => (
        <ModuleTestRow
          key={test.id}
          test={test}
          isOpen={editId === test.id}
          onToggle={() => setEditId(editId === test.id ? null : test.id)}
          onSave={(payload) => onSave(moduleId, test.id, payload)}
          onDelete={() => onDelete(moduleId, test.id)}
        />
      ))}
    </div>
  );
}

export default function App() {
  const initialAdminNav = (() => {
    const fallbackArea = "product";
    const fallbackView = (VIEWS_BY_AREA[fallbackArea] || PRODUCT_VIEWS)[0]?.id || "kunden";
    try {
      const savedArea = localStorage.getItem("@adminLastArea");
      const area = savedArea && VIEWS_BY_AREA[savedArea] ? savedArea : fallbackArea;
      const savedView = localStorage.getItem("@adminLastView");
      const viewIds = (VIEWS_BY_AREA[area] || []).map((v) => v.id);
      const normalizedView =
        savedView === "manualtests"
          ? "tests"
          : savedView === "ai_agents_context" || savedView === "ai_agents_tools"
            ? "ai_agents_services"
            : savedView;
      const view =
        normalizedView && viewIds.includes(normalizedView)
          ? normalizedView
          : viewIds[0] || fallbackView;
      return { area, view };
    } catch {
      return { area: fallbackArea, view: fallbackView };
    }
  })();

  const [isMemberInvitePath, setIsMemberInvitePath] = useState(() => {
    if (typeof window === "undefined") return false;
    return adminPathnameIsMemberInvite();
  });
  const [isLoggedIn, setIsLoggedIn] = useState(false);
  // SMS-Login: 2-Step (Phone -> Code). Member-Login per E-Mail/Passwort
  // wurde durch Twilio Verify ersetzt.
  const [loginStep, setLoginStep] = useState("phone"); // "phone" | "code"
  const [loginPhoneInput, setLoginPhoneInput] = useState("");
  const [loginPhoneE164, setLoginPhoneE164] = useState("");
  const [loginCode, setLoginCode] = useState("");
  const [loginBusy, setLoginBusy] = useState(false);
  const [loginResendIn, setLoginResendIn] = useState(0);
  const [adminToken, setAdminToken] = useState("");
  const [adminUser, setAdminUser] = useState(null);
  const [error, setError] = useState("");
  const [activeArea, setActiveArea] = useState(initialAdminNav.area);
  const [activeView, setActiveView] = useState(initialAdminNav.view);
  const [productUsers, setProductUsers] = useState([]);
  const [selectedProductUserId, setSelectedProductUserId] = useState(null);
  const [productUserStatusFilter, setProductUserStatusFilter] = useState(() => {
    try {
      const saved = localStorage.getItem("@adminKundenStatusFilter");
      if (saved) {
        const parsed = JSON.parse(saved);
        if (parsed && typeof parsed === "object") {
          return {
            active: parsed.active !== false,
            disabled: parsed.disabled !== false,
            test: parsed.test === true,
          };
        }
      }
    } catch { /* ignore */ }
    return { active: true, disabled: true, test: false };
  });
  const [impersonateStatus, setImpersonateStatus] = useState("");
  const [onboardingResetUserId, setOnboardingResetUserId] = useState(null);
  const [productUserKis, setProductUserKis] = useState([]);
  const [productUserKisLoading, setProductUserKisLoading] = useState(false);
  const [productUserCalls, setProductUserCalls] = useState([]);
  const [productUserCallsLoading, setProductUserCallsLoading] = useState(false);
  const [productUserCallsError, setProductUserCallsError] = useState("");
  const [credEmailDraft, setCredEmailDraft] = useState("");
  const [credPasswordDraft, setCredPasswordDraft] = useState("");
  // Telefonnummer-Pflichtfeld (Auth-relevant) seit SMS-Login.
  const [credPhoneDraft, setCredPhoneDraft] = useState("");
  const [credNameDraft, setCredNameDraft] = useState("");
  const [credSaving, setCredSaving] = useState(false);
  const [credStatus, setCredStatus] = useState({ kind: "", text: "" });
  const [aicoworkers, setAicoworkers] = useState([]);
  const [selectedAicoworkerId, setSelectedAicoworkerId] = useState(null);
  const [aicoworkerDetail, setAicoworkerDetail] = useState(null);
  const [selectedDesignPatternId, setSelectedDesignPatternId] = useState(null);
  const [moduleList, setModuleList] = useState([]);
  const [selectedModuleId, setSelectedModuleId] = useState(null);
  const [moduleDetail, setModuleDetail] = useState(null);
  const [moduleTools, setModuleTools] = useState([]);
  const [moduleToolsLoading, setModuleToolsLoading] = useState(false);
  const [moduleToolEditId, setModuleToolEditId] = useState(null);
  /** Vordefinierte E2E-Tests pro Modul (Quelle fuer den Kunden-Login-Tab "Deine KI"). */
  const [moduleTests, setModuleTests] = useState([]);
  const [moduleTestsLoading, setModuleTestsLoading] = useState(false);
  const [moduleTestEditId, setModuleTestEditId] = useState(null);
  /** Welcher KI-Mitarbeiter-Template im Modul-Header fuer Telefon-Tools-Toggle gewaehlt ist */
  const [moduleHeaderToolsAicoworkerId, setModuleHeaderToolsAicoworkerId] =
    useState(null);
  const [testScenarios, setTestScenarios] = useState([]);
  const [adminTestsList, setAdminTestsList] = useState([]);
  const [selectedTestRunId, setSelectedTestRunId] = useState(null);
  const [testRunDetail, setTestRunDetail] = useState(null);
  const [testNewAiId, setTestNewAiId] = useState("");
  const [testNewScenarioKey, setTestNewScenarioKey] = useState("");
  const [testStarting, setTestStarting] = useState(false);
  const [selectedManualTestId, setSelectedManualTestId] = useState(null);
  const [manualTestDetail, setManualTestDetail] = useState(null);
  const [manualInputDraft, setManualInputDraft] = useState("");
  const [manualSending, setManualSending] = useState(false);
  const [manualStarting, setManualStarting] = useState(false);
  const [manualConnecting, setManualConnecting] = useState(false);
  const [adminAgendas, setAdminAgendas] = useState([]);
  const [adminAgendasLoading, setAdminAgendasLoading] = useState(false);
  const [adminAgendasError, setAdminAgendasError] = useState("");
  const [selectedAdminAgendaId, setSelectedAdminAgendaId] = useState(null);
  const [agendaCreateOpen, setAgendaCreateOpen] = useState(false);
  const [agendaCreateSaving, setAgendaCreateSaving] = useState(false);
  const [agendaUpdateSaving, setAgendaUpdateSaving] = useState(false);
  const [agendaNameDraft, setAgendaNameDraft] = useState("");
  const [agendaDateDraft, setAgendaDateDraft] = useState("");
  const [agendaNotesDraft, setAgendaNotesDraft] = useState("");
  const [agendaPrSloganDraft, setAgendaPrSloganDraft] = useState("");
  const [agendaSalesFocusDraft, setAgendaSalesFocusDraft] = useState("");
  const [agendaPeopleFocusDraft, setAgendaPeopleFocusDraft] = useState("");
  const [agendaCreateError, setAgendaCreateError] = useState("");
  const [userStories, setUserStories] = useState([]);
  const [userStoriesLoading, setUserStoriesLoading] = useState(false);
  const [userStoriesError, setUserStoriesError] = useState("");
  const [selectedUserStoryId, setSelectedUserStoryId] = useState(null);
  const [userStoryCreateOpen, setUserStoryCreateOpen] = useState(false);
  const [userStoryCreateSaving, setUserStoryCreateSaving] = useState(false);
  const [userStoryUpdateSaving, setUserStoryUpdateSaving] = useState(false);
  const [userStoryTitleDraft, setUserStoryTitleDraft] = useState("");
  const [userStoryDescriptionDraft, setUserStoryDescriptionDraft] =
    useState("");
  const [userStoryCreateError, setUserStoryCreateError] = useState("");
  const [userStoryRatings, setUserStoryRatings] = useState([]);
  const [userStoryRatingsLoading, setUserStoryRatingsLoading] = useState(false);
  const [userStoryRatingNewScore, setUserStoryRatingNewScore] = useState("7");
  const [userStoryRatingNewComment, setUserStoryRatingNewComment] =
    useState("");
  const [userStoryRatingAddSaving, setUserStoryRatingAddSaving] =
    useState(false);
  const [userStoryRatingDeleteId, setUserStoryRatingDeleteId] = useState(null);
  const [userStoryDeleteSaving, setUserStoryDeleteSaving] = useState(false);
  const [membersList, setMembersList] = useState([]);
  const [membersLoading, setMembersLoading] = useState(false);
  const [membersError, setMembersError] = useState("");
  const [memberCreateOpen, setMemberCreateOpen] = useState(false);
  const [selectedMemberId, setSelectedMemberId] = useState(null);
  const [memberSaving, setMemberSaving] = useState(false);
  const [memberEmailDraft, setMemberEmailDraft] = useState("");
  const [memberDisplayNameDraft, setMemberDisplayNameDraft] = useState("");
  const [memberPasswordDraft, setMemberPasswordDraft] = useState("");
  const [memberAccessDraft, setMemberAccessDraft] = useState({
    product: false,
    marketing: false,
    sales: false,
    admin: false,
    people: false,
    ai_agents: false,
    finance: false,
    design: false,
  });
  const [memberFormError, setMemberFormError] = useState("");
  const [memberInvitedFromDraft, setMemberInvitedFromDraft] = useState("");

  const [selectedAiAgentServiceId, setSelectedAiAgentServiceId] =
    useState(null);
  const [aiAgentServiceDomainFilter, setAiAgentServiceDomainFilter] =
    useState("all");
  const [aiAgentServiceStatusFilter, setAiAgentServiceStatusFilter] =
    useState("all");
  const [aiAgentServiceOwnerFilter, setAiAgentServiceOwnerFilter] =
    useState("all");
  const [aiAgentServiceSearch, setAiAgentServiceSearch] = useState("");
  const [selectedAiAgentConsumerId, setSelectedAiAgentConsumerId] =
    useState(null);
  const [selectedAiAgentRunId, setSelectedAiAgentRunId] = useState(null);
  const [aiAgentRunServiceFilter, setAiAgentRunServiceFilter] =
    useState("all");
  const [aiAgentRunConsumerFilter, setAiAgentRunConsumerFilter] =
    useState("all");
  const [aiAgentRunStatusFilter, setAiAgentRunStatusFilter] =
    useState("all");
  const [aiAgentRunSearch, setAiAgentRunSearch] = useState("");
  const [selectedAiAgentEvalId, setSelectedAiAgentEvalId] = useState(null);
  const [aiAgentGovernanceMode, setAiAgentGovernanceMode] = useState("cost");
  const [selectedAiAgentCostId, setSelectedAiAgentCostId] = useState(null);
  const [selectedAiAgentApprovalId, setSelectedAiAgentApprovalId] =
    useState(null);
  const [selectedAiAgentIncidentId, setSelectedAiAgentIncidentId] =
    useState(null);
  const [dismissedApprovalIds, setDismissedApprovalIds] = useState(
    () => new Set(),
  );
  const [selectedAiAgentToolId, setSelectedAiAgentToolId] = useState(null);
  const [selectedAiAgentActionId, setSelectedAiAgentActionId] = useState(null);
  const [selectedAiAgentDataId, setSelectedAiAgentDataId] = useState(null);
  const [aiAgentToolSearch, setAiAgentToolSearch] = useState("");
  const [aiAgentActionKindFilter, setAiAgentActionKindFilter] = useState("all");
  const [aiAgentDataKindFilter, setAiAgentDataKindFilter] = useState("all");

  const [aiAgentServicePublishedById, setAiAgentServicePublishedById] =
    useState({});
  const [aiAgentServiceDraftById, setAiAgentServiceDraftById] = useState({});
  const [aiAgentServicePromoteOpen, setAiAgentServicePromoteOpen] =
    useState(false);
  const [aiAgentServicePromoteStatus, setAiAgentServicePromoteStatus] =
    useState("beta");

  const [salesCustomers, setSalesCustomers] = useState([]);
  const [acquisitionCustomers, setAcquisitionCustomers] = useState([]);
  const [serviceCustomers, setServiceCustomers] = useState([]);
  const [salesCustomersLoading, setSalesCustomersLoading] = useState(false);
  const [acquisitionCustomersLoading, setAcquisitionCustomersLoading] = useState(false);
  const [serviceCustomersLoading, setServiceCustomersLoading] = useState(false);
  const [salesCustomersError, setSalesCustomersError] = useState("");
  const [acquisitionCustomersError, setAcquisitionCustomersError] = useState("");
  const [serviceCustomersError, setServiceCustomersError] = useState("");
  const [selectedSalesCustomerId, setSelectedSalesCustomerId] = useState(null);
  const [salesCustomerPanelOpen, setSalesCustomerPanelOpen] = useState(false);
  const [salesCustomerSaving, setSalesCustomerSaving] = useState(false);
  const [salesCustomerFormError, setSalesCustomerFormError] = useState("");
  const [salesCustomerCompanyDraft, setSalesCustomerCompanyDraft] = useState("");
  const [salesCustomerContactDraft, setSalesCustomerContactDraft] = useState("");
  const [salesCustomerEmailDraft, setSalesCustomerEmailDraft] = useState("");
  const [salesCustomerPhoneDraft, setSalesCustomerPhoneDraft] = useState("");
  const [salesCustomerIndustryDraft, setSalesCustomerIndustryDraft] = useState("");
  const [salesCustomerNotesDraft, setSalesCustomerNotesDraft] = useState("");
  const [salesCustomerDetailMoreOpen, setSalesCustomerDetailMoreOpen] = useState(false);
  const [salesCustomerHistory, setSalesCustomerHistory] = useState([]);
  const [salesCustomerHistoryLoading, setSalesCustomerHistoryLoading] = useState(false);
  const [salesCustomerHistoryError, setSalesCustomerHistoryError] = useState("");
  const [salesCustomerActionError, setSalesCustomerActionError] = useState("");
  const [salesCustomerActionSaving, setSalesCustomerActionSaving] = useState(false);
  const [salesCustomerApptDraft, setSalesCustomerApptDraft] = useState(
    "Hallo, hiermit bestätigen wir Ihren Termin am …",
  );
  const [salesCustomerApptOpen, setSalesCustomerApptOpen] = useState(false);
  const [salesCustomerUserIdDraft, setSalesCustomerUserIdDraft] = useState(null);
  const [salesCustomerLinkUsers, setSalesCustomerLinkUsers] = useState([]);
  const [salesCustomerLinkUsersLoading, setSalesCustomerLinkUsersLoading] =
    useState(false);
  /** Wenn der Kunde-Detail-Eintrag aus Sales › Home geöffnet wurde, zeigt das auf "sales_home". */
  const [salesCustomerReturnView, setSalesCustomerReturnView] = useState(null);

  const [salesContents, setSalesContents] = useState([]);
  const [salesContentsLoading, setSalesContentsLoading] = useState(false);
  const [salesContentsError, setSalesContentsError] = useState("");
  const [selectedSalesContentId, setSelectedSalesContentId] = useState(null);
  const [salesContentPanelOpen, setSalesContentPanelOpen] = useState(false);
  /** Wenn der Akquise-Detail-Eintrag aus Sales › Home geöffnet wurde, zeigt das auf "sales_home". */
  const [salesContentReturnView, setSalesContentReturnView] = useState(null);
  /** Akquise-Detail: Felder/Bestätigung je Content-ID; Bestätigen → POST members_posts */
  const [salesAkquiseSocialByContentId, setSalesAkquiseSocialByContentId] = useState({});
  /** Nach POST: Anfrage liegt bei Marketing (members_posts pending) – nicht mit Marketing-Bestätigung verwechseln */
  const [salesAkquiseAwaitingMarketingByContentId, setSalesAkquiseAwaitingMarketingByContentId] =
    useState({});
  const [salesAkquisePostError, setSalesAkquisePostError] = useState("");
  /** Nach erfolgreichem POST: Hinweis dass Eintrag als offene Anfrage (DB status pending) liegt */
  const [salesAkquisePostInfo, setSalesAkquisePostInfo] = useState("");
  const [salesAkquiseConfirmSavingKey, setSalesAkquiseConfirmSavingKey] = useState(null);

  const [membersPosts, setMembersPosts] = useState([]);
  const [membersPostsLoading, setMembersPostsLoading] = useState(false);
  const [membersPostsError, setMembersPostsError] = useState("");
  const [selectedMembersPostId, setSelectedMembersPostId] = useState(null);
  const [membersPostConfirmSaving, setMembersPostConfirmSaving] = useState(false);
  const [membersPostConfirmError, setMembersPostConfirmError] = useState("");

  const [marketingCommunityPanelOpen, setMarketingCommunityPanelOpen] = useState(false);
  const [marketingCommunitySaving, setMarketingCommunitySaving] = useState(false);
  const [marketingCommunityFormError, setMarketingCommunityFormError] = useState("");
  const [marketingCommunityKindDraft, setMarketingCommunityKindDraft] = useState("image");
  const [marketingCommunityTitleDraft, setMarketingCommunityTitleDraft] = useState("");
  const [marketingCommunityDescriptionDraft, setMarketingCommunityDescriptionDraft] =
    useState("");
  const [marketingCommunityEarningsEurDraft, setMarketingCommunityEarningsEurDraft] =
    useState("0");
  /** Optionale Marketing-Zielgruppe (FK members_contents.targetgroup_id); "" = keine */
  const [marketingCommunityTargetgroupDraft, setMarketingCommunityTargetgroupDraft] =
    useState("");
  const [marketingCommunityFileDraft, setMarketingCommunityFileDraft] = useState(null);
  const [selectedMarketingCommunityContentId, setSelectedMarketingCommunityContentId] =
    useState(null);

  const [channelContents, setChannelContents] = useState([]);
  const [channelContentsLoading, setChannelContentsLoading] = useState(false);
  const [channelContentsError, setChannelContentsError] = useState("");
  const [marketingChannelContentPanelOpen, setMarketingChannelContentPanelOpen] =
    useState(false);
  const [marketingChannelContentSaving, setMarketingChannelContentSaving] =
    useState(false);
  const [marketingChannelContentFormError, setMarketingChannelContentFormError] =
    useState("");
  const [marketingChannelContentKindDraft, setMarketingChannelContentKindDraft] =
    useState("image");
  const [marketingChannelContentTitleDraft, setMarketingChannelContentTitleDraft] =
    useState("");
  const [
    marketingChannelContentDescriptionDraft,
    setMarketingChannelContentDescriptionDraft,
  ] = useState("");
  const [
    marketingChannelContentEarningsEurDraft,
    setMarketingChannelContentEarningsEurDraft,
  ] = useState("0");
  /** Optionale Channel-Zuordnung (FK members_channelcontents.channel_id); "" = keine */
  const [marketingChannelContentChannelDraft, setMarketingChannelContentChannelDraft] =
    useState("");
  const [marketingChannelContentFileDraft, setMarketingChannelContentFileDraft] =
    useState(null);
  const [
    selectedMarketingChannelContentId,
    setSelectedMarketingChannelContentId,
  ] = useState(null);

  const [marketingTargetgroups, setMarketingTargetgroups] = useState([]);
  const [marketingTargetgroupsLoading, setMarketingTargetgroupsLoading] = useState(false);
  const [marketingTargetgroupsError, setMarketingTargetgroupsError] = useState("");
  const [selectedMarketingTargetgroupId, setSelectedMarketingTargetgroupId] =
    useState(null);
  const [marketingTargetgroupPanelOpen, setMarketingTargetgroupPanelOpen] =
    useState(false);
  const [marketingTargetgroupSaving, setMarketingTargetgroupSaving] = useState(false);
  const [marketingTargetgroupFormError, setMarketingTargetgroupFormError] = useState("");
  const [marketingTargetgroupNameDraft, setMarketingTargetgroupNameDraft] =
    useState("");
  const [marketingTargetgroupDescriptionDraft, setMarketingTargetgroupDescriptionDraft] =
    useState("");

  const [marketingChannels, setMarketingChannels] = useState([]);
  const [marketingChannelsLoading, setMarketingChannelsLoading] = useState(false);
  const [marketingChannelsError, setMarketingChannelsError] = useState("");
  const [selectedMarketingChannelId, setSelectedMarketingChannelId] = useState(null);
  const [marketingChannelPanelOpen, setMarketingChannelPanelOpen] = useState(false);
  const [marketingChannelSaving, setMarketingChannelSaving] = useState(false);
  const [marketingChannelFormError, setMarketingChannelFormError] = useState("");
  const [marketingChannelNameDraft, setMarketingChannelNameDraft] = useState("");
  const [marketingChannelDescriptionDraft, setMarketingChannelDescriptionDraft] =
    useState("");

  const [salesTrainings, setSalesTrainings] = useState([]);
  const [salesTrainingsLoading, setSalesTrainingsLoading] = useState(false);
  const [salesTrainingsError, setSalesTrainingsError] = useState("");
  const [selectedSalesTrainingId, setSelectedSalesTrainingId] = useState(null);
  const [salesTrainingPanelOpen, setSalesTrainingPanelOpen] = useState(false);
  const [salesTrainingSaving, setSalesTrainingSaving] = useState(false);
  const [salesTrainingFormError, setSalesTrainingFormError] = useState("");
  const [salesTrainingKindDraft, setSalesTrainingKindDraft] = useState("image");
  const [salesTrainingTitleDraft, setSalesTrainingTitleDraft] = useState("");
  const [salesTrainingDescriptionDraft, setSalesTrainingDescriptionDraft] =
    useState("");
  const [salesTrainingFileDraft, setSalesTrainingFileDraft] = useState(null);
  const [salesTrainingEarningsEurDraft, setSalesTrainingEarningsEurDraft] =
    useState("0");
  /** Wenn der Schulung-Detail-Eintrag aus Sales › Home geöffnet wurde, zeigt das auf "sales_home". */
  const [salesTrainingReturnView, setSalesTrainingReturnView] = useState(null);

  const [salesEarnings, setSalesEarnings] = useState([]);
  /** Offene members_posts (Anfrage) des eingeloggten Sales – von GET members-earnings */
  const [salesPendingPosts, setSalesPendingPosts] = useState([]);
  const [salesEarningsLoading, setSalesEarningsLoading] = useState(false);
  const [salesEarningsError, setSalesEarningsError] = useState("");
  /** Einnahmen: Detail rechts (Desktop) bzw. Vollbild (Mobile): Zeilen Verdienst | Karriere */
  const [salesRevenueDetailKind, setSalesRevenueDetailKind] = useState(null);

  const [teamInvitations, setTeamInvitations] = useState([]);
  const [teamInvLoading, setTeamInvLoading] = useState(false);
  const [teamInvError, setTeamInvError] = useState("");
  const [teamInviteCreateOpen, setTeamInviteCreateOpen] = useState(false);
  const [teamInviteEmailDraft, setTeamInviteEmailDraft] = useState("");
  // SMS-Login: Telefonnummer ist Pflicht in der Einladung; E-Mail nur fuer
  // den optionalen Versand des Einladungslinks.
  const [teamInvitePhoneDraft, setTeamInvitePhoneDraft] = useState("");
  const [teamInviteSaving, setTeamInviteSaving] = useState(false);
  const [teamInviteFormError, setTeamInviteFormError] = useState("");
  const [teamInviteRevokeId, setTeamInviteRevokeId] = useState(null);
  /** Wenn das Team-Einladungs-Panel aus Sales › Home geöffnet wurde, zeigt das auf "sales_home". */
  const [teamInviteReturnView, setTeamInviteReturnView] = useState(null);
  /** Sales › Team: Klick auf eine Person öffnet rechts den Chat mit dieser Person. */
  const [selectedTeamMemberId, setSelectedTeamMemberId] = useState(null);
  /** Sales › Team: true → rechte Sidebar zeigt die Member-Detailansicht statt des Chats. */
  const [teamMemberDetailOpen, setTeamMemberDetailOpen] = useState(false);
  /** Sales › Team Chat: Nachrichten-Verlauf mit dem aktuell ausgewaehlten Member. */
  const [teamChatMessages, setTeamChatMessages] = useState([]);
  const [teamChatLoading, setTeamChatLoading] = useState(false);
  const [teamChatError, setTeamChatError] = useState("");
  const [teamChatDraft, setTeamChatDraft] = useState("");
  const [teamChatSending, setTeamChatSending] = useState(false);
  const teamChatScrollRef = useRef(null);
  /** Sales › Team: Live-Suche, filtert Roster + Pending-Einladungen nach display_name/email. */
  const [teamSearchDraft, setTeamSearchDraft] = useState("");
  /** Sales > Team Roster (alle Member + Stats fuer Liste). */
  const [teamRoster, setTeamRoster] = useState([]);
  /** Counter zum Cache-Bust der Profilbild-<img>-URLs nach Upload. */
  const [profilePicVersion, setProfilePicVersion] = useState(0);
  /** Konto > Profil: Form-State + Saving-/Fehlerstatus. */
  const [kontoNameDraft, setKontoNameDraft] = useState("");
  const [kontoCurrentPwDraft, setKontoCurrentPwDraft] = useState("");
  const [kontoNewPwDraft, setKontoNewPwDraft] = useState("");
  const [kontoNewPwRepeatDraft, setKontoNewPwRepeatDraft] = useState("");
  const [kontoSaving, setKontoSaving] = useState(false);
  const [kontoError, setKontoError] = useState("");
  const [kontoInfo, setKontoInfo] = useState("");
  const [kontoPicUploading, setKontoPicUploading] = useState(false);
  const [kontoPicError, setKontoPicError] = useState("");
  /** Aktive Konto-Detailseite: null (Liste), "profile", "appearance". */
  const [kontoSubpage, setKontoSubpage] = useState(null);
  /** SSO-Reverse-Switch (Admin -> App): Busy/Error-State fuer die "Zur App"-Zeile. */
  const [appSwitchBusy, setAppSwitchBusy] = useState(false);
  const [appSwitchError, setAppSwitchError] = useState("");

  // Verfolgt die *aktuell im Detail angezeigte* offene Chat-ID. Wird nur ueber
  // einen useEffect-Vergleich (nicht in Cleanup) aktualisiert, damit beim
  // Wechsel der Auswahl der vorherige Chat ge-end-et werden kann, OHNE den
  // gerade neu geoeffneten zu treffen.
  const lastViewedManualIdRef = useRef(null);
  const [callsList, setCallsList] = useState([]);
  const [callsLoading, setCallsLoading] = useState(false);
  const [callsTotal, setCallsTotal] = useState(0);
  const [errorLogsItems, setErrorLogsItems] = useState([]);
  const [errorLogsLoading, setErrorLogsLoading] = useState(false);
  const [errorLogsTotal, setErrorLogsTotal] = useState(0);
  const [callsTools, setCallsTools] = useState([]);
  const [callsFilters, setCallsFilters] = useState({
    q: "",
    userId: "",
    tool: "",
    unmatched: false,
    from: "",
    to: "",
  });
  const [callsDatePreset, setCallsDatePreset] = useState("");
  const [selectedCallId, setSelectedCallId] = useState(null);
  const [callDetail, setCallDetail] = useState(null);
  const [callDetailLoading, setCallDetailLoading] = useState(false);
  const [callAudioType, setCallAudioType] = useState("combined");
  const [aisList, setAisList] = useState([]);
  const [aisLoading, setAisLoading] = useState(false);
  const [selectedAiId, setSelectedAiId] = useState(null);
  const [dbSchema, setDbSchema] = useState({});
  const [dbSchemaLoaded, setDbSchemaLoaded] = useState(false);
  const [schemaTableFilter, setSchemaTableFilter] = useState("");
  const [expandedSchemaTables, setExpandedSchemaTables] = useState({});
  const [productAutoSaveRingKey, setProductAutoSaveRingKey] = useState(0);
  const [productAutoSaveRingActive, setProductAutoSaveRingActive] =
    useState(false);
  const [settingsOpen, setSettingsOpen] = useState(() =>
    adminPathnameIsKonto(),
  );
  const [areaPickerOpen, setAreaPickerOpen] = useState(false);
  const [darkMode, setDarkMode] = useState(() => {
    try {
      return localStorage.getItem("@adminTheme") === "dark";
    } catch {
      return false;
    }
  });
  const isMobile = useIsMobile();
  const detailRef = useRef(null);
  const productBodyRef = useRef(null);
  const productScrollRef = useRef({});
  const productAutoSaveTimer = useRef(null);
  const productPendingSave = useRef(null);
  const prevAicoworkerIdRef = useRef(null);
  const prevModuleIdRef = useRef(null);
  const salesCustomerLinkDebounceRef = useRef(null);

  const [sidebarWidth, setSidebarWidth] = useState(() => {
    try {
      const v = parseInt(localStorage.getItem("@adminSidebarWidth"), 10);
      if (v >= 400) return v;
    } catch {}
    return 620;
  });
  const isResizing = useRef(false);
  const sidebarSaveTimer = useRef(null);

  const setSidebarWidthAndPersist = useCallback((w) => {
    setSidebarWidth(w);
    if (sidebarSaveTimer.current) clearTimeout(sidebarSaveTimer.current);
    sidebarSaveTimer.current = setTimeout(() => {
      try {
        localStorage.setItem("@adminSidebarWidth", String(w));
      } catch {}
    }, 1000);
  }, []);

  useEffect(() => {
    return () => {
      if (sidebarSaveTimer.current) clearTimeout(sidebarSaveTimer.current);
    };
  }, []);

  const handleResizeStart = useCallback(
    (e) => {
      e.preventDefault();
      isResizing.current = true;
      document.body.style.cursor = "col-resize";
      document.body.style.userSelect = "none";

      const onMouseMove = (moveEvt) => {
        if (!isResizing.current) return;
        const layoutEl = detailRef.current?.parentElement;
        if (!layoutEl) return;
        const layoutRect = layoutEl.getBoundingClientRect();
        const leftSidebarWidth = 180;
        const minCenter = 400;
        const minSidebar = 400;
        const maxSidebar = layoutRect.width - leftSidebarWidth - minCenter;
        const newWidth = layoutRect.right - moveEvt.clientX;
        setSidebarWidthAndPersist(
          Math.max(minSidebar, Math.min(maxSidebar, newWidth)),
        );
      };
      const onMouseUp = () => {
        isResizing.current = false;
        document.body.style.cursor = "";
        document.body.style.userSelect = "";
        window.removeEventListener("mousemove", onMouseMove);
        window.removeEventListener("mouseup", onMouseUp);
      };
      window.addEventListener("mousemove", onMouseMove);
      window.addEventListener("mouseup", onMouseUp);
    },
    [setSidebarWidthAndPersist],
  );

  useEffect(() => {
    document.documentElement.setAttribute(
      "data-theme",
      darkMode ? "dark" : "light",
    );
    try {
      localStorage.setItem("@adminTheme", darkMode ? "dark" : "light");
    } catch {}
  }, [darkMode]);

  const apiBase = getApiBase();
  const headers = (token) => ({
    Authorization: "Bearer " + (token || adminToken),
  });

  // Sales-Browser-Anrufe (Twilio Voice JS SDK). Genau ein aktiver Anruf.
  const voice = useVoiceCall({ apiBase, token: adminToken });
  const formatCallDuration = (s) => {
    const sec = Math.max(0, Number(s) || 0);
    const m = Math.floor(sec / 60);
    const r = sec % 60;
    return `${m}:${String(r).padStart(2, "0")}`;
  };
  // Anrufen/Auflegen-Steuerung fuer einen Kunden. Wird in der mobilen
  // Detail-Ansicht und in der Desktop-Detail-Sidebar verwendet.
  const renderCallControl = (customerId, phone) => {
    const phoneTrim = String(phone || "").trim();
    const isThisActive =
      voice.isBusy &&
      voice.activeCustomerId != null &&
      Number(voice.activeCustomerId) === Number(customerId);
    const otherActive = voice.isBusy && !isThisActive;
    const phoneSvg = (
      <svg
        width="18"
        height="18"
        viewBox="0 0 24 24"
        fill="none"
        stroke="currentColor"
        strokeWidth="2">
        <path d="M22 16.92v3a2 2 0 0 1-2.18 2 19.79 19.79 0 0 1-8.63-3.07 19.5 19.5 0 0 1-6-6 19.79 19.79 0 0 1-3.07-8.67A2 2 0 0 1 4.11 2h3a2 2 0 0 1 2 1.72c.12.86.3 1.7.54 2.5a2 2 0 0 1-.45 2.11L8.09 9.91a16 16 0 0 0 6 6l1.58-1.11a2 2 0 0 1 2.11-.45c.8.24 1.64.42 2.5.54A2 2 0 0 1 22 16.92z" />
      </svg>
    );
    if (isThisActive) {
      const label =
        voice.status === "open"
          ? `Auflegen · ${formatCallDuration(voice.durationSec)}`
          : voice.status === "ringing"
          ? "Klingelt… Auflegen"
          : "Verbinden… Auflegen";
      return (
        <button
          type="button"
          className="admin_sales_action_btn admin_sales_call_btn--active"
          onClick={() => voice.hangup()}>
          <svg
            width="18"
            height="18"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="2">
            <path d="M22 16.92v3a2 2 0 0 1-2.18 2 19.79 19.79 0 0 1-8.63-3.07 19.5 19.5 0 0 1-6-6 19.79 19.79 0 0 1-3.07-8.67A2 2 0 0 1 4.11 2h3a2 2 0 0 1 2 1.72c.12.86.3 1.7.54 2.5a2 2 0 0 1-.45 2.11L8.09 9.91a16 16 0 0 0 6 6l1.58-1.11a2 2 0 0 1 2.11-.45c.8.24 1.64.42 2.5.54A2 2 0 0 1 22 16.92z" />
            <line x1="1" y1="1" x2="23" y2="23" />
          </svg>
          {label}
        </button>
      );
    }
    return (
      <button
        type="button"
        className={
          "admin_sales_action_btn admin_sales_action_btn--primary" +
          (!phoneTrim || otherActive ? " admin_sales_action_btn--disabled" : "")
        }
        disabled={!phoneTrim || otherActive}
        title={otherActive ? "Es läuft bereits ein Anruf" : undefined}
        onClick={() => voice.start({ to: phoneTrim, customerId })}>
        {phoneSvg}
        Anrufen
      </button>
    );
  };

  const verifyToken = useCallback(
    async (token) => {
      setError("");
      try {
        const res = await fetch(`${apiBase}/api/admin/verify`, {
          headers: headers(token),
        });
        const data = await res.json().catch(() => ({}));
        if (res.ok && data.ok) {
          setAdminToken(token);
          setAdminUser(data.user || null);
          setIsLoggedIn(true);
          return true;
        }
        try {
          localStorage.removeItem("@adminToken");
        } catch {}
        // Admin-Session ungueltig/abgelaufen -> Bereich zurueck auf "frontend",
        // damit der naechste Kaltstart nicht erneut hierher (Admin-Login)
        // umgeleitet wird, obwohl ggf. eine gueltige Frontend-Session existiert.
        markFrontendArea();
        return false;
      } catch {
        return false;
      }
    },
    [apiBase],
  );

  const normalizePhoneInput = useCallback((raw, defaultCountry = "DE") => {
    if (!raw) return null;
    let s = String(raw).trim().replace(/[\s\-().]/g, "");
    if (!s) return null;
    if (s.startsWith("00")) s = "+" + s.slice(2);
    if (defaultCountry === "DE" && /^0\d+$/.test(s)) s = "+49" + s.slice(1);
    if (!s.startsWith("+")) {
      if (defaultCountry === "DE" && /^\d+$/.test(s)) s = "+49" + s.replace(/^0+/, "");
      else return null;
    }
    return /^\+[1-9]\d{6,14}$/.test(s) ? s : null;
  }, []);

  const startLoginSms = useCallback(
    async (phoneE164) => {
      const res = await fetch(`${apiBase}/api/admin/auth/phone/start`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ phone: phoneE164, country: "DE" }),
      });
      const ct = res.headers.get("content-type") || "";
      if (ct.includes("text/html")) throw new Error("Backend nicht erreichbar (HTML statt JSON)");
      const data = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(data.error || "SMS konnte nicht versendet werden");
      return data;
    },
    [apiBase],
  );

  const checkLoginSms = useCallback(
    async (phoneE164, code) => {
      const res = await fetch(`${apiBase}/api/admin/auth/phone/check`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ phone: phoneE164, code, country: "DE" }),
      });
      const ct = res.headers.get("content-type") || "";
      if (ct.includes("text/html")) throw new Error("Backend nicht erreichbar (HTML statt JSON)");
      const data = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(data.error || "Code ist falsch oder abgelaufen");
      return data;
    },
    [apiBase],
  );

  useEffect(() => {
    // SSO-Forward-Handover (App -> Admin): telephonio.com schickt den
    // exchanged Member-JWT als URL-Fragment `#adminToken=...`. Wir lesen
    // das Token, persistieren es als `@adminToken` und strippen den Hash.
    // Muss VOR dem normalen `verifyToken(saved)`-Flow laufen, damit der
    // frische Token immer Vorrang vor einem eventuell aelteren localStorage-
    // Eintrag bekommt.
    let bootstrapped = null;
    try {
      const rawHash = (window.location.hash || "").replace(/^#/, "");
      if (rawHash) {
        const hashParams = new URLSearchParams(rawHash);
        const incomingAdminToken = hashParams.get("adminToken");
        if (incomingAdminToken) {
          try {
            localStorage.setItem("@adminToken", incomingAdminToken);
          } catch {}
          try {
            window.history.replaceState(
              {},
              "",
              window.location.pathname + window.location.search,
            );
          } catch (_) {}
          bootstrapped = incomingAdminToken;
        }
      }
    } catch (_) {}
    try {
      const saved = bootstrapped || localStorage.getItem("@adminToken");
      if (saved) verifyToken(saved);
    } catch {}
  }, [verifyToken]);

  // Cross-Origin-Bereichs-Marker: solange der Member eingeloggt ist, ist der
  // Admin-Bereich der "zuletzt aktive". Das Frontend liest dieses Cookie beim
  // Kaltstart und leitet die App dann direkt hierher (member.telephonio.com).
  useEffect(() => {
    if (isLoggedIn) markAdminArea();
  }, [isLoggedIn]);

  // Resend-Cooldown ticker fuer SMS-Login
  useEffect(() => {
    if (loginResendIn <= 0) return undefined;
    const t = setTimeout(() => setLoginResendIn((s) => Math.max(0, s - 1)), 1000);
    return () => clearTimeout(t);
  }, [loginResendIn]);

  const handleLoginPhoneSubmit = async (e) => {
    e.preventDefault();
    setError("");
    const norm = normalizePhoneInput(loginPhoneInput, "DE");
    if (!norm) {
      setError("Bitte gueltige Mobilnummer eingeben (z. B. +4915112345678).");
      return;
    }
    setLoginBusy(true);
    try {
      const data = await startLoginSms(norm);
      setLoginPhoneE164(data.phone || norm);
      setLoginStep("code");
      setLoginCode("");
      setLoginResendIn(60);
    } catch (err) {
      setError(err.message || "SMS konnte nicht versendet werden.");
    } finally {
      setLoginBusy(false);
    }
  };

  const handleLoginCodeSubmit = async (e, overrideCode) => {
    if (e && e.preventDefault) e.preventDefault();
    setError("");
    const c = (overrideCode != null ? overrideCode : loginCode || "").trim();
    if (!/^\d{4,10}$/.test(c)) {
      setError("Bitte 6-stelligen Code eingeben.");
      return;
    }
    setLoginBusy(true);
    try {
      const data = await checkLoginSms(loginPhoneE164, c);
      if (data && data.token) {
        setAdminToken(data.token);
        setAdminUser(data.user || null);
        setIsLoggedIn(true);
        setLoginStep("phone");
        setLoginPhoneInput("");
        setLoginPhoneE164("");
        setLoginCode("");
        try {
          localStorage.setItem("@adminToken", data.token);
        } catch {}
      } else {
        setError(data.error || "Anmeldung fehlgeschlagen");
      }
    } catch (err) {
      setError(err.message || "Code ist falsch oder abgelaufen.");
    } finally {
      setLoginBusy(false);
    }
  };

  const handleLoginCodeChange = (raw) => {
    const digits = String(raw || "").replace(/\D+/g, "").slice(0, 6);
    setLoginCode(digits);
    if (digits.length === 6 && !loginBusy) {
      handleLoginCodeSubmit(null, digits);
    }
  };

  const handleLoginResend = async () => {
    if (loginResendIn > 0 || loginBusy) return;
    setError("");
    setLoginBusy(true);
    try {
      await startLoginSms(loginPhoneE164);
      setLoginResendIn(60);
    } catch (err) {
      setError(err.message || "SMS konnte nicht erneut versendet werden.");
    } finally {
      setLoginBusy(false);
    }
  };

  const handleLoginBack = () => {
    setLoginStep("phone");
    setLoginCode("");
    setError("");
  };

  const handleLogout = () => {
    setAdminToken("");
    setAdminUser(null);
    setIsLoggedIn(false);
    setSettingsOpen(false);
    // Beim Logout den zuletzt aktiven Bereich auf "frontend" zuruecksetzen,
    // damit ein App-Neustart nicht wieder im (jetzt ausgeloggten) Admin-
    // Login landet.
    markFrontendArea();
    try {
      localStorage.removeItem("@adminToken");
    } catch {}
    try {
      if (typeof window !== "undefined" && adminPathnameIsKonto()) {
        window.history.replaceState({}, "", "/");
      }
    } catch { /* ignore */ }
  };

  /**
   * SSO-Reverse: Member -> App-Account. Tauscht Admin-JWT gegen User-JWT
   * (Backend legt User on-the-fly an, wenn noch keiner zur Phone existiert)
   * und navigiert mit `#userToken=...` zur App.
   *
   * Bewusst KEIN Bridge-Mechanismus (`native.navigateWithUserToken`) — die
   * im AppStore deployte Native-App ist ein alter Build dessen onMessage-
   * Handler diese Message-Type nicht kennt. Mit `window.location.href`
   * funktioniert es:
   *   - Browser: direkter Origin-Wechsel.
   *   - Neuer Native-Build: `telephonio.com` ist whitelisted, WebView
   *     navigiert in-App.
   *   - Alter Native-Build (im Store): admin.aimafia.de ist nicht in der
   *     Host-Whitelist, telephonio.com aber schon — also navigiert die
   *     bestehende admin.aimafia.de-WebView trotzdem zur telephonio.com-
   *     URL und der Hash-Bootstrap im AuthContext zieht den User-JWT.
   */
  const handleSwitchToApp = useCallback(async () => {
    if (appSwitchBusy || !adminToken) return;
    setAppSwitchBusy(true);
    setAppSwitchError("");
    try {
      const data = await exchangeMemberToUser({ apiBase, token: adminToken });
      if (!data || !data.token) throw new Error("Kein User-Token erhalten");
      // Wechsel in die App -> Frontend wird der zuletzt aktive Bereich.
      markFrontendArea();
      window.location.href =
        "https://telephonio.com/anrufe#userToken=" + encodeURIComponent(data.token);
    } catch (err) {
      setAppSwitchError(err.message || "Wechsel fehlgeschlagen");
      setAppSwitchBusy(false);
    }
  }, [appSwitchBusy, adminToken, apiBase]);

  /** Konto > Profil: Anzeigename + optional Passwort speichern. */
  const saveKontoProfile = useCallback(async () => {
    if (!apiBase || !adminToken) return;
    setKontoError("");
    setKontoInfo("");
    const newName = String(kontoNameDraft || "").trim();
    const newPw = String(kontoNewPwDraft || "");
    const newPwRepeat = String(kontoNewPwRepeatDraft || "");
    const currentPw = String(kontoCurrentPwDraft || "");
    const wantsName = !!adminUser && newName !== String(adminUser.display_name || "").trim();
    const wantsPw = newPw.length > 0 || newPwRepeat.length > 0;
    if (wantsPw) {
      if (newPw.length < 8) {
        setKontoError("Neues Passwort muss mind. 8 Zeichen haben.");
        return;
      }
      if (newPw !== newPwRepeat) {
        setKontoError("Neue Passwoerter stimmen nicht ueberein.");
        return;
      }
      if (!currentPw) {
        setKontoError("Aktuelles Passwort eingeben.");
        return;
      }
    }
    if (!wantsName && !wantsPw) {
      setKontoError("Keine Aenderungen.");
      return;
    }
    setKontoSaving(true);
    try {
      const payload = {};
      if (wantsName) payload.display_name = newName;
      if (wantsPw) {
        payload.current_password = currentPw;
        payload.new_password = newPw;
      }
      const res = await fetch(`${apiBase}/api/admin/me`, {
        method: "PUT",
        headers: { "Content-Type": "application/json", ...headers() },
        body: JSON.stringify(payload),
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) {
        setKontoError(data.error || "Speichern fehlgeschlagen.");
        return;
      }
      if (data.user) {
        setAdminUser((prev) => ({ ...(prev || {}), ...data.user }));
      }
      setKontoCurrentPwDraft("");
      setKontoNewPwDraft("");
      setKontoNewPwRepeatDraft("");
      setKontoInfo("Profil gespeichert.");
    } catch {
      setKontoError("Netzwerkfehler.");
    } finally {
      setKontoSaving(false);
    }
  }, [
    adminToken,
    adminUser,
    apiBase,
    kontoCurrentPwDraft,
    kontoNameDraft,
    kontoNewPwDraft,
    kontoNewPwRepeatDraft,
  ]);

  /** Konto > Profil: Profilbild via multipart hochladen. */
  const uploadKontoProfilePicture = useCallback(
    async (file) => {
      if (!apiBase || !adminToken || !file) return;
      setKontoPicError("");
      const mime = String(file.type || "").toLowerCase();
      if (!mime.startsWith("image/")) {
        setKontoPicError("Bitte eine Bilddatei auswaehlen.");
        return;
      }
      setKontoPicUploading(true);
      try {
        const fd = new FormData();
        fd.append("file", file);
        const res = await fetch(`${apiBase}/api/admin/me/profile-picture`, {
          method: "POST",
          headers: headers(),
          body: fd,
        });
        const data = await res.json().catch(() => ({}));
        if (!res.ok) {
          setKontoPicError(data.error || "Upload fehlgeschlagen.");
          return;
        }
        setAdminUser((prev) =>
          prev ? { ...prev, profile_picture_key: data.profile_picture_key || null } : prev,
        );
        setProfilePicVersion((v) => v + 1);
      } catch {
        setKontoPicError("Netzwerkfehler beim Upload.");
      } finally {
        setKontoPicUploading(false);
      }
    },
    [adminToken, apiBase],
  );

  /** Konto > Profil: aktuelles Profilbild entfernen. */
  const removeKontoProfilePicture = useCallback(async () => {
    if (!apiBase || !adminToken) return;
    setKontoPicError("");
    setKontoPicUploading(true);
    try {
      const res = await fetch(`${apiBase}/api/admin/me/profile-picture`, {
        method: "DELETE",
        headers: headers(),
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) {
        setKontoPicError(data.error || "Entfernen fehlgeschlagen.");
        return;
      }
      setAdminUser((prev) => (prev ? { ...prev, profile_picture_key: null } : prev));
      setProfilePicVersion((v) => v + 1);
    } catch {
      setKontoPicError("Netzwerkfehler.");
    } finally {
      setKontoPicUploading(false);
    }
  }, [adminToken, apiBase]);

  /** Beim Oeffnen von Konto den Namens-Draft mit dem aktuellen Wert vorbefuellen. */
  useEffect(() => {
    if (!settingsOpen) {
      setKontoSubpage(null);
      return;
    }
    setKontoNameDraft(String(adminUser?.display_name || ""));
    setKontoCurrentPwDraft("");
    setKontoNewPwDraft("");
    setKontoNewPwRepeatDraft("");
    setKontoError("");
    setKontoInfo("");
    setKontoPicError("");
  }, [settingsOpen, adminUser]);

  const allowedAreas = ADMIN_AREAS.filter((area) => {
    if (!adminUser) return area.id === "product";
    const field = areaAccessField(area.id);
    if (!field) return false;
    return !!adminUser[field];
  });
  const effectiveArea =
    allowedAreas.find((a) => a.id === activeArea)?.id ||
    allowedAreas[0]?.id ||
    "product";
  const views = VIEWS_BY_AREA[effectiveArea] || PRODUCT_VIEWS;

  const setKontoOpen = useCallback((open) => {
    setSettingsOpen(open);
    syncKontoUrl(open);
  }, []);

  useEffect(() => {
    const onPopState = () => {
      setSettingsOpen(adminPathnameIsKonto());
    };
    window.addEventListener("popstate", onPopState);
    return () => window.removeEventListener("popstate", onPopState);
  }, []);

  useEffect(() => {
    if (settingsOpen) setAreaPickerOpen(false);
  }, [settingsOpen]);

  useEffect(() => {
    // Vor bekanntem Member (`verify`/`login`) ist `allowedAreas` künstlich nur Product —
    // Navigation sonst auf Product zurücksetzen und LocalStorage überschreiben würde
    // gespeicherte Bereiche/Tabs (Sales, People, …) zerstören.
    if (!adminUser) return;
    if (effectiveArea !== activeArea) {
      setActiveArea(effectiveArea);
      const nextViews = VIEWS_BY_AREA[effectiveArea] || PRODUCT_VIEWS;
      if (!nextViews.find((v) => v.id === activeView)) {
        setActiveView(nextViews[0]?.id || "kunden");
      }
    }
  }, [adminUser, effectiveArea, activeArea, activeView]);

  const loadProductUsers = useCallback(() => {
    if (!adminToken || !apiBase) return;
    const statuses = [];
    if (productUserStatusFilter.active) statuses.push("active");
    if (productUserStatusFilter.disabled) statuses.push("disabled");
    if (productUserStatusFilter.test) statuses.push("test");
    const qs = statuses.length ? `?status=${statuses.join(",")}` : "?status=none";
    fetch(`${apiBase}/api/admin/kunden${qs}`, { headers: headers() })
      .then((r) => r.json())
      .then((data) => {
        if (data && Array.isArray(data.users)) {
          setProductUsers(data.users.map(normalizeAdminKunde));
        }
      })
      .catch(() => {});
  }, [apiBase, adminToken, productUserStatusFilter]);

  useEffect(() => {
    if (isLoggedIn && activeView === "kunden") loadProductUsers();
  }, [isLoggedIn, activeView, loadProductUsers]);

  useEffect(() => {
    try { localStorage.setItem("@adminKundenStatusFilter", JSON.stringify(productUserStatusFilter)); } catch { /* ignore */ }
  }, [productUserStatusFilter]);

  useEffect(() => {
    if (!adminToken || !apiBase || !selectedProductUserId) {
      setProductUserKis([]);
      return;
    }
    let cancelled = false;
    setProductUserKisLoading(true);
    fetch(`${apiBase}/api/admin/kunden/${selectedProductUserId}/kis`, {
      headers: headers(),
    })
      .then((r) => r.json())
      .then((data) => {
        if (cancelled) return;
        setProductUserKis(Array.isArray(data?.kis) ? data.kis : []);
      })
      .catch(() => {
        if (!cancelled) setProductUserKis([]);
      })
      .finally(() => {
        if (!cancelled) setProductUserKisLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [apiBase, adminToken, selectedProductUserId]);

  useEffect(() => {
    if (!adminToken || !apiBase || !selectedProductUserId) {
      setProductUserCalls([]);
      setProductUserCallsError("");
      return;
    }
    let cancelled = false;
    setProductUserCallsLoading(true);
    setProductUserCallsError("");
    const params = new URLSearchParams();
    params.set("userId", String(selectedProductUserId));
    params.set("pageSize", "50");
    fetch(`${apiBase}/api/admin/calls?${params.toString()}`, {
      headers: headers(),
    })
      .then((r) =>
        r.json().then((d) => ({ ok: r.ok, status: r.status, data: d })),
      )
      .then(({ ok, status, data }) => {
        if (cancelled) return;
        if (!ok) {
          setProductUserCalls([]);
          setProductUserCallsError((data && data.error) || `HTTP ${status}`);
          return;
        }
        setProductUserCalls(Array.isArray(data?.calls) ? data.calls : []);
      })
      .catch(() => {
        if (!cancelled) {
          setProductUserCalls([]);
          setProductUserCallsError("Netzwerkfehler.");
        }
      })
      .finally(() => {
        if (!cancelled) setProductUserCallsLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [apiBase, adminToken, selectedProductUserId]);

  useEffect(() => {
    const u = productUsers.find((x) => x.id === selectedProductUserId);
    setCredEmailDraft(u ? u.email || "" : "");
    setCredNameDraft(u ? u.display_name || "" : "");
    setCredPhoneDraft(u ? u.phone_e164 || "" : "");
    setCredPasswordDraft("");
    setCredStatus({ kind: "", text: "" });
  }, [selectedProductUserId, productUsers]);

  const handleSaveCredentials = useCallback(async () => {
    if (!selectedProductUserId) return;
    const current = productUsers.find((u) => u.id === selectedProductUserId);
    const trimmedEmail = (credEmailDraft || "").trim();
    const trimmedName = (credNameDraft || "").trim();
    const trimmedPhone = (credPhoneDraft || "").trim();
    const emailChanged =
      trimmedEmail && trimmedEmail !== (current?.email || "");
    const phoneChanged = trimmedPhone !== (current?.phone_e164 || "");
    const nameChanged = trimmedName !== (current?.display_name || "").trim();
    if (!emailChanged && !phoneChanged && !nameChanged) {
      setCredStatus({ kind: "error", text: "Keine Aenderung." });
      return;
    }
    setCredSaving(true);
    setCredStatus({ kind: "", text: "" });
    try {
      const payload = {};
      if (emailChanged) payload.email = trimmedEmail;
      if (phoneChanged) { payload.phone = trimmedPhone; payload.country = "DE"; }
      if (nameChanged) payload.display_name = trimmedName;
      const res = await fetch(
        `${apiBase}/api/admin/kunden/${selectedProductUserId}`,
        {
          method: "PUT",
          headers: { "Content-Type": "application/json", ...headers() },
          body: JSON.stringify(payload),
        },
      );
      const data = await res.json().catch(() => ({}));
      if (!res.ok) {
        setCredStatus({
          kind: "error",
          text: data.error || `Fehler (${res.status})`,
        });
        return;
      }
      if (data.user) {
        setProductUsers((prev) =>
          prev.map((u) => (u.id === data.user.id ? { ...u, ...data.user } : u)),
        );
      }
      setCredPasswordDraft("");
      const parts = [];
      if (nameChanged) parts.push("Name");
      if (emailChanged) parts.push("Email");
      if (phoneChanged) parts.push("Telefonnummer");
      setCredStatus({
        kind: "success",
        text: parts.join(" & ") + " aktualisiert.",
      });
    } catch {
      setCredStatus({ kind: "error", text: "Netzwerkfehler." });
    } finally {
      setCredSaving(false);
    }
  }, [
    apiBase,
    adminToken,
    selectedProductUserId,
    productUsers,
    credEmailDraft,
    credNameDraft,
    credPhoneDraft,
  ]);

  const handleSetUserStatus = useCallback(async (userId, newStatus) => {
    if (!userId || !["active", "disabled", "test"].includes(newStatus)) return;
    try {
      const res = await fetch(`${apiBase}/api/admin/kunden/${userId}`, {
        method: "PUT",
        headers: { "Content-Type": "application/json", ...headers() },
        body: JSON.stringify({ status: newStatus }),
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) return;
      setProductUsers((prev) => {
        const next = prev.map((u) => u.id === userId ? { ...u, status: newStatus } : u);
        const stillVisible = (
          (newStatus === "active" && productUserStatusFilter.active) ||
          (newStatus === "disabled" && productUserStatusFilter.disabled) ||
          (newStatus === "test" && productUserStatusFilter.test)
        );
        return stillVisible ? next : next.filter((u) => u.id !== userId);
      });
      const stillVisible = (
        (newStatus === "active" && productUserStatusFilter.active) ||
        (newStatus === "disabled" && productUserStatusFilter.disabled) ||
        (newStatus === "test" && productUserStatusFilter.test)
      );
      if (!stillVisible && selectedProductUserId === userId) {
        setSelectedProductUserId(null);
      }
      void data;
    } catch { /* ignore */ }
  }, [apiBase, adminToken, productUserStatusFilter, selectedProductUserId]);

  const handleResetUserOnboarding = useCallback(
    async (userId) => {
      if (!userId) return;
      if (
        !window.confirm(
          "Einrichtung auf «nicht begonnen» zurücksetzen? Der Nutzer sieht den Einrichtungs-Chat erneut.",
        )
      ) {
        return;
      }
      setOnboardingResetUserId(userId);
      try {
        const res = await fetch(
          `${apiBase}/api/admin/kunden/${userId}/onboarding-reset`,
          {
            method: "POST",
            headers: headers(),
          },
        );
        const data = await res.json().catch(() => ({}));
        if (!res.ok) {
          window.alert(data.error || "Zurücksetzen fehlgeschlagen.");
          return;
        }
        if (data.user) {
          const merged = normalizeAdminKunde({ ...data.user });
          setProductUsers((prev) =>
            prev.map((x) =>
              x.id === merged.id ? { ...x, ...merged } : x,
            ),
          );
        }
      } catch {
        window.alert("Netzwerkfehler.");
      } finally {
        setOnboardingResetUserId(null);
      }
    },
    [apiBase, adminToken],
  );

  const handleImpersonate = useCallback(
    async (userId) => {
      setImpersonateStatus("");
      try {
        const res = await fetch(
          `${apiBase}/api/admin/kunden/${userId}/impersonate`,
          {
            method: "POST",
            headers: headers(),
          },
        );
        const data = await res.json().catch(() => ({}));
        if (res.ok && data.token) {
          const frontendUrl =
            window.location.hostname === "localhost"
              ? "http://localhost:3000"
              : "https://telephonio.com";
          // Wiederverwendung des erprobten SSO-Hash-Bootstraps (#userToken=)
          // statt ?impersonate=. Der AuthContext konsumiert #userToken SYNCHRON
          // vor dem ersten Render und landet direkt im eingeloggten /anrufe-
          // Dashboard. Das frühere ?impersonate= war im Frontend hinter
          // REACT_APP_ALLOW_IMPERSONATE gegated (in Produktion aus) und landete
          // deshalb wirkungslos auf der Landingpage.
          window.open(
            `${frontendUrl}/anrufe#userToken=${encodeURIComponent(data.token)}`,
            "_blank",
          );
          setImpersonateStatus("Frontend in neuem Tab geöffnet.");
        } else {
          setImpersonateStatus("Fehler: " + (data.error || res.status));
        }
      } catch {
        setImpersonateStatus("Netzwerkfehler.");
      }
    },
    [apiBase, adminToken],
  );

  const loadAicoworkers = useCallback(() => {
    if (!adminToken || !apiBase) return;
    fetch(`${apiBase}/api/admin/aicoworker`, { headers: headers() })
      .then((r) => r.json())
      .then((data) => {
        if (Array.isArray(data)) setAicoworkers(data);
      })
      .catch(() => {});
  }, [apiBase, adminToken]);

  useEffect(() => {
    if (isLoggedIn && activeView === "aicoworker") loadAicoworkers();
  }, [isLoggedIn, activeView, loadAicoworkers]);

  useEffect(() => {
    if (selectedAicoworkerId) {
      const found = aicoworkers.find((m) => m.id === selectedAicoworkerId);
      setAicoworkerDetail(found || null);
    } else {
      setAicoworkerDetail(null);
    }
  }, [selectedAicoworkerId, aicoworkers]);

  useEffect(() => {
    if (!selectedAicoworkerId) return;
    setAicoworkerDetail((prev) => {
      const found = aicoworkers.find((m) => m.id === selectedAicoworkerId);
      if (!found) return prev;
      if (!prev) return found;
      return { ...prev, modules: found.modules };
    });
  }, [aicoworkers, selectedAicoworkerId]);

  const saveAicoworkerDetail = useCallback(
    (id, fields) => {
      if (!adminToken || !apiBase) return;
      fetch(`${apiBase}/api/admin/aicoworker/${id}`, {
        method: "PUT",
        headers: { "Content-Type": "application/json", ...headers() },
        body: JSON.stringify(fields),
      })
        .then((r) => r.json())
        .then(() => {
          loadAicoworkers();
        })
        .catch(() => {});
    },
    [apiBase, adminToken, loadAicoworkers],
  );

  const deleteAicoworker = useCallback(
    (id) => {
      if (!adminToken || !apiBase) return;
      fetch(`${apiBase}/api/admin/aicoworker/${id}`, {
        method: "DELETE",
        headers: headers(),
      })
        .then(() => {
          setSelectedAicoworkerId(null);
          loadAicoworkers();
        })
        .catch(() => {});
    },
    [apiBase, adminToken, loadAicoworkers],
  );

  const addAicoworkerModule = useCallback(
    (aicoworkerId, module_id) => {
      if (!adminToken || !apiBase) return;
      fetch(`${apiBase}/api/admin/aicoworker/${aicoworkerId}/module`, {
        method: "POST",
        headers: { "Content-Type": "application/json", ...headers() },
        body: JSON.stringify({ module_id }),
      })
        .then(() => loadAicoworkers())
        .catch(() => {});
    },
    [apiBase, adminToken, loadAicoworkers],
  );

  const removeAicoworkerModule = useCallback(
    (aicoworkerId, moduleId) => {
      if (!adminToken || !apiBase) return;
      fetch(
        `${apiBase}/api/admin/aicoworker/${aicoworkerId}/module/${moduleId}`,
        { method: "DELETE", headers: headers() },
      )
        .then(() => loadAicoworkers())
        .catch(() => {});
    },
    [apiBase, adminToken, loadAicoworkers],
  );

  const updateAicoworkerModule = useCallback(
    (aicoworkerId, moduleId, data) => {
      if (!adminToken || !apiBase) return;
      fetch(
        `${apiBase}/api/admin/aicoworker/${aicoworkerId}/module/${moduleId}`,
        {
          method: "PUT",
          headers: { "Content-Type": "application/json", ...headers() },
          body: JSON.stringify(data),
        },
      )
        .then(() => loadAicoworkers())
        .catch(() => {});
    },
    [apiBase, adminToken, loadAicoworkers],
  );

  const loadModuleList = useCallback(() => {
    if (!adminToken || !apiBase) return;
    fetch(`${apiBase}/api/admin/module`, { headers: headers() })
      .then((r) => r.json())
      .then((data) => {
        if (Array.isArray(data)) setModuleList(data);
      })
      .catch(() => {});
  }, [apiBase, adminToken]);

  const loadDbSchema = useCallback(() => {
    if (!adminToken || !apiBase) return;
    fetch(`${apiBase}/api/admin/db/schema`, { headers: headers() })
      .then((r) => r.json())
      .then((data) => {
        if (data && data.tables && typeof data.tables === "object") {
          setDbSchema(data.tables);
          setDbSchemaLoaded(true);
        }
      })
      .catch(() => {});
  }, [apiBase, adminToken]);

  const saveModuleDetail = useCallback(
    (id, data) => {
      if (!adminToken || !apiBase) return;
      fetch(`${apiBase}/api/admin/module/${id}`, {
        method: "PUT",
        headers: { "Content-Type": "application/json", ...headers() },
        body: JSON.stringify(data),
      })
        .then(() => loadModuleList())
        .catch(() => {});
    },
    [apiBase, adminToken, loadModuleList],
  );

  const deleteModule = useCallback(
    (id) => {
      if (!adminToken || !apiBase || !id) return;
      fetch(`${apiBase}/api/admin/module/${id}`, {
        method: "DELETE",
        headers: headers(),
      })
        .then(() => {
          setSelectedModuleId(null);
          loadModuleList();
        })
        .catch(() => {});
    },
    [apiBase, adminToken, loadModuleList],
  );

  const loadModuleTools = useCallback(
    (svcId) => {
      if (!adminToken || !apiBase || !svcId) return;
      setModuleToolsLoading(true);
      fetch(`${apiBase}/api/admin/module/${svcId}/tools`, {
        headers: headers(),
      })
        .then((r) => r.json())
        .then((data) => {
          if (Array.isArray(data)) setModuleTools(data);
        })
        .catch(() => {})
        .finally(() => setModuleToolsLoading(false));
    },
    [apiBase, adminToken],
  );

  const createModuleTool = useCallback(
    (svcId, payload) => {
      if (!adminToken || !apiBase || !svcId) return;
      fetch(`${apiBase}/api/admin/module/${svcId}/tools`, {
        method: "POST",
        headers: { "Content-Type": "application/json", ...headers() },
        body: JSON.stringify(payload),
      })
        .then((r) => r.json())
        .then((data) => {
          loadModuleTools(svcId);
          if (data && data.id) setModuleToolEditId(data.id);
        })
        .catch(() => {});
    },
    [apiBase, adminToken, loadModuleTools],
  );

  const saveModuleTool = useCallback(
    (svcId, toolId, payload) => {
      if (!adminToken || !apiBase || !toolId) return;
      fetch(`${apiBase}/api/admin/module-tools/${toolId}`, {
        method: "PUT",
        headers: { "Content-Type": "application/json", ...headers() },
        body: JSON.stringify(payload),
      })
        .then(() => loadModuleTools(svcId))
        .catch(() => {});
    },
    [apiBase, adminToken, loadModuleTools],
  );

  const deleteModuleTool = useCallback(
    (svcId, toolId) => {
      if (!adminToken || !apiBase || !toolId) return;
      fetch(`${apiBase}/api/admin/module-tools/${toolId}`, {
        method: "DELETE",
        headers: headers(),
      })
        .then(() => {
          if (moduleToolEditId === toolId) setModuleToolEditId(null);
          loadModuleTools(svcId);
        })
        .catch(() => {});
    },
    [apiBase, adminToken, loadModuleTools, moduleToolEditId],
  );

  // ---- modules_tests CRUD (Tab "Deine KI" im Kunden-Login) ----
  const loadModuleTests = useCallback(
    (svcId) => {
      if (!adminToken || !apiBase || !svcId) return;
      setModuleTestsLoading(true);
      fetch(`${apiBase}/api/admin/module/${svcId}/tests`, {
        headers: headers(),
      })
        .then((r) => r.json())
        .then((data) => {
          if (data && Array.isArray(data.tests)) setModuleTests(data.tests);
        })
        .catch(() => {})
        .finally(() => setModuleTestsLoading(false));
    },
    [apiBase, adminToken],
  );

  const createModuleTest = useCallback(
    (svcId, payload) => {
      if (!adminToken || !apiBase || !svcId) return;
      fetch(`${apiBase}/api/admin/module/${svcId}/tests`, {
        method: "POST",
        headers: { "Content-Type": "application/json", ...headers() },
        body: JSON.stringify(payload),
      })
        .then((r) => r.json())
        .then((data) => {
          loadModuleTests(svcId);
          if (data && data.id) setModuleTestEditId(data.id);
        })
        .catch(() => {});
    },
    [apiBase, adminToken, loadModuleTests],
  );

  const saveModuleTest = useCallback(
    (svcId, testId, payload) => {
      if (!adminToken || !apiBase || !testId) return Promise.resolve(null);
      return fetch(`${apiBase}/api/admin/modules-tests/${testId}`, {
        method: "PUT",
        headers: { "Content-Type": "application/json", ...headers() },
        body: JSON.stringify(payload),
      })
        .then(async (r) => {
          const data = await r.json().catch(() => ({}));
          if (!r.ok) throw new Error(data.error || "Fehler beim Speichern");
          loadModuleTests(svcId);
          return data;
        });
    },
    [apiBase, adminToken, loadModuleTests],
  );

  const deleteModuleTest = useCallback(
    (svcId, testId) => {
      if (!adminToken || !apiBase || !testId) return;
      fetch(`${apiBase}/api/admin/modules-tests/${testId}`, {
        method: "DELETE",
        headers: headers(),
      })
        .then(() => {
          if (moduleTestEditId === testId) setModuleTestEditId(null);
          loadModuleTests(svcId);
        })
        .catch(() => {});
    },
    [apiBase, adminToken, loadModuleTests, moduleTestEditId],
  );

  const loadCallsList = useCallback(() => {
    if (!adminToken || !apiBase) return;
    setCallsLoading(true);
    const params = new URLSearchParams();
    if (callsFilters.q) params.set("q", callsFilters.q);
    if (callsFilters.userId)
      params.set("userId", callsFilters.userId);
    if (callsFilters.tool) params.set("tool", callsFilters.tool);
    if (callsFilters.unmatched) params.set("unmatched", "1");
    if (callsFilters.from) params.set("from", callsFilters.from);
    if (callsFilters.to) params.set("to", callsFilters.to);
    params.set("pageSize", "100");
    fetch(`${apiBase}/api/admin/calls?${params.toString()}`, {
      headers: headers(),
    })
      .then((r) => r.json())
      .then((data) => {
        if (data && Array.isArray(data.calls)) {
          setCallsList(data.calls);
          setCallsTotal(data.total || data.calls.length);
        }
      })
      .catch(() => {})
      .finally(() => setCallsLoading(false));
  }, [apiBase, adminToken, callsFilters]);

  const loadCallsTools = useCallback(() => {
    if (!adminToken || !apiBase) return;
    fetch(`${apiBase}/api/admin/calls/tools`, { headers: headers() })
      .then((r) => r.json())
      .then((data) => {
        if (data && Array.isArray(data.tools)) setCallsTools(data.tools);
      })
      .catch(() => {});
  }, [apiBase, adminToken]);

  const loadCallDetail = useCallback(
    (id) => {
      if (!adminToken || !apiBase || !id) return;
      setCallDetailLoading(true);
      fetch(`${apiBase}/api/admin/calls/${id}`, { headers: headers() })
        .then((r) => r.json())
        .then((data) => {
          setCallDetail(data || null);
        })
        .catch(() => {
          setCallDetail(null);
        })
        .finally(() => setCallDetailLoading(false));
    },
    [apiBase, adminToken],
  );

  useEffect(() => {
    if (isLoggedIn && activeView === "anrufe") {
      loadCallsList();
      loadCallsTools();
    }
  }, [isLoggedIn, activeView, loadCallsList, loadCallsTools]);

  const loadErrorLogs = useCallback(() => {
    if (!adminToken || !apiBase) return;
    setErrorLogsLoading(true);
    fetch(`${apiBase}/api/admin/error-logs?limit=200`, { headers: headers() })
      .then((r) => r.json())
      .then((data) => {
        if (data && Array.isArray(data.items)) {
          setErrorLogsItems(data.items);
          setErrorLogsTotal(data.total || data.items.length);
        }
      })
      .catch(() => {})
      .finally(() => setErrorLogsLoading(false));
  }, [apiBase, adminToken]);

  useEffect(() => {
    if (isLoggedIn && activeView === "error_logs") {
      loadErrorLogs();
    }
  }, [isLoggedIn, activeView, loadErrorLogs]);

  const loadAisList = useCallback(() => {
    if (!adminToken || !apiBase) return;
    setAisLoading(true);
    fetch(`${apiBase}/api/admin/ais`, { headers: headers() })
      .then((r) => r.json())
      .then((data) => {
        if (data && Array.isArray(data.ais)) setAisList(data.ais);
      })
      .catch(() => {})
      .finally(() => setAisLoading(false));
  }, [apiBase, adminToken]);

  useEffect(() => {
    if (isLoggedIn && activeView === "kis") loadAisList();
  }, [isLoggedIn, activeView, loadAisList]);

  // ---------- E2E Tests ----------
  const loadTestScenarios = useCallback(() => {
    if (!adminToken || !apiBase) return;
    fetch(`${apiBase}/api/admin/test-scenarios`, { headers: headers() })
      .then((r) => r.json())
      .then((data) => {
        if (data && Array.isArray(data.scenarios))
          setTestScenarios(data.scenarios);
      })
      .catch(() => {});
  }, [apiBase, adminToken]);

  const loadAdminTests = useCallback(() => {
    if (!adminToken || !apiBase) return;
    fetch(`${apiBase}/api/admin/calls-tests?limit=200`, { headers: headers() })
      .then(async (r) => {
        const data = await r.json().catch(() => ({}));
        if (!r.ok || !data || !Array.isArray(data.tests)) return;
        setAdminTestsList(data.tests);
      })
      .catch(() => {});
  }, [apiBase, adminToken]);

  const loadTestRunDetail = useCallback(
    (runId) => {
      if (!adminToken || !apiBase || !runId) return;
      fetch(`${apiBase}/api/admin/test-runs/${runId}`, { headers: headers() })
        .then((r) => r.json())
        .then((data) => {
          if (data && data.id) setTestRunDetail(data);
        })
        .catch(() => {});
    },
    [apiBase, adminToken],
  );

  const startTestRun = useCallback(
    (aiId, scenarioKey) => {
      if (!adminToken || !apiBase || !aiId || !scenarioKey) return;
      setTestStarting(true);
      const aid = parseInt(aiId, 10);
      fetch(`${apiBase}/api/admin/test-runs`, {
        method: "POST",
        headers: { "Content-Type": "application/json", ...headers() },
        body: JSON.stringify({
          ai_id: aid,
          scenario_key: scenarioKey,
        }),
      })
        .then((r) => r.json())
        .then((data) => {
          if (data && data.id != null) {
            const rid = Number(data.id);
            const sc = testScenarios.find((s) => s.key === scenarioKey);
            setSelectedManualTestId(null);
            setSelectedTestRunId(rid);
            setAdminTestsList((prev) => {
              if (prev.some((x) => Number(x.id) === rid)) return prev;
              const ai = aisList.find((a) => Number(a.id) === aid);
              return [
                {
                  id: rid,
                  test_kind: "e2e",
                  ai_id: aid,
                  status: data.status || "running",
                  scenario_key: scenarioKey,
                  scenario_name: sc ? sc.name || scenarioKey : scenarioKey,
                  started_at: new Date().toISOString(),
                  duration_ms: null,
                  summary: null,
                  ended_by: null,
                  ended_at: null,
                  admin_user_id: null,
                  error_message: null,
                  ai_name: ai ? ai.ai_name : "",
                  industry_slug: ai ? ai.industry_slug : "",
                },
                ...prev,
              ];
            });
            loadAdminTests();
          }
        })
        .catch(() => {})
        .finally(() => setTestStarting(false));
    },
    [apiBase, adminToken, loadAdminTests, testScenarios, aisList],
  );

  const deleteTestRun = useCallback(
    (runId) => {
      if (!adminToken || !apiBase || !runId) return;
      fetch(`${apiBase}/api/admin/test-runs/${runId}`, {
        method: "DELETE",
        headers: headers(),
      })
        .then(() => {
          if (Number(selectedTestRunId) === Number(runId)) setSelectedTestRunId(null);
          loadAdminTests();
        })
        .catch(() => {});
    },
    [apiBase, adminToken, loadAdminTests, selectedTestRunId],
  );

  const loadManualTestDetail = useCallback((id) => {
    if (!adminToken || !apiBase || !id) return;
    fetch(`${apiBase}/api/admin/calls-tests/${id}`, { headers: headers() })
      .then((r) => r.json())
      .then((data) => {
        if (data && data.id) setManualTestDetail(data);
      })
      .catch(() => {});
  }, [apiBase, adminToken]);

  const startManualTest = useCallback((aiId) => {
    if (!adminToken || !apiBase || !aiId) return;
    setManualStarting(true);
    setManualConnecting(true);
    fetch(`${apiBase}/api/admin/calls-tests`, {
      method: "POST",
      headers: { "Content-Type": "application/json", ...headers() },
      body: JSON.stringify({ ai_id: parseInt(aiId, 10) }),
    })
      .then((r) => r.json())
      .then((data) => {
        if (data && data.id != null) {
          const mid = Number(data.id);
          setTestRunDetail(null);
          setSelectedTestRunId(null);
          setManualTestDetail({ ...data, id: mid });
          setSelectedManualTestId(mid);
          loadAdminTests();
          // Verbindungs-Aufbau-Animation: kurz halten, dann wegnehmen.
          setTimeout(() => setManualConnecting(false), 1200);
        } else {
          setManualConnecting(false);
        }
      })
      .catch(() => setManualConnecting(false))
      .finally(() => setManualStarting(false));
  }, [apiBase, adminToken, loadAdminTests]);

  const sendManualMessage = useCallback((id, content) => {
    if (!adminToken || !apiBase || !id || !content || !content.trim()) return;
    setManualSending(true);
    // Optimistic: User-Message sofort anzeigen.
    setManualTestDetail((prev) => prev && prev.id === id
      ? { ...prev, transcript: [...(prev.transcript || []), { role: "user", content }] }
      : prev);
    fetch(`${apiBase}/api/admin/calls-tests/${id}/message`, {
      method: "POST",
      headers: { "Content-Type": "application/json", ...headers() },
      body: JSON.stringify({ content }),
    })
      .then((r) => r.json())
      .then((data) => {
        if (data && Array.isArray(data.transcript)) {
          setManualTestDetail((prev) => prev && prev.id === id
            ? { ...prev, transcript: data.transcript, status: data.status || prev.status, ended_by: data.ended_by || prev.ended_by }
            : prev);
          if (data.status === "ended") {
            if (lastViewedManualIdRef.current === id) lastViewedManualIdRef.current = null;
            loadAdminTests();
          }
        }
      })
      .catch(() => {})
      .finally(() => setManualSending(false));
  }, [apiBase, adminToken, loadAdminTests]);

  const endManualTest = useCallback((id, endedBy = "admin") => {
    if (!adminToken || !apiBase || !id) return;
    if (lastViewedManualIdRef.current === id) lastViewedManualIdRef.current = null;
    fetch(`${apiBase}/api/admin/calls-tests/${id}/end`, {
      method: "POST",
      headers: { "Content-Type": "application/json", ...headers() },
      body: JSON.stringify({ ended_by: endedBy }),
    })
      .then(() => {
        setManualTestDetail((prev) => prev && prev.id === id
          ? { ...prev, status: "ended", ended_by: endedBy }
          : prev);
        loadAdminTests();
      })
      .catch(() => {});
  }, [apiBase, adminToken, loadAdminTests]);

  useEffect(() => {
    if (isLoggedIn && activeView === "tests") {
      loadTestScenarios();
      loadAdminTests();
      if (!aisList.length) loadAisList();
    }
  }, [
    isLoggedIn,
    activeView,
    loadTestScenarios,
    loadAdminTests,
    loadAisList,
    aisList.length,
  ]);

  useEffect(() => {
    if (selectedTestRunId) loadTestRunDetail(selectedTestRunId);
    else setTestRunDetail(null);
  }, [selectedTestRunId, loadTestRunDetail]);

  // ---------- Manuelle Chat-Tests: Lifecycle ----------
  const loadAdminAgendas = useCallback(() => {
    if (!adminToken || !apiBase) return;
    setAdminAgendasLoading(true);
    setAdminAgendasError("");
    fetch(`${apiBase}/api/admin/agendas`, { headers: headers() })
      .then(async (r) => {
        const j = await r.json().catch(() => ({}));
        if (!r.ok) throw new Error(j.error || "Laden fehlgeschlagen");
        setAdminAgendas(Array.isArray(j.agendas) ? j.agendas : []);
      })
      .catch((e) =>
        setAdminAgendasError(
          e && e.message ? e.message : "Agendas konnten nicht geladen werden.",
        ),
      )
      .finally(() => setAdminAgendasLoading(false));
  }, [apiBase, adminToken]);

  const openAgendaCreatePanel = useCallback(() => {
    setAgendaCreateError("");
    setSelectedAdminAgendaId(null);
    setAgendaNameDraft("");
    setAgendaDateDraft(formatLocalDate(new Date()));
    setAgendaNotesDraft("");
    setAgendaPrSloganDraft("");
    setAgendaSalesFocusDraft("");
    setAgendaPeopleFocusDraft("");
    setAgendaCreateOpen(true);
  }, []);

  const closeAgendaCreatePanel = useCallback(() => {
    setAgendaCreateOpen(false);
    setAgendaCreateError("");
    if (isMobile) {
      setSelectedAdminAgendaId(null);
      return;
    }
    if (selectedAdminAgendaId != null) {
      const currentAgenda = adminAgendas.find((a) => a.id === selectedAdminAgendaId);
      if (currentAgenda) {
        setAgendaNameDraft(String(currentAgenda.name || ""));
        setAgendaDateDraft(
          currentAgenda.agenda_date != null
            ? String(currentAgenda.agenda_date).slice(0, 10)
            : "",
        );
        setAgendaNotesDraft(String(currentAgenda.notes || ""));
        setAgendaPrSloganDraft(String(currentAgenda.pr_slogan || ""));
        setAgendaSalesFocusDraft(String(currentAgenda.sales_focus || ""));
        setAgendaPeopleFocusDraft(String(currentAgenda.people_focus || ""));
      }
    }
  }, [isMobile, selectedAdminAgendaId, adminAgendas]);

  const openAgendaEditPanel = useCallback((agenda) => {
    if (!agenda || agenda.id == null) return;
    setSelectedAdminAgendaId(agenda.id);
    setAgendaCreateOpen(true);
    setAgendaCreateError("");
    setAgendaNameDraft(String(agenda.name || ""));
    const dateStr =
      agenda.agenda_date != null ? String(agenda.agenda_date).slice(0, 10) : "";
    setAgendaDateDraft(dateStr);
    setAgendaNotesDraft(String(agenda.notes || ""));
    setAgendaPrSloganDraft(String(agenda.pr_slogan || ""));
    setAgendaSalesFocusDraft(String(agenda.sales_focus || ""));
    setAgendaPeopleFocusDraft(String(agenda.people_focus || ""));
  }, []);

  const saveAdminAgenda = useCallback(async () => {
    if (!adminToken || !apiBase) return;
    const name = agendaNameDraft.trim();
    const agenda_date = agendaDateDraft.trim();
    const notes = agendaNotesDraft.trim();
    const pr_slogan = agendaPrSloganDraft.trim();
    const sales_focus = agendaSalesFocusDraft.trim();
    const people_focus = agendaPeopleFocusDraft.trim();
    if (!name) {
      setAgendaCreateError("Name eingeben.");
      return;
    }
    if (!/^\d{4}-\d{2}-\d{2}$/.test(agenda_date)) {
      setAgendaCreateError("Datum als YYYY-MM-DD angeben.");
      return;
    }
    setAgendaCreateSaving(true);
    setAgendaCreateError("");
    try {
      const res = await fetch(`${apiBase}/api/admin/agendas`, {
        method: "POST",
        headers: { "Content-Type": "application/json", ...headers() },
        body: JSON.stringify({
          name,
          agenda_date,
          notes,
          pr_slogan,
          sales_focus,
          people_focus,
        }),
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) {
        setAgendaCreateError(data.error || "Speichern fehlgeschlagen.");
        return;
      }
      setAgendaCreateOpen(false);
      setSelectedAdminAgendaId(data.id || null);
      await loadAdminAgendas();
    } catch {
      setAgendaCreateError("Netzwerkfehler.");
    } finally {
      setAgendaCreateSaving(false);
    }
  }, [
    adminToken,
    apiBase,
    agendaDateDraft,
    agendaNameDraft,
    agendaNotesDraft,
    agendaPrSloganDraft,
    agendaSalesFocusDraft,
    agendaPeopleFocusDraft,
    loadAdminAgendas,
  ]);

  const saveAdminAgendaUpdate = useCallback(async () => {
    if (!adminToken || !apiBase || selectedAdminAgendaId == null) return;
    const name = agendaNameDraft.trim();
    const agenda_date = agendaDateDraft.trim();
    const notes = agendaNotesDraft.trim();
    const pr_slogan = agendaPrSloganDraft.trim();
    const sales_focus = agendaSalesFocusDraft.trim();
    const people_focus = agendaPeopleFocusDraft.trim();
    if (!name) {
      setAgendaCreateError("Name eingeben.");
      return;
    }
    if (!/^\d{4}-\d{2}-\d{2}$/.test(agenda_date)) {
      setAgendaCreateError("Datum als YYYY-MM-DD angeben.");
      return;
    }
    setAgendaUpdateSaving(true);
    setAgendaCreateError("");
    try {
      const res = await fetch(
        `${apiBase}/api/admin/agendas/${selectedAdminAgendaId}`,
        {
          method: "PUT",
          headers: { "Content-Type": "application/json", ...headers() },
          body: JSON.stringify({
            name,
            agenda_date,
            notes,
            pr_slogan,
            sales_focus,
            people_focus,
          }),
        },
      );
      const data = await res.json().catch(() => ({}));
      if (!res.ok) {
        setAgendaCreateError(data.error || "Speichern fehlgeschlagen.");
        return;
      }
      await loadAdminAgendas();
    } catch {
      setAgendaCreateError("Netzwerkfehler.");
    } finally {
      setAgendaUpdateSaving(false);
    }
  }, [
    adminToken,
    apiBase,
    selectedAdminAgendaId,
    agendaNameDraft,
    agendaDateDraft,
    agendaNotesDraft,
    agendaPrSloganDraft,
    agendaSalesFocusDraft,
    agendaPeopleFocusDraft,
    loadAdminAgendas,
  ]);

  useEffect(() => {
    if (isLoggedIn && (activeView === "admin_agendas" || activeView === "sales_home"))
      loadAdminAgendas();
  }, [isLoggedIn, activeView, loadAdminAgendas]);

  const loadUserStories = useCallback(() => {
    if (!adminToken || !apiBase) return;
    setUserStoriesLoading(true);
    setUserStoriesError("");
    membersUserStoriesApi
      .listUserStories({ apiBase, token: adminToken })
      .then((rows) => setUserStories(rows))
      .catch((e) =>
        setUserStoriesError(
          e && e.message
            ? e.message
            : "User Stories konnten nicht geladen werden.",
        ),
      )
      .finally(() => setUserStoriesLoading(false));
  }, [adminToken, apiBase]);

  const loadUserStoryRatings = useCallback(
    (id) => {
      if (!adminToken || !apiBase || id == null) {
        setUserStoryRatings([]);
        return;
      }
      setUserStoryRatingsLoading(true);
      membersUserStoriesApi
        .listRatings({ apiBase, token: adminToken, userStoryId: id })
        .then((rows) => setUserStoryRatings(rows))
        .catch(() => setUserStoryRatings([]))
        .finally(() => setUserStoryRatingsLoading(false));
    },
    [adminToken, apiBase],
  );

  const openUserStoryCreatePanel = useCallback(() => {
    setUserStoryCreateError("");
    setSelectedUserStoryId(null);
    setUserStoryTitleDraft("");
    setUserStoryDescriptionDraft("");
    setUserStoryRatings([]);
    setUserStoryRatingNewScore("7");
    setUserStoryRatingNewComment("");
    setUserStoryCreateOpen(true);
  }, []);

  const closeUserStoryCreatePanel = useCallback(() => {
    setUserStoryCreateOpen(false);
    setUserStoryCreateError("");
    if (isMobile) {
      setSelectedUserStoryId(null);
      return;
    }
    if (selectedUserStoryId != null) {
      const s = userStories.find((u) => u.id === selectedUserStoryId);
      if (s) {
        setUserStoryTitleDraft(String(s.title || ""));
        setUserStoryDescriptionDraft(String(s.description || ""));
      }
    }
  }, [isMobile, selectedUserStoryId, userStories]);

  const openUserStoryEditPanel = useCallback(
    (story) => {
      if (!story || story.id == null) return;
      setSelectedUserStoryId(story.id);
      setUserStoryCreateOpen(true);
      setUserStoryCreateError("");
      setUserStoryTitleDraft(String(story.title || ""));
      setUserStoryDescriptionDraft(String(story.description || ""));
      setUserStoryRatingNewScore("7");
      setUserStoryRatingNewComment("");
      loadUserStoryRatings(story.id);
    },
    [loadUserStoryRatings],
  );

  const saveUserStoryNew = useCallback(async () => {
    if (!adminToken || !apiBase) return;
    const title = userStoryTitleDraft.trim();
    if (!title) {
      setUserStoryCreateError("Titel eingeben.");
      return;
    }
    setUserStoryCreateSaving(true);
    setUserStoryCreateError("");
    try {
      const data = await membersUserStoriesApi.createUserStory({
        apiBase,
        token: adminToken,
        title,
        description: userStoryDescriptionDraft,
      });
      setUserStoryCreateOpen(false);
      setSelectedUserStoryId(data.id || null);
      await loadUserStories();
      if (data.id) {
        setUserStoryTitleDraft(String(title));
        setUserStoryDescriptionDraft(String(userStoryDescriptionDraft));
        loadUserStoryRatings(data.id);
      }
    } catch (e) {
      setUserStoryCreateError(
        e && e.message ? e.message : "Netzwerkfehler.",
      );
    } finally {
      setUserStoryCreateSaving(false);
    }
  }, [
    adminToken,
    apiBase,
    userStoryDescriptionDraft,
    userStoryTitleDraft,
    loadUserStories,
    loadUserStoryRatings,
  ]);

  const saveUserStoryUpdate = useCallback(async () => {
    if (!adminToken || !apiBase || selectedUserStoryId == null) return;
    const title = userStoryTitleDraft.trim();
    if (!title) {
      setUserStoryCreateError("Titel eingeben.");
      return;
    }
    setUserStoryUpdateSaving(true);
    setUserStoryCreateError("");
    try {
      await membersUserStoriesApi.updateUserStory({
        apiBase,
        token: adminToken,
        id: selectedUserStoryId,
        title,
        description: userStoryDescriptionDraft,
      });
      await loadUserStories();
    } catch (e) {
      setUserStoryCreateError(
        e && e.message ? e.message : "Netzwerkfehler.",
      );
    } finally {
      setUserStoryUpdateSaving(false);
    }
  }, [
    adminToken,
    apiBase,
    selectedUserStoryId,
    userStoryDescriptionDraft,
    userStoryTitleDraft,
    loadUserStories,
  ]);

  const saveUserStoryHandler = useCallback(() => {
    if (selectedUserStoryId == null) saveUserStoryNew();
    else saveUserStoryUpdate();
  }, [saveUserStoryNew, saveUserStoryUpdate, selectedUserStoryId]);

  const deleteUserStoryAction = useCallback(async () => {
    if (!adminToken || !apiBase || selectedUserStoryId == null) return;
    if (
      !window.confirm("Diese User Story wirklich löschen? (inkl. Bewertungen.)")
    )
      return;
    setUserStoryDeleteSaving(true);
    setUserStoryCreateError("");
    try {
      await membersUserStoriesApi.deleteUserStory({
        apiBase,
        token: adminToken,
        id: selectedUserStoryId,
      });
      setUserStoryCreateOpen(false);
      setSelectedUserStoryId(null);
      setUserStoryTitleDraft("");
      setUserStoryDescriptionDraft("");
      setUserStoryRatings([]);
      await loadUserStories();
    } catch (e) {
      setUserStoryCreateError(
        e && e.message ? e.message : "Löschen fehlgeschlagen.",
      );
    } finally {
      setUserStoryDeleteSaving(false);
    }
  }, [adminToken, apiBase, selectedUserStoryId, loadUserStories]);

  const addUserStoryRating = useCallback(async () => {
    if (!adminToken || !apiBase || selectedUserStoryId == null) return;
    const s = String(userStoryRatingNewScore || "").trim();
    const score = parseInt(s, 10);
    if (!Number.isFinite(score) || score < 1 || score > 10) {
      setUserStoryCreateError("Score: Ganzzahl von 1 bis 10.");
      return;
    }
    setUserStoryCreateError("");
    setUserStoryRatingAddSaving(true);
    try {
      await membersUserStoriesApi.addRating({
        apiBase,
        token: adminToken,
        userStoryId: selectedUserStoryId,
        score,
        comment: userStoryRatingNewComment,
      });
      setUserStoryRatingNewComment("");
      loadUserStoryRatings(selectedUserStoryId);
    } catch (e) {
      setUserStoryCreateError(
        e && e.message ? e.message : "Bewertung konnte nicht gespeichert werden.",
      );
    } finally {
      setUserStoryRatingAddSaving(false);
    }
  }, [
    adminToken,
    apiBase,
    selectedUserStoryId,
    userStoryRatingNewScore,
    userStoryRatingNewComment,
    loadUserStoryRatings,
  ]);

  const deleteUserStoryRating = useCallback(
    async (ratingId) => {
      if (!adminToken || !apiBase || selectedUserStoryId == null) return;
      if (!window.confirm("Diese Bewertung löschen?")) return;
      setUserStoryRatingDeleteId(ratingId);
      try {
        await membersUserStoriesApi.deleteRating({
          apiBase,
          token: adminToken,
          userStoryId: selectedUserStoryId,
          ratingId,
        });
        loadUserStoryRatings(selectedUserStoryId);
      } catch (e) {
        setUserStoryCreateError(
          e && e.message ? e.message : "Löschen fehlgeschlagen.",
        );
      } finally {
        setUserStoryRatingDeleteId(null);
      }
    },
    [adminToken, apiBase, selectedUserStoryId, loadUserStoryRatings],
  );

  useEffect(() => {
    if (isLoggedIn && activeView === "product_userstories") loadUserStories();
  }, [isLoggedIn, activeView, loadUserStories]);

  const loadMembers = useCallback(() => {
    if (!adminToken || !apiBase) return;
    setMembersLoading(true);
    setMembersError("");
    fetch(`${apiBase}/api/admin/members`, { headers: headers() })
      .then(async (r) => {
        const j = await r.json().catch(() => ({}));
        if (!r.ok) throw new Error(j.error || "Laden fehlgeschlagen");
        setMembersList(Array.isArray(j.members) ? j.members : []);
      })
      .catch((e) =>
        setMembersError(e && e.message ? e.message : "Members konnten nicht geladen werden."),
      )
      .finally(() => setMembersLoading(false));
  }, [adminToken, apiBase]);

  const openNewMemberPanel = useCallback(() => {
    setMemberCreateOpen(true);
    setSelectedMemberId(null);
    setMemberFormError("");
    setMemberEmailDraft("");
    setMemberDisplayNameDraft("");
    setMemberPasswordDraft("");
    setMemberInvitedFromDraft(
      adminUser?.id != null ? String(adminUser.id) : "1",
    );
    setMemberAccessDraft({
      product: false,
      marketing: false,
      sales: false,
      admin: false,
      people: false,
      ai_agents: false,
      finance: false,
      design: false,
    });
  }, [adminUser]);

  const openEditMemberPanel = useCallback((member) => {
    if (!member || member.id == null) return;
    setMemberCreateOpen(true);
    setSelectedMemberId(member.id);
    setMemberFormError("");
    setMemberEmailDraft(String(member.email || ""));
    setMemberDisplayNameDraft(String(member.display_name || ""));
    setMemberPasswordDraft(String(member.phone_e164 || ""));
    setMemberAccessDraft({
      product: !!member.access_product,
      marketing: !!member.access_marketing,
      sales: !!member.access_sales,
      admin: !!member.access_admin,
      people: !!member.access_people,
      ai_agents: !!member.access_ai_agents,
      finance: !!member.access_finance,
      design: !!member.access_design,
    });
    setMemberInvitedFromDraft(
      member.invited_from != null ? String(member.invited_from) : "1",
    );
  }, []);

  const saveMemberForm = useCallback(async () => {
    if (!adminToken || !apiBase) return;
    const invitedFromNum = parseInt(String(memberInvitedFromDraft || "").trim(), 10);
    if (!Number.isFinite(invitedFromNum)) {
      setMemberFormError("Eingeladen von: gültigen Member wählen.");
      return;
    }
    const phoneTrim = (memberPasswordDraft || "").trim();
    // Hinweis: nach SMS-Auth-Umstellung wird `memberPasswordDraft` als
    // Mobilnummer-Eingabefeld weitergenutzt (UI-Label aktualisiert).
    const payload = {
      email: memberEmailDraft.trim().toLowerCase() || null,
      phone: phoneTrim,
      country: "DE",
      display_name: memberDisplayNameDraft.trim(),
      access_product: !!memberAccessDraft.product,
      access_marketing: !!memberAccessDraft.marketing,
      access_sales: !!memberAccessDraft.sales,
      access_admin: !!memberAccessDraft.admin,
      access_people: !!memberAccessDraft.people,
      access_ai_agents: !!memberAccessDraft.ai_agents,
      access_finance: !!memberAccessDraft.finance,
      access_design: !!memberAccessDraft.design,
      invited_from: invitedFromNum,
    };
    if (!phoneTrim) {
      setMemberFormError("Mobilnummer (E.164, z. B. +4915112345678) ist Pflichtfeld.");
      return;
    }
    setMemberSaving(true);
    setMemberFormError("");
    try {
      const url =
        selectedMemberId == null
          ? `${apiBase}/api/admin/members`
          : `${apiBase}/api/admin/members/${selectedMemberId}`;
      const method = selectedMemberId == null ? "POST" : "PUT";
      const res = await fetch(url, {
        method,
        headers: { "Content-Type": "application/json", ...headers() },
        body: JSON.stringify(payload),
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) {
        setMemberFormError(data.error || "Speichern fehlgeschlagen.");
        return;
      }
      await loadMembers();
      if (selectedMemberId == null) {
        setSelectedMemberId(data.id || null);
      }
    } catch {
      setMemberFormError("Netzwerkfehler.");
    } finally {
      setMemberSaving(false);
    }
  }, [
    adminToken,
    apiBase,
    memberEmailDraft,
    memberDisplayNameDraft,
    memberPasswordDraft,
    memberAccessDraft,
    memberInvitedFromDraft,
    selectedMemberId,
    loadMembers,
  ]);

  const closeMemberPanel = useCallback(() => {
    setMemberCreateOpen(false);
    setMemberFormError("");
    if (isMobile) {
      setSelectedMemberId(null);
    }
  }, [isMobile]);

  useEffect(() => {
    if (isLoggedIn && activeView === "people_members") loadMembers();
  }, [isLoggedIn, activeView, loadMembers]);

  const salesMediaUrl = useCallback(
    (fileKey) => {
      if (!apiBase || !adminToken || !fileKey) return "";
      return (
        `${apiBase}/api/admin/members-media/${encodeURIComponent(fileKey)}` +
        `?token=${encodeURIComponent(adminToken)}`
      );
    },
    [apiBase, adminToken],
  );

  const loadSalesCustomers = useCallback(() => {
    if (!adminToken || !apiBase) return;
    setSalesCustomersLoading(true);
    setSalesCustomersError("");
    fetch(`${apiBase}/api/admin/members-customers`, { headers: headers() })
      .then(async (r) => {
        const j = await r.json().catch(() => ({}));
        if (!r.ok) throw new Error(j.error || "Laden fehlgeschlagen");
        setSalesCustomers(Array.isArray(j.customers) ? j.customers : []);
      })
      .catch((e) =>
        setSalesCustomersError(
          e && e.message ? e.message : "Kunden konnten nicht geladen werden.",
        ),
      )
      .finally(() => setSalesCustomersLoading(false));
  }, [apiBase, adminToken]);

  const loadAcquisitionCustomers = useCallback(() => {
    if (!adminToken || !apiBase) return;
    setAcquisitionCustomersLoading(true);
    setAcquisitionCustomersError("");
    membersCustomersApi
      .fetchAcquisition({ apiBase, token: adminToken })
      .then(setAcquisitionCustomers)
      .catch((e) =>
        setAcquisitionCustomersError(
          e && e.message ? e.message : "Akquise konnte nicht geladen werden.",
        ),
      )
      .finally(() => setAcquisitionCustomersLoading(false));
  }, [apiBase, adminToken]);

  const loadServiceCustomers = useCallback(() => {
    if (!adminToken || !apiBase) return;
    setServiceCustomersLoading(true);
    setServiceCustomersError("");
    membersCustomersApi
      .fetchService({ apiBase, token: adminToken })
      .then(setServiceCustomers)
      .catch((e) =>
        setServiceCustomersError(
          e && e.message ? e.message : "Service konnte nicht geladen werden.",
        ),
      )
      .finally(() => setServiceCustomersLoading(false));
  }, [apiBase, adminToken]);

  const loadCustomerHistory = useCallback(
    (customerId) => {
      if (!adminToken || !apiBase || customerId == null) return;
      setSalesCustomerHistoryLoading(true);
      setSalesCustomerHistoryError("");
      membersCustomersApi
        .fetchHistory({ apiBase, token: adminToken, customerId })
        .then((payload) => {
          setSalesCustomerHistory(
            Array.isArray(payload.history) ? payload.history : [],
          );
        })
        .catch((e) =>
          setSalesCustomerHistoryError(
            e && e.message ? e.message : "Historie konnte nicht geladen werden.",
          ),
        )
        .finally(() => setSalesCustomerHistoryLoading(false));
    },
    [apiBase, adminToken],
  );

  const openSalesCustomerCreatePanel = useCallback(() => {
    setSalesCustomerFormError("");
    setSelectedSalesCustomerId(null);
    setSalesCustomerCompanyDraft("");
    setSalesCustomerContactDraft("");
    setSalesCustomerEmailDraft("");
    setSalesCustomerPhoneDraft("");
    setSalesCustomerIndustryDraft("");
    setSalesCustomerNotesDraft("");
    setSalesCustomerUserIdDraft(null);
    setSalesCustomerLinkUsers([]);
    setSalesCustomerLinkUsersLoading(false);
    setSalesCustomerDetailMoreOpen(false);
    setSalesCustomerApptOpen(false);
    setSalesCustomerHistory([]);
    setSalesCustomerPanelOpen(true);
  }, []);

  const closeSalesCustomerPanel = useCallback(() => {
    setSalesCustomerPanelOpen(false);
    setSalesCustomerFormError("");
    setSalesCustomerLinkUsers([]);
    setSalesCustomerLinkUsersLoading(false);
    if (salesCustomerLinkDebounceRef.current) {
      clearTimeout(salesCustomerLinkDebounceRef.current);
      salesCustomerLinkDebounceRef.current = null;
    }
    if (isMobile) {
      setSelectedSalesCustomerId(null);
      setSalesCustomerUserIdDraft(null);
      if (salesCustomerReturnView) {
        const target = salesCustomerReturnView;
        setSalesCustomerReturnView(null);
        setActiveView(target);
      }
      return;
    }
    if (selectedSalesCustomerId != null) {
      const cur = salesCustomers.find((c) => c.id === selectedSalesCustomerId);
      if (cur) {
        setSalesCustomerCompanyDraft(String(cur.company_name || ""));
        setSalesCustomerContactDraft(String(cur.contact_name || ""));
        setSalesCustomerEmailDraft(String(cur.email || ""));
        setSalesCustomerPhoneDraft(String(cur.phone || ""));
        setSalesCustomerIndustryDraft(String(cur.industry || ""));
        setSalesCustomerNotesDraft(String(cur.notes || ""));
        setSalesCustomerUserIdDraft(
          cur.user_id != null && Number.isFinite(Number(cur.user_id))
            ? Number(cur.user_id)
            : null,
        );
      }
    }
  }, [isMobile, selectedSalesCustomerId, salesCustomers, salesCustomerReturnView]);

  const openSalesCustomerEditPanel = useCallback(
    (row) => {
      if (!row || row.id == null) return;
      setSelectedSalesCustomerId(row.id);
      setSalesCustomerPanelOpen(true);
      setSalesCustomerFormError("");
      setSalesCustomerActionError("");
      setSalesCustomerCompanyDraft(String(row.company_name || ""));
      setSalesCustomerContactDraft(String(row.contact_name || ""));
      setSalesCustomerEmailDraft(String(row.email || ""));
      setSalesCustomerPhoneDraft(String(row.phone || ""));
      setSalesCustomerIndustryDraft(String(row.industry || ""));
      setSalesCustomerNotesDraft(String(row.notes || ""));
      setSalesCustomerUserIdDraft(
        row.user_id != null && Number.isFinite(Number(row.user_id))
          ? Number(row.user_id)
          : null,
      );
      setSalesCustomerDetailMoreOpen(false);
      setSalesCustomerApptOpen(false);
      loadCustomerHistory(row.id);
    },
    [loadCustomerHistory],
  );

  const saveSalesCustomerField = useCallback(
    async (overrides = {}) => {
      if (!adminToken || !apiBase || selectedSalesCustomerId == null) return false;
      const company_name = (
        overrides.company_name != null
          ? String(overrides.company_name)
          : salesCustomerCompanyDraft
      ).trim();
      const contact_name = (
        overrides.contact_name != null
          ? String(overrides.contact_name)
          : salesCustomerContactDraft
      ).trim();
      const email = (
        overrides.email != null ? String(overrides.email) : salesCustomerEmailDraft
      ).trim();
      const phone = (
        overrides.phone != null ? String(overrides.phone) : salesCustomerPhoneDraft
      ).trim();
      const industry = (
        overrides.industry != null
          ? String(overrides.industry)
          : salesCustomerIndustryDraft
      ).trim();
      const notes = (
        overrides.notes != null ? String(overrides.notes) : salesCustomerNotesDraft
      ).trim();
      if (!company_name) {
        setSalesCustomerFormError("Firmenname eingeben.");
        return false;
      }
      setSalesCustomerSaving(true);
      setSalesCustomerFormError("");
      try {
        await membersCustomersApi.saveCustomer({
          apiBase,
          token: adminToken,
          id: selectedSalesCustomerId,
          payload: {
            company_name,
            contact_name,
            email,
            phone,
            industry,
            notes,
            user_id: salesCustomerUserIdDraft,
          },
        });
        loadAcquisitionCustomers();
        loadServiceCustomers();
        loadSalesCustomers();
        return true;
      } catch (e) {
        setSalesCustomerFormError(
          e && e.message ? e.message : "Speichern fehlgeschlagen.",
        );
        return false;
      } finally {
        setSalesCustomerSaving(false);
      }
    },
    [
      adminToken,
      apiBase,
      salesCustomerCompanyDraft,
      salesCustomerContactDraft,
      salesCustomerEmailDraft,
      salesCustomerPhoneDraft,
      salesCustomerIndustryDraft,
      salesCustomerNotesDraft,
      salesCustomerUserIdDraft,
      selectedSalesCustomerId,
      loadAcquisitionCustomers,
      loadServiceCustomers,
      loadSalesCustomers,
    ],
  );

  const saveSalesCustomer = useCallback(async () => {
    if (!adminToken || !apiBase) return;
    const company_name = salesCustomerCompanyDraft.trim();
    const contact_name = salesCustomerContactDraft.trim();
    const email = salesCustomerEmailDraft.trim();
    const phone = salesCustomerPhoneDraft.trim();
    const industry = salesCustomerIndustryDraft.trim();
    const notes = salesCustomerNotesDraft.trim();
    if (!company_name) {
      setSalesCustomerFormError("Firmenname eingeben.");
      return;
    }
    setSalesCustomerSaving(true);
    setSalesCustomerFormError("");
    try {
      const isEdit = selectedSalesCustomerId != null;
      const data = await membersCustomersApi.saveCustomer({
        apiBase,
        token: adminToken,
        id: isEdit ? selectedSalesCustomerId : null,
        payload: {
          company_name,
          contact_name,
          email,
          phone,
          industry,
          notes,
          user_id: salesCustomerUserIdDraft,
        },
      });
      setSalesCustomerPanelOpen(false);
      if (!isEdit) setSelectedSalesCustomerId(data.id || null);
      loadAcquisitionCustomers();
      loadServiceCustomers();
      loadSalesCustomers();
    } catch (e) {
      setSalesCustomerFormError(
        e && e.message ? e.message : "Netzwerkfehler.",
      );
    } finally {
      setSalesCustomerSaving(false);
    }
  }, [
    adminToken,
    apiBase,
    salesCustomerCompanyDraft,
    salesCustomerContactDraft,
    salesCustomerEmailDraft,
    salesCustomerPhoneDraft,
    salesCustomerIndustryDraft,
    salesCustomerNotesDraft,
    salesCustomerUserIdDraft,
    selectedSalesCustomerId,
    loadAcquisitionCustomers,
    loadServiceCustomers,
    loadSalesCustomers,
  ]);

  const handleSendAppUrl = useCallback(async () => {
    if (!selectedSalesCustomerId || !adminToken || !apiBase) return;
    setSalesCustomerActionSaving(true);
    setSalesCustomerActionError("");
    try {
      await membersCustomersApi.sendAppUrl({
        apiBase,
        token: adminToken,
        customerId: selectedSalesCustomerId,
      });
      loadCustomerHistory(selectedSalesCustomerId);
    } catch (e) {
      setSalesCustomerActionError(
        e && e.message ? e.message : "SMS konnte nicht gesendet werden.",
      );
    } finally {
      setSalesCustomerActionSaving(false);
    }
  }, [selectedSalesCustomerId, adminToken, apiBase, loadCustomerHistory]);

  const handleSendAppointmentSms = useCallback(async () => {
    if (!selectedSalesCustomerId || !adminToken || !apiBase) return;
    const body = salesCustomerApptDraft.trim();
    if (!body) {
      setSalesCustomerActionError("Bitte SMS-Text eingeben.");
      return;
    }
    setSalesCustomerActionSaving(true);
    setSalesCustomerActionError("");
    try {
      await membersCustomersApi.sendAppointmentSms({
        apiBase,
        token: adminToken,
        customerId: selectedSalesCustomerId,
        body,
      });
      setSalesCustomerApptOpen(false);
      loadCustomerHistory(selectedSalesCustomerId);
    } catch (e) {
      setSalesCustomerActionError(
        e && e.message ? e.message : "SMS konnte nicht gesendet werden.",
      );
    } finally {
      setSalesCustomerActionSaving(false);
    }
  }, [
    selectedSalesCustomerId,
    adminToken,
    apiBase,
    salesCustomerApptDraft,
    loadCustomerHistory,
  ]);

  const historyKindMeta = (kind) => {
    if (kind === "app_url_sent") {
      return { title: "App URL gesendet", subtitle: "an", icon: "chat" };
    }
    if (kind === "appointment_sms_sent") {
      return { title: "Terminbestätigung gesendet", subtitle: "", icon: "calendar" };
    }
    if (kind === "call_outbound") {
      return { title: "Ausgehender Anruf", subtitle: "", icon: "phone" };
    }
    if (kind === "call_inbound") {
      return { title: "Eingehender Anruf", subtitle: "", icon: "phone" };
    }
    if (kind === "note") {
      return { title: "Notiz", subtitle: "", icon: "note" };
    }
    return { title: kind || "Ereignis", subtitle: "", icon: "note" };
  };

  const renderSalesCustomerDetailBody = () => {
    const phoneTrim = salesCustomerPhoneDraft.trim();
    const selectedRow =
      [...acquisitionCustomers, ...serviceCustomers, ...salesCustomers].find(
        (c) => c.id === selectedSalesCustomerId,
      ) || null;
    const platformPlan =
      selectedRow && selectedRow.platform_plan != null
        ? selectedRow.platform_plan
        : null;
    return (
      <div className="admin_sales_customer_detail">
        <div className="admin_sales_customer_actions">
          {renderCallControl(selectedSalesCustomerId, phoneTrim)}
          <button
            type="button"
            className="admin_sales_action_btn admin_sales_action_btn--secondary"
            disabled={salesCustomerActionSaving || !phoneTrim}
            onClick={handleSendAppUrl}>
            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" />
              <polyline points="7 10 12 15 17 10" />
              <line x1="12" y1="15" x2="12" y2="3" />
            </svg>
            App URL senden
          </button>
          <button
            type="button"
            className="admin_sales_action_btn admin_sales_action_btn--secondary"
            disabled={salesCustomerActionSaving || !phoneTrim}
            onClick={() => setSalesCustomerApptOpen((v) => !v)}>
            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <rect x="3" y="4" width="18" height="18" rx="2" ry="2" />
              <line x1="16" y1="2" x2="16" y2="6" />
              <line x1="8" y1="2" x2="8" y2="6" />
              <line x1="3" y1="10" x2="21" y2="10" />
            </svg>
            Terminbestätigung SMS senden
          </button>
        </div>
        {voice.error && voice.status === "error" ? (
          <p className="admin_error" style={{ margin: "0 0 12px" }}>
            Anruf nicht möglich: {voice.error}
          </p>
        ) : null}
        {salesCustomerApptOpen ? (
          <div className="admin_sales_appt_compose">
            <textarea
              className="admin_input"
              rows={4}
              value={salesCustomerApptDraft}
              onChange={(e) => setSalesCustomerApptDraft(e.target.value)}
            />
            <button
              type="button"
              className="admin_btn admin_btn--primary"
              disabled={salesCustomerActionSaving}
              onClick={handleSendAppointmentSms}>
              {salesCustomerActionSaving ? "Senden…" : "SMS senden"}
            </button>
          </div>
        ) : null}
        {salesCustomerActionError ? (
          <p className="admin_error" style={{ margin: "0 0 12px" }}>
            {salesCustomerActionError}
          </p>
        ) : null}
        <div className="admin_sales_customer_section">
          <h3 className="admin_sales_customer_section_title">Firma</h3>
          <div className="admin_sales_pill_grid">
            <input
              type="text"
              className="admin_pill_input"
              placeholder="Ansprechpartner"
              value={salesCustomerContactDraft}
              onChange={(e) => setSalesCustomerContactDraft(e.target.value)}
              onBlur={() => saveSalesCustomerField({ contact_name: salesCustomerContactDraft })}
            />
            <input
              type="text"
              className="admin_pill_input"
              placeholder="Firmenname"
              value={salesCustomerCompanyDraft}
              onChange={(e) => setSalesCustomerCompanyDraft(e.target.value)}
              onBlur={() => saveSalesCustomerField({ company_name: salesCustomerCompanyDraft })}
            />
            <input
              type="text"
              className="admin_pill_input admin_pill_input--full"
              placeholder="Branche"
              value={salesCustomerIndustryDraft}
              onChange={(e) => setSalesCustomerIndustryDraft(e.target.value)}
              onBlur={() => saveSalesCustomerField({ industry: salesCustomerIndustryDraft })}
            />
          </div>
          <button
            type="button"
            className="admin_sales_more_toggle"
            onClick={() => setSalesCustomerDetailMoreOpen((v) => !v)}>
            {salesCustomerDetailMoreOpen ? "Weniger anzeigen" : "Weitere Daten öffnen"}
          </button>
          {salesCustomerDetailMoreOpen ? (
            <div className="admin_sales_more_fields">
              <label className="admin_product_user_credentials_label">
                <span>E-Mail</span>
                <input
                  type="email"
                  className="admin_input"
                  value={salesCustomerEmailDraft}
                  onChange={(e) => setSalesCustomerEmailDraft(e.target.value)}
                  onBlur={() => saveSalesCustomerField({ email: salesCustomerEmailDraft })}
                />
              </label>
              <label className="admin_product_user_credentials_label">
                <span>Telefon</span>
                <input
                  type="tel"
                  className="admin_input"
                  value={salesCustomerPhoneDraft}
                  onChange={(e) => setSalesCustomerPhoneDraft(e.target.value)}
                  onBlur={() => saveSalesCustomerField({ phone: salesCustomerPhoneDraft })}
                />
              </label>
              <label className="admin_product_user_credentials_label">
                <span>Notizen</span>
                <textarea
                  className="admin_input"
                  rows={3}
                  value={salesCustomerNotesDraft}
                  onChange={(e) => setSalesCustomerNotesDraft(e.target.value)}
                  onBlur={() => saveSalesCustomerField({ notes: salesCustomerNotesDraft })}
                />
              </label>
              <label className="admin_product_user_credentials_label">
                <span>Telephonio-Nutzer verknüpfen</span>
                <select
                  className="admin_input"
                  disabled={salesCustomerLinkUsersLoading}
                  value={salesCustomerUserIdDraft != null ? String(salesCustomerUserIdDraft) : ""}
                  onChange={(e) => {
                    const v = e.target.value;
                    setSalesCustomerUserIdDraft(v ? Number(v) : null);
                  }}
                  onBlur={() => saveSalesCustomerField()}>
                  <option value="">— Kein Nutzer —</option>
                  {salesCustomerLinkUsers.map((u) => (
                    <option key={u.id} value={String(u.id)}>
                      {(u.display_name || u.email || `#${u.id}`) +
                        (u.plan ? ` (${labelTelephonioPlan(u.plan)})` : "")}
                    </option>
                  ))}
                </select>
              </label>
              {platformPlan ? (
                <p className="admin_product_user_email" style={{ margin: 0 }}>
                  Telephonio-Abo: {labelTelephonioPlan(platformPlan)}
                </p>
              ) : null}
              {selectedRow && Number(selectedRow.open_ticket_count) > 0 ? (
                <p className="admin_product_user_email" style={{ margin: 0 }}>
                  Offene Serviceanfragen: {selectedRow.open_ticket_count}
                  {selectedRow.open_ticket_at
                    ? ` (zuletzt ${formatServiceTime(selectedRow.open_ticket_at)})`
                    : ""}
                </p>
              ) : null}
            </div>
          ) : null}
        </div>
        <div className="admin_sales_customer_section">
          <h3 className="admin_sales_customer_section_title">Historie</h3>
          {salesCustomerHistoryError ? (
            <p className="admin_error">{salesCustomerHistoryError}</p>
          ) : null}
          {salesCustomerHistoryLoading ? (
            <p className="admin_product_empty">Laden…</p>
          ) : salesCustomerHistory.length === 0 ? (
            <p className="admin_product_empty">Noch keine Einträge.</p>
          ) : (
            <div className="admin_sales_history_list">
              {salesCustomerHistory.map((item) => {
                const meta = historyKindMeta(item.kind);
                const sub =
                  item.kind === "appointment_sms_sent"
                    ? String(item.message || "").slice(0, 40)
                    : item.message || meta.subtitle || "—";
                const hasRecording =
                  item.source === "sales_call" && item.recording_sid;
                return (
                  <div
                    key={`${item.source}-${item.id}`}
                    className="admin_sales_history_row">
                    <div className="admin_sales_history_row_top">
                      <div className="admin_sales_history_icon" aria-hidden="true">
                        {meta.icon === "phone" ? "📞" : meta.icon === "calendar" ? "📅" : "💬"}
                      </div>
                      <div className="admin_sales_history_main">
                        <strong>{meta.title}</strong>
                        <span>{sub}</span>
                      </div>
                      <div className="admin_sales_history_meta">
                        <span>{item.member_name || "—"}</span>
                        <strong>{formatHistoryDate(item.created_at)}</strong>
                      </div>
                    </div>
                    {hasRecording ? (
                      <audio
                        className="admin_sales_history_audio"
                        controls
                        preload="none"
                        src={`${apiBase}/api/admin/voice/recordings/${item.recording_sid}/audio?token=${encodeURIComponent(
                          adminToken || "",
                        )}`}
                      />
                    ) : null}
                  </div>
                );
              })}
            </div>
          )}
        </div>
        {salesCustomerFormError ? (
          <p className="admin_error" style={{ marginTop: "12px" }}>
            {salesCustomerFormError}
          </p>
        ) : null}
      </div>
    );
  };

  const loadSalesContents = useCallback(() => {
    if (!adminToken || !apiBase) return;
    setSalesContentsLoading(true);
    setSalesContentsError("");
    fetch(`${apiBase}/api/admin/members-contents`, { headers: headers() })
      .then(async (r) => {
        const j = await r.json().catch(() => ({}));
        if (!r.ok) throw new Error(j.error || "Laden fehlgeschlagen");
        setSalesContents(Array.isArray(j.contents) ? j.contents : []);
      })
      .catch((e) =>
        setSalesContentsError(
          e && e.message ? e.message : "Akquise-Content konnte nicht geladen werden.",
        ),
      )
      .finally(() => setSalesContentsLoading(false));
  }, [apiBase, adminToken]);

  const closeSalesContentPanel = useCallback(() => {
    setSalesContentPanelOpen(false);
    if (isMobile) {
      setSelectedSalesContentId(null);
      if (salesContentReturnView) {
        const target = salesContentReturnView;
        setSalesContentReturnView(null);
        setActiveView(target);
      }
    }
  }, [isMobile, salesContentReturnView]);

  const openSalesContentEditPanel = useCallback((row) => {
    if (!row || row.id == null) return;
    setSelectedSalesContentId(row.id);
    setSalesContentPanelOpen(true);
  }, []);

  const patchAkquiseSocialField = useCallback((contentId, field, value) => {
    setSalesAkquisePostError("");
    setSalesAkquisePostInfo("");
    setSalesAkquiseSocialByContentId((prev) => ({
      ...prev,
      [contentId]: {
        whatsapp: "",
        instagram: "",
        facebook: "",
        tiktok: "",
        ...(prev[contentId] || {}),
        [field]: value,
      },
    }));
  }, []);

  const confirmAkquiseSocialRow = useCallback(
    async (contentId, platform) => {
      if (!adminToken || !apiBase) return;
      const social = salesAkquiseSocialByContentId[contentId] || {};
      const contact_value = String(social[platform] ?? "").trim();
      if (!contact_value) {
        setSalesAkquisePostError("Bitte zuerst Rufnummer oder URL eintragen.");
        return;
      }
      setSalesAkquisePostError("");
      setSalesAkquisePostInfo("");
      const key = `${contentId}:${platform}`;
      setSalesAkquiseConfirmSavingKey(key);
      try {
        const res = await fetch(`${apiBase}/api/admin/members-posts`, {
          method: "POST",
          headers: { "Content-Type": "application/json", ...headers() },
          body: JSON.stringify({
            content_id: contentId,
            channel: platform,
            contact_value,
          }),
        });
        const data = await res.json().catch(() => ({}));
        if (!res.ok) {
          setSalesAkquisePostError(data.error || "Speichern fehlgeschlagen.");
          return;
        }
        setSalesAkquisePostInfo(
          "Anfrage ist bei Marketing eingegangen – dort unter „Posts“ zur Prüfung sichtbar.",
        );
        setSalesAkquiseAwaitingMarketingByContentId((prev) => ({
          ...prev,
          [contentId]: {
            ...(prev[contentId] || {}),
            [platform]: true,
          },
        }));
      } catch {
        setSalesAkquisePostError("Netzwerkfehler.");
      } finally {
        setSalesAkquiseConfirmSavingKey(null);
      }
    },
    [adminToken, apiBase, salesAkquiseSocialByContentId],
  );

  const shareSalesAkquiseContent = useCallback(async () => {
    const row =
      selectedSalesContentId != null
        ? salesContents.find((x) => x.id === selectedSalesContentId)
        : null;
    const title = String(row?.title || "").trim() || "Content";
    const text = String(row?.description || "").trim() || "";
    const mediaUrl = row?.file_key ? salesMediaUrl(row.file_key) : "";
    const payload = {
      title,
      ...(text ? { text } : {}),
      ...(mediaUrl ? { url: mediaUrl } : {}),
    };
    if (typeof navigator !== "undefined" && typeof navigator.share === "function") {
      try {
        await navigator.share(payload);
        return;
      } catch (err) {
        if (err && err.name === "AbortError") return;
      }
    }
    if (mediaUrl && navigator.clipboard && navigator.clipboard.writeText) {
      try {
        await navigator.clipboard.writeText(mediaUrl);
        window.alert("Link wurde in die Zwischenablage kopiert.");
        return;
      } catch {
        /* fall through */
      }
    }
    if (mediaUrl) {
      window.prompt("Link kopieren:", mediaUrl);
    } else {
      window.alert("Kein Medien-Link zum Teilen vorhanden.");
    }
  }, [salesContents, selectedSalesContentId, salesMediaUrl]);

  const downloadSalesAkquiseContent = useCallback(async () => {
    const row =
      selectedSalesContentId != null
        ? salesContents.find((x) => x.id === selectedSalesContentId)
        : null;
    if (!row?.file_key) {
      window.alert("Keine Datei zum Herunterladen.");
      return;
    }
    const mediaUrl = salesMediaUrl(row.file_key);
    if (!mediaUrl) return;
    const baseName =
      String(row.file_key).split("/").pop().split("?")[0] || "medium";
    try {
      const res = await fetch(mediaUrl);
      if (!res.ok) throw new Error("fetch failed");
      const blob = await res.blob();
      const blobUrl = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = blobUrl;
      a.download = baseName;
      a.rel = "noopener";
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(blobUrl);
    } catch {
      try {
        const a = document.createElement("a");
        a.href = mediaUrl;
        a.download = baseName;
        a.rel = "noopener noreferrer";
        a.target = "_blank";
        document.body.appendChild(a);
        a.click();
        a.remove();
      } catch {
        window.open(mediaUrl, "_blank", "noopener,noreferrer");
      }
    }
  }, [salesContents, selectedSalesContentId, salesMediaUrl]);

  const openMarketingCommunityContentCreatePanel = useCallback(() => {
    setMarketingCommunityFormError("");
    setSelectedMarketingCommunityContentId(null);
    setMarketingCommunityKindDraft("image");
    setMarketingCommunityTitleDraft("");
    setMarketingCommunityDescriptionDraft("");
    setMarketingCommunityEarningsEurDraft("0");
    setMarketingCommunityTargetgroupDraft("");
    setMarketingCommunityFileDraft(null);
    setMarketingCommunityPanelOpen(true);
  }, []);

  const closeMarketingCommunityContentPanel = useCallback(() => {
    setMarketingCommunityPanelOpen(false);
    setMarketingCommunityFormError("");
    setMarketingCommunityFileDraft(null);
    if (isMobile) {
      setSelectedMarketingCommunityContentId(null);
      return;
    }
    if (selectedMarketingCommunityContentId != null) {
      const cur = salesContents.find((c) => c.id === selectedMarketingCommunityContentId);
      if (cur) {
        setMarketingCommunityKindDraft(cur.kind === "video" ? "video" : "image");
        setMarketingCommunityTitleDraft(String(cur.title || ""));
        setMarketingCommunityDescriptionDraft(String(cur.description || ""));
        setMarketingCommunityEarningsEurDraft(
          String(
            cur.earnings_eur != null && Number.isFinite(Number(cur.earnings_eur))
              ? Number(cur.earnings_eur)
              : 0,
          ),
        );
        setMarketingCommunityTargetgroupDraft(
          cur.targetgroup_id != null && Number.isFinite(Number(cur.targetgroup_id))
            ? String(cur.targetgroup_id)
            : "",
        );
      }
    }
  }, [isMobile, selectedMarketingCommunityContentId, salesContents]);

  const openMarketingCommunityContentEditPanel = useCallback((row) => {
    if (!row || row.id == null) return;
    setSelectedMarketingCommunityContentId(row.id);
    setMarketingCommunityPanelOpen(true);
    setMarketingCommunityFormError("");
    setMarketingCommunityKindDraft(row.kind === "video" ? "video" : "image");
    setMarketingCommunityTitleDraft(String(row.title || ""));
    setMarketingCommunityDescriptionDraft(String(row.description || ""));
    setMarketingCommunityEarningsEurDraft(
      String(
        row.earnings_eur != null && Number.isFinite(Number(row.earnings_eur))
          ? Number(row.earnings_eur)
          : 0,
      ),
    );
    setMarketingCommunityTargetgroupDraft(
      row.targetgroup_id != null && Number.isFinite(Number(row.targetgroup_id))
        ? String(row.targetgroup_id)
        : "",
    );
    setMarketingCommunityFileDraft(null);
  }, []);

  const saveMarketingCommunityContent = useCallback(async () => {
    if (!adminToken || !apiBase) return;
    const title = marketingCommunityTitleDraft.trim();
    const description = marketingCommunityDescriptionDraft.trim();
    const kind = marketingCommunityKindDraft === "video" ? "video" : "image";
    const isEdit = selectedMarketingCommunityContentId != null;
    if (!isEdit && !marketingCommunityFileDraft) {
      setMarketingCommunityFormError("Datei auswählen.");
      return;
    }
    const earningsTrim = String(marketingCommunityEarningsEurDraft).trim();
    let earningsEur = 0;
    if (earningsTrim !== "") {
      const n = parseInt(earningsTrim, 10);
      if (!Number.isFinite(n) || n < 0) {
        setMarketingCommunityFormError("Verdienst: gültige nichtnegative ganze Zahl.");
        return;
      }
      earningsEur = n;
    }
    setMarketingCommunitySaving(true);
    setMarketingCommunityFormError("");
    try {
      const fd = new FormData();
      fd.append("kind", kind);
      fd.append("title", title);
      fd.append("description", description);
      fd.append("earnings_eur", String(earningsEur));
      fd.append(
        "targetgroup_id",
        String(marketingCommunityTargetgroupDraft || "").trim(),
      );
      if (marketingCommunityFileDraft) fd.append("file", marketingCommunityFileDraft);

      const url = isEdit
        ? `${apiBase}/api/admin/members-contents/${selectedMarketingCommunityContentId}`
        : `${apiBase}/api/admin/members-contents`;
      const method = isEdit ? "PUT" : "POST";
      const res = await fetch(url, {
        method,
        headers: { Authorization: "Bearer " + adminToken },
        body: fd,
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) {
        setMarketingCommunityFormError(data.error || "Speichern fehlgeschlagen.");
        return;
      }
      setMarketingCommunityPanelOpen(false);
      setMarketingCommunityFileDraft(null);
      if (!isEdit) setSelectedMarketingCommunityContentId(data.id || null);
      await loadSalesContents();
    } catch {
      setMarketingCommunityFormError("Netzwerkfehler.");
    } finally {
      setMarketingCommunitySaving(false);
    }
  }, [
    adminToken,
    apiBase,
    marketingCommunityDescriptionDraft,
    marketingCommunityEarningsEurDraft,
    marketingCommunityFileDraft,
    marketingCommunityKindDraft,
    marketingCommunityTitleDraft,
    marketingCommunityTargetgroupDraft,
    selectedMarketingCommunityContentId,
    loadSalesContents,
  ]);

  const loadChannelContents = useCallback(() => {
    if (!adminToken || !apiBase) return;
    setChannelContentsLoading(true);
    setChannelContentsError("");
    fetch(`${apiBase}/api/admin/members-channelcontents`, { headers: headers() })
      .then(async (r) => {
        const j = await r.json().catch(() => ({}));
        if (!r.ok) throw new Error(j.error || "Laden fehlgeschlagen");
        setChannelContents(Array.isArray(j.contents) ? j.contents : []);
      })
      .catch((e) =>
        setChannelContentsError(
          e && e.message ? e.message : "Channel Content konnte nicht geladen werden.",
        ),
      )
      .finally(() => setChannelContentsLoading(false));
  }, [apiBase, adminToken]);

  const openMarketingChannelContentCreatePanel = useCallback(() => {
    setMarketingChannelContentFormError("");
    setSelectedMarketingChannelContentId(null);
    setMarketingChannelContentKindDraft("image");
    setMarketingChannelContentTitleDraft("");
    setMarketingChannelContentDescriptionDraft("");
    setMarketingChannelContentEarningsEurDraft("0");
    setMarketingChannelContentChannelDraft("");
    setMarketingChannelContentFileDraft(null);
    setMarketingChannelContentPanelOpen(true);
  }, []);

  const closeMarketingChannelContentPanel = useCallback(() => {
    setMarketingChannelContentPanelOpen(false);
    setMarketingChannelContentFormError("");
    setMarketingChannelContentFileDraft(null);
    if (isMobile) {
      setSelectedMarketingChannelContentId(null);
      return;
    }
    if (selectedMarketingChannelContentId != null) {
      const cur = channelContents.find((c) => c.id === selectedMarketingChannelContentId);
      if (cur) {
        setMarketingChannelContentKindDraft(cur.kind === "video" ? "video" : "image");
        setMarketingChannelContentTitleDraft(String(cur.title || ""));
        setMarketingChannelContentDescriptionDraft(String(cur.description || ""));
        setMarketingChannelContentEarningsEurDraft(
          String(
            cur.earnings_eur != null && Number.isFinite(Number(cur.earnings_eur))
              ? Number(cur.earnings_eur)
              : 0,
          ),
        );
        setMarketingChannelContentChannelDraft(
          cur.channel_id != null && Number.isFinite(Number(cur.channel_id))
            ? String(cur.channel_id)
            : "",
        );
      }
    }
  }, [isMobile, selectedMarketingChannelContentId, channelContents]);

  const openMarketingChannelContentEditPanel = useCallback((row) => {
    if (!row || row.id == null) return;
    setSelectedMarketingChannelContentId(row.id);
    setMarketingChannelContentPanelOpen(true);
    setMarketingChannelContentFormError("");
    setMarketingChannelContentKindDraft(row.kind === "video" ? "video" : "image");
    setMarketingChannelContentTitleDraft(String(row.title || ""));
    setMarketingChannelContentDescriptionDraft(String(row.description || ""));
    setMarketingChannelContentEarningsEurDraft(
      String(
        row.earnings_eur != null && Number.isFinite(Number(row.earnings_eur))
          ? Number(row.earnings_eur)
          : 0,
      ),
    );
    setMarketingChannelContentChannelDraft(
      row.channel_id != null && Number.isFinite(Number(row.channel_id))
        ? String(row.channel_id)
        : "",
    );
    setMarketingChannelContentFileDraft(null);
  }, []);

  const saveMarketingChannelContent = useCallback(async () => {
    if (!adminToken || !apiBase) return;
    const title = marketingChannelContentTitleDraft.trim();
    const description = marketingChannelContentDescriptionDraft.trim();
    const kind = marketingChannelContentKindDraft === "video" ? "video" : "image";
    const isEdit = selectedMarketingChannelContentId != null;
    if (!isEdit && !marketingChannelContentFileDraft) {
      setMarketingChannelContentFormError("Datei auswählen.");
      return;
    }
    const earningsTrim = String(marketingChannelContentEarningsEurDraft).trim();
    let earningsEur = 0;
    if (earningsTrim !== "") {
      const n = parseInt(earningsTrim, 10);
      if (!Number.isFinite(n) || n < 0) {
        setMarketingChannelContentFormError(
          "Verdienst: gültige nichtnegative ganze Zahl.",
        );
        return;
      }
      earningsEur = n;
    }
    setMarketingChannelContentSaving(true);
    setMarketingChannelContentFormError("");
    try {
      const fd = new FormData();
      fd.append("kind", kind);
      fd.append("title", title);
      fd.append("description", description);
      fd.append("earnings_eur", String(earningsEur));
      fd.append(
        "channel_id",
        String(marketingChannelContentChannelDraft || "").trim(),
      );
      if (marketingChannelContentFileDraft)
        fd.append("file", marketingChannelContentFileDraft);

      const url = isEdit
        ? `${apiBase}/api/admin/members-channelcontents/${selectedMarketingChannelContentId}`
        : `${apiBase}/api/admin/members-channelcontents`;
      const method = isEdit ? "PUT" : "POST";
      const res = await fetch(url, {
        method,
        headers: { Authorization: "Bearer " + adminToken },
        body: fd,
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) {
        setMarketingChannelContentFormError(data.error || "Speichern fehlgeschlagen.");
        return;
      }
      setMarketingChannelContentPanelOpen(false);
      setMarketingChannelContentFileDraft(null);
      if (!isEdit) setSelectedMarketingChannelContentId(data.id || null);
      await loadChannelContents();
    } catch {
      setMarketingChannelContentFormError("Netzwerkfehler.");
    } finally {
      setMarketingChannelContentSaving(false);
    }
  }, [
    adminToken,
    apiBase,
    marketingChannelContentDescriptionDraft,
    marketingChannelContentEarningsEurDraft,
    marketingChannelContentFileDraft,
    marketingChannelContentKindDraft,
    marketingChannelContentTitleDraft,
    marketingChannelContentChannelDraft,
    selectedMarketingChannelContentId,
    loadChannelContents,
  ]);

  const loadMarketingTargetgroups = useCallback(() => {
    if (!adminToken || !apiBase) return;
    setMarketingTargetgroupsLoading(true);
    setMarketingTargetgroupsError("");
    fetch(`${apiBase}/api/admin/members-targetgroups`, { headers: headers() })
      .then(async (r) => {
        const j = await r.json().catch(() => ({}));
        if (!r.ok) throw new Error(j.error || "Laden fehlgeschlagen");
        setMarketingTargetgroups(Array.isArray(j.targetgroups) ? j.targetgroups : []);
      })
      .catch((e) =>
        setMarketingTargetgroupsError(
          e && e.message ? e.message : "Zielgruppen konnten nicht geladen werden.",
        ),
      )
      .finally(() => setMarketingTargetgroupsLoading(false));
  }, [apiBase, adminToken]);

  /** Für Akquise: Namen auflösen ohne Fehlerbanner (403 bei nur Sales-Recht ignorieren). */
  const loadMarketingTargetgroupsSilent = useCallback(() => {
    if (!adminToken || !apiBase) return;
    fetch(`${apiBase}/api/admin/members-targetgroups`, { headers: headers() })
      .then(async (r) => {
        const j = await r.json().catch(() => ({}));
        if (!r.ok) return;
        setMarketingTargetgroups(Array.isArray(j.targetgroups) ? j.targetgroups : []);
      })
      .catch(() => {});
  }, [apiBase, adminToken]);

  const loadMembersPosts = useCallback(() => {
    if (!adminToken || !apiBase) return;
    setMembersPostsLoading(true);
    setMembersPostsError("");
    fetch(`${apiBase}/api/admin/members-posts`, { headers: headers() })
      .then(async (r) => {
        const j = await r.json().catch(() => ({}));
        if (!r.ok) throw new Error(j.error || "Laden fehlgeschlagen");
        setMembersPosts(Array.isArray(j.posts) ? j.posts : []);
      })
      .catch((e) =>
        setMembersPostsError(e && e.message ? e.message : "Posts konnten nicht geladen werden."),
      )
      .finally(() => setMembersPostsLoading(false));
  }, [apiBase, adminToken]);

  const confirmMarketingMembersPost = useCallback(async () => {
    if (!adminToken || !apiBase || selectedMembersPostId == null) return;
    setMembersPostConfirmSaving(true);
    setMembersPostConfirmError("");
    try {
      const res = await fetch(`${apiBase}/api/admin/members-posts/${selectedMembersPostId}`, {
        method: "PUT",
        headers: { "Content-Type": "application/json", ...headers() },
        body: JSON.stringify({ status: "confirmed" }),
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) {
        setMembersPostConfirmError(data.error || "Speichern fehlgeschlagen.");
        return;
      }
      await loadMembersPosts();
    } catch {
      setMembersPostConfirmError("Netzwerkfehler.");
    } finally {
      setMembersPostConfirmSaving(false);
    }
  }, [adminToken, apiBase, selectedMembersPostId, loadMembersPosts]);

  const closeMembersPostPanel = useCallback(() => {
    setSelectedMembersPostId(null);
    setMembersPostConfirmError("");
  }, []);

  const openMarketingTargetgroupCreatePanel = useCallback(() => {
    setMarketingTargetgroupFormError("");
    setSelectedMarketingTargetgroupId(null);
    setMarketingTargetgroupNameDraft("");
    setMarketingTargetgroupDescriptionDraft("");
    setMarketingTargetgroupPanelOpen(true);
  }, []);

  const closeMarketingTargetgroupPanel = useCallback(() => {
    setMarketingTargetgroupPanelOpen(false);
    setMarketingTargetgroupFormError("");
    if (isMobile) {
      setSelectedMarketingTargetgroupId(null);
      return;
    }
    if (selectedMarketingTargetgroupId != null) {
      const cur = marketingTargetgroups.find(
        (c) => c.id === selectedMarketingTargetgroupId,
      );
      if (cur) {
        setMarketingTargetgroupNameDraft(String(cur.name || ""));
        setMarketingTargetgroupDescriptionDraft(String(cur.description || ""));
      }
    }
  }, [isMobile, selectedMarketingTargetgroupId, marketingTargetgroups]);

  const openMarketingTargetgroupEditPanel = useCallback((row) => {
    if (!row || row.id == null) return;
    setSelectedMarketingTargetgroupId(row.id);
    setMarketingTargetgroupPanelOpen(true);
    setMarketingTargetgroupFormError("");
    setMarketingTargetgroupNameDraft(String(row.name || ""));
    setMarketingTargetgroupDescriptionDraft(String(row.description || ""));
  }, []);

  const saveMarketingTargetgroup = useCallback(async () => {
    if (!adminToken || !apiBase) return;
    const name = marketingTargetgroupNameDraft.trim();
    const description = marketingTargetgroupDescriptionDraft.trim();
    if (!name) {
      setMarketingTargetgroupFormError("Name eingeben.");
      return;
    }
    const isEdit = selectedMarketingTargetgroupId != null;
    setMarketingTargetgroupSaving(true);
    setMarketingTargetgroupFormError("");
    try {
      const url = isEdit
        ? `${apiBase}/api/admin/members-targetgroups/${selectedMarketingTargetgroupId}`
        : `${apiBase}/api/admin/members-targetgroups`;
      const method = isEdit ? "PUT" : "POST";
      const res = await fetch(url, {
        method,
        headers: { "Content-Type": "application/json", ...headers() },
        body: JSON.stringify({ name, description }),
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) {
        setMarketingTargetgroupFormError(data.error || "Speichern fehlgeschlagen.");
        return;
      }
      setMarketingTargetgroupPanelOpen(false);
      if (!isEdit && data.id != null) setSelectedMarketingTargetgroupId(Number(data.id));
      await loadMarketingTargetgroups();
    } catch {
      setMarketingTargetgroupFormError("Netzwerkfehler.");
    } finally {
      setMarketingTargetgroupSaving(false);
    }
  }, [
    adminToken,
    apiBase,
    marketingTargetgroupDescriptionDraft,
    marketingTargetgroupNameDraft,
    selectedMarketingTargetgroupId,
    loadMarketingTargetgroups,
  ]);

  const loadMarketingChannels = useCallback(() => {
    if (!adminToken || !apiBase) return;
    setMarketingChannelsLoading(true);
    setMarketingChannelsError("");
    fetch(`${apiBase}/api/admin/members-channels`, { headers: headers() })
      .then(async (r) => {
        const j = await r.json().catch(() => ({}));
        if (!r.ok) throw new Error(j.error || "Laden fehlgeschlagen");
        setMarketingChannels(Array.isArray(j.channels) ? j.channels : []);
      })
      .catch((e) =>
        setMarketingChannelsError(
          e && e.message ? e.message : "Kanäle konnten nicht geladen werden.",
        ),
      )
      .finally(() => setMarketingChannelsLoading(false));
  }, [apiBase, adminToken]);

  const openMarketingChannelCreatePanel = useCallback(() => {
    setMarketingChannelFormError("");
    setSelectedMarketingChannelId(null);
    setMarketingChannelNameDraft("");
    setMarketingChannelDescriptionDraft("");
    setMarketingChannelPanelOpen(true);
  }, []);

  const closeMarketingChannelPanel = useCallback(() => {
    setMarketingChannelPanelOpen(false);
    setMarketingChannelFormError("");
    if (isMobile) {
      setSelectedMarketingChannelId(null);
      return;
    }
    if (selectedMarketingChannelId != null) {
      const cur = marketingChannels.find((c) => c.id === selectedMarketingChannelId);
      if (cur) {
        setMarketingChannelNameDraft(String(cur.name || ""));
        setMarketingChannelDescriptionDraft(String(cur.description || ""));
      }
    }
  }, [isMobile, selectedMarketingChannelId, marketingChannels]);

  const openMarketingChannelEditPanel = useCallback((row) => {
    if (!row || row.id == null) return;
    setSelectedMarketingChannelId(row.id);
    setMarketingChannelPanelOpen(true);
    setMarketingChannelFormError("");
    setMarketingChannelNameDraft(String(row.name || ""));
    setMarketingChannelDescriptionDraft(String(row.description || ""));
  }, []);

  const saveMarketingChannel = useCallback(async () => {
    if (!adminToken || !apiBase) return;
    const name = marketingChannelNameDraft.trim();
    const description = marketingChannelDescriptionDraft.trim();
    if (!name) {
      setMarketingChannelFormError("Name eingeben.");
      return;
    }
    const isEdit = selectedMarketingChannelId != null;
    setMarketingChannelSaving(true);
    setMarketingChannelFormError("");
    try {
      const url = isEdit
        ? `${apiBase}/api/admin/members-channels/${selectedMarketingChannelId}`
        : `${apiBase}/api/admin/members-channels`;
      const method = isEdit ? "PUT" : "POST";
      const res = await fetch(url, {
        method,
        headers: { "Content-Type": "application/json", ...headers() },
        body: JSON.stringify({ name, description }),
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) {
        setMarketingChannelFormError(data.error || "Speichern fehlgeschlagen.");
        return;
      }
      setMarketingChannelPanelOpen(false);
      if (!isEdit && data.id != null) setSelectedMarketingChannelId(Number(data.id));
      await loadMarketingChannels();
    } catch {
      setMarketingChannelFormError("Netzwerkfehler.");
    } finally {
      setMarketingChannelSaving(false);
    }
  }, [
    adminToken,
    apiBase,
    marketingChannelDescriptionDraft,
    marketingChannelNameDraft,
    selectedMarketingChannelId,
    loadMarketingChannels,
  ]);

  const loadSalesTrainings = useCallback(() => {
    if (!adminToken || !apiBase) return;
    setSalesTrainingsLoading(true);
    setSalesTrainingsError("");
    fetch(`${apiBase}/api/admin/members-trainings`, { headers: headers() })
      .then(async (r) => {
        const j = await r.json().catch(() => ({}));
        if (!r.ok) throw new Error(j.error || "Laden fehlgeschlagen");
        setSalesTrainings(Array.isArray(j.trainings) ? j.trainings : []);
      })
      .catch((e) =>
        setSalesTrainingsError(
          e && e.message ? e.message : "Schulung konnte nicht geladen werden.",
        ),
      )
      .finally(() => setSalesTrainingsLoading(false));
  }, [apiBase, adminToken]);

  const openSalesTrainingCreatePanel = useCallback(() => {
    setSalesTrainingFormError("");
    setSelectedSalesTrainingId(null);
    setSalesTrainingKindDraft("image");
    setSalesTrainingTitleDraft("");
    setSalesTrainingDescriptionDraft("");
    setSalesTrainingEarningsEurDraft("0");
    setSalesTrainingFileDraft(null);
    setSalesTrainingPanelOpen(true);
  }, []);

  const closeSalesTrainingPanel = useCallback(() => {
    setSalesTrainingPanelOpen(false);
    setSalesTrainingFormError("");
    setSalesTrainingFileDraft(null);
    if (isMobile) {
      setSelectedSalesTrainingId(null);
      if (salesTrainingReturnView) {
        const target = salesTrainingReturnView;
        setSalesTrainingReturnView(null);
        setActiveView(target);
      }
      return;
    }
    if (selectedSalesTrainingId != null) {
      const cur = salesTrainings.find((c) => c.id === selectedSalesTrainingId);
      if (cur) {
        setSalesTrainingKindDraft(cur.kind === "video" ? "video" : "image");
        setSalesTrainingTitleDraft(String(cur.title || ""));
        setSalesTrainingDescriptionDraft(String(cur.description || ""));
        setSalesTrainingEarningsEurDraft(
          String(Number(cur.earnings_eur || 0) || 0),
        );
      }
    }
  }, [isMobile, salesTrainingReturnView, selectedSalesTrainingId, salesTrainings]);

  const openSalesTrainingEditPanel = useCallback((row) => {
    if (!row || row.id == null) return;
    setSelectedSalesTrainingId(row.id);
    setSalesTrainingPanelOpen(true);
    setSalesTrainingFormError("");
    setSalesTrainingKindDraft(row.kind === "video" ? "video" : "image");
    setSalesTrainingTitleDraft(String(row.title || ""));
    setSalesTrainingDescriptionDraft(String(row.description || ""));
    setSalesTrainingEarningsEurDraft(
      String(Number(row.earnings_eur || 0) || 0),
    );
    setSalesTrainingFileDraft(null);
  }, []);

  const saveSalesTraining = useCallback(async () => {
    if (!adminToken || !apiBase) return;
    const title = salesTrainingTitleDraft.trim();
    const description = salesTrainingDescriptionDraft.trim();
    const kind = salesTrainingKindDraft === "video" ? "video" : "image";
    const earningsEurStrRaw = String(salesTrainingEarningsEurDraft || "0").trim();
    const earningsEurNum = parseInt(earningsEurStrRaw, 10);
    if (!Number.isFinite(earningsEurNum) || earningsEurNum < 0) {
      setSalesTrainingFormError("Verdienst muss eine ganze Zahl ≥ 0 sein.");
      return;
    }
    const isEdit = selectedSalesTrainingId != null;
    if (!isEdit && !salesTrainingFileDraft) {
      setSalesTrainingFormError("Datei auswählen.");
      return;
    }
    setSalesTrainingSaving(true);
    setSalesTrainingFormError("");
    try {
      const fd = new FormData();
      fd.append("kind", kind);
      fd.append("title", title);
      fd.append("description", description);
      fd.append("earnings_eur", String(earningsEurNum));
      if (salesTrainingFileDraft) fd.append("file", salesTrainingFileDraft);

      const url = isEdit
        ? `${apiBase}/api/admin/members-trainings/${selectedSalesTrainingId}`
        : `${apiBase}/api/admin/members-trainings`;
      const method = isEdit ? "PUT" : "POST";
      const res = await fetch(url, {
        method,
        headers: { Authorization: "Bearer " + adminToken },
        body: fd,
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) {
        setSalesTrainingFormError(data.error || "Speichern fehlgeschlagen.");
        return;
      }
      setSalesTrainingPanelOpen(false);
      setSalesTrainingFileDraft(null);
      if (!isEdit) setSelectedSalesTrainingId(data.id || null);
      await loadSalesTrainings();
    } catch {
      setSalesTrainingFormError("Netzwerkfehler.");
    } finally {
      setSalesTrainingSaving(false);
    }
  }, [
    adminToken,
    apiBase,
    salesTrainingDescriptionDraft,
    salesTrainingEarningsEurDraft,
    salesTrainingFileDraft,
    salesTrainingKindDraft,
    salesTrainingTitleDraft,
    selectedSalesTrainingId,
    loadSalesTrainings,
  ]);

  const loadSalesEarnings = useCallback(() => {
    if (!adminToken || !apiBase) return;
    setSalesEarningsLoading(true);
    setSalesEarningsError("");
    fetch(`${apiBase}/api/admin/members-earnings`, { headers: headers() })
      .then(async (r) => {
        const j = await r.json().catch(() => ({}));
        if (!r.ok) throw new Error(j.error || "Laden fehlgeschlagen");
        setSalesEarnings(Array.isArray(j.earnings) ? j.earnings : []);
        setSalesPendingPosts(Array.isArray(j.pending_posts) ? j.pending_posts : []);
      })
      .catch((e) =>
        setSalesEarningsError(
          e && e.message ? e.message : "Einnahmen konnten nicht geladen werden.",
        ),
      )
      .finally(() => setSalesEarningsLoading(false));
  }, [apiBase, adminToken]);

  const loadTeamInvitations = useCallback(() => {
    if (!adminToken || !apiBase) return Promise.resolve();
    setTeamInvLoading(true);
    setTeamInvError("");
    return membersInvitationsApi
      .listInvitations({ apiBase, token: adminToken })
      .then((list) => setTeamInvitations(list))
      .catch((e) =>
        setTeamInvError(
          e && e.message ? e.message : "Einladungen konnten nicht geladen werden.",
        ),
      )
      .finally(() => setTeamInvLoading(false));
  }, [apiBase, adminToken]);

  /** Sales > Team Roster: laedt Member-Liste + offene Einladungen in einem Call. */
  const loadTeamRoster = useCallback(() => {
    if (!adminToken || !apiBase) return Promise.resolve();
    setTeamInvLoading(true);
    setTeamInvError("");
    return fetch(`${apiBase}/api/admin/team/roster`, { headers: headers() })
      .then(async (r) => {
        const j = await r.json().catch(() => ({}));
        if (!r.ok) throw new Error(j.error || "Team konnte nicht geladen werden.");
        setTeamRoster(Array.isArray(j.members) ? j.members : []);
        setTeamInvitations(Array.isArray(j.invitations) ? j.invitations : []);
      })
      .catch((e) =>
        setTeamInvError(
          e && e.message ? e.message : "Team konnte nicht geladen werden.",
        ),
      )
      .finally(() => setTeamInvLoading(false));
  }, [apiBase, adminToken]);

  const openTeamInviteCreatePanel = useCallback(() => {
    setTeamInviteFormError("");
    setTeamInviteEmailDraft("");
    setTeamInvitePhoneDraft("");
    setSelectedTeamMemberId(null);
    setTeamInviteCreateOpen(true);
  }, []);

  const closeTeamInviteCreatePanel = useCallback(() => {
    setTeamInviteCreateOpen(false);
    setTeamInviteFormError("");
    if (isMobile && teamInviteReturnView) {
      const target = teamInviteReturnView;
      setTeamInviteReturnView(null);
      setActiveView(target);
    }
  }, [isMobile, teamInviteReturnView]);

  /**
   * Sales › Team: Klick auf eine Person öffnet den **Chat** mit dieser Person
   * (frueher: Detail). Setzt Member-Selection, schliesst die Detail-Subansicht
   * und resettet Chat-Draft + -Verlauf — der Polling-Effekt unten laedt frisch.
   */
  const openTeamMemberChat = useCallback((memberId) => {
    setTeamInviteCreateOpen(false);
    setTeamInviteFormError("");
    setTeamMemberDetailOpen(false);
    setTeamChatDraft("");
    setTeamChatError("");
    setTeamChatMessages([]);
    setSelectedTeamMemberId(memberId);
  }, []);

  /** Vom Chat-Header (Avatar + Name) → oeffnet die Detailansicht ueber dem Chat. */
  const openTeamMemberDetail = useCallback(() => {
    setTeamMemberDetailOpen(true);
  }, []);

  /** Detail-Header Arrow-Back → zurueck zum Chat (Mobile + Desktop). */
  const closeTeamMemberDetail = useCallback(() => {
    setTeamMemberDetailOpen(false);
  }, []);

  /** Chat-Header Arrow-Back (nur Mobile) → zurueck zur Liste. */
  const closeTeamMemberChat = useCallback(() => {
    setSelectedTeamMemberId(null);
    setTeamMemberDetailOpen(false);
    setTeamChatDraft("");
    setTeamChatError("");
    setTeamChatMessages([]);
  }, []);

  const submitTeamInvite = useCallback(async () => {
    if (!apiBase || !adminToken) return;
    setTeamInviteFormError("");
    const em = (teamInviteEmailDraft || "").trim();
    const ph = (teamInvitePhoneDraft || "").trim();
    if (!ph) {
      setTeamInviteFormError("Mobilnummer eingeben (E.164, z. B. +4915112345678).");
      return;
    }
    setTeamInviteSaving(true);
    try {
      const res = await membersInvitationsApi.createInvitation({
        apiBase,
        token: adminToken,
        email: em || null,
        phone: ph,
      });
      setTeamInviteCreateOpen(false);
      setTeamInviteEmailDraft("");
      setTeamInvitePhoneDraft("");
      if (isMobile && teamInviteReturnView) {
        const target = teamInviteReturnView;
        setTeamInviteReturnView(null);
        setActiveView(target);
      }
      await loadTeamRoster();
      if (em && res && res.mail_sent === false) {
        setTeamInvError(
          "Einladung wurde angelegt, die E-Mail konnte aber nicht gesendet werden: " +
            (res.mail_error || "Unbekannt") +
            ". Link steht weiterhin in den Logs (member_invite).",
        );
      }
    } catch (e) {
      setTeamInviteFormError(e && e.message ? e.message : "Fehler");
    } finally {
      setTeamInviteSaving(false);
    }
  }, [
    adminToken,
    apiBase,
    isMobile,
    loadTeamRoster,
    teamInviteEmailDraft,
    teamInvitePhoneDraft,
    teamInviteReturnView,
  ]);

  const doRevokeTeamInvite = useCallback(
    async (id) => {
      if (!apiBase || !adminToken || !id) return;
      setTeamInviteRevokeId(id);
      try {
        await membersInvitationsApi.revokeInvitation({ apiBase, token: adminToken, id });
        loadTeamRoster();
      } catch (e) {
        setTeamInvError(e && e.message ? e.message : "Widerruf fehlgeschlagen");
      } finally {
        setTeamInviteRevokeId(null);
      }
    },
    [adminToken, apiBase, loadTeamRoster],
  );

  /** Sales › Team Chat: laedt den Verlauf mit dem aktuell ausgewaehlten Member. */
  const loadTeamChat = useCallback(
    async ({ silent } = {}) => {
      if (!apiBase || !adminToken || !selectedTeamMemberId) return;
      if (!silent) {
        setTeamChatLoading(true);
        setTeamChatError("");
      }
      try {
        const messages = await membersMessagesApi.listMessages({
          apiBase,
          token: adminToken,
          peerId: selectedTeamMemberId,
        });
        setTeamChatMessages(messages || []);
        setTeamChatError("");
      } catch (e) {
        if (!silent) {
          setTeamChatError(
            e && e.message ? e.message : "Nachrichten konnten nicht geladen werden",
          );
        }
      } finally {
        if (!silent) setTeamChatLoading(false);
      }
    },
    [apiBase, adminToken, selectedTeamMemberId],
  );

  /** Sales › Team Chat: sendet die aktuelle Draft-Nachricht. */
  const sendTeamChat = useCallback(async () => {
    if (!apiBase || !adminToken || !selectedTeamMemberId) return;
    const content = (teamChatDraft || "").trim();
    if (!content || teamChatSending) return;
    setTeamChatSending(true);
    setTeamChatError("");
    try {
      const message = await membersMessagesApi.sendMessage({
        apiBase,
        token: adminToken,
        peerId: selectedTeamMemberId,
        content,
      });
      if (message) {
        setTeamChatMessages((prev) => {
          if (prev.some((m) => m.id === message.id)) return prev;
          return [...prev, message];
        });
      }
      setTeamChatDraft("");
      loadTeamRoster();
    } catch (e) {
      setTeamChatError(e && e.message ? e.message : "Senden fehlgeschlagen");
    } finally {
      setTeamChatSending(false);
    }
  }, [
    apiBase,
    adminToken,
    selectedTeamMemberId,
    teamChatDraft,
    teamChatSending,
    loadTeamRoster,
  ]);

  /** Sales › Team Chat: initial Verlauf laden, sobald jemand ausgewaehlt wird. */
  useEffect(() => {
    if (
      isLoggedIn &&
      activeView === "sales_team" &&
      selectedTeamMemberId &&
      !teamMemberDetailOpen
    ) {
      loadTeamChat();
    }
  }, [
    isLoggedIn,
    activeView,
    selectedTeamMemberId,
    teamMemberDetailOpen,
    loadTeamChat,
  ]);

  /** Sales › Team Chat: Polling alle 4s, solange der Chat offen ist. */
  useEffect(() => {
    if (
      !isLoggedIn ||
      activeView !== "sales_team" ||
      !selectedTeamMemberId ||
      teamMemberDetailOpen
    ) {
      return undefined;
    }
    const id = setInterval(() => {
      loadTeamChat({ silent: true });
    }, 4000);
    return () => clearInterval(id);
  }, [
    isLoggedIn,
    activeView,
    selectedTeamMemberId,
    teamMemberDetailOpen,
    loadTeamChat,
  ]);

  /** Sales › Team: Roster periodisch nachladen, damit Previews/Unread aktuell bleiben. */
  useEffect(() => {
    if (!isLoggedIn || activeView !== "sales_team") return undefined;
    const id = setInterval(() => {
      loadTeamRoster();
    }, 15000);
    return () => clearInterval(id);
  }, [isLoggedIn, activeView, loadTeamRoster]);

  /** Sales › Team Chat: nach Update auf Bottom scrollen (neue Nachricht / Reload). */
  useEffect(() => {
    const el = teamChatScrollRef.current;
    if (!el) return;
    el.scrollTop = el.scrollHeight;
  }, [teamChatMessages, selectedTeamMemberId, teamMemberDetailOpen]);

  useEffect(() => {
    if (
      isLoggedIn &&
      (activeView === "sales_home" ||
        activeView === "sales_akquise" ||
        activeView === "sales_service")
    ) {
      loadSalesCustomers();
      loadAcquisitionCustomers();
      loadServiceCustomers();
    }
  }, [
    isLoggedIn,
    activeView,
    loadSalesCustomers,
    loadAcquisitionCustomers,
    loadServiceCustomers,
  ]);

  useEffect(() => {
    if (isLoggedIn && activeView === "sales_akquise") loadAcquisitionCustomers();
  }, [isLoggedIn, activeView, loadAcquisitionCustomers]);

  useEffect(() => {
    if (isLoggedIn && activeView === "sales_service") loadServiceCustomers();
  }, [isLoggedIn, activeView, loadServiceCustomers]);

  useEffect(() => {
    if (isLoggedIn && activeView === "marketing_targetgroups")
      loadMarketingTargetgroups();
  }, [isLoggedIn, activeView, loadMarketingTargetgroups]);

  useEffect(() => {
    if (isLoggedIn && activeView === "marketing_channels") loadMarketingChannels();
  }, [isLoggedIn, activeView, loadMarketingChannels]);

  useEffect(() => {
    if (isLoggedIn && activeView === "marketing_posts") loadMembersPosts();
  }, [isLoggedIn, activeView, loadMembersPosts]);

  useEffect(() => {
    if (isLoggedIn && activeView === "marketing_community_content")
      loadMarketingTargetgroups();
  }, [isLoggedIn, activeView, loadMarketingTargetgroups]);

  useEffect(() => {
    if (isLoggedIn && activeView === "marketing_channel_content") {
      loadChannelContents();
      loadMarketingChannels();
    }
  }, [isLoggedIn, activeView, loadChannelContents, loadMarketingChannels]);

  useEffect(() => {
    if (isLoggedIn && activeView === "marketing_community_content")
      loadMarketingTargetgroupsSilent();
  }, [isLoggedIn, activeView, loadMarketingTargetgroupsSilent]);

  useEffect(() => {
    if (!salesCustomerPanelOpen) {
      setSalesCustomerLinkUsers([]);
      setSalesCustomerLinkUsersLoading(false);
      if (salesCustomerLinkDebounceRef.current) {
        clearTimeout(salesCustomerLinkDebounceRef.current);
        salesCustomerLinkDebounceRef.current = null;
      }
      return;
    }
    const em = salesCustomerEmailDraft.trim();
    if (!em || !apiBase || !adminToken) {
      setSalesCustomerLinkUsers([]);
      setSalesCustomerLinkUsersLoading(false);
      return;
    }
    setSalesCustomerLinkUsersLoading(true);
    if (salesCustomerLinkDebounceRef.current) {
      clearTimeout(salesCustomerLinkDebounceRef.current);
    }
    salesCustomerLinkDebounceRef.current = setTimeout(async () => {
      salesCustomerLinkDebounceRef.current = null;
      try {
        const u = `${apiBase}/api/admin/users-for-sales-link?email=${encodeURIComponent(em)}`;
        const r = await fetch(u, {
          headers: { Authorization: "Bearer " + adminToken },
        });
        const j = await r.json().catch(() => ({}));
        if (!r.ok) throw new Error(j.error || "Lookup fehlgeschlagen");
        setSalesCustomerLinkUsers(Array.isArray(j.users) ? j.users : []);
      } catch {
        setSalesCustomerLinkUsers([]);
      } finally {
        setSalesCustomerLinkUsersLoading(false);
      }
    }, 350);
    return () => {
      if (salesCustomerLinkDebounceRef.current) {
        clearTimeout(salesCustomerLinkDebounceRef.current);
        salesCustomerLinkDebounceRef.current = null;
      }
    };
  }, [salesCustomerPanelOpen, salesCustomerEmailDraft, apiBase, adminToken]);

  useEffect(() => {
    if (!salesCustomerPanelOpen || salesCustomerLinkUsersLoading) return;
    if (salesCustomerUserIdDraft == null) return;
    const inList = salesCustomerLinkUsers.some(
      (u) => Number(u.id) === Number(salesCustomerUserIdDraft),
    );
    if (inList) return;
    const row = salesCustomers.find((c) => c.id === selectedSalesCustomerId);
    if (
      row &&
      row.user_id != null &&
      Number(row.user_id) === Number(salesCustomerUserIdDraft) &&
      String(row.email || "")
        .trim()
        .toLowerCase() === String(salesCustomerEmailDraft || "").trim().toLowerCase()
    ) {
      return;
    }
    setSalesCustomerUserIdDraft(null);
  }, [
    salesCustomerLinkUsers,
    salesCustomerLinkUsersLoading,
    salesCustomerUserIdDraft,
    salesCustomerPanelOpen,
    salesCustomerEmailDraft,
    selectedSalesCustomerId,
    salesCustomers,
  ]);

  useEffect(() => {
    if (!salesCustomerPanelOpen) return;
    if (String(salesCustomerEmailDraft || "").trim()) return;
    if (salesCustomerUserIdDraft != null) setSalesCustomerUserIdDraft(null);
  }, [salesCustomerEmailDraft, salesCustomerPanelOpen, salesCustomerUserIdDraft]);

  useEffect(() => {
    if (
      isLoggedIn &&
      (activeView === "marketing_community_content" ||
        activeView === "sales_home")
    )
      loadSalesContents();
  }, [isLoggedIn, activeView, loadSalesContents]);

  useEffect(() => {
    if (
      isLoggedIn &&
      (activeView === "sales_training" ||
        activeView === "people_schulungen" ||
        activeView === "sales_home")
    )
      loadSalesTrainings();
  }, [isLoggedIn, activeView, loadSalesTrainings]);

  useEffect(() => {
    if (isLoggedIn && activeView === "sales_karriere") loadSalesEarnings();
  }, [isLoggedIn, activeView, loadSalesEarnings]);

  useEffect(() => {
    if (isLoggedIn && activeView === "sales_team") loadTeamRoster();
  }, [isLoggedIn, activeView, loadTeamRoster]);

  useEffect(() => {
    if (activeView !== "sales_akquise" && activeView !== "sales_service") return;
    const list =
      activeView === "sales_service" ? serviceCustomers : acquisitionCustomers;
    if (!list.length) {
      if (!salesCustomerPanelOpen) setSelectedSalesCustomerId(null);
      return;
    }
    const exists = list.some((c) => c.id === selectedSalesCustomerId);
    if (!exists && !salesCustomerPanelOpen && !isMobile) {
      const first = list[0];
      openSalesCustomerEditPanel(first);
    }
  }, [
    activeView,
    acquisitionCustomers,
    serviceCustomers,
    selectedSalesCustomerId,
    salesCustomerPanelOpen,
    isMobile,
    openSalesCustomerEditPanel,
  ]);

  useEffect(() => {
    if (activeView !== "marketing_targetgroups") return;
    if (!marketingTargetgroups.length) {
      if (!marketingTargetgroupPanelOpen) setSelectedMarketingTargetgroupId(null);
      return;
    }
    const exists = marketingTargetgroups.some(
      (c) => c.id === selectedMarketingTargetgroupId,
    );
    if (!exists && !marketingTargetgroupPanelOpen && !isMobile) {
      const first = marketingTargetgroups[0];
      setSelectedMarketingTargetgroupId(first.id);
      setMarketingTargetgroupNameDraft(String(first.name || ""));
      setMarketingTargetgroupDescriptionDraft(String(first.description || ""));
    }
  }, [
    activeView,
    marketingTargetgroups,
    selectedMarketingTargetgroupId,
    marketingTargetgroupPanelOpen,
    isMobile,
  ]);

  useEffect(() => {
    if (activeView !== "marketing_channels") return;
    if (!marketingChannels.length) {
      if (!marketingChannelPanelOpen) setSelectedMarketingChannelId(null);
      return;
    }
    const exists = marketingChannels.some((c) => c.id === selectedMarketingChannelId);
    if (!exists && !marketingChannelPanelOpen && !isMobile) {
      const first = marketingChannels[0];
      setSelectedMarketingChannelId(first.id);
      setMarketingChannelNameDraft(String(first.name || ""));
      setMarketingChannelDescriptionDraft(String(first.description || ""));
    }
  }, [
    activeView,
    marketingChannels,
    selectedMarketingChannelId,
    marketingChannelPanelOpen,
    isMobile,
  ]);

  useEffect(() => {
    if (activeView !== "marketing_posts") return;
    if (!membersPosts.length) {
      setSelectedMembersPostId(null);
      return;
    }
    const exists = membersPosts.some((p) => p.id === selectedMembersPostId);
    if (!exists && !isMobile) {
      setSelectedMembersPostId(membersPosts[0].id);
    }
  }, [activeView, membersPosts, selectedMembersPostId, isMobile]);

  useEffect(() => {
    setSalesAkquisePostError("");
    setSalesAkquisePostInfo("");
  }, [selectedSalesContentId]);

  useEffect(() => {
    if (activeView !== "marketing_community_content") return;
    if (!salesContents.length) {
      if (!marketingCommunityPanelOpen) setSelectedMarketingCommunityContentId(null);
      return;
    }
    const exists = salesContents.some((c) => c.id === selectedMarketingCommunityContentId);
    if (!exists && !marketingCommunityPanelOpen && !isMobile) {
      const first = salesContents[0];
      setSelectedMarketingCommunityContentId(first.id);
      setMarketingCommunityKindDraft(first.kind === "video" ? "video" : "image");
      setMarketingCommunityTitleDraft(String(first.title || ""));
      setMarketingCommunityDescriptionDraft(String(first.description || ""));
      setMarketingCommunityEarningsEurDraft(
        String(
          first.earnings_eur != null && Number.isFinite(Number(first.earnings_eur))
            ? Number(first.earnings_eur)
            : 0,
        ),
      );
      setMarketingCommunityTargetgroupDraft(
        first.targetgroup_id != null && Number.isFinite(Number(first.targetgroup_id))
          ? String(first.targetgroup_id)
          : "",
      );
    }
  }, [
    activeView,
    salesContents,
    selectedMarketingCommunityContentId,
    marketingCommunityPanelOpen,
    isMobile,
  ]);

  useEffect(() => {
    if (activeView !== "marketing_channel_content") return;
    if (!channelContents.length) {
      if (!marketingChannelContentPanelOpen) setSelectedMarketingChannelContentId(null);
      return;
    }
    const exists = channelContents.some(
      (c) => c.id === selectedMarketingChannelContentId,
    );
    if (!exists && !marketingChannelContentPanelOpen && !isMobile) {
      const first = channelContents[0];
      setSelectedMarketingChannelContentId(first.id);
      setMarketingChannelContentKindDraft(first.kind === "video" ? "video" : "image");
      setMarketingChannelContentTitleDraft(String(first.title || ""));
      setMarketingChannelContentDescriptionDraft(String(first.description || ""));
      setMarketingChannelContentEarningsEurDraft(
        String(
          first.earnings_eur != null && Number.isFinite(Number(first.earnings_eur))
            ? Number(first.earnings_eur)
            : 0,
        ),
      );
      setMarketingChannelContentChannelDraft(
        first.channel_id != null && Number.isFinite(Number(first.channel_id))
          ? String(first.channel_id)
          : "",
      );
    }
  }, [
    activeView,
    channelContents,
    selectedMarketingChannelContentId,
    marketingChannelContentPanelOpen,
    isMobile,
  ]);

  useEffect(() => {
    if (activeView !== "sales_training" && activeView !== "people_schulungen") return;
    if (!salesTrainings.length) {
      if (!salesTrainingPanelOpen) setSelectedSalesTrainingId(null);
      return;
    }
    const exists = salesTrainings.some((c) => c.id === selectedSalesTrainingId);
    if (!exists && !salesTrainingPanelOpen && !isMobile) {
      const first = salesTrainings[0];
      setSelectedSalesTrainingId(first.id);
      setSalesTrainingKindDraft(first.kind === "video" ? "video" : "image");
      setSalesTrainingTitleDraft(String(first.title || ""));
      setSalesTrainingDescriptionDraft(String(first.description || ""));
    }
  }, [
    activeView,
    salesTrainings,
    selectedSalesTrainingId,
    salesTrainingPanelOpen,
    isMobile,
  ]);

  useEffect(() => {
    if (activeView !== "admin_agendas") return;
    if (!adminAgendas.length) {
      if (!agendaCreateOpen) setSelectedAdminAgendaId(null);
      return;
    }
    const selectedExists = adminAgendas.some((a) => a.id === selectedAdminAgendaId);
    if (!selectedExists && !agendaCreateOpen && !isMobile) {
      const firstAgenda = adminAgendas[0];
      setSelectedAdminAgendaId(firstAgenda.id);
      setAgendaNameDraft(String(firstAgenda.name || ""));
      setAgendaDateDraft(
        firstAgenda.agenda_date != null
          ? String(firstAgenda.agenda_date).slice(0, 10)
          : "",
      );
      setAgendaNotesDraft(String(firstAgenda.notes || ""));
    }
  }, [
    activeView,
    adminAgendas,
    selectedAdminAgendaId,
    agendaCreateOpen,
    isMobile,
  ]);

  useEffect(() => {
    if (selectedManualTestId) loadManualTestDetail(selectedManualTestId);
    else setManualTestDetail(null);
  }, [selectedManualTestId, loadManualTestDetail]);

  // Default-Auswahl im Dropdown: erste verfuegbare KI vorbelegen.
  useEffect(() => {
    if (activeView !== "tests") return;
    if (!testNewAiId && aisList.length > 0) setTestNewAiId(String(aisList[0].id));
  }, [activeView, aisList, testNewAiId]);

  // Auto-End wenn der User im offenen Chat woanders hin navigiert oder
  // einen anderen Chat auswaehlt (verlassen = ended_by 'leave').
  // Wir vergleichen 'previous' aus dem Ref (vorheriges Render) mit dem aktuell
  // angezeigten Chat. So wird beim ERSTEN Oeffnen NICHTS beendet (previous=null),
  // sondern erst bei der NAECHSTEN Selektionsaenderung der vorherige Chat.
  useEffect(() => {
    const previous = lastViewedManualIdRef.current;
    const currentlyViewing =
      activeView === "tests" ? selectedManualTestId : null;
    lastViewedManualIdRef.current = currentlyViewing;
    if (previous && previous !== currentlyViewing && apiBase && adminToken) {
      // Idempotent: Backend gibt 200 zurueck wenn der Chat bereits beendet ist.
      fetch(`${apiBase}/api/admin/calls-tests/${previous}/end`, {
        method: "POST",
        headers: { "Content-Type": "application/json", Authorization: "Bearer " + adminToken },
        body: JSON.stringify({ ended_by: "leave" }),
        keepalive: true,
      }).catch(() => {});
    }
  }, [selectedManualTestId, activeView, apiBase, adminToken]);

  // Browser-Tab schliessen / Reload -> beforeunload sendet keepalive end.
  useEffect(() => {
    const onBeforeUnload = () => {
      const openId = lastViewedManualIdRef.current;
      if (!openId || !apiBase || !adminToken) return;
      try {
        fetch(`${apiBase}/api/admin/calls-tests/${openId}/end`, {
          method: "POST",
          headers: { "Content-Type": "application/json", Authorization: "Bearer " + adminToken },
          body: JSON.stringify({ ended_by: "leave" }),
          keepalive: true,
        });
      } catch { /* ignore */ }
    };
    window.addEventListener("beforeunload", onBeforeUnload);
    return () => window.removeEventListener("beforeunload", onBeforeUnload);
  }, [apiBase, adminToken]);

  // Polling: solange Detail-Run noch laeuft, alle 1.5s nachladen
  useEffect(() => {
    if (
      !selectedTestRunId ||
      !testRunDetail ||
      testRunDetail.status !== "running"
    )
      return;
    const t = setInterval(() => {
      loadTestRunDetail(selectedTestRunId);
      loadAdminTests();
    }, 1500);
    return () => clearInterval(t);
  }, [selectedTestRunId, testRunDetail, loadTestRunDetail, loadAdminTests]);

  // Auto-Select first Eintrag on entering Tests view (Desktop)
  useEffect(() => {
    if (isMobile || activeView !== "tests") return;
    if (selectedTestRunId != null || selectedManualTestId != null) return;
    if (adminTestsList.length === 0) return;
    const first = adminTestsList[0];
    const fk = String(first.test_kind || "manual").toLowerCase();
    if (first && fk === "e2e") setSelectedTestRunId(Number(first.id));
    else if (first) setSelectedManualTestId(Number(first.id));
  }, [isMobile, activeView, selectedTestRunId, selectedManualTestId, adminTestsList]);

  useEffect(() => {
    if (selectedCallId) loadCallDetail(selectedCallId);
    else setCallDetail(null);
  }, [selectedCallId, loadCallDetail]);

  useEffect(() => {
    if (isMobile || activeView !== "anrufe") return;
    if (selectedCallId == null && callsList.length > 0) {
      setSelectedCallId(callsList[0].id);
    }
  }, [isMobile, activeView, selectedCallId, callsList]);

  useEffect(() => {
    if (activeView === "module") {
      loadModuleList();
      if (!dbSchemaLoaded) loadDbSchema();
      if (!aicoworkers || aicoworkers.length === 0) loadAicoworkers();
    } else if (activeView === "aicoworker") {
      loadModuleList();
    }
  }, [
    activeView,
    loadModuleList,
    loadDbSchema,
    dbSchemaLoaded,
    loadAicoworkers,
    aicoworkers,
  ]);

  useEffect(() => {
    if (selectedModuleId && moduleList.length) {
      const found = moduleList.find((s) => s.id === selectedModuleId);
      setModuleDetail(found ? { ...found } : null);
    } else {
      setModuleDetail(null);
    }
    setModuleTools([]);
    setModuleToolEditId(null);
    setModuleTests([]);
    setModuleTestEditId(null);
    if (selectedModuleId) {
      loadModuleTools(selectedModuleId);
      loadModuleTests(selectedModuleId);
    }
  }, [selectedModuleId, loadModuleTools, loadModuleTests]);

  useEffect(() => {
    if (!selectedModuleId) {
      setModuleHeaderToolsAicoworkerId(null);
      return;
    }
    const ids = [];
    for (const ac of aicoworkers || []) {
      if (!Array.isArray(ac.modules)) continue;
      if (ac.modules.some((s) => s.module_id === selectedModuleId)) ids.push(ac.id);
    }
    if (ids.length === 0) {
      setModuleHeaderToolsAicoworkerId(null);
      return;
    }
    setModuleHeaderToolsAicoworkerId((prev) =>
      prev != null && ids.includes(prev) ? prev : ids[0],
    );
  }, [selectedModuleId, aicoworkers]);

  useEffect(() => {
    if (isMobile || activeView !== "kis") return;
    if (selectedAiId == null && aisList.length > 0) {
      setSelectedAiId(aisList[0].id);
    }
  }, [isMobile, activeView, selectedAiId, aisList]);

  useEffect(() => {
    if (isMobile || activeView !== "kunden") return;
    if (selectedProductUserId == null && productUsers.length > 0) {
      setSelectedProductUserId(productUsers[0].id);
    }
  }, [isMobile, activeView, selectedProductUserId, productUsers]);

  useEffect(() => {
    if (isMobile || activeView !== "aicoworker") return;
    if (selectedAicoworkerId == null && aicoworkers.length > 0) {
      setSelectedAicoworkerId(aicoworkers[0].id);
    }
  }, [isMobile, activeView, selectedAicoworkerId, aicoworkers]);

  useEffect(() => {
    if (isMobile || activeView !== "module") return;
    if (selectedModuleId == null && moduleList.length > 0) {
      setSelectedModuleId(moduleList[0].id);
    }
  }, [isMobile, activeView, selectedModuleId, moduleList]);

  useEffect(() => {
    const body = productBodyRef.current;
    if (!body) return;
    const view = activeView;
    const onScroll = () => {
      productScrollRef.current[view] = body.scrollTop;
    };
    body.addEventListener("scroll", onScroll, { passive: true });
    const saved = productScrollRef.current[view];
    requestAnimationFrame(() => {
      if (productBodyRef.current === body && typeof saved === "number") {
        body.scrollTop = saved;
      }
    });
    return () => body.removeEventListener("scroll", onScroll);
  }, [
    activeView,
    aicoworkerDetail,
    moduleDetail,
    selectedProductUserId,
    callDetail,
  ]);

  const flushProductPendingSave = useCallback(() => {
    setProductAutoSaveRingActive(false);
    if (productAutoSaveTimer.current) {
      clearTimeout(productAutoSaveTimer.current);
      productAutoSaveTimer.current = null;
    }
    if (productPendingSave.current) {
      const fn = productPendingSave.current;
      productPendingSave.current = null;
      try {
        fn();
      } catch {}
    }
  }, []);

  const scheduleProductAutoSave = useCallback((saveFn) => {
    productPendingSave.current = saveFn;
    setProductAutoSaveRingActive(true);
    setProductAutoSaveRingKey((k) => k + 1);
    if (productAutoSaveTimer.current)
      clearTimeout(productAutoSaveTimer.current);
    productAutoSaveTimer.current = setTimeout(() => {
      productAutoSaveTimer.current = null;
      setProductAutoSaveRingActive(false);
      if (productPendingSave.current) {
        const fn = productPendingSave.current;
        productPendingSave.current = null;
        try {
          fn();
        } catch {}
      }
    }, AUTO_SAVE_MS);
  }, []);

  const switchView = useCallback(
    (newView) => {
      flushProductPendingSave();
      setActiveView(newView);
      setKontoOpen(false);
      setAreaPickerOpen(false);
      setImpersonateStatus("");
      if (newView !== "admin_agendas") {
        setAgendaCreateOpen(false);
        setSelectedAdminAgendaId(null);
        setAgendaCreateError("");
      }
      if (newView !== "product_userstories") {
        setUserStoryCreateOpen(false);
        setSelectedUserStoryId(null);
        setUserStoryCreateError("");
        setUserStoryTitleDraft("");
        setUserStoryDescriptionDraft("");
        setUserStoryRatings([]);
        setUserStoryRatingNewScore("7");
        setUserStoryRatingNewComment("");
      }
      if (newView !== "people_members") {
        setMemberCreateOpen(false);
        setSelectedMemberId(null);
        setMemberFormError("");
        setMemberInvitedFromDraft("");
      }
      if (newView !== "sales_akquise" && newView !== "sales_service") {
        setSalesCustomerPanelOpen(false);
        setSelectedSalesCustomerId(null);
        setSalesCustomerFormError("");
        setSalesCustomerCompanyDraft("");
        setSalesCustomerContactDraft("");
        setSalesCustomerEmailDraft("");
        setSalesCustomerPhoneDraft("");
        setSalesCustomerIndustryDraft("");
        setSalesCustomerNotesDraft("");
        setSalesCustomerUserIdDraft(null);
        setSalesCustomerLinkUsers([]);
        setSalesCustomerLinkUsersLoading(false);
        setSalesCustomerDetailMoreOpen(false);
        setSalesCustomerApptOpen(false);
        setSalesCustomerHistory([]);
        if (salesCustomerLinkDebounceRef.current) {
          clearTimeout(salesCustomerLinkDebounceRef.current);
          salesCustomerLinkDebounceRef.current = null;
        }
      }
      if (newView !== "sales_acquisition_content") {
        setSalesContentPanelOpen(false);
        setSelectedSalesContentId(null);
      }
      if (newView !== "marketing_community_content") {
        setMarketingCommunityPanelOpen(false);
        setSelectedMarketingCommunityContentId(null);
        setMarketingCommunityFormError("");
        setMarketingCommunityKindDraft("image");
        setMarketingCommunityTitleDraft("");
        setMarketingCommunityDescriptionDraft("");
        setMarketingCommunityEarningsEurDraft("0");
        setMarketingCommunityTargetgroupDraft("");
        setMarketingCommunityFileDraft(null);
      }
      if (newView !== "marketing_channel_content") {
        setMarketingChannelContentPanelOpen(false);
        setSelectedMarketingChannelContentId(null);
        setMarketingChannelContentFormError("");
        setMarketingChannelContentKindDraft("image");
        setMarketingChannelContentTitleDraft("");
        setMarketingChannelContentDescriptionDraft("");
        setMarketingChannelContentEarningsEurDraft("0");
        setMarketingChannelContentChannelDraft("");
        setMarketingChannelContentFileDraft(null);
      }
      if (newView !== "marketing_targetgroups") {
        setMarketingTargetgroupPanelOpen(false);
        setSelectedMarketingTargetgroupId(null);
        setMarketingTargetgroupFormError("");
        setMarketingTargetgroupNameDraft("");
        setMarketingTargetgroupDescriptionDraft("");
      }
      if (newView !== "marketing_channels") {
        setMarketingChannelPanelOpen(false);
        setSelectedMarketingChannelId(null);
        setMarketingChannelFormError("");
        setMarketingChannelNameDraft("");
        setMarketingChannelDescriptionDraft("");
      }
      if (newView !== "marketing_posts") {
        setSelectedMembersPostId(null);
        setMembersPostConfirmError("");
      }
      if (newView !== "sales_training" && newView !== "people_schulungen") {
        setSalesTrainingPanelOpen(false);
        setSelectedSalesTrainingId(null);
        setSalesTrainingFormError("");
        setSalesTrainingKindDraft("image");
        setSalesTrainingTitleDraft("");
        setSalesTrainingDescriptionDraft("");
        setSalesTrainingFileDraft(null);
      }
      if (newView !== "sales_karriere") {
        setSalesEarningsError("");
        setSalesRevenueDetailKind(null);
      }
      if (
        newView !== "sales_home" &&
        newView !== "sales_akquise" &&
        newView !== "sales_service" &&
        newView !== "sales_acquisition_content" &&
        newView !== "sales_team" &&
        newView !== "sales_training" &&
        newView !== "sales_karriere" &&
        newView !== "people_schulungen"
      ) {
        setTeamInviteCreateOpen(false);
        setTeamInviteFormError("");
        setTeamInvError("");
      }
      if (newView !== "sales_team") {
        setSelectedTeamMemberId(null);
        setTeamMemberDetailOpen(false);
        setTeamChatMessages([]);
        setTeamChatDraft("");
        setTeamChatError("");
        setTeamSearchDraft("");
      }
      if (newView !== "design_patterns") {
        setSelectedDesignPatternId(null);
      }
      if (AI_AGENT_VIEW_IDS.has(newView)) {
        if (newView !== "ai_agents_services") setSelectedAiAgentServiceId(null);
        if (newView !== "ai_agents_consumers")
          setSelectedAiAgentConsumerId(null);
        if (newView !== "ai_agents_runs") setSelectedAiAgentRunId(null);
        if (newView !== "ai_agents_evals") setSelectedAiAgentEvalId(null);
        if (newView !== "ai_agents_tools") setSelectedAiAgentToolId(null);
        if (newView !== "ai_agents_actions") setSelectedAiAgentActionId(null);
        if (newView !== "ai_agents_data") setSelectedAiAgentDataId(null);
        if (newView !== "ai_agents_governance") {
          setSelectedAiAgentCostId(null);
          setSelectedAiAgentApprovalId(null);
          setSelectedAiAgentIncidentId(null);
        }
      }
      if (!AI_AGENT_AREA_VIEW_IDS.has(newView)) {
        setSelectedAiAgentServiceId(null);
        setSelectedAiAgentConsumerId(null);
        setSelectedAiAgentRunId(null);
        setSelectedAiAgentEvalId(null);
        setSelectedAiAgentCostId(null);
        setSelectedAiAgentApprovalId(null);
        setSelectedAiAgentIncidentId(null);
        setSelectedAiAgentToolId(null);
        setSelectedAiAgentActionId(null);
        setSelectedAiAgentDataId(null);
        setAiAgentServiceDomainFilter("all");
        setAiAgentServiceStatusFilter("all");
        setAiAgentServiceOwnerFilter("all");
        setAiAgentServiceSearch("");
        setAiAgentRunServiceFilter("all");
        setAiAgentRunConsumerFilter("all");
        setAiAgentRunStatusFilter("all");
        setAiAgentRunSearch("");
        setAiAgentToolSearch("");
        setAiAgentActionKindFilter("all");
        setAiAgentDataKindFilter("all");
        setAiAgentGovernanceMode("cost");
        setDismissedApprovalIds(new Set());
        setAiAgentServiceDraftById({});
        setAiAgentServicePromoteOpen(false);
      }
      if (newView === "ai_agents_overview") {
        setSelectedAiAgentServiceId(null);
        setSelectedAiAgentConsumerId(null);
        setSelectedAiAgentRunId(null);
        setSelectedAiAgentEvalId(null);
        setSelectedAiAgentCostId(null);
        setSelectedAiAgentApprovalId(null);
        setSelectedAiAgentIncidentId(null);
        setSelectedAiAgentToolId(null);
        setSelectedAiAgentActionId(null);
        setSelectedAiAgentDataId(null);
      }
    },
    [flushProductPendingSave, setKontoOpen],
  );

  const closeAreaPicker = useCallback(() => {
    setAreaPickerOpen(false);
    // Bereichswahl wird nur aus Konto geöffnet — Zurück führt wieder zur Konto-Liste.
    setKontoOpen(true);
  }, [setKontoOpen]);

  const switchArea = useCallback(
    (areaId) => {
      const list = VIEWS_BY_AREA[areaId];
      if (!list || !list.length) return;
      flushProductPendingSave();
      setActiveArea(areaId);
      setActiveView(list[0].id);
      setAreaPickerOpen(false);
      setKontoOpen(false);
      setImpersonateStatus("");
      setAgendaCreateOpen(false);
      setSelectedAdminAgendaId(null);
      setAgendaCreateError("");
      setUserStoryCreateOpen(false);
      setSelectedUserStoryId(null);
      setUserStoryCreateError("");
      setUserStoryTitleDraft("");
      setUserStoryDescriptionDraft("");
      setUserStoryRatings([]);
      setUserStoryRatingNewScore("7");
      setUserStoryRatingNewComment("");
      setMemberCreateOpen(false);
      setSelectedMemberId(null);
      setMemberFormError("");
      setMemberInvitedFromDraft("");

      setSalesCustomerPanelOpen(false);
      setSelectedSalesCustomerId(null);
      setSalesCustomerFormError("");
      setSalesCustomerCompanyDraft("");
      setSalesCustomerContactDraft("");
      setSalesCustomerEmailDraft("");
      setSalesCustomerPhoneDraft("");
      setSalesCustomerNotesDraft("");
      setSalesCustomerUserIdDraft(null);
      setSalesCustomerLinkUsers([]);
      setSalesCustomerLinkUsersLoading(false);
      if (salesCustomerLinkDebounceRef.current) {
        clearTimeout(salesCustomerLinkDebounceRef.current);
        salesCustomerLinkDebounceRef.current = null;
      }

      setSalesContentPanelOpen(false);
      setSelectedSalesContentId(null);

      setMarketingCommunityPanelOpen(false);
      setSelectedMarketingCommunityContentId(null);
      setMarketingCommunityFormError("");
      setMarketingCommunityKindDraft("image");
      setMarketingCommunityTitleDraft("");
      setMarketingCommunityDescriptionDraft("");
      setMarketingCommunityEarningsEurDraft("0");
      setMarketingCommunityTargetgroupDraft("");
      setMarketingCommunityFileDraft(null);

      setMarketingTargetgroupPanelOpen(false);
      setSelectedMarketingTargetgroupId(null);
      setMarketingTargetgroupFormError("");
      setMarketingTargetgroupNameDraft("");
      setMarketingTargetgroupDescriptionDraft("");

      setMarketingChannelPanelOpen(false);
      setSelectedMarketingChannelId(null);
      setMarketingChannelFormError("");
      setMarketingChannelNameDraft("");
      setMarketingChannelDescriptionDraft("");

      setSelectedMembersPostId(null);
      setMembersPostConfirmError("");

      setSalesTrainingPanelOpen(false);
      setSelectedSalesTrainingId(null);
      setSalesTrainingFormError("");
      setSalesTrainingKindDraft("image");
      setSalesTrainingTitleDraft("");
      setSalesTrainingDescriptionDraft("");
      setSalesTrainingFileDraft(null);

      setSalesEarningsError("");
      setSalesRevenueDetailKind(null);
      setTeamInviteCreateOpen(false);
      setTeamInviteFormError("");
      setTeamInvError("");

      setSelectedDesignPatternId(null);
    },
    [flushProductPendingSave, setKontoOpen],
  );

  const openKonto = useCallback(() => {
    flushProductPendingSave();
    setAreaPickerOpen(false);
    setKontoOpen(true);
  }, [flushProductPendingSave, setKontoOpen]);

  const mobileTabTitle =
    views.find((v) => v.id === activeView)?.label ?? "Telephonio";

  let mobileMidActionLabel = "";
  let mobileMidAction = null;
  if (activeArea === "sales" && (activeView === "sales_akquise" || activeView === "sales_service")) {
    mobileMidActionLabel = "Neuer Lead";
    mobileMidAction = openSalesCustomerCreatePanel;
  } else if (
    activeArea === "marketing" &&
    activeView === "marketing_community_content"
  ) {
    mobileMidActionLabel = "Neuer Content";
    mobileMidAction = openMarketingCommunityContentCreatePanel;
  } else if (
    activeArea === "marketing" &&
    activeView === "marketing_channel_content"
  ) {
    mobileMidActionLabel = "Neuer Channel-Content";
    mobileMidAction = openMarketingChannelContentCreatePanel;
  } else if (
    activeArea === "marketing" &&
    activeView === "marketing_targetgroups"
  ) {
    mobileMidActionLabel = "Neue Zielgruppe";
    mobileMidAction = openMarketingTargetgroupCreatePanel;
  } else if (
    activeArea === "marketing" &&
    activeView === "marketing_channels"
  ) {
    mobileMidActionLabel = "Neuer Kanal";
    mobileMidAction = openMarketingChannelCreatePanel;
  } else if (activeArea === "people" && activeView === "people_schulungen") {
    mobileMidActionLabel = "Neue Schulung";
    mobileMidAction = openSalesTrainingCreatePanel;
  } else if (activeArea === "sales" && activeView === "sales_team") {
    mobileMidActionLabel = "Einladen";
    mobileMidAction = openTeamInviteCreatePanel;
  } else if (activeArea === "product" && activeView === "product_userstories") {
    mobileMidActionLabel = "Neue User Story";
    mobileMidAction = openUserStoryCreatePanel;
  } else if (
    activeArea === "ai_agents" &&
    activeView === "ai_agents_services"
  ) {
    mobileMidActionLabel = "Neuer Agent";
    mobileMidAction = () => {
      /* Mockup: später API */
    };
  }

  const revenueEarningsTotalCents = salesEarnings.reduce(
    (acc, e) => acc + (Number(e.amount_cents) || 0),
    0,
  );
  const revenueTotalDisplay = formatEarningsAmountDisplay(
    revenueEarningsTotalCents,
    "EUR",
  );

  useEffect(() => {
    if (!adminUser) return;
    try {
      localStorage.setItem("@adminLastArea", activeArea);
      localStorage.setItem("@adminLastView", activeView);
    } catch {}
  }, [adminUser, activeArea, activeView]);

  useEffect(() => () => flushProductPendingSave(), []);

  useEffect(() => {
    if (!aicoworkerDetail || !aicoworkerDetail.id) return;
    if (prevAicoworkerIdRef.current !== aicoworkerDetail.id) {
      prevAicoworkerIdRef.current = aicoworkerDetail.id;
      return;
    }
    const id = aicoworkerDetail.id;
    const payload = {
      label: aicoworkerDetail.label || "",
      description: aicoworkerDetail.description || "",
      icon: aicoworkerDetail.icon || "",
      system_prompt: aicoworkerDetail.system_prompt || "",
      is_active: aicoworkerDetail.is_active,
    };
    scheduleProductAutoSave(() => saveAicoworkerDetail(id, payload));
  }, [
    aicoworkerDetail?.id,
    aicoworkerDetail?.label,
    aicoworkerDetail?.description,
    aicoworkerDetail?.icon,
    aicoworkerDetail?.system_prompt,
    aicoworkerDetail?.is_active,
    scheduleProductAutoSave,
    saveAicoworkerDetail,
  ]);

  useEffect(() => {
    if (!moduleDetail || !moduleDetail.id) return;
    if (prevModuleIdRef.current !== moduleDetail.id) {
      prevModuleIdRef.current = moduleDetail.id;
      return;
    }
    const id = moduleDetail.id;
    const payload = {
      label: moduleDetail.label || "",
      description: moduleDetail.description || "",
      icon: moduleDetail.icon || "",
      is_active: moduleDetail.is_active,
    };
    scheduleProductAutoSave(() => saveModuleDetail(id, payload));
  }, [
    moduleDetail?.id,
    moduleDetail?.label,
    moduleDetail?.description,
    moduleDetail?.icon,
    moduleDetail?.is_active,
    scheduleProductAutoSave,
    saveModuleDetail,
  ]);

  if (isMemberInvitePath) {
    return (
      <MemberInviteRegister
        apiBase={apiBase}
        onRegistered={(token) => {
          try {
            localStorage.setItem("@adminToken", token);
          } catch { /* ignore */ }
          try {
            window.history.replaceState({ admin: "app" }, "", "/");
          } catch { /* ignore */ }
          setIsMemberInvitePath(false);
          verifyToken(token);
        }}
      />
    );
  }

  if (!isLoggedIn) {
    return (
      <div className="admin_screen admin_screen--login">
        <div className="admin_login_card">
          <div className="admin_login_logo_wrap">
            <img src="/favicon.svg" alt="" className="admin_login_logo" />
            <span className="admin_login_brand">Telephonio</span>
          </div>
          <h1 className="admin_login_title">
            {loginStep === "phone" ? "Member Login" : "SMS-Code eingeben"}
          </h1>
          {loginStep === "phone" && (
            <form onSubmit={handleLoginPhoneSubmit} className="admin_form">
              <p className="admin_label" style={{ marginBottom: 6, opacity: 0.85 }}>
                Wir schicken dir per SMS einen 6-stelligen Code an deine hinterlegte Mobilnummer.
              </p>
              <label className="admin_label">
                Mobilnummer
                <input
                  type="tel"
                  value={loginPhoneInput}
                  onChange={(e) => setLoginPhoneInput(e.target.value)}
                  className="admin_input"
                  autoComplete="tel"
                  inputMode="tel"
                  placeholder="+4915112345678"
                  disabled={loginBusy}
                />
              </label>
              {error && <p className="admin_error">{error}</p>}
              <button type="submit" className="admin_btn" disabled={loginBusy}>
                {loginBusy ? "SMS wird versendet …" : "Code per SMS schicken"}
              </button>
            </form>
          )}
          {loginStep === "code" && (
            <form onSubmit={(e) => handleLoginCodeSubmit(e)} className="admin_form">
              <p className="admin_label" style={{ marginBottom: 6, opacity: 0.85 }}>
                SMS gesendet an <strong>{loginPhoneE164}</strong>
              </p>
              <label className="admin_label">
                Code
                <input
                  type="text"
                  value={loginCode}
                  onChange={(e) => handleLoginCodeChange(e.target.value)}
                  className="admin_input"
                  autoComplete="one-time-code"
                  inputMode="numeric"
                  pattern="[0-9]{6}"
                  placeholder="123456"
                  maxLength={6}
                  disabled={loginBusy}
                />
              </label>
              {error && <p className="admin_error">{error}</p>}
              <button type="submit" className="admin_btn" disabled={loginBusy || loginCode.length < 6}>
                {loginBusy ? "Wird geprueft …" : "Anmelden"}
              </button>
              <div style={{ marginTop: 10, display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                <button
                  type="button"
                  className="admin_back"
                  onClick={handleLoginBack}
                  disabled={loginBusy}
                  style={{ background: "none", border: 0, padding: 0, cursor: loginBusy ? "default" : "pointer" }}
                >
                  Andere Nummer
                </button>
                <button
                  type="button"
                  className="admin_back"
                  onClick={handleLoginResend}
                  disabled={loginResendIn > 0 || loginBusy}
                  style={{ background: "none", border: 0, padding: 0, cursor: loginResendIn > 0 || loginBusy ? "default" : "pointer" }}
                >
                  {loginResendIn > 0 ? `Neue SMS in ${loginResendIn}s` : "Neue SMS schicken"}
                </button>
              </div>
            </form>
          )}
        </div>
      </div>
    );
  }

  const filteredMockAiServices = (() => {
    let s = MOCK_AI_SERVICES;
    if (aiAgentServiceDomainFilter !== "all") {
      s = s.filter((x) => x.domain === aiAgentServiceDomainFilter);
    }
    if (aiAgentServiceStatusFilter !== "all") {
      s = s.filter((x) => {
        const pub = mergeAiAgentPublished(
          x,
          aiAgentServicePublishedById[x.id],
        );
        return pub.status === aiAgentServiceStatusFilter;
      });
    }
    if (aiAgentServiceOwnerFilter !== "all") {
      s = s.filter((x) => x.owner === aiAgentServiceOwnerFilter);
    }
    const q = aiAgentServiceSearch.trim().toLowerCase();
    if (q) {
      s = s.filter(
        (x) =>
          String(x.name || "")
            .toLowerCase()
            .includes(q) ||
          String(x.slug || "")
            .toLowerCase()
            .includes(q) ||
          String(x.domain || "")
            .toLowerCase()
            .includes(q),
      );
    }
    return s;
  })();

  const filteredMockAiRuns = (() => {
    let s = MOCK_AI_RUNS;
    if (aiAgentRunServiceFilter !== "all") {
      s = s.filter((x) => x.service_id === aiAgentRunServiceFilter);
    }
    if (aiAgentRunConsumerFilter !== "all") {
      s = s.filter((x) => x.caller_id === aiAgentRunConsumerFilter);
    }
    if (aiAgentRunStatusFilter !== "all") {
      s = s.filter((x) => x.status === aiAgentRunStatusFilter);
    }
    const q = aiAgentRunSearch.trim().toLowerCase();
    if (q) {
      s = s.filter(
        (x) =>
          String(x.description || "")
            .toLowerCase()
            .includes(q) ||
          String(x.id || "")
            .toLowerCase()
            .includes(q),
      );
    }
    return s;
  })();

  const pendingAiApprovalCount = MOCK_AI_APPROVALS.filter(
    (a) => !dismissedApprovalIds.has(a.id),
  ).length;
  const visibleMockAiApprovals = MOCK_AI_APPROVALS.filter(
    (a) => !dismissedApprovalIds.has(a.id),
  );

  const selectedServiceBase =
    selectedAiAgentServiceId != null
      ? MOCK_AI_SERVICES.find((x) => x.id === selectedAiAgentServiceId)
      : null;
  const selectedServicePublished = selectedServiceBase
    ? mergeAiAgentPublished(
        selectedServiceBase,
        aiAgentServicePublishedById[selectedAiAgentServiceId],
      )
    : null;
  const selectedServiceDraft =
    selectedAiAgentServiceId != null
      ? aiAgentServiceDraftById[selectedAiAgentServiceId]
      : null;
  const selectedMockAiService = selectedServicePublished
    ? mergeAiAgentDraft(selectedServicePublished, selectedServiceDraft)
    : null;
  const selectedServiceLive = selectedServicePublished;
  const hasAiServiceDraft = Boolean(
    selectedAiAgentServiceId && selectedServiceDraft,
  );

  const selectedMockAiConsumer = MOCK_AI_CONSUMERS.find(
    (x) => x.id === selectedAiAgentConsumerId,
  );
  const selectedMockAiRun = MOCK_AI_RUNS.find(
    (x) => x.id === selectedAiAgentRunId,
  );
  const selectedMockAiEval = MOCK_AI_EVALS.find(
    (x) => x.id === selectedAiAgentEvalId,
  );
  const selectedMockAiCost = MOCK_AI_COSTS.find(
    (x) => x.id === selectedAiAgentCostId,
  );
  const selectedMockAiApproval = MOCK_AI_APPROVALS.find(
    (x) => x.id === selectedAiAgentApprovalId,
  );
  const selectedMockAiIncident = MOCK_AI_INCIDENTS.find(
    (x) => x.id === selectedAiAgentIncidentId,
  );
  const selectedMockAiTool = MOCK_AI_TOOLS.find(
    (x) => x.id === selectedAiAgentToolId,
  );
  const selectedMockAiAction = MOCK_AI_ACTIONS.find(
    (x) => x.id === selectedAiAgentActionId,
  );
  const selectedMockAiData = MOCK_AI_DATA_STORES.find(
    (x) => x.id === selectedAiAgentDataId,
  );

  const filteredMockAiTools = MOCK_AI_TOOLS.filter((t) => {
    const q = aiAgentToolSearch.trim().toLowerCase();
    if (!q) return true;
    return (
      String(t.name).toLowerCase().includes(q) ||
      String(t.slug).toLowerCase().includes(q) ||
      String(t.description).toLowerCase().includes(q) ||
      String(t.category).toLowerCase().includes(q)
    );
  });
  const filteredMockAiActions = MOCK_AI_ACTIONS.filter((a) => {
    if (aiAgentActionKindFilter === "all") return true;
    return a.kind === aiAgentActionKindFilter;
  });
  const filteredMockAiData = MOCK_AI_DATA_STORES.filter((d) => {
    if (aiAgentDataKindFilter === "all") return true;
    return d.kind === aiAgentDataKindFilter;
  });

  /** Konto > Profil: Form-Body als JSX-Konstante (wiederverwendbar in Mitte mobil + rechter Sidebar desktop). */
  const kontoProfileBody = adminUser ? (
    <div className="admin_konto_section">
      <div className="admin_konto_profile_section">
        <div className="admin_konto_profile_pic_row">
          {memberProfilePicSrc(adminUser, apiBase, adminToken, profilePicVersion) ? (
            <img
              src={memberProfilePicSrc(adminUser, apiBase, adminToken, profilePicVersion)}
              alt=""
              className="admin_konto_profile_pic"
            />
          ) : (
            <div
              className="admin_konto_profile_pic admin_konto_profile_pic--initials"
              aria-hidden="true">
              {memberInitials(adminUser)}
            </div>
          )}
          <div className="admin_konto_profile_pic_actions">
            <label
              className={
                "admin_btn" + (kontoPicUploading ? " admin_btn--disabled" : "")
              }>
              {kontoPicUploading ? "Lade…" : "Bild waehlen"}
              <input
                type="file"
                accept="image/*"
                style={{ display: "none" }}
                disabled={kontoPicUploading}
                onChange={(e) => {
                  const f = e.target.files && e.target.files[0];
                  if (f) uploadKontoProfilePicture(f);
                  e.target.value = "";
                }}
              />
            </label>
            {adminUser.profile_picture_key ? (
              <button
                type="button"
                className="admin_btn"
                disabled={kontoPicUploading}
                onClick={() => {
                  if (typeof window !== "undefined" && !window.confirm("Profilbild entfernen?")) {
                    return;
                  }
                  removeKontoProfilePicture();
                }}>
                Entfernen
              </button>
            ) : null}
          </div>
        </div>
        {kontoPicError ? (
          <p className="admin_error" style={{ margin: "4px 0 0" }}>
            {kontoPicError}
          </p>
        ) : null}
        <div className="admin_konto_form_field">
          <label className="admin_konto_form_label" htmlFor="konto_name">
            Anzeigename
          </label>
          <input
            id="konto_name"
            type="text"
            className="admin_input"
            value={kontoNameDraft}
            onChange={(e) => {
              setKontoNameDraft(e.target.value);
              setKontoError("");
              setKontoInfo("");
            }}
            placeholder={adminUser.email || ""}
            autoComplete="name"
          />
        </div>
        <div className="admin_konto_form_field">
          <label className="admin_konto_form_label" htmlFor="konto_pw_current">
            Aktuelles Passwort
          </label>
          <input
            id="konto_pw_current"
            type="password"
            className="admin_input"
            value={kontoCurrentPwDraft}
            onChange={(e) => {
              setKontoCurrentPwDraft(e.target.value);
              setKontoError("");
              setKontoInfo("");
            }}
            placeholder="Nur bei Passwort-Aenderung"
            autoComplete="current-password"
          />
        </div>
        <div className="admin_konto_form_field">
          <label className="admin_konto_form_label" htmlFor="konto_pw_new">
            Neues Passwort
          </label>
          <input
            id="konto_pw_new"
            type="password"
            className="admin_input"
            value={kontoNewPwDraft}
            onChange={(e) => {
              setKontoNewPwDraft(e.target.value);
              setKontoError("");
              setKontoInfo("");
            }}
            placeholder="min. 8 Zeichen"
            autoComplete="new-password"
          />
        </div>
        <div className="admin_konto_form_field">
          <label className="admin_konto_form_label" htmlFor="konto_pw_repeat">
            Neues Passwort wiederholen
          </label>
          <input
            id="konto_pw_repeat"
            type="password"
            className="admin_input"
            value={kontoNewPwRepeatDraft}
            onChange={(e) => {
              setKontoNewPwRepeatDraft(e.target.value);
              setKontoError("");
              setKontoInfo("");
            }}
            placeholder="Wiederholen"
            autoComplete="new-password"
          />
        </div>
        {kontoError ? (
          <p className="admin_error" style={{ margin: "4px 0 0" }}>
            {kontoError}
          </p>
        ) : null}
        {kontoInfo ? (
          <p className="admin_info" style={{ margin: "4px 0 0" }}>
            {kontoInfo}
          </p>
        ) : null}
        <div className="admin_konto_form_actions">
          <button
            type="button"
            className="admin_btn admin_btn--primary"
            disabled={kontoSaving}
            onClick={saveKontoProfile}>
            {kontoSaving ? "Speichere…" : "Speichern"}
          </button>
        </div>
      </div>
    </div>
  ) : null;

  /** Konto > Darstellung: Form-Body als JSX-Konstante. */
  const kontoAppearanceBody = (
    <div className="admin_konto_section">
      <div className="admin_konto_row">
        <div className="admin_konto_row_main">
          <span className="admin_konto_row_title">Dark Mode</span>
          <span className="admin_konto_row_sub">
            {darkMode
              ? "Dunkles Farbschema aktiv."
              : "Helles Farbschema aktiv."}
          </span>
        </div>
        <button
          type="button"
          className={
            "admin_toggle" + (darkMode ? " admin_toggle--on" : "")
          }
          onClick={() => setDarkMode((v) => !v)}
          aria-label="Dark Mode umschalten">
          <span className="admin_toggle_knob" />
        </button>
      </div>
    </div>
  );

  return (
    <div
      className={
        "admin_layout" +
        (isMobile && !settingsOpen && !areaPickerOpen
          ? " admin_layout--mobile_unified"
          : "")
      }>

      {/* Desktop left sidebar */}
      <div className="admin_left_sidebar">
        <div className="admin_left_sidebar_brand">
          <div className="admin_sidebar_logo_wrap">
            <img src="/favicon.svg" alt="" className="admin_sidebar_logo" />
            <span className="admin_sidebar_brand">Telephonio</span>
          </div>
        </div>
        <nav className="admin_left_sidebar_nav">
          {views.map((view) => (
            <button
              key={view.id}
              type="button"
              className={
                "admin_left_sidebar_item" +
                (activeView === view.id
                  ? " admin_left_sidebar_item--active"
                  : "")
              }
              onClick={() => switchView(view.id)}>
              <span className="admin_left_sidebar_icon">
                <ViewNavIcon viewId={view.id} size={20} />
              </span>
              <span className="admin_left_sidebar_label">{view.label}</span>
            </button>
          ))}
        </nav>
        <div className="admin_left_sidebar_footer">
          <button
            type="button"
            className={
              "admin_left_sidebar_logout_btn" +
              (settingsOpen ? " admin_left_sidebar_logout_btn--active" : "")
            }
            onClick={() => setKontoOpen(!settingsOpen)}>
            Konto
          </button>
        </div>
      </div>

      {/* Main content */}
      <div
        className={
          "admin_kanban_content" +
          (areaPickerOpen && isMobile
            ? " admin_kanban_content--no_bottom_padding"
            : "")
        }>
        {settingsOpen ? (
          <div className="admin_settings_main">
            <div className="admin_settings_main_header">
              {isMobile ? (
                <button
                  type="button"
                  className="admin_settings_main_back"
                  onClick={() => {
                    if (kontoSubpage) {
                      setKontoSubpage(null);
                    } else {
                      setKontoOpen(false);
                    }
                  }}
                  aria-label="Zurück">
                  <svg
                    width="22"
                    height="22"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="2.25"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    aria-hidden="true">
                    <path d="M15 18l-6-6 6-6" />
                  </svg>
                </button>
              ) : null}
              <h2 className="admin_settings_main_title">
                {isMobile && kontoSubpage === "profile"
                  ? "Profil"
                  : isMobile && kontoSubpage === "appearance"
                  ? "Darstellung"
                  : "Konto"}
              </h2>
            </div>
            <div className="admin_settings_main_body">
              {isMobile && kontoSubpage === "profile" ? (
                kontoProfileBody
              ) : isMobile && kontoSubpage === "appearance" ? (
                kontoAppearanceBody
              ) : (
                <div className="admin_konto_section">
                  {adminUser ? (
                    <div
                      role="button"
                      tabIndex={0}
                      className={
                        "admin_konto_row admin_konto_row--clickable admin_konto_row--profile" +
                        (!isMobile && kontoSubpage === "profile"
                          ? " admin_konto_row--active"
                          : "")
                      }
                      onClick={() => setKontoSubpage("profile")}
                      onKeyDown={(e) => {
                        if (e.key === "Enter" || e.key === " ") {
                          e.preventDefault();
                          setKontoSubpage("profile");
                        }
                      }}>
                      {memberProfilePicSrc(adminUser, apiBase, adminToken, profilePicVersion) ? (
                        <img
                          src={memberProfilePicSrc(adminUser, apiBase, adminToken, profilePicVersion)}
                          alt=""
                          className="admin_konto_row_avatar"
                        />
                      ) : (
                        <div
                          className="admin_konto_row_avatar admin_konto_row_avatar--initials"
                          aria-hidden="true">
                          {memberInitials(adminUser)}
                        </div>
                      )}
                      <div className="admin_konto_row_main">
                        <span className="admin_konto_row_title">
                          {adminUser.display_name || adminUser.email}
                        </span>
                        <span className="admin_konto_row_sub">
                          {adminUser.display_name && adminUser.email
                            ? adminUser.email
                            : "Profil bearbeiten"}
                        </span>
                      </div>
                      <span className="admin_konto_chevron" aria-hidden="true">
                        <AdminKontoChevronIcon />
                      </span>
                    </div>
                  ) : null}
                  <div
                    role="button"
                    tabIndex={0}
                    className={
                      "admin_konto_row admin_konto_row--clickable" +
                      (!isMobile && kontoSubpage === "appearance"
                        ? " admin_konto_row--active"
                        : "")
                    }
                    onClick={() => setKontoSubpage("appearance")}
                    onKeyDown={(e) => {
                      if (e.key === "Enter" || e.key === " ") {
                        e.preventDefault();
                        setKontoSubpage("appearance");
                      }
                    }}>
                    <div className="admin_konto_row_main">
                      <span className="admin_konto_row_title">Darstellung</span>
                      <span className="admin_konto_row_sub">
                        {darkMode ? "Dark Mode" : "Light Mode"}
                      </span>
                    </div>
                    <span className="admin_konto_chevron" aria-hidden="true">
                      <AdminKontoChevronIcon />
                    </span>
                  </div>
                  {allowedAreas.map((area) => {
                    const isActiveArea = !areaPickerOpen && activeArea === area.id;
                    return (
                      <div
                        key={area.id}
                        role="button"
                        tabIndex={0}
                        className={
                          "admin_konto_row admin_konto_row--clickable" +
                          (isActiveArea ? " admin_konto_row--active" : "")
                        }
                        onClick={() => switchArea(area.id)}
                        onKeyDown={(e) => {
                          if (e.key === "Enter" || e.key === " ") {
                            e.preventDefault();
                            switchArea(area.id);
                          }
                        }}>
                        <span
                          className="admin_konto_row_icon"
                          aria-hidden="true">
                          <AdminAreaNavIcon areaId={area.id} size={20} />
                        </span>
                        <div className="admin_konto_row_main">
                          <span className="admin_konto_row_title">
                            {area.label}
                          </span>
                          <span className="admin_konto_row_sub">
                            {area.description}
                          </span>
                        </div>
                        <span className="admin_konto_chevron" aria-hidden="true">
                          <AdminKontoChevronIcon />
                        </span>
                      </div>
                    );
                  })}
                  <div
                    role="button"
                    tabIndex={0}
                    aria-busy={appSwitchBusy}
                    className="admin_konto_row admin_konto_row--clickable"
                    onClick={handleSwitchToApp}
                    onKeyDown={(e) => {
                      if (e.key === "Enter" || e.key === " ") {
                        e.preventDefault();
                        handleSwitchToApp();
                      }
                    }}>
                    <div className="admin_konto_row_main">
                      <span className="admin_konto_row_title">Zur App</span>
                      <span className="admin_konto_row_sub">
                        {appSwitchBusy
                          ? "Wechsle …"
                          : appSwitchError || "Zum Kunden-Frontend (telephonio.com)"}
                      </span>
                    </div>
                    <span className="admin_konto_chevron" aria-hidden="true">
                      <AdminKontoChevronIcon />
                    </span>
                  </div>
                  <button
                    type="button"
                    className="admin_konto_row admin_konto_row--logout"
                    onClick={handleLogout}>
                    Ausloggen
                  </button>
                </div>
              )}
            </div>
          </div>
        ) : areaPickerOpen ? (
          <div className="admin_product_view admin_area_picker_wrap">
            <div className="admin_product_header admin_area_picker_header">
              <button
                type="button"
                className="admin_area_picker_back"
                onClick={closeAreaPicker}
                aria-label="Zurück zu Konto">
                <svg
                  width="22"
                  height="22"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2.25"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  aria-hidden="true">
                  <path d="M15 18l-6-6 6-6" />
                </svg>
              </button>
              <div className="admin_area_picker_header_text">
                <h2 className="admin_product_title">Bereich wählen</h2>
                <p className="admin_product_desc">
                  Wähle einen Arbeitsbereich. Die Tabs in der Sidebar gelten nur
                  für den gewählten Bereich.
                </p>
              </div>
            </div>
            <div className="admin_area_picker_list">
              {allowedAreas.map((area) => (
                <button
                  key={area.id}
                  type="button"
                  className={
                    "admin_area_card" +
                    (activeArea === area.id ? " admin_area_card--current" : "")
                  }
                  onClick={() => switchArea(area.id)}>
                  <span className="admin_area_card_icon_wrap" aria-hidden="true">
                    <AdminAreaNavIcon areaId={area.id} size={26} />
                  </span>
                  <span className="admin_area_card_text_col">
                    <span className="admin_area_card_title">{area.label}</span>
                    <span className="admin_area_card_desc">
                      {area.description}
                    </span>
                  </span>
                </button>
              ))}
            </div>
          </div>
        ) : (
          <>
            {isMobile ? (
              <AdminMobileTabHeader
                tabLabel={mobileTabTitle}
                onOpenKonto={openKonto}
                showNewAgenda={
                  activeArea === "admin" && activeView === "admin_agendas"
                }
                onNewAgenda={openAgendaCreatePanel}
                midActionLabel={mobileMidActionLabel}
                onMidAction={mobileMidAction}
              />
            ) : null}
            {activeView === "anrufe" ? (
          <div className="admin_product_view">
            <div className="admin_product_header">
              <h2 className="admin_product_title">Anrufe</h2>
              <p className="admin_product_desc">
                Alle eingehenden Telefonate, Browser-Tests und gemappte Tools.
              </p>
            </div>
            <div
              style={{
                padding: "8px 16px",
                display: "flex",
                flexDirection: "column",
                gap: "6px",
                borderBottom: "1px solid var(--admin-border, #ddd)",
              }}>
              <input
                type="text"
                value={callsFilters.q}
                onChange={(e) =>
                  setCallsFilters((p) => ({ ...p, q: e.target.value }))
                }
                placeholder="Volltextsuche (Telefonnr, Summary, Transkript)…"
                style={{
                  width: "100%",
                  padding: "6px 10px",
                  border: "1px solid var(--admin-border, #ddd)",
                  borderRadius: "6px",
                  fontSize: "12px",
                  boxSizing: "border-box",
                  background: "var(--admin-bg-input, #fff)",
                  color: "var(--admin-text, #222)",
                }}
              />
              <div style={{ display: "flex", gap: "6px" }}>
                <select
                  value={callsDatePreset}
                  onChange={(e) => {
                    const preset = e.target.value;
                    setCallsDatePreset(preset);
                    const { from, to } = computeCallsDateRange(preset);
                    setCallsFilters((p) => ({ ...p, from, to }));
                  }}
                  style={{
                    flex: 1,
                    padding: "6px 8px",
                    border: "1px solid var(--admin-border, #ddd)",
                    borderRadius: "6px",
                    fontSize: "12px",
                    background: "var(--admin-bg-input, #fff)",
                    color: "var(--admin-text, #222)",
                  }}>
                  <option value="">Alle Zeiträume</option>
                  <option value="today">Heute</option>
                  <option value="yesterday">Gestern</option>
                  <option value="7d">Letzten 7 Tage</option>
                  <option value="30d">Letzten 30 Tage</option>
                  <optgroup label="Monat">
                    {buildCallsMonthOptions(12).map((m) => (
                      <option key={m.value} value={m.value}>
                        {m.label}
                      </option>
                    ))}
                  </optgroup>
                  <option value="lastyear">{`Vorjahr (${new Date().getFullYear() - 1})`}</option>
                </select>
                <button
                  type="button"
                  onClick={() => loadCallsList()}
                  style={{
                    padding: "5px 10px",
                    background: "var(--admin-accent, #FF5A5F)",
                    color: "#fff",
                    border: "none",
                    borderRadius: "6px",
                    cursor: "pointer",
                    fontSize: "11px",
                    fontWeight: 600,
                  }}>
                  ↻
                </button>
              </div>
              <div
                style={{
                  fontSize: "10px",
                  color: "var(--admin-text-secondary, #717171)",
                }}>
                {callsLoading
                  ? ""
                  : `${callsList.length}${callsTotal > callsList.length ? " / " + callsTotal : ""} Anrufe`}
              </div>
            </div>
            <div className="admin_product_user_list">
              {callsList.map((c) => {
                const startedAt = c.started_at ? new Date(c.started_at) : null;
                const dateStr = startedAt
                  ? startedAt.toLocaleString("de-DE", {
                      day: "2-digit",
                      month: "2-digit",
                      hour: "2-digit",
                      minute: "2-digit",
                    })
                  : "–";
                const durationStr = c.duration_seconds
                  ? `${Math.floor(c.duration_seconds / 60)}:${String(c.duration_seconds % 60).padStart(2, "0")}`
                  : "–";
                const callerName =
                  c.relationship_person_name ||
                  c.contact_name ||
                  c.caller_phone ||
                  (c.is_test ? "Test" : "Unbekannt");
                const providerLabel =
                  c.provider_display_name ||
                  c.provider_email ||
                  `#${c.user_id || "?"}`;
                return (
                  <button
                    key={c.id}
                    type="button"
                    className={
                      "admin_product_user_card" +
                      (selectedCallId === c.id
                        ? " admin_product_user_card--selected"
                        : "")
                    }
                    onClick={() => setSelectedCallId(c.id)}
                    style={{ width: "100%", textAlign: "left" }}>
                    <div
                      className="admin_product_user_avatar"
                      style={{
                        background: c.fatal_error
                          ? "rgba(193,53,21,0.14)"
                          : c.matched_tool_name
                            ? "var(--admin-accent-soft, rgba(255,90,95,0.12))"
                            : "var(--admin-bg-input, #fff)",
                        color: c.fatal_error
                          ? "#c13515"
                          : c.matched_tool_name
                            ? "var(--admin-accent, #FF5A5F)"
                            : "var(--admin-text-secondary, #717171)",
                        border: c.fatal_error
                          ? "1px solid #c13515"
                          : "1px solid var(--admin-border, #ddd)",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        fontSize: "16px",
                      }}>
                      {c.fatal_error ? "⚠" : c.matched_tool_name ? "🔧" : "📞"}
                    </div>
                    <div
                      className="admin_product_user_info"
                      style={{ minWidth: 0 }}>
                      <span
                        className="admin_product_user_name"
                        style={{
                          display: "flex",
                          justifyContent: "space-between",
                          gap: "6px",
                        }}>
                        <span
                          style={{
                            overflow: "hidden",
                            textOverflow: "ellipsis",
                            whiteSpace: "nowrap",
                          }}>
                          {callerName}
                        </span>
                        <span
                          style={{
                            fontSize: "10px",
                            color: "var(--admin-text-secondary, #717171)",
                            fontWeight: 400,
                            flexShrink: 0,
                          }}>
                          {durationStr}
                        </span>
                      </span>
                      <span
                        className="admin_product_user_email"
                        style={{
                          display: "flex",
                          justifyContent: "space-between",
                          gap: "6px",
                        }}>
                        <span
                          style={{
                            overflow: "hidden",
                            textOverflow: "ellipsis",
                            whiteSpace: "nowrap",
                          }}>
                          {providerLabel}
                        </span>
                        <span style={{ fontSize: "10px", flexShrink: 0 }}>
                          {dateStr}
                        </span>
                      </span>
                      {c.fatal_error && (
                        <span
                          style={{
                            display: "inline-block",
                            marginTop: "3px",
                            padding: "1px 6px",
                            borderRadius: "8px",
                            background: "#c13515",
                            color: "#fff",
                            fontSize: "10px",
                            fontWeight: 700,
                            letterSpacing: "0.4px",
                          }}>
                          FATAL ERROR
                        </span>
                      )}
                      {!c.fatal_error && c.matched_tool_name && (
                        <span
                          style={{
                            display: "inline-block",
                            marginTop: "3px",
                            padding: "1px 6px",
                            borderRadius: "8px",
                            background:
                              "var(--admin-accent-soft, rgba(255,90,95,0.12))",
                            color: "var(--admin-accent, #FF5A5F)",
                            fontSize: "10px",
                            fontWeight: 600,
                          }}>
                          {c.matched_tool_name}
                        </span>
                      )}
                      {c.call_summary && !c.matched_tool_name && (
                        <span
                          style={{
                            display: "block",
                            marginTop: "2px",
                            fontSize: "11px",
                            color: "var(--admin-text-secondary, #717171)",
                            overflow: "hidden",
                            textOverflow: "ellipsis",
                            whiteSpace: "nowrap",
                          }}>
                          {c.call_summary}
                        </span>
                      )}
                    </div>
                  </button>
                );
              })}
              {!callsLoading && callsList.length === 0 && (
                <p className="admin_product_empty">Keine Anrufe vorhanden</p>
              )}
            </div>
          </div>
        ) : activeView === "error_logs" ? (
          <div className="admin_product_view">
            <div className="admin_product_header">
              <h2 className="admin_product_title">Error Logs</h2>
              <p className="admin_product_desc">
                Backend-Errors plus Anrufe mit FATAL-ERROR-Markierung
                (z.B. Failsafe-Anrufe auf der SHARED-Twilio-Nummer).
              </p>
            </div>
            <div
              style={{
                padding: "8px 16px",
                display: "flex",
                gap: "8px",
                alignItems: "center",
                borderBottom: "1px solid var(--admin-border, #ddd)",
              }}>
              <button
                type="button"
                onClick={() => loadErrorLogs()}
                style={{
                  padding: "5px 10px",
                  background: "var(--admin-accent, #FF5A5F)",
                  color: "#fff",
                  border: "none",
                  borderRadius: "6px",
                  cursor: "pointer",
                  fontSize: "11px",
                  fontWeight: 600,
                }}>
                ↻
              </button>
              <span
                style={{
                  fontSize: "10px",
                  color: "var(--admin-text-secondary, #717171)",
                }}>
                {errorLogsLoading
                  ? "lade…"
                  : `${errorLogsItems.length}${
                      errorLogsTotal > errorLogsItems.length
                        ? " / " + errorLogsTotal
                        : ""
                    } Eintr\u00e4ge`}
              </span>
            </div>
            <div className="admin_product_user_list">
              {errorLogsItems.map((it) => {
                const ts = it.ts ? new Date(it.ts) : null;
                const dateStr = ts
                  ? ts.toLocaleString("de-DE", {
                      day: "2-digit",
                      month: "2-digit",
                      hour: "2-digit",
                      minute: "2-digit",
                    })
                  : "\u2013";
                const isCall = it.type === "call";
                const dataObj = (() => {
                  if (!it.data) return null;
                  if (typeof it.data === "object") return it.data;
                  try { return JSON.parse(it.data); } catch { return null; }
                })();
                let dataPreview = "";
                if (dataObj && typeof dataObj === "object") {
                  try { dataPreview = JSON.stringify(dataObj); } catch {}
                  if (dataPreview.length > 220) dataPreview = dataPreview.slice(0, 220) + "\u2026";
                } else if (typeof it.data === "string") {
                  dataPreview = it.data.length > 220 ? it.data.slice(0, 220) + "\u2026" : it.data;
                }
                return (
                  <button
                    key={it.id}
                    type="button"
                    className="admin_product_user_card"
                    onClick={() => {
                      if (isCall) {
                        setSelectedCallId(it.call_id);
                        setActiveView("anrufe");
                      }
                    }}
                    style={{
                      width: "100%",
                      textAlign: "left",
                      cursor: isCall ? "pointer" : "default",
                    }}>
                    <div
                      className="admin_product_user_avatar"
                      style={{
                        background: isCall
                          ? "rgba(193,53,21,0.14)"
                          : "rgba(193,53,21,0.08)",
                        color: "#c13515",
                        border: "1px solid #c13515",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        fontSize: "16px",
                      }}>
                      {isCall ? "\u26A0" : "\u2716"}
                    </div>
                    <div className="admin_product_user_info" style={{ minWidth: 0 }}>
                      <span
                        className="admin_product_user_name"
                        style={{
                          display: "flex",
                          justifyContent: "space-between",
                          gap: "6px",
                        }}>
                        <span
                          style={{
                            overflow: "hidden",
                            textOverflow: "ellipsis",
                            whiteSpace: "nowrap",
                          }}>
                          {isCall
                            ? "FATAL ERROR \u00b7 " +
                              (it.contact_name || it.caller_phone || it.fatal_error || "Anruf")
                            : it.message || "(ohne Message)"}
                        </span>
                        <span
                          style={{
                            fontSize: "10px",
                            color: "var(--admin-text-secondary, #717171)",
                            fontWeight: 400,
                            flexShrink: 0,
                          }}>
                          {dateStr}
                        </span>
                      </span>
                      <span
                        className="admin_product_user_email"
                        style={{
                          display: "flex",
                          justifyContent: "space-between",
                          gap: "6px",
                        }}>
                        <span
                          style={{
                            overflow: "hidden",
                            textOverflow: "ellipsis",
                            whiteSpace: "nowrap",
                          }}>
                          {isCall
                            ? "Anruf #" + it.call_id +
                              (it.provider_display_name || it.provider_email
                                ? " \u00b7 " +
                                  (it.provider_display_name || it.provider_email)
                                : "")
                            : (it.source ? it.source + " \u00b7 " : "") +
                              (it.level || "")}
                        </span>
                        {isCall && (
                          <span
                            style={{
                              fontSize: "10px",
                              padding: "1px 6px",
                              borderRadius: "8px",
                              background: "#c13515",
                              color: "#fff",
                              fontWeight: 700,
                              letterSpacing: "0.4px",
                              flexShrink: 0,
                            }}>
                            FATAL
                          </span>
                        )}
                      </span>
                      {(isCall ? it.data && it.data.call_summary : dataPreview) && (
                        <span
                          style={{
                            display: "block",
                            marginTop: "3px",
                            fontSize: "11px",
                            color: "var(--admin-text-secondary, #717171)",
                            fontFamily: isCall ? "inherit" : "monospace",
                            overflow: "hidden",
                            textOverflow: "ellipsis",
                            whiteSpace: "nowrap",
                          }}>
                          {isCall ? it.data.call_summary : dataPreview}
                        </span>
                      )}
                    </div>
                  </button>
                );
              })}
              {!errorLogsLoading && errorLogsItems.length === 0 && (
                <p className="admin_product_empty">Keine Errors</p>
              )}
            </div>
          </div>
        ) : activeView === "aicoworker" ? (
          <div className="admin_product_view">
            <div className="admin_product_header">
              <h2 className="admin_product_title">Agenten</h2>
              <p className="admin_product_desc">
                Erstelle und verwalte Agenten mit individuellem
                Systemprompt und Modulen.
              </p>
            </div>
            <div className="admin_product_user_list">
              {aicoworkers.map((mode) => (
                <button
                  key={mode.id}
                  type="button"
                  className={
                    "admin_product_user_card" +
                    (selectedAicoworkerId === mode.id
                      ? " admin_product_user_card--selected"
                      : "")
                  }
                  onClick={() => {
                    flushProductPendingSave();
                    setSelectedAicoworkerId(mode.id);
                  }}
                  style={{ textAlign: "left" }}>
                  <div
                    className="admin_product_user_avatar"
                    style={{
                      background: "var(--admin-bg-input, #fff)",
                      border:
                        "1px " +
                        (mode.icon ? "solid" : "dashed") +
                        " var(--admin-border, #ddd)",
                      color: "var(--admin-text, #222)",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                    }}>
                    {mode.icon ? (
                      <ModuleIconSvg name={mode.icon} size={18} />
                    ) : null}
                  </div>
                  <div className="admin_product_user_info">
                    <span className="admin_product_user_name">
                      {mode.label}
                    </span>
                    <span className="admin_product_user_email">
                      {mode.slug}
                      {!mode.is_active ? " (inaktiv)" : ""}
                    </span>
                  </div>
                </button>
              ))}
              {aicoworkers.length === 0 && (
                <p className="admin_product_empty">
                  Keine Agenten vorhanden
                </p>
              )}
            </div>
          </div>
        ) : activeView === "module" ? (
          <div className="admin_product_view">
            <div className="admin_product_header">
              <h2 className="admin_product_title">Module</h2>
              <p className="admin_product_desc">
                Logische Module mit beliebig vielen Tools (z. B. „Tische
                abfragen" + „Reservierung anlegen").
              </p>
            </div>
            <div className="admin_product_user_list">
              {moduleList.map((svc) => {
                const toolsCount = Number(svc.tools_count || 0);
                const activeTools = Number(svc.active_tools_count || 0);
                const subline =
                  toolsCount === 0
                    ? "Keine Tools"
                    : toolsCount +
                      " Tool" +
                      (toolsCount === 1 ? "" : "s") +
                      (activeTools !== toolsCount
                        ? " (" + activeTools + " aktiv)"
                        : "");
                return (
                  <div
                    key={svc.id}
                    onClick={() => {
                      flushProductPendingSave();
                      setSelectedModuleId(svc.id);
                    }}
                    className={
                      "admin_product_user_card" +
                      (selectedModuleId === svc.id
                        ? " admin_product_user_card--selected"
                        : "")
                    }
                    style={{ cursor: "pointer" }}>
                    <div
                      className="admin_product_user_avatar"
                      style={{
                        background: "var(--admin-bg-input, #fff)",
                        border:
                          "1px " +
                          (svc.icon ? "solid" : "dashed") +
                          " var(--admin-border, #ddd)",
                        color: "var(--admin-text, #222)",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                      }}>
                      {svc.icon ? (
                        <ModuleIconSvg name={svc.icon} size={18} />
                      ) : null}
                    </div>
                    <div className="admin_product_user_info">
                      <span className="admin_product_user_name">
                        {svc.label}
                        {!svc.is_active ? " (inaktiv)" : ""}
                      </span>
                      <span className="admin_product_user_email">
                        {subline}
                      </span>
                    </div>
                  </div>
                );
              })}
              {moduleList.length === 0 && (
                <p className="admin_product_empty">Keine Module vorhanden</p>
              )}
            </div>
          </div>
        ) : activeView === "kis" ? (
          <div className="admin_product_view">
            <div className="admin_product_header">
              <h2 className="admin_product_title">KI's</h2>
              <p className="admin_product_desc">
                Alle existierenden KI-Assistenten der Plattform.
              </p>
            </div>
            <div className="admin_product_user_list">
              {aisList.map((ai) => {
                const ownerLabel =
                  ai.user_display_name ||
                  ai.user_email ||
                  (ai.user_id ? "User #" + ai.user_id : "ohne Owner");
                const initial = (ai.ai_name || ai.display_name || "?")
                  .charAt(0)
                  .toUpperCase();
                return (
                  <button
                    key={ai.id}
                    type="button"
                    className={
                      "admin_product_user_card" +
                      (selectedAiId === ai.id
                        ? " admin_product_user_card--selected"
                        : "")
                    }
                    onClick={() => setSelectedAiId(ai.id)}>
                    <div className="admin_product_user_avatar">{initial}</div>
                    <div className="admin_product_user_info">
                      <span className="admin_product_user_name">
                        {ai.ai_name || ai.display_name || "Assistenz"}
                      </span>
                      <span className="admin_product_user_email">
                        {ownerLabel}
                        {ai.industry_label
                          ? " · " + ai.industry_label
                          : ai.industry_slug
                            ? " · " + ai.industry_slug
                            : ""}
                      </span>
                    </div>
                  </button>
                );
              })}
              {!aisLoading && aisList.length === 0 && (
                <p className="admin_product_empty">Keine KI's vorhanden</p>
              )}
            </div>
          </div>
        ) : activeView === "kunden" ? (
          <div className="admin_product_view">
            <div className="admin_product_header">
              <h2 className="admin_product_title">Nutzer</h2>
              <p className="admin_product_desc">
                Registrierte Nutzer der Plattform.
              </p>
              <p
                className="admin_product_desc"
                style={{
                  fontSize: "11px",
                  marginTop: "6px",
                  opacity: 0.85,
                  wordBreak: "break-all",
                }}>
                Datenquelle (API): {apiBase}
              </p>
            </div>
            <div className="admin_kunden_filterbar">
              {[
                { key: "active",   label: "Aktiv",       color: "var(--cn-semantic-success, #008a05)" },
                { key: "disabled", label: "Deaktiviert", color: "var(--admin-text-secondary, #717171)" },
                { key: "test",     label: "Test",        color: "#8B00C8" },
              ].map((opt) => {
                const active = !!productUserStatusFilter[opt.key];
                return (
                  <button
                    key={opt.key}
                    type="button"
                    className={"admin_kunden_filter_pill" + (active ? " admin_kunden_filter_pill--active" : "")}
                    onClick={() => setProductUserStatusFilter((prev) => ({ ...prev, [opt.key]: !prev[opt.key] }))}
                    style={active ? { borderColor: opt.color, color: opt.color, background: "transparent" } : undefined}>
                    {opt.label}
                  </button>
                );
              })}
            </div>
            <div className="admin_product_user_list">
              {productUsers.map((u) => {
                const isTest = u.status === "test";
                const isDisabled = u.status === "disabled";
                const badge = isTest
                  ? { label: "TEST", color: "#8B00C8" }
                  : isDisabled
                    ? { label: "AUS", color: "var(--admin-text-secondary, #717171)" }
                    : null;
                const ob = productUserOnboardingLabel(u);
                return (
                  <button
                    key={u.id}
                    type="button"
                    className={
                      "admin_product_user_card" +
                      (selectedProductUserId === u.id
                        ? " admin_product_user_card--selected"
                        : "")
                    }
                    onClick={() => {
                      setSelectedProductUserId(u.id);
                      setImpersonateStatus("");
                    }}
                    style={isDisabled ? { opacity: 0.6 } : undefined}>
                    <div className="admin_product_user_avatar">
                      {(u.display_name || u.email || "?").charAt(0).toUpperCase()}
                    </div>
                    <div className="admin_product_user_info" style={{ minWidth: 0 }}>
                      <span className="admin_product_user_name" style={{ display: "flex", justifyContent: "space-between", gap: "6px", alignItems: "center" }}>
                        <span style={{ overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{u.display_name || "–"}</span>
                        {badge && (
                          <span style={{ flexShrink: 0, fontSize: "9px", fontWeight: 700, padding: "2px 6px", borderRadius: "10px", border: "1px solid " + badge.color, color: badge.color, letterSpacing: "0.5px" }}>
                            {badge.label}
                          </span>
                        )}
                      </span>
                      <span className="admin_product_user_email">
                        {u.email}
                        <span
                          title={ob.title}
                          style={{
                            marginLeft: "8px",
                            fontSize: "10px",
                            fontWeight: 700,
                            padding: "1px 6px",
                            borderRadius: "8px",
                            border: "1px solid " + ob.color,
                            color: ob.color,
                          }}>
                          {ob.short}
                        </span>
                      </span>
                    </div>
                  </button>
                );
              })}
              {productUsers.length === 0 && (
                <p className="admin_product_empty">Keine Nutzer fuer den aktuellen Filter.</p>
              )}
            </div>
          </div>
        ) : activeView === "tests" ? (
          <div className="admin_product_view">
            <div className="admin_product_header">
              <h2 className="admin_product_title">Tests</h2>
              <p className="admin_product_desc">
                E2E-Scenarios (simulierter Dialog, Assertions) und manuelle Chats
                mit der KI in einer Liste. Ohne Scenario startest du nur den
                manuellen Chat.
              </p>
            </div>
            <div
              style={{
                padding: "12px 16px",
                borderBottom: "1px solid var(--cn-border, #ebebeb)",
                display: "flex",
                flexDirection: "column",
                gap: "8px",
              }}>
              <div
                style={{ display: "flex", gap: "6px", alignItems: "center" }}>
                <select
                  value={testNewAiId}
                  onChange={(e) => setTestNewAiId(e.target.value)}
                  style={{
                    flex: 1,
                    padding: "6px 8px",
                    border: "1px solid var(--admin-border, #ddd)",
                    borderRadius: "6px",
                    fontSize: "12px",
                    background: "var(--admin-bg-input, #fff)",
                    color: "var(--admin-text, #222)",
                  }}>
                  <option value="">– KI auswaehlen –</option>
                  {aisList.map((ai) => (
                    <option key={ai.id} value={ai.id}>
                      #{ai.id} {ai.ai_name || "Assistenz"} (
                      {ai.industry_slug || "?"})
                      {ai.user_email ? " · " + ai.user_email : ""}
                    </option>
                  ))}
                </select>
              </div>
              <div
                style={{ display: "flex", gap: "6px", alignItems: "center" }}>
                <select
                  value={testNewScenarioKey}
                  onChange={(e) => setTestNewScenarioKey(e.target.value)}
                  style={{
                    flex: 1,
                    padding: "6px 8px",
                    border: "1px solid var(--admin-border, #ddd)",
                    borderRadius: "6px",
                    fontSize: "12px",
                    background: "var(--admin-bg-input, #fff)",
                    color: "var(--admin-text, #222)",
                  }}>
                  <option value="">Ohne Szenario (manueller Chat)</option>
                  {testScenarios.map((sc) => (
                    <option key={sc.key} value={sc.key}>
                      {sc.name} ({sc.industry_slug || "?"} · {sc.turns_count}{" "}
                      Turn{sc.turns_count === 1 ? "" : "s"})
                    </option>
                  ))}
                </select>
                <button
                  type="button"
                  onClick={() => {
                    if (testNewScenarioKey) {
                      startTestRun(testNewAiId, testNewScenarioKey);
                    } else {
                      startManualTest(testNewAiId);
                    }
                  }}
                  disabled={
                    !testNewAiId ||
                    (testNewScenarioKey
                      ? testStarting
                      : manualStarting)
                  }
                  style={{
                    padding: "6px 14px",
                    background:
                      !testNewAiId ||
                      (testNewScenarioKey
                        ? testStarting
                        : manualStarting)
                        ? "var(--admin-border, #ddd)"
                        : "var(--admin-accent, #FF5A5F)",
                    color: "#fff",
                    border: "none",
                    borderRadius: "6px",
                    cursor:
                      !testNewAiId ||
                      (testNewScenarioKey
                        ? testStarting
                        : manualStarting)
                        ? "not-allowed"
                        : "pointer",
                    fontWeight: 600,
                    fontSize: "12px",
                    whiteSpace: "nowrap",
                  }}>
                  {testNewScenarioKey
                    ? testStarting
                      ? "..."
                      : "Run starten"
                    : manualStarting
                      ? "..."
                      : "Neuer Chat"}
                </button>
              </div>
              <div
                style={{
                  fontSize: "10px",
                  color: "var(--admin-text-secondary, #717171)",
                }}>
                {adminTestsList.length} Eintrag{adminTestsList.length === 1 ? "" : "e"} ·{" "}
                {testScenarios.length} Scenario
                {testScenarios.length === 1 ? "" : "s"} verfuegbar
              </div>
            </div>
            <div className="admin_product_user_list">
              {adminTestsList.length === 0 && (
                <p className="admin_product_empty">
                  Noch keine Tests. Waehle KI, optional Scenario, starte E2E oder
                  manuellen Chat.
                </p>
              )}
              {adminTestsList.map((t) => {
                const kind = String(t.test_kind || "manual").toLowerCase();
                const isE2e = kind === "e2e";
                const dt = t.started_at
                  ? new Date(t.started_at).toLocaleString("de-DE", {
                      day: "2-digit",
                      month: "2-digit",
                      hour: "2-digit",
                      minute: "2-digit",
                    })
                  : "–";
                if (isE2e) {
                  const statusColor =
                    t.status === "passed"
                      ? "var(--cn-semantic-success, #008a05)"
                      : t.status === "failed"
                        ? "var(--cn-semantic-error, #c13515)"
                        : t.status === "error"
                          ? "var(--cn-semantic-error, #c13515)"
                          : "var(--admin-text-secondary, #717171)";
                  const dur = t.duration_ms
                    ? Math.round(t.duration_ms / 100) / 10 + "s"
                    : t.status === "running"
                      ? "läuft…"
                      : "";
                  return (
                    <button
                      key={"e2e-" + t.id}
                      type="button"
                      className={
                        "admin_product_user_card" +
                        (Number(selectedTestRunId) === Number(t.id)
                          ? " admin_product_user_card--selected"
                          : "")
                      }
                      onClick={() => {
                        setSelectedTestRunId(Number(t.id));
                        setSelectedManualTestId(null);
                      }}
                      style={{ width: "100%", textAlign: "left" }}>
                      <div
                        className="admin_product_user_avatar"
                        style={{
                          background: "var(--admin-bg-input, #fff)",
                          border: "1px solid var(--admin-border, #ddd)",
                          color: statusColor,
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "center",
                          fontSize: "10px",
                          fontWeight: 700,
                        }}>
                        E2E
                      </div>
                      <div
                        className="admin_product_user_info"
                        style={{ minWidth: 0 }}>
                        <span
                          className="admin_product_user_name"
                          style={{
                            display: "flex",
                            justifyContent: "space-between",
                            gap: "6px",
                          }}>
                          <span
                            style={{
                              overflow: "hidden",
                              textOverflow: "ellipsis",
                              whiteSpace: "nowrap",
                            }}>
                            {t.scenario_name || t.scenario_key}
                          </span>
                          <span
                            style={{
                              fontSize: "10px",
                              color: statusColor,
                              fontWeight: 700,
                              textTransform: "uppercase",
                              flexShrink: 0,
                            }}>
                            {t.status}
                          </span>
                        </span>
                        <span
                          className="admin_product_user_email"
                          style={{
                            display: "flex",
                            justifyContent: "space-between",
                            gap: "6px",
                          }}>
                          <span
                            style={{
                              overflow: "hidden",
                              textOverflow: "ellipsis",
                              whiteSpace: "nowrap",
                            }}>
                            #{t.ai_id} {t.ai_name || ""}
                          </span>
                          <span style={{ fontSize: "10px", flexShrink: 0 }}>
                            {dt}
                            {dur ? " · " + dur : ""}
                          </span>
                        </span>
                      </div>
                    </button>
                  );
                }
                const isOpen = t.status === "open";
                const statusColor = isOpen
                  ? "var(--cn-semantic-success, #008a05)"
                  : "var(--admin-text-secondary, #717171)";
                const durM = t.duration_ms
                  ? Math.round(t.duration_ms / 100) / 10 + "s"
                  : isOpen
                    ? "läuft…"
                    : "";
                return (
                  <button
                    key={"m-" + t.id}
                    type="button"
                    className={
                      "admin_product_user_card" +
                      (Number(selectedManualTestId) === Number(t.id)
                        ? " admin_product_user_card--selected"
                        : "")
                    }
                    onClick={() => {
                      setSelectedManualTestId(Number(t.id));
                      setSelectedTestRunId(null);
                    }}
                    style={{ width: "100%", textAlign: "left" }}>
                    <div
                      className="admin_product_user_avatar"
                      style={{
                        background: "var(--admin-bg-input, #fff)",
                        border: "1px solid var(--admin-border, #ddd)",
                        color: statusColor,
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        fontSize: "10px",
                        fontWeight: 700,
                      }}>
                      Chat
                    </div>
                    <div className="admin_product_user_info" style={{ minWidth: 0 }}>
                      <span
                        className="admin_product_user_name"
                        style={{ display: "flex", justifyContent: "space-between", gap: "6px" }}>
                        <span style={{ overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                          {t.summary || "(noch keine Nachricht)"}
                        </span>
                        <span
                          style={{
                            fontSize: "10px",
                            color: statusColor,
                            fontWeight: 700,
                            textTransform: "uppercase",
                            flexShrink: 0,
                          }}>
                          {isOpen ? "OFFEN" : t.ended_by || "ENDED"}
                        </span>
                      </span>
                      <span
                        className="admin_product_user_email"
                        style={{ display: "flex", justifyContent: "space-between", gap: "6px" }}>
                        <span
                          style={{
                            overflow: "hidden",
                            textOverflow: "ellipsis",
                            whiteSpace: "nowrap",
                          }}>
                          #{t.ai_id} {t.ai_name || ""}
                        </span>
                        <span style={{ fontSize: "10px", flexShrink: 0 }}>
                          {dt}
                          {durM ? " · " + durM : ""}
                        </span>
                      </span>
                    </div>
                  </button>
                );
              })}
            </div>
          </div>
        ) : activeView === "people_members" ? (
          <div className="admin_product_view">
            <div
              className="admin_product_header"
              style={{
                display: "flex",
                flexDirection: "row",
                alignItems: "center",
                justifyContent: "space-between",
                gap: "12px",
                width: "100%",
              }}>
              <div style={{ flex: 1, minWidth: 0 }}>
                <h2 className="admin_product_title">Member</h2>
                <p className="admin_product_desc">
                  Zugänge für den Mitarbeiterbereich und Bereichsrechte.
                </p>
              </div>
              {!isMobile ? (
                <button
                  type="button"
                  className="admin_btn admin_btn--primary"
                  onClick={openNewMemberPanel}>
                  Neuer Member
                </button>
              ) : null}
            </div>
            {membersError ? (
              <p className="admin_error" style={{ margin: "0 16px 12px" }}>
                {membersError}
              </p>
            ) : null}
            <div className="admin_product_user_list">
              {!membersLoading && !membersError && membersList.length === 0 ? (
                <p className="admin_product_empty">Noch keine Member vorhanden.</p>
              ) : null}
              {membersList.map((m) => {
                const isSelected = selectedMemberId === m.id;
                const inviterLine =
                  [m.inviter_display_name, m.inviter_email]
                    .filter(Boolean)
                    .join(" · ") ||
                  (m.invited_from != null ? `#${m.invited_from}` : "—");
                return (
                  <button
                    key={m.id}
                    type="button"
                    className={
                      "admin_product_user_card" +
                      (isSelected ? " admin_product_user_card--selected" : "")
                    }
                    onClick={() => openEditMemberPanel(m)}
                    style={{ width: "100%", textAlign: "left" }}>
                    <div className="admin_product_user_avatar">👤</div>
                    <div className="admin_product_user_info" style={{ minWidth: 0 }}>
                      <span className="admin_product_user_name">
                        {m.display_name || m.email}
                      </span>
                      <span className="admin_product_user_email">{m.email}</span>
                      <span
                        className="admin_product_user_email"
                        style={{
                          display: "block",
                          marginTop: "2px",
                          fontSize: "11px",
                        }}>
                        Eingeladen von: {inviterLine}
                      </span>
                    </div>
                  </button>
                );
              })}
            </div>
          </div>
        ) : activeView === "sales_home" ? (
          <div className="admin_product_view sales_home_view">
            {(() => {
              const todayKey = formatLocalDate(new Date());
              const currentAgenda = [...adminAgendas]
                .filter(
                  (a) =>
                    a.agenda_date != null &&
                    String(a.agenda_date).slice(0, 10) <= todayKey,
                )
                .sort((a, b) =>
                  String(b.agenda_date || "").localeCompare(
                    String(a.agenda_date || ""),
                  ),
                )[0];
              const dateLabel = currentAgenda
                ? (() => {
                    const ds = String(currentAgenda.agenda_date || "").slice(0, 10);
                    if (!ds) return "";
                    const [y, m, d] = ds.split("-").map(Number);
                    return new Date(y, (m || 1) - 1, d || 1).toLocaleDateString(
                      "de-DE",
                      {
                        weekday: "long",
                        day: "2-digit",
                        month: "2-digit",
                        year: "numeric",
                      },
                    );
                  })()
                : "";
              return (
                <button
                  type="button"
                  className="sales_home_banner"
                  onClick={() => {}}>
                  <div className="sales_home_banner_label">Aktuelle Agenda</div>
                  {currentAgenda ? (
                    <>
                      <div className="sales_home_banner_title">
                        {currentAgenda.sales_focus
                          ? currentAgenda.sales_focus
                          : "(kein Sales-Schwerpunkt eingetragen)"}
                      </div>
                      <div className="sales_home_banner_date">{dateLabel}</div>
                    </>
                  ) : (
                    <div className="sales_home_banner_empty">
                      {adminAgendasLoading
                        ? ""
                        : "Noch keine aktuelle Agenda vorhanden."}
                    </div>
                  )}
                </button>
              );
            })()}

            <div className="sales_home_section">
              <div className="sales_home_section_title">Schulung</div>
              <div className="sales_home_scroller">
                {salesTrainings.length === 0 ? (
                  <div className="sales_home_empty">
                    {salesTrainingsLoading
                      ? ""
                      : "Noch keine Schulung."}
                  </div>
                ) : (
                  salesTrainings.map((it) => {
                    const earningsEurNum = Number(it.earnings_eur);
                    const earningsFormatted =
                      Number.isFinite(earningsEurNum) && earningsEurNum > 0
                        ? new Intl.NumberFormat("de-DE", {
                            maximumFractionDigits: 0,
                          }).format(earningsEurNum) + "\u00a0€"
                        : null;
                    const src = it.file_key ? salesMediaUrl(it.file_key) : "";
                    return (
                      <button
                        key={it.id}
                        type="button"
                        className="sales_home_card sales_home_card--media"
                        onClick={() => {
                          if (isMobile) setSalesTrainingReturnView("sales_home");
                          switchView("sales_training");
                          setSelectedSalesTrainingId(it.id);
                        }}>
                        {src ? (
                          it.kind === "video" ? (
                            <video
                              className="sales_home_card_media"
                              src={src}
                              muted
                              playsInline
                              preload="metadata"
                            />
                          ) : (
                            <img
                              className="sales_home_card_media"
                              src={src}
                              alt=""
                              loading="lazy"
                            />
                          )
                        ) : (
                          <div className="sales_home_card_media sales_home_card_media--placeholder" />
                        )}
                        {earningsFormatted ? (
                          <div className="sales_home_card_price">{earningsFormatted}</div>
                        ) : null}
                        <span className="sales_home_card_label sales_home_card_label--overlay">
                          {it.title || "(ohne Titel)"}
                        </span>
                      </button>
                    );
                  })
                )}
              </div>
            </div>

            <div className="sales_home_section">
              <div className="sales_home_section_title">Akquise</div>
              <div className="sales_home_scroller">
                {acquisitionCustomers.length === 0 ? (
                  <div className="sales_home_empty">
                    {acquisitionCustomersLoading ? "" : "Noch keine Akquise-Einträge."}
                  </div>
                ) : (
                  acquisitionCustomers.slice(0, 8).map((c) => (
                    <button
                      key={c.id}
                      type="button"
                      className="sales_home_card"
                      onClick={() => {
                        if (isMobile) setSalesCustomerReturnView("sales_home");
                        switchView("sales_akquise");
                        openSalesCustomerEditPanel(c);
                      }}>
                      <span className="sales_home_card_label">
                        {c.company_name || "(ohne Firma)"}
                      </span>
                    </button>
                  ))
                )}
              </div>
            </div>

            <div className="sales_home_section">
              <div className="sales_home_section_title">Service</div>
              <div className="sales_home_scroller">
                {serviceCustomers.length === 0 ? (
                  <div className="sales_home_empty">
                    {serviceCustomersLoading ? "" : "Noch keine Service-Kunden."}
                  </div>
                ) : (
                  serviceCustomers.slice(0, 8).map((c) => (
                    <button
                      key={c.id}
                      type="button"
                      className="sales_home_card"
                      onClick={() => {
                        if (isMobile) setSalesCustomerReturnView("sales_home");
                        switchView("sales_service");
                        openSalesCustomerEditPanel(c);
                      }}>
                      <span className="sales_home_card_label">
                        {c.company_name || "(ohne Firma)"}
                      </span>
                    </button>
                  ))
                )}
              </div>
            </div>

            <div className="sales_home_section">
              <div className="sales_home_section_title">Team</div>
              <div className="sales_home_scroller">
                {[
                  { label: "Teammitglied hinzufügen", price: "20 €" },
                  { label: "Teammitglied hinzufügen", price: "20 €" },
                  { label: "Teammitglied hinzufügen", price: "20 €" },
                  { label: "Teammitglied hinzufügen", price: "20 €" },
                  { label: "Teammitglied hinzufügen", price: "20 €" },
                ].map((it, i) => (
                  <button
                    key={i}
                    type="button"
                    className="sales_home_card"
                    onClick={() => {
                      if (isMobile) setTeamInviteReturnView("sales_home");
                      switchView("sales_team");
                      openTeamInviteCreatePanel();
                    }}>
                    <div className="sales_home_card_price">{it.price}</div>
                    <span className="sales_home_card_label">{it.label}</span>
                  </button>
                ))}
              </div>
            </div>
          </div>
        ) : activeView === "sales_akquise" ? (
          <div className="admin_product_view">
            {isMobile && salesCustomerPanelOpen && selectedSalesCustomerId != null ? (
              <>
                <div
                  className="admin_product_header"
                  style={{ display: "flex", alignItems: "center", gap: "12px" }}>
                  <button
                    type="button"
                    className="admin_detail_back"
                    onClick={closeSalesCustomerPanel}
                    aria-label="Zurück">
                    &larr;
                  </button>
                  <h2 className="admin_product_title" style={{ margin: 0 }}>
                    {salesCustomerCompanyDraft || "Lead"}
                  </h2>
                </div>
                <div className="admin_detail_body" style={{ padding: "1rem" }}>
                  {renderSalesCustomerDetailBody()}
                </div>
              </>
            ) : (
              <>
                <div className="admin_sales_list_header_bar">
                  {acquisitionCustomers.length} Akquisegespräche
                </div>
                {acquisitionCustomersError ? (
                  <p className="admin_error" style={{ margin: "0 16px 12px" }}>
                    {acquisitionCustomersError}
                  </p>
                ) : null}
                <div className="admin_sales_lead_list">
                  {!acquisitionCustomersLoading &&
                    !acquisitionCustomersError &&
                    acquisitionCustomers.length === 0 && (
                      <p className="admin_product_empty">Noch keine Akquise-Einträge.</p>
                    )}
                  {acquisitionCustomers.map((c) => (
                    <button
                      key={c.id}
                      type="button"
                      className={
                        "admin_sales_lead_row" +
                        (selectedSalesCustomerId === c.id
                          ? " admin_sales_lead_row--selected"
                          : "")
                      }
                      onClick={() => openSalesCustomerEditPanel(c)}>
                      <PlanCloud plan={null} />
                      <div className="admin_sales_lead_text">
                        <strong>{c.company_name || "(ohne Firma)"}</strong>
                        <span>{c.contact_name || "–"}</span>
                      </div>
                      {c.industry ? (
                        <span className="admin_sales_lead_tag">{c.industry}</span>
                      ) : null}
                      <span className="admin_sales_lead_chevron" aria-hidden="true">
                        ›
                      </span>
                    </button>
                  ))}
                </div>
              </>
            )}
          </div>
        ) : activeView === "sales_service" ? (
          <div className="admin_product_view">
            {isMobile && salesCustomerPanelOpen && selectedSalesCustomerId != null ? (
              <>
                <div
                  className="admin_product_header"
                  style={{ display: "flex", alignItems: "center", gap: "12px" }}>
                  <button
                    type="button"
                    className="admin_detail_back"
                    onClick={closeSalesCustomerPanel}
                    aria-label="Zurück">
                    &larr;
                  </button>
                  <h2 className="admin_product_title" style={{ margin: 0 }}>
                    {salesCustomerCompanyDraft || "Service"}
                  </h2>
                </div>
                <div className="admin_detail_body" style={{ padding: "1rem" }}>
                  {renderSalesCustomerDetailBody()}
                </div>
              </>
            ) : (
              <>
                <div className="admin_sales_list_header_bar">
                  {serviceCustomers.filter((c) => Number(c.open_ticket_count) > 0).length}{" "}
                  Serviceanfragen
                </div>
                {serviceCustomersError ? (
                  <p className="admin_error" style={{ margin: "0 16px 12px" }}>
                    {serviceCustomersError}
                  </p>
                ) : null}
                <div className="admin_sales_lead_list">
                  {!serviceCustomersLoading &&
                    !serviceCustomersError &&
                    serviceCustomers.length === 0 && (
                      <p className="admin_product_empty">Noch keine Service-Kunden.</p>
                    )}
                  {serviceCustomers.map((c) => (
                    <button
                      key={c.id}
                      type="button"
                      className={
                        "admin_sales_lead_row" +
                        (selectedSalesCustomerId === c.id
                          ? " admin_sales_lead_row--selected"
                          : "")
                      }
                      onClick={() => openSalesCustomerEditPanel(c)}>
                      <PlanCloud plan={c.platform_plan} />
                      <div className="admin_sales_lead_text">
                        <strong>{c.company_name || "(ohne Firma)"}</strong>
                        <span>{c.contact_name || "–"}</span>
                      </div>
                      {c.open_ticket_at ? (
                        <span className="admin_sales_service_time">
                          {formatServiceTime(c.open_ticket_at)}
                        </span>
                      ) : null}
                      <span className="admin_sales_lead_chevron" aria-hidden="true">
                        ›
                      </span>
                    </button>
                  ))}
                </div>
              </>
            )}
          </div>
        ) : activeView === "marketing_community_content" ? (
          <div className="admin_product_view">
            <div
              className="admin_product_header"
              style={{
                display: "flex",
                flexDirection: "row",
                alignItems: "center",
                justifyContent: "space-between",
                gap: "12px",
                width: "100%",
              }}>
              <div style={{ flex: 1, minWidth: 0 }}>
                <h2 className="admin_product_title">Community Content</h2>
                <p className="admin_product_desc">
                  Bilder und Videos für die Community – Medien werden sicher gespeichert.
                </p>
              </div>
              {!isMobile ? (
                <button
                  type="button"
                  className="admin_btn admin_btn--primary"
                  onClick={openMarketingCommunityContentCreatePanel}>
                  Neuer Content
                </button>
              ) : null}
            </div>
            {salesContentsError ? (
              <p className="admin_error" style={{ margin: "0 16px 12px" }}>
                {salesContentsError}
              </p>
            ) : null}
            {!salesContentsLoading &&
            !salesContentsError &&
            salesContents.length === 0 ? (
              <p className="admin_product_empty" style={{ padding: "0 16px" }}>
                Noch kein Content. Klicke auf „Neuer Content“.
              </p>
            ) : null}
            <div
              className="admin_sales_media_grid admin_sales_media_grid--portrait"
              style={{ padding: "8px 16px 16px" }}>
              {salesContents.map((it) => {
                const isSelected = selectedMarketingCommunityContentId === it.id;
                const src = it.file_key ? salesMediaUrl(it.file_key) : "";
                const earningsEurNum = Number(it.earnings_eur);
                const earningsFormatted =
                  Number.isFinite(earningsEurNum) && earningsEurNum >= 0
                    ? new Intl.NumberFormat("de-DE", { maximumFractionDigits: 0 }).format(
                        earningsEurNum,
                      ) + "\u00a0€"
                    : null;
                return (
                  <button
                    key={it.id}
                    type="button"
                    className={
                      "admin_sales_media_card admin_sales_media_card--portrait" +
                      (isSelected ? " admin_sales_media_card--selected" : "")
                    }
                    onClick={() => openMarketingCommunityContentEditPanel(it)}>
                    <div className="admin_sales_media_thumb">
                      {earningsFormatted ? (
                        <div className="admin_sales_media_overlay_value">{earningsFormatted}</div>
                      ) : null}
                      {it.kind === "video" ? (
                        <video
                          className="admin_sales_media_el"
                          src={src || undefined}
                          muted
                          playsInline
                          preload="metadata"
                        />
                      ) : (
                        <img
                          className="admin_sales_media_el"
                          src={src || undefined}
                          alt=""
                          loading="lazy"
                        />
                      )}
                      <div className="admin_sales_media_overlay">
                        <span className="admin_sales_media_overlay_icon" aria-hidden="true">
                          {it.kind === "video" ? (
                            <svg
                              xmlns="http://www.w3.org/2000/svg"
                              width="18"
                              height="18"
                              viewBox="0 0 24 24"
                              fill="none"
                              stroke="currentColor"
                              strokeWidth="2"
                              strokeLinecap="round"
                              strokeLinejoin="round">
                              <rect x="2" y="4" width="20" height="16" rx="2" />
                              <polygon
                                points="10 9 16 12 10 15"
                                fill="currentColor"
                                stroke="none"
                              />
                            </svg>
                          ) : (
                            <svg
                              xmlns="http://www.w3.org/2000/svg"
                              width="18"
                              height="18"
                              viewBox="0 0 24 24"
                              fill="none"
                              stroke="currentColor"
                              strokeWidth="2"
                              strokeLinecap="round"
                              strokeLinejoin="round">
                              <rect x="3" y="3" width="18" height="18" rx="2" ry="2" />
                              <circle cx="8.5" cy="8.5" r="1.5" fill="currentColor" stroke="none" />
                              <path d="M3 19l4.5-5.5 3 3.5L14 12l7 7" />
                            </svg>
                          )}
                        </span>
                        <span className="admin_sales_media_overlay_title">
                          {it.title || "(ohne Titel)"}
                        </span>
                      </div>
                    </div>
                  </button>
                );
              })}
            </div>
          </div>
        ) : activeView === "marketing_channel_content" ? (
          <div className="admin_product_view">
            <div
              className="admin_product_header"
              style={{
                display: "flex",
                flexDirection: "row",
                alignItems: "center",
                justifyContent: "space-between",
                gap: "12px",
                width: "100%",
              }}>
              <div style={{ flex: 1, minWidth: 0 }}>
                <h2 className="admin_product_title">Channel Content</h2>
                <p className="admin_product_desc">
                  Bilder und Videos pro Kanal – optional einem Kanal zugeordnet.
                </p>
              </div>
              {!isMobile ? (
                <button
                  type="button"
                  className="admin_btn admin_btn--primary"
                  onClick={openMarketingChannelContentCreatePanel}>
                  Neuer Channel-Content
                </button>
              ) : null}
            </div>
            {channelContentsError ? (
              <p className="admin_error" style={{ margin: "0 16px 12px" }}>
                {channelContentsError}
              </p>
            ) : null}
            {!channelContentsLoading &&
            !channelContentsError &&
            channelContents.length === 0 ? (
              <p className="admin_product_empty" style={{ padding: "0 16px" }}>
                Noch kein Channel-Content. Klicke auf „Neuer Channel-Content“.
              </p>
            ) : null}
            <div
              className="admin_sales_media_grid admin_sales_media_grid--portrait"
              style={{ padding: "8px 16px 16px" }}>
              {channelContents.map((it) => {
                const isSelected = selectedMarketingChannelContentId === it.id;
                const src = it.file_key ? salesMediaUrl(it.file_key) : "";
                const earningsEurNum = Number(it.earnings_eur);
                const earningsFormatted =
                  Number.isFinite(earningsEurNum) && earningsEurNum >= 0
                    ? new Intl.NumberFormat("de-DE", { maximumFractionDigits: 0 }).format(
                        earningsEurNum,
                      ) + "\u00a0€"
                    : null;
                return (
                  <button
                    key={it.id}
                    type="button"
                    className={
                      "admin_sales_media_card admin_sales_media_card--portrait" +
                      (isSelected ? " admin_sales_media_card--selected" : "")
                    }
                    onClick={() => openMarketingChannelContentEditPanel(it)}>
                    <div className="admin_sales_media_thumb">
                      {earningsFormatted ? (
                        <div className="admin_sales_media_overlay_value">{earningsFormatted}</div>
                      ) : null}
                      {it.kind === "video" ? (
                        <video
                          className="admin_sales_media_el"
                          src={src || undefined}
                          muted
                          playsInline
                          preload="metadata"
                        />
                      ) : (
                        <img
                          className="admin_sales_media_el"
                          src={src || undefined}
                          alt=""
                          loading="lazy"
                        />
                      )}
                      <div className="admin_sales_media_overlay">
                        <span className="admin_sales_media_overlay_icon" aria-hidden="true">
                          {it.kind === "video" ? (
                            <svg
                              xmlns="http://www.w3.org/2000/svg"
                              width="18"
                              height="18"
                              viewBox="0 0 24 24"
                              fill="none"
                              stroke="currentColor"
                              strokeWidth="2"
                              strokeLinecap="round"
                              strokeLinejoin="round">
                              <rect x="2" y="4" width="20" height="16" rx="2" />
                              <polygon
                                points="10 9 16 12 10 15"
                                fill="currentColor"
                                stroke="none"
                              />
                            </svg>
                          ) : (
                            <svg
                              xmlns="http://www.w3.org/2000/svg"
                              width="18"
                              height="18"
                              viewBox="0 0 24 24"
                              fill="none"
                              stroke="currentColor"
                              strokeWidth="2"
                              strokeLinecap="round"
                              strokeLinejoin="round">
                              <rect x="3" y="3" width="18" height="18" rx="2" ry="2" />
                              <circle cx="8.5" cy="8.5" r="1.5" fill="currentColor" stroke="none" />
                              <path d="M3 19l4.5-5.5 3 3.5L14 12l7 7" />
                            </svg>
                          )}
                        </span>
                        <span className="admin_sales_media_overlay_title">
                          {it.title || "(ohne Titel)"}
                        </span>
                      </div>
                    </div>
                  </button>
                );
              })}
            </div>
          </div>
        ) : activeView === "marketing_targetgroups" ? (
          <div className="admin_product_view">
            <div
              className="admin_product_header"
              style={{
                display: "flex",
                flexDirection: "row",
                alignItems: "center",
                justifyContent: "space-between",
                gap: "12px",
                width: "100%",
              }}>
              <div style={{ flex: 1, minWidth: 0 }}>
                <h2 className="admin_product_title">Zielgruppen</h2>
                <p className="admin_product_desc">
                  Marketing-Zielgruppen definieren – Auswahl rechts bearbeiten.
                </p>
              </div>
              {!isMobile ? (
                <button
                  type="button"
                  className="admin_btn admin_btn--primary"
                  onClick={openMarketingTargetgroupCreatePanel}>
                  Neue Zielgruppe
                </button>
              ) : null}
            </div>
            {marketingTargetgroupsError ? (
              <p className="admin_error" style={{ margin: "0 16px 12px" }}>
                {marketingTargetgroupsError}
              </p>
            ) : null}
            <div className="admin_product_user_list">
              {!marketingTargetgroupsLoading &&
                !marketingTargetgroupsError &&
                marketingTargetgroups.length === 0 && (
                  <p className="admin_product_empty">
                    Noch keine Zielgruppe. Klicke auf „Neue Zielgruppe“.
                  </p>
                )}
              {marketingTargetgroups.map((tg) => {
                const isSelected = selectedMarketingTargetgroupId === tg.id;
                const preview = String(tg.description || "").trim();
                return (
                  <button
                    key={tg.id}
                    type="button"
                    className={
                      "admin_product_user_card" +
                      (isSelected ? " admin_product_user_card--selected" : "")
                    }
                    onClick={() => openMarketingTargetgroupEditPanel(tg)}
                    style={{ width: "100%", textAlign: "left" }}>
                    <div
                      className="admin_product_user_avatar"
                      style={{
                        background: "var(--admin-bg-input, #fff)",
                        border: "1px solid var(--admin-border, #ddd)",
                        color: "var(--cn-accent-promo, #fc642d)",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        fontSize: "14px",
                        fontWeight: 700,
                      }}>
                      {(String(tg.name || "?").trim().slice(0, 1) || "?").toUpperCase()}
                    </div>
                    <div className="admin_product_user_info" style={{ minWidth: 0 }}>
                      <span
                        className="admin_product_user_name"
                        style={{
                          display: "block",
                          overflow: "hidden",
                          textOverflow: "ellipsis",
                          whiteSpace: "nowrap",
                        }}>
                        {tg.name || "(ohne Name)"}
                      </span>
                      <span
                        className="admin_product_user_email"
                        style={{
                          display: "block",
                          overflow: "hidden",
                          textOverflow: "ellipsis",
                          whiteSpace: "nowrap",
                        }}>
                        {preview || "–"}
                      </span>
                    </div>
                  </button>
                );
              })}
            </div>
          </div>
        ) : activeView === "marketing_channels" ? (
          <div className="admin_product_view">
            <div
              className="admin_product_header"
              style={{
                display: "flex",
                flexDirection: "row",
                alignItems: "center",
                justifyContent: "space-between",
                gap: "12px",
                width: "100%",
              }}>
              <div style={{ flex: 1, minWidth: 0 }}>
                <h2 className="admin_product_title">Kanäle</h2>
                <p className="admin_product_desc">
                  Marketing-Kanäle pflegen – Auswahl rechts bearbeiten.
                </p>
              </div>
              {!isMobile ? (
                <button
                  type="button"
                  className="admin_btn admin_btn--primary"
                  onClick={openMarketingChannelCreatePanel}>
                  Neuer Kanal
                </button>
              ) : null}
            </div>
            {marketingChannelsError ? (
              <p className="admin_error" style={{ margin: "0 16px 12px" }}>
                {marketingChannelsError}
              </p>
            ) : null}
            <div className="admin_product_user_list">
              {!marketingChannelsLoading &&
                !marketingChannelsError &&
                marketingChannels.length === 0 && (
                  <p className="admin_product_empty">
                    Noch kein Kanal. Klicke auf „Neuer Kanal“.
                  </p>
                )}
              {marketingChannels.map((ch) => {
                const isSelected = selectedMarketingChannelId === ch.id;
                const preview = String(ch.description || "").trim();
                return (
                  <button
                    key={ch.id}
                    type="button"
                    className={
                      "admin_product_user_card" +
                      (isSelected ? " admin_product_user_card--selected" : "")
                    }
                    onClick={() => openMarketingChannelEditPanel(ch)}
                    style={{ width: "100%", textAlign: "left" }}>
                    <div
                      className="admin_product_user_avatar"
                      style={{
                        background: "var(--admin-bg-input, #fff)",
                        border: "1px solid var(--admin-border, #ddd)",
                        color: "var(--cn-accent-secondary, #00a699)",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        fontSize: "14px",
                        fontWeight: 700,
                      }}>
                      {(String(ch.name || "?").trim().slice(0, 1) || "?").toUpperCase()}
                    </div>
                    <div className="admin_product_user_info" style={{ minWidth: 0 }}>
                      <span
                        className="admin_product_user_name"
                        style={{
                          display: "block",
                          overflow: "hidden",
                          textOverflow: "ellipsis",
                          whiteSpace: "nowrap",
                        }}>
                        {ch.name || "(ohne Name)"}
                      </span>
                      <span
                        className="admin_product_user_email"
                        style={{
                          display: "block",
                          overflow: "hidden",
                          textOverflow: "ellipsis",
                          whiteSpace: "nowrap",
                        }}>
                        {preview || "–"}
                      </span>
                    </div>
                  </button>
                );
              })}
            </div>
          </div>
        ) : activeView === "sales_team" ? (() => {
          const teamSearchKey = teamSearchDraft.trim().toLowerCase();
          const matchesTeamSearch = (m) => {
            if (!teamSearchKey) return true;
            const hay = `${m.display_name || ""} ${m.email || ""}`.toLowerCase();
            return hay.includes(teamSearchKey);
          };
          const filteredTeamRoster = teamRoster.filter(matchesTeamSearch);
          const pendingInvitations = teamInvitations.filter(
            (i) => String(i.status || "").toLowerCase() === "pending",
          );
          const filteredPendingInvitations = pendingInvitations.filter((inv) => {
            if (!teamSearchKey) return true;
            return String(inv.email || "").toLowerCase().includes(teamSearchKey);
          });
          const totalAfterFilter =
            filteredTeamRoster.length + filteredPendingInvitations.length;
          const hasAnyEntries =
            teamRoster.length + pendingInvitations.length > 0;
          return (
          <div className="admin_product_view">
            <div
              className="admin_product_header"
              style={{
                display: "flex",
                flexDirection: "row",
                alignItems: "center",
                justifyContent: "space-between",
                gap: "12px",
                width: "100%",
              }}>
              <div style={{ flex: 1, minWidth: 0 }}>
                <h2 className="admin_product_title">Team</h2>
                <p className="admin_product_desc">
                  Alle Mitarbeiter:innen mit Kunden-, Posts-Anzahl und Karrierestufe. Lade neue
                  Personen per E-Mail ein.
                </p>
              </div>
              {!isMobile ? (
                <button
                  type="button"
                  className="admin_btn admin_btn--primary"
                  onClick={openTeamInviteCreatePanel}>
                  Person einladen
                </button>
              ) : null}
            </div>
            <div
              style={{
                padding: "0 16px 8px",
                display: "flex",
                width: "100%",
                boxSizing: "border-box",
              }}>
              <input
                type="search"
                className="admin_input"
                placeholder="Team durchsuchen (Name oder E-Mail)…"
                value={teamSearchDraft}
                onChange={(e) => setTeamSearchDraft(e.target.value)}
                aria-label="Team durchsuchen"
                style={{ flex: 1, minWidth: 0 }}
              />
            </div>
            {teamInvError ? (
              <p className="admin_error" style={{ margin: "0 16px 12px" }}>{teamInvError}</p>
            ) : null}
            <div className="admin_product_user_list">
              {!teamInvLoading &&
                !teamInvError &&
                !hasAnyEntries && (
                <p className="admin_product_empty">
                  Noch kein Team. Klicke auf „Person einladen“.
                </p>
                )}
              {!teamInvLoading &&
                !teamInvError &&
                hasAnyEntries &&
                totalAfterFilter === 0 && (
                <p className="admin_product_empty">
                  Keine Treffer für „{teamSearchDraft.trim()}".
                </p>
                )}
              {filteredTeamRoster.map((m) => {
                const picSrc = memberProfilePicSrc(m, apiBase, adminToken, profilePicVersion);
                const isMe = adminUser && Number(adminUser.id) === Number(m.id);
                const isSelected = selectedTeamMemberId === m.id;
                const lastContent =
                  typeof m.last_message_content === "string"
                    ? m.last_message_content
                    : "";
                const lastAt = m.last_message_at || null;
                const meIsLastSender =
                  adminUser &&
                  m.last_message_sender_id != null &&
                  Number(m.last_message_sender_id) === Number(adminUser.id);
                const unread = Number(m.unread_count || 0);
                const previewTime = lastAt ? formatChatPreviewTime(lastAt) : "";
                return (
                  <button
                    type="button"
                    key={`m-${m.id}`}
                    className={
                      "admin_product_user_card admin_team_member_card admin_team_chat_card" +
                      (isSelected ? " admin_product_user_card--selected" : "") +
                      (unread > 0 ? " admin_team_chat_card--unread" : "")
                    }
                    onClick={() => openTeamMemberChat(m.id)}
                    style={{
                      display: "flex",
                      flexDirection: "row",
                      alignItems: "center",
                      cursor: "pointer",
                      textAlign: "left",
                      width: "100%",
                    }}>
                    {picSrc ? (
                      <img
                        src={picSrc}
                        alt=""
                        className="admin_team_member_avatar admin_team_member_avatar--img"
                      />
                    ) : (
                      <div
                        className="admin_team_member_avatar admin_team_member_avatar--initials"
                        aria-hidden="true">
                        {memberInitials(m)}
                      </div>
                    )}
                    <div
                      className="admin_team_chat_info"
                      style={{ flex: 1, minWidth: 0 }}>
                      <div className="admin_team_chat_top_row">
                        <span className="admin_team_chat_name">
                          {m.display_name || m.email}
                          {isMe ? (
                            <span className="admin_team_member_self_badge"> Du</span>
                          ) : null}
                        </span>
                        {previewTime ? (
                          <span className="admin_team_chat_time">{previewTime}</span>
                        ) : null}
                      </div>
                      <div className="admin_team_chat_preview_row">
                        <span
                          className={
                            "admin_team_chat_preview" +
                            (lastContent ? "" : " admin_team_chat_preview--empty")
                          }>
                          {lastContent
                            ? (meIsLastSender ? "Du: " : "") + lastContent
                            : "Noch keine Nachrichten"}
                        </span>
                        {unread > 0 ? (
                          <span
                            className="admin_team_chat_badge"
                            aria-label={`${unread} ungelesen`}>
                            {unread > 99 ? "99+" : unread}
                          </span>
                        ) : null}
                      </div>
                    </div>
                  </button>
                );
              })}
              {filteredPendingInvitations
                .map((inv) => {
                  const exp = inv.expires_at
                    ? new Date(String(inv.expires_at).replace(" ", "T")).toLocaleString("de-DE", {
                        day: "2-digit",
                        month: "2-digit",
                        year: "numeric",
                        hour: "2-digit",
                        minute: "2-digit",
                      })
                    : "—";
                  return (
                    <div
                      key={`inv-${inv.id}`}
                      className="admin_product_user_card admin_team_invite_card"
                      style={{
                        display: "flex",
                        flexDirection: "row",
                        alignItems: "center",
                        cursor: "default",
                        textAlign: "left",
                      }}>
                      <div
                        className="admin_team_member_avatar admin_team_member_avatar--invite"
                        aria-hidden="true">
                        ✉
                      </div>
                      <div className="admin_product_user_info" style={{ flex: 1, minWidth: 0 }}>
                        <span
                          className="admin_product_user_name"
                          style={{
                            overflow: "hidden",
                            textOverflow: "ellipsis",
                            whiteSpace: "nowrap",
                          }}>
                          {inv.email}
                          <span className="admin_team_invite_badge"> Eingeladen</span>
                        </span>
                        <span className="admin_product_user_email" style={{ fontSize: "11px" }}>
                          Eingeladen von:{" "}
                          {[inv.inviter_display_name, inv.inviter_email]
                            .filter(Boolean)
                            .join(" · ") || "—"}
                          <span> · Abläuft: {exp}</span>
                        </span>
                      </div>
                      <button
                        type="button"
                        className="admin_btn"
                        style={{ marginLeft: "8px", flexShrink: 0 }}
                        disabled={teamInviteRevokeId === inv.id}
                        onClick={() => {
                          if (typeof window !== "undefined" && !window.confirm("Einladung wirklich widerrufen?")) {
                            return;
                          }
                          doRevokeTeamInvite(inv.id);
                        }}>
                        {teamInviteRevokeId === inv.id ? "…" : "Widerrufen"}
                      </button>
                    </div>
                  );
                })}
            </div>
          </div>
          );
        })() : activeView === "sales_training" || activeView === "people_schulungen" ? (
          <div className="admin_product_view">
            <div
              className="admin_product_header"
              style={{
                display: "flex",
                flexDirection: "row",
                alignItems: "center",
                justifyContent: "space-between",
                gap: "12px",
                width: "100%",
              }}>
              <div style={{ flex: 1, minWidth: 0 }}>
                <h2 className="admin_product_title">
                  {activeView === "people_schulungen" ? "Schulungen" : "Schulung"}
                </h2>
                <p className="admin_product_desc">
                  {activeView === "people_schulungen"
                    ? "Schulungs-Medien für das Team (Bild/Video), gleiche Datenbasis wie unter Sales › Schulung."
                    : "Schulungs-Material als Bild/Video – getrennt von Akquise-Content."}
                </p>
              </div>
              {!isMobile && activeView === "people_schulungen" ? (
                <button
                  type="button"
                  className="admin_btn admin_btn--primary"
                  onClick={openSalesTrainingCreatePanel}>
                  Neue Schulung
                </button>
              ) : null}
            </div>
            {salesTrainingsError ? (
              <p className="admin_error" style={{ margin: "0 16px 12px" }}>
                {salesTrainingsError}
              </p>
            ) : null}
            {!salesTrainingsLoading &&
            !salesTrainingsError &&
            salesTrainings.length === 0 ? (
              <p className="admin_product_empty" style={{ padding: "0 16px" }}>
                {activeView === "people_schulungen"
                  ? "Noch keine Schulung. Klicke auf „Neue Schulung“."
                  : "Noch kein Schulungs-Content."}
              </p>
            ) : null}
            <div
              className={
                "admin_sales_media_grid" +
                (activeView === "sales_training" ? " admin_sales_media_grid--row" : "")
              }
              style={{ padding: "8px 16px 16px" }}>
              {salesTrainings.map((it) => {
                const isSelected = selectedSalesTrainingId === it.id;
                const src = it.file_key ? salesMediaUrl(it.file_key) : "";
                const rowLayout = activeView === "sales_training";
                return (
                  <button
                    key={it.id}
                    type="button"
                    className={
                      "admin_sales_media_card" +
                      (rowLayout ? " admin_sales_media_card--row" : "") +
                      (isSelected ? " admin_sales_media_card--selected" : "")
                    }
                    onClick={() => openSalesTrainingEditPanel(it)}>
                    <div className="admin_sales_media_thumb">
                      {it.kind === "video" ? (
                        <video
                          className="admin_sales_media_el"
                          src={src || undefined}
                          muted
                          playsInline
                          preload="metadata"
                        />
                      ) : (
                        <img
                          className="admin_sales_media_el"
                          src={src || undefined}
                          alt=""
                          loading="lazy"
                        />
                      )}
                      {rowLayout ? null : (
                        <span className="admin_sales_media_badge">
                          {it.kind === "video" ? "Video" : "Bild"}
                        </span>
                      )}
                    </div>
                    <div className="admin_sales_media_meta">
                      <div className="admin_sales_media_title">
                        {it.title || "(ohne Titel)"}
                      </div>
                      <div className="admin_sales_media_sub">
                        {rowLayout
                          ? <span>{it.kind === "video" ? "Video" : "Bild"}</span>
                          : (it.mime_type ? <span>{it.mime_type}</span> : null)}
                      </div>
                    </div>
                    {(() => {
                      const earningsEurNum = Number(it.earnings_eur);
                      const earningsFormatted =
                        Number.isFinite(earningsEurNum) && earningsEurNum > 0
                          ? new Intl.NumberFormat("de-DE", {
                              maximumFractionDigits: 0,
                            }).format(earningsEurNum) + "\u00a0€"
                          : null;
                      if (!earningsFormatted) return null;
                      return (
                        <span className="admin_sales_media_row_amount">
                          {earningsFormatted}
                        </span>
                      );
                    })()}
                    {rowLayout ? (
                      <span className="admin_sales_media_play" aria-hidden="true">
                        <svg
                          width="14"
                          height="14"
                          viewBox="0 0 24 24"
                          fill="currentColor"
                          xmlns="http://www.w3.org/2000/svg">
                          <path d="M8 5v14l11-7z" />
                        </svg>
                      </span>
                    ) : null}
                  </button>
                );
              })}
            </div>
          </div>
        ) : activeView === "sales_karriere" ? (
          <div className="admin_product_view admin_career_view">
            <div className="admin_career_stats_grid">
              <div className="admin_career_stat_card">
                <span className="admin_career_stat_label">Stufe</span>
                <strong className="admin_career_stat_value">I</strong>
              </div>
              <div className="admin_career_stat_card">
                <span className="admin_career_stat_label">Einnahmen</span>
                <strong className="admin_career_stat_value">{revenueTotalDisplay}</strong>
              </div>
            </div>
            {salesEarningsError ? (
              <p className="admin_error" style={{ margin: "0 16px 12px" }}>
                {salesEarningsError}
              </p>
            ) : null}
            <div className="admin_career_events_list">
              {MOCK_CAREER_EVENTS.map((ev) => (
                <div key={ev.id} className="admin_career_event_card">
                  <div className="admin_career_event_main">
                    <strong>{ev.title}</strong>
                    <span>{ev.subtitle}</span>
                  </div>
                  <div className="admin_career_event_side">
                    <strong className="admin_career_event_amount">{ev.amount}</strong>
                    <span>{ev.tag}</span>
                  </div>
                </div>
              ))}
            </div>
          </div>
        ) : activeView === "product_userstories" ? (
          <div className="admin_product_view">
            <div
              className="admin_product_header"
              style={{
                display: "flex",
                flexDirection: "row",
                alignItems: "center",
                justifyContent: "space-between",
                gap: "12px",
                width: "100%",
              }}>
              <div style={{ flex: 1, minWidth: 0 }}>
                <h2 className="admin_product_title">User Stories</h2>
                <p className="admin_product_desc">
                  Produkt- und UX-Stories sammeln und bewerten.
                </p>
              </div>
              {!isMobile ? (
                <button
                  type="button"
                  className="admin_btn admin_btn--primary"
                  onClick={openUserStoryCreatePanel}>
                  Neue User Story
                </button>
              ) : null}
            </div>
            {userStoriesError ? (
              <p className="admin_error" style={{ margin: "0 16px 12px" }}>
                {userStoriesError}
              </p>
            ) : null}
            <div className="admin_product_user_list">
              {!userStoriesLoading &&
                !userStoriesError &&
                userStories.length === 0 && (
                  <p className="admin_product_empty">
                    Noch keine User Stories. Klicke auf „Neue User Story“.
                  </p>
                )}
              {userStories.map((s) => {
                const isSelected = selectedUserStoryId === s.id;
                const updated = s.updated_at
                  ? new Date(s.updated_at).toLocaleString("de-DE", {
                      day: "2-digit",
                      month: "2-digit",
                      hour: "2-digit",
                      minute: "2-digit",
                    })
                  : "";
                return (
                  <button
                    key={s.id}
                    type="button"
                    className={
                      "admin_product_user_card" +
                      (isSelected ? " admin_product_user_card--selected" : "")
                    }
                    onClick={() => openUserStoryEditPanel(s)}
                    style={{ width: "100%", textAlign: "left" }}>
                    <div
                      className="admin_product_user_avatar"
                      style={{
                        background: "var(--admin-bg-input, #fff)",
                        border: "1px solid var(--admin-border, #ddd)",
                        color: "var(--cn-accent-promo, #fc642d)",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        fontSize: "14px",
                        fontWeight: 700,
                      }}>
                      US
                    </div>
                    <div
                      className="admin_product_user_info"
                      style={{ minWidth: 0 }}>
                      <span
                        className="admin_product_user_name"
                        style={{
                          display: "flex",
                          justifyContent: "space-between",
                          gap: "6px",
                        }}>
                        <span
                          style={{
                            overflow: "hidden",
                            textOverflow: "ellipsis",
                            whiteSpace: "nowrap",
                          }}>
                          {s.title || "(ohne Titel)"}
                        </span>
                        <span
                          style={{
                            fontSize: "10px",
                            color: "var(--admin-text-secondary, #717171)",
                            flexShrink: 0,
                          }}>
                          #{s.id}
                        </span>
                      </span>
                      <span
                        className="admin_product_user_email"
                        style={{
                          display: "flex",
                          justifyContent: "space-between",
                          gap: "6px",
                        }}>
                        <span
                          style={{
                            overflow: "hidden",
                            textOverflow: "ellipsis",
                            whiteSpace: "nowrap",
                          }}>
                          {(s.description || "").length > 120
                            ? (s.description || "").slice(0, 120) + "…"
                            : s.description || "—"}
                        </span>
                        {updated ? (
                          <span
                            style={{
                              fontSize: "10px",
                              color: "var(--admin-text-secondary, #717171)",
                              flexShrink: 0,
                            }}>
                            {updated}
                          </span>
                        ) : null}
                      </span>
                    </div>
                  </button>
                );
              })}
            </div>
          </div>
        ) : activeView === "admin_agendas" ? (
          <div className="admin_product_view">
            <div
              className="admin_product_header"
              style={{
                display: "flex",
                flexDirection: "row",
                alignItems: "center",
                justifyContent: "space-between",
                gap: "12px",
                width: "100%",
              }}>
              <div style={{ flex: 1, minWidth: 0 }}>
                <h2 className="admin_product_title">Markt</h2>
                <p className="admin_product_desc">
                  Markt-Termine und Themen – gruppiert nach Monat.
                </p>
              </div>
              {!isMobile ? (
                <button
                  type="button"
                  className="admin_btn admin_btn--primary"
                  onClick={openAgendaCreatePanel}>
                  Neue Agenda
                </button>
              ) : null}
            </div>
            {adminAgendasError ? (
              <p className="admin_error" style={{ margin: "0 16px 12px" }}>
                {adminAgendasError}
              </p>
            ) : null}
            <div className="admin_product_user_list">
              {!adminAgendasLoading &&
                !adminAgendasError &&
                adminAgendas.length === 0 && (
                  <p className="admin_product_empty">
                    Noch keine Markt-Einträge. Klicke auf „Neue Agenda“.
                  </p>
                )}
              {(() => {
                const agendaRows = [...adminAgendas]
                  .filter((ag) =>
                    ag.agenda_date != null && String(ag.agenda_date).slice(0, 10),
                  )
                  .sort((a, b) => {
                    const ad = String(a.agenda_date || "");
                    const bd = String(b.agenda_date || "");
                    return ad.localeCompare(bd);
                  });
                let lastMonthLabel = "";
                return agendaRows.map((ag) => {
                  const dateStr =
                    ag.agenda_date != null
                      ? String(ag.agenda_date).slice(0, 10)
                      : "";
                  const [y, m, d] = dateStr.split("-").map(Number);
                  const agendaDate = new Date(y, (m || 1) - 1, d || 1);
                  const monthLabel = agendaDate.toLocaleDateString("de-DE", {
                    month: "long",
                    year: "numeric",
                  });
                  const showMonthTitle = monthLabel !== lastMonthLabel;
                  lastMonthLabel = monthLabel;
                  const dateLabel = dateStr
                    ? agendaDate.toLocaleDateString("de-DE", {
                        weekday: "short",
                        day: "2-digit",
                        month: "2-digit",
                        year: "numeric",
                      })
                    : "–";
                  const isSelected = selectedAdminAgendaId === ag.id;
                  return (
                    <React.Fragment key={ag.id}>
                      {showMonthTitle ? (
                        <div
                          style={{
                            padding: "10px 16px 6px",
                            fontSize: "12px",
                            fontWeight: 700,
                            textTransform: "uppercase",
                            letterSpacing: "0.4px",
                            color: "var(--admin-text-secondary, #717171)",
                          }}>
                          {monthLabel}
                        </div>
                      ) : null}
                      <button
                        type="button"
                        className={
                          "admin_product_user_card admin_agenda_list_card" +
                          (isSelected ? " admin_product_user_card--selected" : "")
                        }
                        onClick={() => openAgendaEditPanel(ag)}
                        style={{ width: "100%", textAlign: "left" }}>
                        <div
                          className="admin_product_user_avatar"
                          style={{
                            background: "var(--admin-bg-input, #fff)",
                            border: "1px solid var(--admin-border, #ddd)",
                            color: "var(--cn-accent-secondary, #00a699)",
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                            fontSize: "14px",
                            fontWeight: 700,
                          }}>
                          {dateStr ? dateStr.slice(8, 10) : "?"}
                        </div>
                        <div
                          className="admin_product_user_info"
                          style={{ minWidth: 0 }}>
                          <span
                            className="admin_product_user_name"
                            style={{
                              display: "block",
                              overflow: "hidden",
                              textOverflow: "ellipsis",
                              whiteSpace: "nowrap",
                            }}>
                            {ag.name || "(ohne Name)"}
                          </span>
                          <span className="admin_product_user_email">{dateLabel}</span>
                          {ag.notes ? (
                            <span
                              style={{
                                display: "block",
                                marginTop: "2px",
                                fontSize: "11px",
                                color: "var(--admin-text-secondary, #717171)",
                                overflow: "hidden",
                                textOverflow: "ellipsis",
                                whiteSpace: "nowrap",
                              }}>
                              {ag.notes}
                            </span>
                          ) : null}
                        </div>
                      </button>
                    </React.Fragment>
                  );
                });
              })()}
            </div>
          </div>
        ) : activeView === "marketing_posts" ? (
          <div className="admin_product_view">
            <div
              className="admin_product_header"
              style={{
                display: "flex",
                flexDirection: "row",
                alignItems: "center",
                justifyContent: "space-between",
                gap: "12px",
                width: "100%",
              }}>
              <div style={{ flex: 1, minWidth: 0 }}>
                <h2 className="admin_product_title">Posts</h2>
                <p className="admin_product_desc">
                  Sharing-Anfragen aus der Akquise – rechts bearbeiten und als bestätigt markieren.
                </p>
              </div>
            </div>
            {membersPostsError ? (
              <p className="admin_error" style={{ margin: "0 16px 12px" }}>
                {membersPostsError}
              </p>
            ) : null}
            <div className="admin_product_user_list">
              {!membersPostsLoading &&
                !membersPostsError &&
                membersPosts.length === 0 && (
                  <p className="admin_product_empty">
                    Noch keine Anfragen. Sales bestätigt Kontakte in Akquise unter Sales › Akquise.
                  </p>
                )}
              {membersPosts.map((p) => {
                const isSelected = selectedMembersPostId === p.id;
                const st = String(p.status || "").toLowerCase();
                const isConfirmed = st === "confirmed";
                const titleShort = String(p.content_title || "").trim() || "(ohne Titel)";
                return (
                  <button
                    key={p.id}
                    type="button"
                    className={
                      "admin_product_user_card" +
                      (isSelected ? " admin_product_user_card--selected" : "")
                    }
                    onClick={() => {
                      setSelectedMembersPostId(p.id);
                      setMembersPostConfirmError("");
                    }}
                    style={{ width: "100%", textAlign: "left" }}>
                    <div
                      className="admin_product_user_avatar"
                      style={{
                        background: "var(--admin-bg-input, #fff)",
                        border: "1px solid var(--admin-border, #ddd)",
                        color: "var(--cn-accent, #ff5a5f)",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        fontSize: "12px",
                        fontWeight: 700,
                      }}>
                      {(membersPostChannelLabel(p.channel).slice(0, 2) || "?").toUpperCase()}
                    </div>
                    <div className="admin_product_user_info" style={{ minWidth: 0 }}>
                      <span
                        className="admin_product_user_name"
                        style={{
                          display: "flex",
                          alignItems: "center",
                          gap: "8px",
                          flexWrap: "wrap",
                        }}>
                        <span
                          style={{
                            overflow: "hidden",
                            textOverflow: "ellipsis",
                            whiteSpace: "nowrap",
                            minWidth: 0,
                          }}>
                          {titleShort}
                        </span>
                        <span
                          className={
                            "admin_members_post_badge " +
                            (isConfirmed
                              ? "admin_members_post_badge--confirmed"
                              : "admin_members_post_badge--pending")
                          }>
                          {isConfirmed ? "Bestätigt" : "Zur Prüfung"}
                        </span>
                      </span>
                      <span
                        className="admin_product_user_email"
                        style={{
                          display: "block",
                          overflow: "hidden",
                          textOverflow: "ellipsis",
                          whiteSpace: "nowrap",
                        }}>
                        {membersPostChannelLabel(p.channel)} · {formatMembersPostTs(p.created_at)}
                      </span>
                    </div>
                  </button>
                );
              })}
            </div>
          </div>
        ) : activeView === "ai_agents_overview" ? (
          <div className="admin_product_view">
            <div className="admin_product_header">
              <h2 className="admin_product_title">Übersicht</h2>
              <p className="admin_product_desc">
                Schichtenmodell und Zuordnung zu den Tabs (Mockup ohne Backend). Siehe auch
                docs/agent-platform-schichten.md.
              </p>
            </div>
            <p className="agent_overview_mnemonic">
              <strong>Kurzform (Englisch):</strong> Gate → Agent → Tools → Action → Data
            </p>
            <p className="agent_overview_mnemonic">
              <strong>Deutsch:</strong> Zugang → KI → Werkzeuge → Umsetzung → Daten
            </p>
            <div className="agent_overview_table_wrap">
              <table className="agent_overview_table">
                <thead>
                  <tr>
                    <th>Engl.</th>
                    <th>Deutsch</th>
                    <th>Verwaltet im Tab</th>
                    <th>Kurzbeschreibung</th>
                  </tr>
                </thead>
                <tbody>
                  {MOCK_AI_PLATFORM_OVERVIEW_ROWS.map((row) => (
                    <tr key={row.id}>
                      <td>{row.short_en}</td>
                      <td>{row.name_de}</td>
                      <td>{row.admin_tab}</td>
                      <td>{row.blurb}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        ) : activeView === "ai_agents_services" ? (
          <div className="admin_product_view">
            <div
              className="admin_product_header"
              style={{
                display: "flex",
                flexDirection: "row",
                alignItems: "flex-start",
                justifyContent: "space-between",
                gap: "12px",
                width: "100%",
              }}>
              <div style={{ flex: 1, minWidth: 0 }}>
                <h2 className="admin_product_title">Agents</h2>
                <p className="admin_product_desc">
                  Agent-as-a-Service — Mockup ohne Backend.
                </p>
              </div>
              {!isMobile ? (
                <button type="button" className="admin_btn admin_btn--primary">
                  Neuer Agent
                </button>
              ) : null}
            </div>
            <div className="agent_filter_bar">
              <select
                className="admin_input agent_filter_select"
                value={aiAgentServiceDomainFilter}
                onChange={(e) =>
                  setAiAgentServiceDomainFilter(e.target.value)
                }
                aria-label="Domäne">
                <option value="all">Alle Domänen</option>
                <option value="Recht">Recht</option>
                <option value="Finanzen">Finanzen</option>
                <option value="Cross">Cross</option>
                <option value="Compliance">Compliance</option>
              </select>
              <select
                className="admin_input agent_filter_select"
                value={aiAgentServiceStatusFilter}
                onChange={(e) =>
                  setAiAgentServiceStatusFilter(e.target.value)
                }
                aria-label="Status">
                <option value="all">Alle Status</option>
                <option value="stable">stable</option>
                <option value="beta">beta</option>
                <option value="draft">draft</option>
                <option value="deprecated">deprecated</option>
              </select>
              <select
                className="admin_input agent_filter_select"
                value={aiAgentServiceOwnerFilter}
                onChange={(e) =>
                  setAiAgentServiceOwnerFilter(e.target.value)
                }
                aria-label="Owner">
                <option value="all">Owner: alle</option>
                <option value="Hassan">Hassan</option>
                <option value="Masih">Masih</option>
                <option value="Elias">Elias</option>
              </select>
              <input
                type="search"
                className="admin_input agent_filter_search"
                placeholder="Suche…"
                value={aiAgentServiceSearch}
                onChange={(e) => setAiAgentServiceSearch(e.target.value)}
                aria-label="Suche"
              />
            </div>
            <div className="admin_product_user_list">
              {filteredMockAiServices.length === 0 ? (
                <p className="admin_product_empty">Keine Services gefunden.</p>
              ) : null}
              {filteredMockAiServices.map((sv) => {
                const pub = mergeAiAgentPublished(
                  sv,
                  aiAgentServicePublishedById[sv.id],
                );
                const isSelected = selectedAiAgentServiceId === sv.id;
                const succ =
                  pub.stats.success != null
                    ? `${(pub.stats.success * 100).toFixed(1)}%`
                    : "–";
                const lat =
                  pub.stats.p95_ms != null ? `${(pub.stats.p95_ms / 1000).toFixed(1)}s` : "–";
                const calls =
                  pub.stats.calls_24h != null
                    ? `${pub.stats.calls_24h.toLocaleString("de-DE")} calls/24h`
                    : "–";
                return (
                  <button
                    key={sv.id}
                    type="button"
                    className={
                      "admin_product_user_card agent_service_card" +
                      (isSelected ? " admin_product_user_card--selected" : "")
                    }
                    onClick={() => setSelectedAiAgentServiceId(sv.id)}
                    style={{ width: "100%", textAlign: "left" }}>
                    <div className="admin_product_user_avatar">{pub.icon}</div>
                    <div className="agent_service_card_main">
                      <div className="agent_service_card_title_row">
                        <span className="admin_product_user_name">{pub.name}</span>
                        <span className="agent_service_card_version_meta">
                          <span className="agent_version_tag">{pub.version}</span>
                          <AgentMockStatusPill status={pub.status} />
                        </span>
                      </div>
                      <span className="admin_product_user_email">
                        {pub.slug} · Domäne: {pub.domain} · Owner: {pub.owner}
                      </span>
                      <div className="agent_stats_badge_row">
                        <span className="agent_stats_badge">⌀ {lat}</span>
                        <span className="agent_stats_badge">{succ} success</span>
                        <span className="agent_stats_badge">{calls}</span>
                        <AgentMockHealthDots level={pub.health} />
                      </div>
                    </div>
                  </button>
                );
              })}
            </div>
          </div>
        ) : activeView === "ai_agents_tools" ? (
          <div className="admin_product_view">
            <div className="admin_product_header">
              <h2 className="admin_product_title">Tools</h2>
              <p className="admin_product_desc">
                Werkzeug-Katalog: was die Agents aufrufen dürfen (Mockup).
              </p>
            </div>
            <div className="agent_filter_bar">
              <input
                type="search"
                className="admin_input agent_filter_search"
                placeholder="Suche…"
                value={aiAgentToolSearch}
                onChange={(e) => setAiAgentToolSearch(e.target.value)}
                aria-label="Suche"
              />
            </div>
            <div className="admin_product_user_list">
              {filteredMockAiTools.length === 0 ? (
                <p className="admin_product_empty">Keine Tools gefunden.</p>
              ) : null}
              {filteredMockAiTools.map((t) => {
                const isSelected = selectedAiAgentToolId === t.id;
                const succ =
                  t.success != null ? `${(t.success * 100).toFixed(1)}%` : "–";
                const calls =
                  t.calls_24h != null
                    ? `${t.calls_24h.toLocaleString("de-DE")} calls/24h`
                    : "–";
                return (
                  <button
                    key={t.id}
                    type="button"
                    className={
                      "admin_product_user_card agent_service_card" +
                      (isSelected ? " admin_product_user_card--selected" : "")
                    }
                    onClick={() => setSelectedAiAgentToolId(t.id)}
                    style={{ width: "100%", textAlign: "left" }}>
                    <div className="admin_product_user_avatar">🔧</div>
                    <div className="agent_service_card_main">
                      <div className="agent_service_card_title_row">
                        <span className="admin_product_user_name">{t.name}</span>
                        <span className="agent_service_card_version_meta">
                          <span className="agent_version_tag">{t.version}</span>
                          <AgentMockStatusPill status={t.status} />
                        </span>
                      </div>
                      <span className="admin_product_user_email">
                        {t.slug} · {t.category} · genutzt von{" "}
                        {t.used_by_agent_ids.length} Agent(s)
                      </span>
                      <div className="agent_stats_badge_row">
                        <span className="agent_stats_badge">{calls}</span>
                        <span className="agent_stats_badge">{succ} success</span>
                        <span className="agent_stats_badge">→ {t.action_id}</span>
                      </div>
                    </div>
                  </button>
                );
              })}
            </div>
          </div>
        ) : activeView === "ai_agents_actions" ? (
          <div className="admin_product_view">
            <div className="admin_product_header">
              <h2 className="admin_product_title">Actions</h2>
              <p className="admin_product_desc">
                Umsetzung hinter den Tools: HTTP, SQL, Worker, A2A (Mockup).
              </p>
            </div>
            <div className="agent_filter_bar">
              <select
                className="admin_input agent_filter_select"
                value={aiAgentActionKindFilter}
                onChange={(e) => setAiAgentActionKindFilter(e.target.value)}
                aria-label="Art">
                <option value="all">Alle Arten</option>
                <option value="http">HTTP</option>
                <option value="sql">SQL</option>
                <option value="worker">Worker</option>
                <option value="files">Files</option>
                <option value="a2a">A2A</option>
              </select>
            </div>
            <div className="admin_product_user_list">
              {filteredMockAiActions.length === 0 ? (
                <p className="admin_product_empty">Keine Actions gefunden.</p>
              ) : null}
              {filteredMockAiActions.map((a) => {
                const isSelected = selectedAiAgentActionId === a.id;
                return (
                  <button
                    key={a.id}
                    type="button"
                    className={
                      "admin_product_user_card agent_service_card" +
                      (isSelected ? " admin_product_user_card--selected" : "")
                    }
                    onClick={() => setSelectedAiAgentActionId(a.id)}
                    style={{ width: "100%", textAlign: "left" }}>
                    <div className="admin_product_user_avatar">⚡</div>
                    <div className="agent_service_card_main">
                      <div className="agent_service_card_title_row">
                        <span className="admin_product_user_name">{a.name}</span>
                        <span className="agent_service_card_version_meta">
                          <span className="agent_version_tag">{a.kind}</span>
                          <AgentMockStatusPill status={a.status} />
                        </span>
                      </div>
                      <span className="admin_product_user_email">{a.target}</span>
                      <div className="agent_stats_badge_row">
                        <span className="agent_stats_badge">
                          {(a.timeout_ms / 1000).toFixed(1)}s timeout
                        </span>
                        <span className="agent_stats_badge">
                          {a.retries}× retry
                        </span>
                        <span className="agent_stats_badge">
                          {a.success_pct}% success
                        </span>
                        {a.sandbox ? (
                          <span className="agent_stats_badge">sandbox</span>
                        ) : null}
                      </div>
                    </div>
                  </button>
                );
              })}
            </div>
          </div>
        ) : activeView === "ai_agents_data" ? (
          <div className="admin_product_view">
            <div className="admin_product_header">
              <h2 className="admin_product_title">Data</h2>
              <p className="admin_product_desc">
                Datenhaltung: DB, Dateien, Vektor-Indizes, Cache (Mockup).
              </p>
            </div>
            <div className="agent_filter_bar">
              <select
                className="admin_input agent_filter_select"
                value={aiAgentDataKindFilter}
                onChange={(e) => setAiAgentDataKindFilter(e.target.value)}
                aria-label="Art">
                <option value="all">Alle Arten</option>
                <option value="mysql">MySQL</option>
                <option value="files">Files</option>
                <option value="vector">Vector</option>
                <option value="cache">Cache</option>
              </select>
            </div>
            <div className="admin_product_user_list">
              {filteredMockAiData.length === 0 ? (
                <p className="admin_product_empty">Keine Speicher gefunden.</p>
              ) : null}
              {filteredMockAiData.map((d) => {
                const isSelected = selectedAiAgentDataId === d.id;
                const icon =
                  d.kind === "mysql"
                    ? "🗄"
                    : d.kind === "files"
                      ? "📁"
                      : d.kind === "cache"
                        ? "⚡"
                        : "🧭";
                const retention =
                  d.retention_days === 0
                    ? "kein Persist"
                    : `${d.retention_days} Tage Retention`;
                return (
                  <button
                    key={d.id}
                    type="button"
                    className={
                      "admin_product_user_card agent_service_card" +
                      (isSelected ? " admin_product_user_card--selected" : "")
                    }
                    onClick={() => setSelectedAiAgentDataId(d.id)}
                    style={{ width: "100%", textAlign: "left" }}>
                    <div className="admin_product_user_avatar">{icon}</div>
                    <div className="agent_service_card_main">
                      <div className="agent_service_card_title_row">
                        <span className="admin_product_user_name">{d.name}</span>
                        <span className="agent_service_card_version_meta">
                          <span className="agent_version_tag">{d.kind}</span>
                          <span className="agent_version_tag">{d.scope}</span>
                        </span>
                      </div>
                      <span className="admin_product_user_email">
                        {d.location}
                      </span>
                      <div className="agent_stats_badge_row">
                        <span className="agent_stats_badge">{d.size_label}</span>
                        <span className="agent_stats_badge">{d.rows_label}</span>
                        <span className="agent_stats_badge">{retention}</span>
                      </div>
                    </div>
                  </button>
                );
              })}
            </div>
          </div>
        ) : activeView === "ai_agents_consumers" ? (
          <div className="admin_product_view">
            <div className="admin_product_header">
              <h2 className="admin_product_title">Clients</h2>
              <p className="admin_product_desc">
                Apps und externe Agenten mit API-Zugang (Mockup).
              </p>
            </div>
            <div className="admin_product_user_list">
              {MOCK_AI_CONSUMERS.map((c) => {
                const isSelected = selectedAiAgentConsumerId === c.id;
                return (
                  <button
                    key={c.id}
                    type="button"
                    className={
                      "admin_product_user_card agent_consumer_card" +
                      (isSelected ? " admin_product_user_card--selected" : "")
                    }
                    onClick={() => setSelectedAiAgentConsumerId(c.id)}
                    style={{ width: "100%", textAlign: "left" }}>
                    <div className="admin_product_user_avatar">{c.icon}</div>
                    <div style={{ minWidth: 0, flex: 1 }}>
                      <div className="agent_consumer_title_row">
                        <span className="admin_product_user_name">{c.name}</span>
                        <span className={"agent_consumer_kind agent_consumer_kind--" + c.kind}>
                          {c.kind}
                        </span>
                      </div>
                      <span className="admin_product_user_email">{c.slug}</span>
                      <span className="admin_product_user_email">
                        {c.api_keys_count} API-Keys · Quota {c.quota_per_day.toLocaleString("de-DE")}
                        /Tag · Nutzt {c.services_count} Services · letzter Call {c.last_call}
                      </span>
                    </div>
                  </button>
                );
              })}
            </div>
          </div>
        ) : activeView === "ai_agents_runs" ? (
          <div className="admin_product_view">
            <div className="admin_product_header">
              <h2 className="admin_product_title">Aufrufe</h2>
              <p className="admin_product_desc">
                Traces aller Service-Calls (Mockup).
              </p>
            </div>
            <div className="agent_filter_bar agent_filter_bar--wrap">
              <select
                className="admin_input agent_filter_select"
                value={aiAgentRunServiceFilter}
                onChange={(e) =>
                  setAiAgentRunServiceFilter(e.target.value)
                }
                aria-label="Service">
                <option value="all">Alle Services</option>
                {MOCK_AI_SERVICES.map((s) => (
                  <option key={s.id} value={s.id}>
                    {s.slug}
                  </option>
                ))}
              </select>
              <select
                className="admin_input agent_filter_select"
                value={aiAgentRunConsumerFilter}
                onChange={(e) =>
                  setAiAgentRunConsumerFilter(e.target.value)
                }
                aria-label="Client">
                <option value="all">Alle Clients</option>
                {MOCK_AI_CONSUMERS.map((c) => (
                  <option key={c.id} value={c.id}>
                    {c.slug}
                  </option>
                ))}
              </select>
              <select
                className="admin_input agent_filter_select"
                value={aiAgentRunStatusFilter}
                onChange={(e) =>
                  setAiAgentRunStatusFilter(e.target.value)
                }
                aria-label="Status">
                <option value="all">Alle Status</option>
                <option value="success">success</option>
                <option value="error">error</option>
                <option value="pending">pending</option>
                <option value="refused">refused</option>
              </select>
              <input
                type="search"
                className="admin_input agent_filter_search"
                placeholder="Suche…"
                value={aiAgentRunSearch}
                onChange={(e) => setAiAgentRunSearch(e.target.value)}
              />
            </div>
            <div className="admin_product_user_list">
              {filteredMockAiRuns.length === 0 ? (
                <p className="admin_product_empty">Keine Aufrufe gefunden.</p>
              ) : null}
              {filteredMockAiRuns.map((r) => {
                const isSelected = selectedAiAgentRunId === r.id;
                const svc = MOCK_AI_SERVICES.find((x) => x.id === r.service_id);
                const lat =
                  r.latency_ms != null
                    ? `${(r.latency_ms / 1000).toFixed(2)}s`
                    : "–";
                const cost =
                  r.cost_eur != null
                    ? `${r.cost_eur.toFixed(3)} €`
                    : "–";
                const tok =
                  r.tokens_in != null && r.tokens_out != null
                    ? `${r.tokens_in + r.tokens_out} tok`
                    : r.tokens_in != null
                      ? `${r.tokens_in} tok in`
                      : "–";
                return (
                  <button
                    key={r.id}
                    type="button"
                    className={
                      "admin_product_user_card agent_run_card" +
                      (isSelected ? " admin_product_user_card--selected" : "")
                    }
                    onClick={() => setSelectedAiAgentRunId(r.id)}
                    style={{ width: "100%", textAlign: "left" }}>
                    <AgentMockRunIcon status={r.status} />
                    <div style={{ minWidth: 0, flex: 1 }}>
                      <div className="agent_run_card_title_row">
                        <span className="admin_product_user_name">
                          {svc ? svc.slug : r.service_id} · {r.version}
                        </span>
                        <span className="agent_run_card_meta">
                          {r.at_label} · {lat}
                        </span>
                      </div>
                      <span className="admin_product_user_email">
                        Caller: {r.caller_id} · {tok} · {cost}
                      </span>
                      <span className="admin_product_user_email">{r.description}</span>
                    </div>
                  </button>
                );
              })}
            </div>
          </div>
        ) : activeView === "ai_agents_evals" ? (
          <div className="admin_product_view">
            <div className="admin_product_header">
              <h2 className="admin_product_title">Evals</h2>
              <p className="admin_product_desc">
                Qualitäts-Suites und Regression (Mockup).
              </p>
            </div>
            <div className="admin_product_user_list">
              {MOCK_AI_EVALS.map((ev) => {
                const isSelected = selectedAiAgentEvalId === ev.id;
                const svc = MOCK_AI_SERVICES.find((x) => x.id === ev.service_id);
                const arrow = ev.trend === "up" ? "▲" : ev.trend === "down" ? "▼" : "–";
                return (
                  <button
                    key={ev.id}
                    type="button"
                    className={
                      "admin_product_user_card agent_eval_card" +
                      (isSelected ? " admin_product_user_card--selected" : "")
                    }
                    onClick={() => setSelectedAiAgentEvalId(ev.id)}
                    style={{ width: "100%", textAlign: "left" }}>
                    <div className="admin_product_user_avatar">📊</div>
                    <div style={{ minWidth: 0, flex: 1 }}>
                      <div className="agent_eval_title_row">
                        <span className="admin_product_user_name">{ev.name}</span>
                        {ev.regression ? (
                          <span className="agent_regression_badge">REGRESSION</span>
                        ) : null}
                      </div>
                      <span className="admin_product_user_email">
                        {svc ? svc.slug : ev.service_id} · {ev.datapoints} Datapoints · letzter Run{" "}
                        {ev.last_run}
                      </span>
                      <div className="agent_eval_score_row">
                        <span>
                          {ev.current_v}: {ev.current_pct}% {arrow} vs {ev.prev_v}:{" "}
                          {ev.prev_pct}%
                        </span>
                        <AgentMockSparkline values={ev.sparkline} />
                      </div>
                    </div>
                  </button>
                );
              })}
            </div>
          </div>
        ) : activeView === "ai_agents_governance" ? (
          <div className="admin_product_view">
            <div className="admin_product_header">
              <h2 className="admin_product_title">Governance</h2>
              <p className="admin_product_desc">
                Kosten, Freigaben, Vorfälle (Mockup).
              </p>
            </div>
            <div className="agent_governance_pillbar">
              <button
                type="button"
                className={
                  "agent_governance_pill" +
                  (aiAgentGovernanceMode === "cost"
                    ? " agent_governance_pill--active"
                    : "")
                }
                onClick={() => {
                  setAiAgentGovernanceMode("cost");
                  setSelectedAiAgentApprovalId(null);
                  setSelectedAiAgentIncidentId(null);
                  setSelectedAiAgentCostId(null);
                }}>
                Cost
              </button>
              <button
                type="button"
                className={
                  "agent_governance_pill" +
                  (aiAgentGovernanceMode === "approvals"
                    ? " agent_governance_pill--active"
                    : "")
                }
                onClick={() => {
                  setAiAgentGovernanceMode("approvals");
                  setSelectedAiAgentCostId(null);
                  setSelectedAiAgentIncidentId(null);
                  setSelectedAiAgentApprovalId(null);
                }}>
                Approvals ⚠ {pendingAiApprovalCount}
              </button>
              <button
                type="button"
                className={
                  "agent_governance_pill" +
                  (aiAgentGovernanceMode === "incidents"
                    ? " agent_governance_pill--active"
                    : "")
                }
                onClick={() => {
                  setAiAgentGovernanceMode("incidents");
                  setSelectedAiAgentCostId(null);
                  setSelectedAiAgentApprovalId(null);
                  setSelectedAiAgentIncidentId(null);
                }}>
                Incidents {MOCK_AI_INCIDENTS.length}
              </button>
            </div>
            {aiAgentGovernanceMode === "cost" ? (
              <div className="admin_product_user_list">
                {MOCK_AI_COSTS.map((co) => {
                  const isSelected = selectedAiAgentCostId === co.id;
                  const svc = MOCK_AI_SERVICES.find((x) => x.id === co.service_id);
                  const pct = Math.min(
                    100,
                    (co.eur_24h / Math.max(co.budget_eur_day, 0.01)) * 100,
                  );
                  return (
                    <button
                      key={co.id}
                      type="button"
                      className={
                        "admin_product_user_card agent_cost_card" +
                        (isSelected ? " admin_product_user_card--selected" : "")
                      }
                      onClick={() => setSelectedAiAgentCostId(co.id)}
                      style={{ width: "100%", textAlign: "left" }}>
                      <div className="admin_product_user_avatar">
                        {svc ? svc.icon : "€"}
                      </div>
                      <div style={{ minWidth: 0, flex: 1 }}>
                        <span className="admin_product_user_name">
                          {svc ? svc.name : co.service_id}
                        </span>
                        <span className="admin_product_user_email">
                          {co.calls_24h.toLocaleString("de-DE")} calls ·{" "}
                          {co.tokens_24h.toLocaleString("de-DE")} tok ·{" "}
                          {co.eur_24h.toFixed(2)} € / Tag
                        </span>
                        <div className="agent_budget_bar_wrap">
                          <div className="agent_budget_bar">
                            <div
                              className={
                                "agent_budget_bar_fill" +
                                (pct >= 90 ? " agent_budget_bar_fill--warn" : "")
                              }
                              style={{ width: pct + "%" }}
                            />
                          </div>
                          <span className="agent_budget_bar_label">
                            Budget {co.budget_eur_day} € · {pct.toFixed(0)} %
                          </span>
                        </div>
                      </div>
                    </button>
                  );
                })}
              </div>
            ) : aiAgentGovernanceMode === "approvals" ? (
              <div className="admin_product_user_list">
                {visibleMockAiApprovals.length === 0 ? (
                  <p className="admin_product_empty">Keine offenen Freigaben.</p>
                ) : null}
                {visibleMockAiApprovals.map((ap) => {
                  const isSelected = selectedAiAgentApprovalId === ap.id;
                  return (
                    <div
                      key={ap.id}
                      className={
                        "admin_product_user_card agent_approval_card" +
                        (isSelected ? " admin_product_user_card--selected" : "")
                      }>
                      <div style={{ minWidth: 0, flex: 1 }}>
                        <span className="admin_product_user_name">
                          Run {ap.run_id} · {ap.service_id}
                        </span>
                        <span className="admin_product_user_email">
                          Caller {ap.caller_id} · risk {ap.risk_score} · {ap.pending_since}
                        </span>
                        <span className="admin_product_user_email">{ap.summary}</span>
                        <div className="agent_approval_actions">
                          <button
                            type="button"
                            className="admin_btn admin_btn--primary"
                            onClick={() => {
                              setDismissedApprovalIds((prev) => {
                                const n = new Set(prev);
                                n.add(ap.id);
                                return n;
                              });
                              if (selectedAiAgentApprovalId === ap.id) {
                                setSelectedAiAgentApprovalId(null);
                              }
                            }}>
                            Freigeben
                          </button>
                          <button
                            type="button"
                            className="admin_btn"
                            onClick={() => {
                              setDismissedApprovalIds((prev) => {
                                const n = new Set(prev);
                                n.add(ap.id);
                                return n;
                              });
                              if (selectedAiAgentApprovalId === ap.id) {
                                setSelectedAiAgentApprovalId(null);
                              }
                            }}>
                            Ablehnen
                          </button>
                          <button
                            type="button"
                            className="admin_btn"
                            onClick={() => {
                              setDismissedApprovalIds((prev) => {
                                const n = new Set(prev);
                                n.add(ap.id);
                                return n;
                              });
                              if (selectedAiAgentApprovalId === ap.id) {
                                setSelectedAiAgentApprovalId(null);
                              }
                            }}>
                            Eingreifen
                          </button>
                          <button
                            type="button"
                            className="admin_btn"
                            onClick={() => setSelectedAiAgentApprovalId(ap.id)}>
                            Detail
                          </button>
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            ) : (
              <div className="admin_product_user_list">
                {MOCK_AI_INCIDENTS.map((inc) => {
                  const isSelected = selectedAiAgentIncidentId === inc.id;
                  return (
                    <button
                      key={inc.id}
                      type="button"
                      className={
                        "admin_product_user_card" +
                        (isSelected ? " admin_product_user_card--selected" : "")
                      }
                      onClick={() => setSelectedAiAgentIncidentId(inc.id)}
                      style={{ width: "100%", textAlign: "left" }}>
                      <div className="admin_product_user_avatar">🐛</div>
                      <div style={{ minWidth: 0, flex: 1 }}>
                        <span className="admin_product_user_name">{inc.title}</span>
                        <span className="admin_product_user_email">
                          {inc.service_id} {inc.version} · {inc.severity} · {inc.opened_at}
                        </span>
                      </div>
                    </button>
                  );
                })}
              </div>
            )}
          </div>
        ) : activeView === "design_patterns" ? (
          <div className="admin_product_view">
            <div className="admin_product_header">
              <h2 className="admin_product_title">Patterns</h2>
              <p className="admin_product_desc">
                Design-Patterns und Komponenten.
              </p>
            </div>
            <div className="admin_product_user_list">
              {DESIGN_PATTERN_PLACEHOLDERS.map((p) => {
                const isSelected = selectedDesignPatternId === p.id;
                return (
                  <button
                    key={p.id}
                    type="button"
                    className={
                      "admin_product_user_card" +
                      (isSelected ? " admin_product_user_card--selected" : "")
                    }
                    onClick={() => setSelectedDesignPatternId(p.id)}
                    style={{ width: "100%", textAlign: "left" }}>
                    <div className="admin_product_user_avatar">🎨</div>
                    <div className="admin_product_user_info" style={{ minWidth: 0 }}>
                      <span className="admin_product_user_name">{p.title}</span>
                      <span className="admin_product_user_email">{p.desc}</span>
                    </div>
                  </button>
                );
              })}
            </div>
          </div>
        ) : WORKSPACE_SHELL_META[activeView] ? (
          <div className="admin_product_view">
            <div className="admin_product_header">
              <h2 className="admin_product_title">
                {WORKSPACE_SHELL_META[activeView].title}
              </h2>
              <p className="admin_product_desc">
                {WORKSPACE_SHELL_META[activeView].desc}
              </p>
            </div>
            <div className="admin_area_placeholder_body">
              <p className="admin_area_placeholder_text">Inhalt folgt.</p>
            </div>
          </div>
        ) : (
          <div className="admin_view_placeholder">
            <p>Nachrichten</p>
          </div>
        )}
          </>
        )}
      </div>

      {/* Detail sidebar */}
      {(() => {
        const isListDetailView =
          activeView === "kunden" ||
          activeView === "kis" ||
          activeView === "aicoworker" ||
          activeView === "module" ||
          activeView === "anrufe" ||
          activeView === "tests";
        const hasSelection =
          (activeView === "kunden" && selectedProductUserId != null) ||
          (activeView === "kis" && selectedAiId != null) ||
          (activeView === "aicoworker" && selectedAicoworkerId != null) ||
          (activeView === "module" && selectedModuleId != null) ||
          (activeView === "anrufe" && selectedCallId != null) ||
          (activeView === "tests" &&
            (selectedTestRunId != null || selectedManualTestId != null));
        const productDetailOpen =
          !settingsOpen &&
          !areaPickerOpen &&
          isListDetailView &&
          (!isMobile || hasSelection);
        const workspaceShellRightOpen =
          !settingsOpen &&
          !areaPickerOpen &&
          isWorkspaceShellTab(activeView) &&
          !isMobile;
        const agendasSidebarOpen =
          activeView === "admin_agendas" &&
          (!isMobile || agendaCreateOpen || selectedAdminAgendaId != null) &&
          !settingsOpen &&
          !areaPickerOpen;
        const agendasFormSidebarOpen =
          agendasSidebarOpen &&
          (agendaCreateOpen || selectedAdminAgendaId != null);
        const userStoriesSidebarOpen =
          activeView === "product_userstories" &&
          (!isMobile ||
            userStoryCreateOpen ||
            selectedUserStoryId != null) &&
          !settingsOpen &&
          !areaPickerOpen;
        const userStoriesFormSidebarOpen =
          userStoriesSidebarOpen &&
          (userStoryCreateOpen || selectedUserStoryId != null);
        const membersSidebarOpen =
          activeView === "people_members" &&
          (!isMobile || memberCreateOpen || selectedMemberId != null) &&
          !settingsOpen &&
          !areaPickerOpen;
        const membersFormSidebarOpen =
          membersSidebarOpen &&
          (memberCreateOpen || selectedMemberId != null);
        const salesCustomersSidebarOpen =
          (activeView === "sales_akquise" || activeView === "sales_service") &&
          (!isMobile ||
            salesCustomerPanelOpen ||
            selectedSalesCustomerId != null) &&
          !settingsOpen &&
          !areaPickerOpen;
        const salesCustomersFormSidebarOpen =
          salesCustomersSidebarOpen &&
          (salesCustomerPanelOpen || selectedSalesCustomerId != null);


        const marketingCommunitySidebarOpen =
          activeView === "marketing_community_content" &&
          (!isMobile ||
            marketingCommunityPanelOpen ||
            selectedMarketingCommunityContentId != null) &&
          !settingsOpen &&
          !areaPickerOpen;
        const marketingCommunityFormSidebarOpen =
          marketingCommunitySidebarOpen &&
          (marketingCommunityPanelOpen || selectedMarketingCommunityContentId != null);

        const marketingChannelContentSidebarOpen =
          activeView === "marketing_channel_content" &&
          (!isMobile ||
            marketingChannelContentPanelOpen ||
            selectedMarketingChannelContentId != null) &&
          !settingsOpen &&
          !areaPickerOpen;
        const marketingChannelContentFormSidebarOpen =
          marketingChannelContentSidebarOpen &&
          (marketingChannelContentPanelOpen ||
            selectedMarketingChannelContentId != null);

        const marketingTargetgroupsSidebarOpen =
          activeView === "marketing_targetgroups" &&
          (!isMobile ||
            marketingTargetgroupPanelOpen ||
            selectedMarketingTargetgroupId != null) &&
          !settingsOpen &&
          !areaPickerOpen;
        const marketingTargetgroupsFormSidebarOpen =
          marketingTargetgroupsSidebarOpen &&
          (marketingTargetgroupPanelOpen || selectedMarketingTargetgroupId != null);

        const marketingChannelsSidebarOpen =
          activeView === "marketing_channels" &&
          (!isMobile ||
            marketingChannelPanelOpen ||
            selectedMarketingChannelId != null) &&
          !settingsOpen &&
          !areaPickerOpen;
        const marketingChannelsFormSidebarOpen =
          marketingChannelsSidebarOpen &&
          (marketingChannelPanelOpen || selectedMarketingChannelId != null);

        const marketingPostsSidebarOpen =
          activeView === "marketing_posts" &&
          (!isMobile || selectedMembersPostId != null) &&
          !settingsOpen &&
          !areaPickerOpen;
        const marketingPostsDetailSidebarOpen =
          marketingPostsSidebarOpen && selectedMembersPostId != null;

        const salesContentsSidebarOpen = false;
        const salesContentsFormSidebarOpen = false;

        const salesTrainingsSidebarOpen =
          (activeView === "sales_training" || activeView === "people_schulungen") &&
          (!isMobile ||
            salesTrainingPanelOpen ||
            selectedSalesTrainingId != null) &&
          !settingsOpen &&
          !areaPickerOpen;
        const salesTrainingsFormSidebarOpen =
          salesTrainingsSidebarOpen &&
          (salesTrainingPanelOpen || selectedSalesTrainingId != null);

        const salesRevenueSidebarOpen = false;
        const salesTeamSidebarOpen =
          activeView === "sales_team" &&
          (!isMobile || teamInviteCreateOpen || selectedTeamMemberId != null) &&
          !settingsOpen &&
          !areaPickerOpen;
        const salesTeamFormSidebarOpen =
          salesTeamSidebarOpen && teamInviteCreateOpen;
        const salesTeamMemberSidebarOpen =
          salesTeamSidebarOpen &&
          !teamInviteCreateOpen &&
          selectedTeamMemberId != null;

        const aiAgentsHasSelection =
          (activeView === "ai_agents_services" &&
            selectedAiAgentServiceId != null) ||
          (activeView === "ai_agents_consumers" &&
            selectedAiAgentConsumerId != null) ||
          (activeView === "ai_agents_tools" &&
            selectedAiAgentToolId != null) ||
          (activeView === "ai_agents_actions" &&
            selectedAiAgentActionId != null) ||
          (activeView === "ai_agents_data" &&
            selectedAiAgentDataId != null) ||
          (activeView === "ai_agents_runs" && selectedAiAgentRunId != null) ||
          (activeView === "ai_agents_evals" && selectedAiAgentEvalId != null) ||
          (activeView === "ai_agents_governance" &&
            aiAgentGovernanceMode === "cost" &&
            selectedAiAgentCostId != null) ||
          (activeView === "ai_agents_governance" &&
            aiAgentGovernanceMode === "approvals" &&
            selectedAiAgentApprovalId != null) ||
          (activeView === "ai_agents_governance" &&
            aiAgentGovernanceMode === "incidents" &&
            selectedAiAgentIncidentId != null);

        const aiAgentsDetailOpen =
          !settingsOpen &&
          !areaPickerOpen &&
          AI_AGENT_VIEW_IDS.has(activeView) &&
          (!isMobile || aiAgentsHasSelection);

        const designPatternsSidebarOpen =
          activeView === "design_patterns" &&
          (!isMobile || selectedDesignPatternId != null) &&
          !settingsOpen &&
          !areaPickerOpen;
        const designPatternsHasSelection =
          designPatternsSidebarOpen && selectedDesignPatternId != null;

        /** Konto: rechte Sidebar nur auf Desktop einblenden, wenn Konto offen ist. */
        const kontoDetailSidebarOpen = settingsOpen && !isMobile;

        const detailSidebarOpen =
          productDetailOpen ||
          workspaceShellRightOpen ||
          userStoriesSidebarOpen ||
          agendasSidebarOpen ||
          membersSidebarOpen ||
          salesCustomersSidebarOpen ||
          marketingCommunitySidebarOpen ||
          marketingChannelContentSidebarOpen ||
          marketingTargetgroupsSidebarOpen ||
          marketingChannelsSidebarOpen ||
          marketingPostsSidebarOpen ||
          salesContentsSidebarOpen ||
          salesTrainingsSidebarOpen ||
          salesRevenueSidebarOpen ||
          salesTeamSidebarOpen ||
          aiAgentsDetailOpen ||
          designPatternsSidebarOpen ||
          kontoDetailSidebarOpen;
        const selectedProductUser = productUsers.find(
          (u) => u.id === selectedProductUserId,
        );
        const selectedAi = aisList.find((a) => a.id === selectedAiId);
        const emptyPlaceholderTitle =
          activeView === "kunden"
            ? "Nutzer auswählen"
            : activeView === "kis"
              ? "KI auswählen"
              : activeView === "aicoworker"
                ? "Agent auswählen"
                : activeView === "module"
                  ? "Modul auswählen"
                  : activeView === "anrufe"
                    ? "Anruf auswählen"
                    : activeView === "tests"
                      ? "Eintrag auswählen"
                      : activeView === "ai_agents_services"
                        ? "Agent auswählen"
                        : activeView === "ai_agents_consumers"
                          ? "Client auswählen"
                          : activeView === "ai_agents_tools"
                            ? "Tool auswählen"
                            : activeView === "ai_agents_actions"
                              ? "Action auswählen"
                              : activeView === "ai_agents_data"
                                ? "Speicher auswählen"
                                : activeView === "ai_agents_runs"
                                  ? "Aufruf auswählen"
                                  : activeView === "ai_agents_evals"
                                    ? "Eval auswählen"
                                    : activeView === "ai_agents_governance"
                                      ? "Eintrag auswählen"
                                      : "Detail";
        const emptyPlaceholderSub = "Wähle einen Eintrag aus der Liste";
        return (
          <div
            ref={detailRef}
            className={
              "admin_detail_sidebar" +
              (detailSidebarOpen ? " admin_detail_sidebar--open" : "")
            }
            style={
              detailSidebarOpen && !isMobile
                ? { width: sidebarWidth, minWidth: sidebarWidth }
                : undefined
            }>
            <div
              className="admin_detail_resize_handle"
              onMouseDown={handleResizeStart}
            />
            {kontoDetailSidebarOpen ? (
              <>
                <div className="admin_detail_header">
                  <span className="admin_detail_heading">
                    <strong>
                      {kontoSubpage === "profile"
                        ? "Profil"
                        : kontoSubpage === "appearance"
                        ? "Darstellung"
                        : "Konto"}
                    </strong>
                    <br />
                    <span className="admin_detail_heading_sub">
                      {kontoSubpage === "profile"
                        ? "Anzeigename, Profilbild und Passwort"
                        : kontoSubpage === "appearance"
                        ? "Helles oder dunkles Farbschema"
                        : "Wähle links einen Eintrag"}
                    </span>
                  </span>
                </div>
                {kontoSubpage === "profile" ? (
                  <div
                    className="admin_detail_body"
                    style={{ padding: "1rem", flexDirection: "column", gap: 0 }}>
                    {kontoProfileBody}
                  </div>
                ) : kontoSubpage === "appearance" ? (
                  <div
                    className="admin_detail_body"
                    style={{ padding: "1rem", flexDirection: "column", gap: 0 }}>
                    {kontoAppearanceBody}
                  </div>
                ) : (
                  <div className="admin_detail_body admin_detail_body--empty">
                    <div className="admin_detail_empty_state">
                      <svg
                        width="40"
                        height="40"
                        viewBox="0 0 24 24"
                        fill="none"
                        stroke="currentColor"
                        strokeWidth="1.5"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        aria-hidden="true">
                        <circle cx="11" cy="11" r="7"></circle>
                        <line x1="21" y1="21" x2="16.65" y2="16.65"></line>
                      </svg>
                      <span>Kein Eintrag ausgewählt</span>
                    </div>
                  </div>
                )}
              </>
            ) : productDetailOpen &&
            activeView === "kunden" &&
            selectedProductUser ? (
              <>
                <div className="admin_detail_header">
                  <button
                    type="button"
                    className="admin_detail_back"
                    onClick={() => setSelectedProductUserId(null)}>
                    &larr;
                  </button>
                  <span className="admin_detail_heading">
                    <strong>
                      {selectedProductUser.display_name || "Nutzer"}
                    </strong>
                    <br />
                    <span className="admin_detail_heading_sub">
                      {selectedProductUser.email}
                    </span>
                  </span>
                  <div className="admin_detail_header_actions">
                    <button
                      type="button"
                      className="admin_btn admin_detail_header_btn"
                      onClick={() => handleImpersonate(selectedProductUser.id)}>
                      Login als Nutzer
                    </button>
                  </div>
                </div>
                <div
                  ref={productBodyRef}
                  className="admin_detail_body"
                  style={{ padding: "1rem", flexDirection: "column", gap: 0 }}>
                  {impersonateStatus && (
                    <p className="admin_impersonate_status">
                      {impersonateStatus}
                    </p>
                  )}
                  <h3 className="admin_product_user_section_title">
                    Logindaten
                  </h3>
                  <form
                    className="admin_product_user_credentials"
                    onSubmit={(e) => {
                      e.preventDefault();
                      handleSaveCredentials();
                    }}>
                    <label className="admin_product_user_credentials_label">
                      <span>Name</span>
                      <input
                        type="text"
                        className="admin_input"
                        value={credNameDraft}
                        onChange={(e) => setCredNameDraft(e.target.value)}
                        autoComplete="off"
                        maxLength={120}
                      />
                    </label>
                    <label className="admin_product_user_credentials_label">
                      <span>E-Mail</span>
                      <input
                        type="email"
                        className="admin_input"
                        value={credEmailDraft}
                        onChange={(e) => setCredEmailDraft(e.target.value)}
                        autoComplete="off"
                        spellCheck={false}
                      />
                    </label>
                    <label className="admin_product_user_credentials_label">
                      <span>Mobilnummer (E.164) - Login per SMS</span>
                      <input
                        type="tel"
                        inputMode="tel"
                        className="admin_input"
                        value={credPhoneDraft}
                        onChange={(e) => setCredPhoneDraft(e.target.value)}
                        placeholder="+4915112345678"
                        autoComplete="tel"
                      />
                    </label>
                    <div className="admin_product_user_credentials_actions">
                      <button
                        type="submit"
                        className="admin_btn admin_btn--primary"
                        disabled={credSaving}>
                        {credSaving ? "Speichere…" : "Speichern"}
                      </button>
                      {credStatus.text && (
                        <span
                          className={
                            "admin_product_user_credentials_status " +
                            (credStatus.kind === "error"
                              ? "admin_product_user_credentials_status--error"
                              : "admin_product_user_credentials_status--success")
                          }>
                          {credStatus.text}
                        </span>
                      )}
                    </div>
                  </form>
                  <h3 className="admin_product_user_section_title" style={{ marginTop: "16px" }}>
                    Status
                  </h3>
                  <div style={{ display: "flex", gap: "6px", padding: "4px 0 8px", flexWrap: "wrap" }}>
                    {[
                      { key: "active",   label: "Aktiv",       color: "var(--cn-semantic-success, #008a05)" },
                      { key: "disabled", label: "Deaktiviert", color: "var(--admin-text-secondary, #717171)" },
                      { key: "test",     label: "Test",        color: "#8B00C8" },
                    ].map((opt) => {
                      const current = (selectedProductUser?.status) || "active";
                      const active = current === opt.key;
                      return (
                        <button
                          key={opt.key}
                          type="button"
                          onClick={() => { if (!active) handleSetUserStatus(selectedProductUser.id, opt.key); }}
                          style={{
                            padding: "6px 12px",
                            borderRadius: "999px",
                            border: "1px solid " + (active ? opt.color : "var(--admin-border, #ddd)"),
                            background: active ? opt.color : "transparent",
                            color: active ? "#fff" : "var(--admin-text-secondary, #717171)",
                            fontSize: "11px",
                            fontWeight: 600,
                            letterSpacing: "0.3px",
                            textTransform: "uppercase",
                            cursor: active ? "default" : "pointer",
                          }}>
                          {opt.label}
                        </button>
                      );
                    })}
                  </div>
                  <h3
                    className="admin_product_user_section_title"
                    style={{ marginTop: "16px" }}>
                    Einrichtung (Onboarding)
                  </h3>
                  <div
                    style={{
                      display: "flex",
                      flexWrap: "wrap",
                      alignItems: "center",
                      gap: "10px",
                      padding: "4px 0 10px",
                    }}>
                    <span
                      title={
                        selectedProductUser?.onboarding_completed_at
                          ? String(selectedProductUser.onboarding_completed_at)
                          : selectedProductUser?.onboarding_skipped_at
                            ? String(selectedProductUser.onboarding_skipped_at)
                            : ""
                      }
                      style={{
                        fontSize: "13px",
                        fontWeight: 600,
                        color:
                          productUserOnboardingLabel(selectedProductUser).color,
                      }}>
                      {productUserOnboardingLabel(selectedProductUser).title}
                    </span>
                    <button
                      type="button"
                      className="admin_btn"
                      disabled={
                        onboardingResetUserId === selectedProductUser?.id
                      }
                      onClick={() =>
                        handleResetUserOnboarding(selectedProductUser.id)
                      }>
                      {onboardingResetUserId === selectedProductUser?.id
                        ? "Setze zurück…"
                        : "Auf «nicht begonnen» setzen"}
                    </button>
                  </div>
                  <h3
                    className="admin_product_user_section_title"
                    style={{ marginTop: "16px" }}>
                    Plan &amp; Nutzung diesen Monat
                  </h3>
                  {(() => {
                    const planKey = normalizeTelephonioPlanKey(
                      selectedProductUser?.plan,
                    );
                    const planLabel = labelTelephonioPlan(
                      selectedProductUser?.plan,
                    );
                    const used = Number(
                      selectedProductUser?.billing_calls_used,
                    );
                    const limit = Number(
                      selectedProductUser?.billing_calls_limit,
                    );
                    const hasUsage =
                      Number.isFinite(used) && used >= 0 && limit > 0;
                    const periodStart = selectedProductUser?.billing_period_start;
                    const periodEnd = selectedProductUser?.billing_period_end;
                    const pct = hasUsage
                      ? Math.max(0, Math.min(100, (used / limit) * 100))
                      : 0;
                    const over = hasUsage && used >= limit;
                    const near80 = hasUsage && used >= limit * 0.8 && !over;
                    const barColor = over
                      ? "var(--cn-semantic-error, #c13515)"
                      : near80
                        ? "var(--cn-semantic-warning, #ffb400)"
                        : "var(--cn-semantic-success, #008a05)";
                    const fmtDate = (v) => {
                      if (!v) return "";
                      const d = new Date(v);
                      if (Number.isNaN(d.getTime())) return "";
                      return d.toLocaleDateString("de-DE", {
                        day: "2-digit",
                        month: "2-digit",
                        year: "numeric",
                      });
                    };
                    return (
                      <div
                        style={{
                          display: "flex",
                          flexDirection: "column",
                          gap: "8px",
                          padding: "4px 0 12px",
                        }}>
                        <div
                          style={{
                            display: "flex",
                            alignItems: "center",
                            gap: "8px",
                            flexWrap: "wrap",
                          }}>
                          <span
                            style={{
                              display: "inline-block",
                              padding: "3px 10px",
                              borderRadius: "999px",
                              border: "1px solid var(--admin-border, #ddd)",
                              background:
                                planKey && planKey !== "free"
                                  ? "var(--cn-accent, #ff5a5f)"
                                  : "transparent",
                              color:
                                planKey && planKey !== "free"
                                  ? "#fff"
                                  : "var(--admin-text, #222)",
                              fontSize: "11px",
                              fontWeight: 700,
                              letterSpacing: "0.3px",
                              textTransform: "uppercase",
                            }}>
                            {planLabel}
                          </span>
                          {periodStart && periodEnd && (
                            <span
                              style={{
                                fontSize: "12px",
                                color:
                                  "var(--admin-text-secondary, #717171)",
                              }}>
                              {fmtDate(periodStart)} – {fmtDate(periodEnd)}
                            </span>
                          )}
                        </div>
                        {hasUsage ? (
                          <>
                            <div
                              style={{
                                fontSize: "13px",
                                color: "var(--admin-text, #222)",
                              }}>
                              <strong>{used}</strong> von {limit} Anrufen
                              {over && (
                                <span
                                  style={{
                                    marginLeft: "6px",
                                    color:
                                      "var(--cn-semantic-error, #c13515)",
                                    fontWeight: 600,
                                  }}>
                                  · Limit erreicht (Hard-Block aktiv)
                                </span>
                              )}
                              {near80 && (
                                <span
                                  style={{
                                    marginLeft: "6px",
                                    color:
                                      "var(--cn-semantic-warning, #ffb400)",
                                    fontWeight: 600,
                                  }}>
                                  · 80%-Push gesendet
                                </span>
                              )}
                            </div>
                            <div
                              style={{
                                height: "6px",
                                width: "100%",
                                borderRadius: "3px",
                                background: "var(--admin-border, #ebebeb)",
                                overflow: "hidden",
                              }}
                              aria-hidden="true">
                              <div
                                style={{
                                  width: pct + "%",
                                  height: "100%",
                                  background: barColor,
                                  transition: "width 0.2s",
                                }}
                              />
                            </div>
                          </>
                        ) : (
                          <p
                            style={{
                              fontSize: "12px",
                              color:
                                "var(--admin-text-secondary, #717171)",
                              margin: 0,
                            }}>
                            Noch keine Anrufe in diesem Monat
                            {limit > 0 ? ` (Limit: ${limit})` : ""}.
                          </p>
                        )}
                      </div>
                    );
                  })()}
                  <div className="admin_product_user_section_divider">
                    <div className="admin_product_user_section_title_row">
                      <h3 className="admin_product_user_section_title">KI's</h3>
                      <span className="admin_product_user_section_count">
                        {productUserKis.length}
                      </span>
                    </div>
                    {!productUserKisLoading && productUserKis.length === 0 && (
                      <p
                        style={{
                          fontSize: "12px",
                          color: "var(--admin-text-secondary, #717171)",
                          margin: "8px 0",
                        }}>
                        Keine KIs angelegt
                      </p>
                    )}
                    {productUserKis.map((ki) => (
                      <div
                        key={ki.id}
                        style={{
                          padding: "8px 10px",
                          marginBottom: "6px",
                          border: "1px solid var(--cn-border, #ebebeb)",
                          borderRadius: "6px",
                          background: "transparent",
                        }}>
                        <div
                          style={{
                            display: "flex",
                            alignItems: "baseline",
                            justifyContent: "space-between",
                            gap: "8px",
                            marginBottom: "4px",
                          }}>
                          <span
                            style={{
                              fontWeight: 600,
                              fontSize: "13px",
                              color: "var(--admin-text, #222)",
                            }}>
                            {ki.ai_name || "Assistenz"}
                          </span>
                          <span
                            style={{
                              fontSize: "11px",
                              color: "var(--admin-text-secondary, #717171)",
                            }}>
                            #{ki.id}
                          </span>
                        </div>
                        <div
                          style={{
                            display: "flex",
                            flexWrap: "wrap",
                            alignItems: "center",
                            gap: "6px",
                            fontSize: "12px",
                            color: "var(--admin-text-secondary, #717171)",
                          }}>
                          <span
                            style={{
                              display: "inline-flex",
                              alignItems: "center",
                              gap: "5px",
                            }}>
                            <svg
                              width="12"
                              height="12"
                              viewBox="0 0 24 24"
                              fill="none"
                              stroke="currentColor"
                              strokeWidth="2"
                              strokeLinecap="round"
                              strokeLinejoin="round"
                              aria-hidden="true">
                              <rect
                                x="2"
                                y="7"
                                width="20"
                                height="14"
                                rx="2"
                                ry="2"></rect>
                              <path d="M16 21V5a2 2 0 0 0-2-2h-4a2 2 0 0 0-2 2v16"></path>
                            </svg>
                            <span>
                              {ki.modus_label || ki.industry_slug || "–"}
                            </span>
                          </span>
                          {ki.industry && ki.industry !== ki.industry_slug && (
                            <span
                              style={{
                                color: "var(--admin-text-secondary, #717171)",
                              }}>
                              · {ki.industry}
                            </span>
                          )}
                          {ki.modules && ki.modules.length > 0 && (
                            <>
                              <span style={{ opacity: 0.5 }}>·</span>
                              <span
                                style={{
                                  display: "inline-flex",
                                  flexWrap: "wrap",
                                  alignItems: "center",
                                  gap: "5px",
                                }}>
                                <svg
                                  width="12"
                                  height="12"
                                  viewBox="0 0 24 24"
                                  fill="none"
                                  stroke="currentColor"
                                  strokeWidth="2"
                                  strokeLinecap="round"
                                  strokeLinejoin="round"
                                  aria-hidden="true">
                                  <circle cx="12" cy="12" r="3"></circle>
                                  <path d="M19.4 15a1.65 1.65 0 0 0 .33 1.82l.06.06a2 2 0 1 1-2.83 2.83l-.06-.06a1.65 1.65 0 0 0-1.82-.33 1.65 1.65 0 0 0-1 1.51V21a2 2 0 1 1-4 0v-.09a1.65 1.65 0 0 0-1-1.51 1.65 1.65 0 0 0-1.82.33l-.06.06a2 2 0 1 1-2.83-2.83l.06-.06a1.65 1.65 0 0 0 .33-1.82 1.65 1.65 0 0 0-1.51-1H3a2 2 0 1 1 0-4h.09a1.65 1.65 0 0 0 1.51-1 1.65 1.65 0 0 0-.33-1.82l-.06-.06a2 2 0 1 1 2.83-2.83l.06.06a1.65 1.65 0 0 0 1.82.33h0a1.65 1.65 0 0 0 1-1.51V3a2 2 0 1 1 4 0v.09a1.65 1.65 0 0 0 1 1.51h0a1.65 1.65 0 0 0 1.82-.33l.06-.06a2 2 0 1 1 2.83 2.83l-.06.06a1.65 1.65 0 0 0-.33 1.82v0a1.65 1.65 0 0 0 1.51 1H21a2 2 0 1 1 0 4h-.09a1.65 1.65 0 0 0-1.51 1z"></path>
                                </svg>
                                {ki.modules.map((svc, idx) => (
                                  <span
                                    key={svc.module_key}
                                    style={{
                                      color: "var(--admin-text, #222)",
                                    }}>
                                    {svc.label}
                                    {idx < ki.modules.length - 1 ? "," : ""}
                                  </span>
                                ))}
                              </span>
                            </>
                          )}
                        </div>
                        {/* Telefonnummer-Mapping (dedicated/shared + Failsafe-Info) */}
                        <div
                          style={{
                            display: "flex",
                            flexWrap: "wrap",
                            alignItems: "center",
                            gap: "6px",
                            marginTop: "4px",
                            fontSize: "12px",
                            color: "var(--admin-text-secondary, #717171)",
                          }}>
                          <svg
                            width="12"
                            height="12"
                            viewBox="0 0 24 24"
                            fill="none"
                            stroke="currentColor"
                            strokeWidth="2"
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            aria-hidden="true">
                            <path d="M22 16.92v3a2 2 0 0 1-2.18 2 19.79 19.79 0 0 1-8.63-3.07 19.5 19.5 0 0 1-6-6 19.79 19.79 0 0 1-3.07-8.67A2 2 0 0 1 4.11 2h3a2 2 0 0 1 2 1.72 12.84 12.84 0 0 0 .7 2.81 2 2 0 0 1-.45 2.11L8.09 9.91a16 16 0 0 0 6 6l1.27-1.27a2 2 0 0 1 2.11-.45 12.84 12.84 0 0 0 2.81.7A2 2 0 0 1 22 16.92z"></path>
                          </svg>
                          {ki.phone && ki.phone.phone_number ? (
                            <>
                              <span
                                style={{
                                  fontFamily:
                                    "ui-monospace, SFMono-Regular, monospace",
                                  color: "var(--admin-text, #222)",
                                }}>
                                {ki.phone.phone_number}
                              </span>
                              <span
                                style={{
                                  display: "inline-flex",
                                  alignItems: "center",
                                  padding: "1px 6px",
                                  borderRadius: "9px",
                                  fontSize: "10px",
                                  fontWeight: 600,
                                  textTransform: "uppercase",
                                  letterSpacing: "0.3px",
                                  background:
                                    ki.phone.routing_mode === "shared"
                                      ? "rgba(0, 138, 5, 0.12)"
                                      : "var(--admin-bg-subtle, #f0f0f0)",
                                  color:
                                    ki.phone.routing_mode === "shared"
                                      ? "#008a05"
                                      : "var(--admin-text-secondary, #717171)",
                                  border:
                                    ki.phone.routing_mode === "shared"
                                      ? "1px solid rgba(0, 138, 5, 0.25)"
                                      : "1px solid var(--cn-border, #ebebeb)",
                                }}>
                                {ki.phone.routing_mode === "shared"
                                  ? "Shared"
                                  : "Eigene"}
                              </span>
                              {ki.phone.routing_mode === "shared" &&
                                ki.phone.forward_source_e164 && (
                                  <span style={{ opacity: 0.85 }}>
                                    · weitergeleitet von{" "}
                                    <span
                                      style={{
                                        fontFamily:
                                          "ui-monospace, SFMono-Regular, monospace",
                                        color: "var(--admin-text, #222)",
                                      }}>
                                      {ki.phone.forward_source_e164}
                                    </span>
                                  </span>
                                )}
                              {ki.phone.shadow_phone_number && (
                                <span style={{ opacity: 0.85 }}>
                                  · Schatten{" "}
                                  <span
                                    style={{
                                      fontFamily:
                                        "ui-monospace, SFMono-Regular, monospace",
                                      color: "var(--admin-text, #222)",
                                    }}>
                                    {ki.phone.shadow_phone_number}
                                  </span>
                                  {ki.phone.shadow_until && (
                                    <>
                                      {" "}
                                      bis{" "}
                                      {new Date(
                                        ki.phone.shadow_until,
                                      ).toLocaleDateString("de-DE", {
                                        day: "2-digit",
                                        month: "2-digit",
                                        year: "2-digit",
                                      })}
                                    </>
                                  )}
                                </span>
                              )}
                            </>
                          ) : (
                            <span style={{ opacity: 0.7 }}>
                              Keine Rufnummer zugeordnet
                            </span>
                          )}
                        </div>
                      </div>
                    ))}
                  </div>
                  <div className="admin_product_user_section_divider">
                    <div className="admin_product_user_section_title_row">
                      <h3 className="admin_product_user_section_title">
                        Anrufe
                      </h3>
                      <span className="admin_product_user_section_count">
                        {productUserCalls.length}
                      </span>
                    </div>
                    {!productUserCallsLoading && productUserCallsError && (
                      <p className="admin_product_user_log_empty admin_product_user_log_empty--error">
                        Fehler: {productUserCallsError}
                      </p>
                    )}
                    {!productUserCallsLoading &&
                      !productUserCallsError &&
                      productUserCalls.length === 0 && (
                        <p className="admin_product_user_log_empty">
                          Keine Anrufe vorhanden.
                        </p>
                      )}
                    {productUserCalls.length > 0 && (
                      <div className="admin_product_user_call_list">
                        {productUserCalls.map((c) => {
                          const startedAt = c.started_at
                            ? new Date(c.started_at)
                            : null;
                          const dateStr = startedAt
                            ? startedAt.toLocaleString("de-DE", {
                                day: "2-digit",
                                month: "2-digit",
                                year: "2-digit",
                                hour: "2-digit",
                                minute: "2-digit",
                              })
                            : "–";
                          const durationStr = c.duration_seconds
                            ? `${Math.floor(c.duration_seconds / 60)}:${String(c.duration_seconds % 60).padStart(2, "0")}`
                            : "–";
                          const callerLabel =
                            c.contact_name ||
                            c.caller_phone ||
                            (c.is_test ? "Test" : "Unbekannt");
                          return (
                            <button
                              key={c.id}
                              type="button"
                              className="admin_product_user_call_row"
                              onClick={() => {
                                setCallsFilters((p) => ({
                                  ...p,
                                  userId: String(selectedProductUserId),
                                }));
                                setSelectedCallId(c.id);
                                setActiveView("anrufe");
                              }}>
                              <div className="admin_product_user_call_meta">
                                <span className="admin_product_user_call_icon">
                                  {c.matched_tool_name ? "🔧" : "📞"}
                                </span>
                                <span className="admin_product_user_call_ts">
                                  {dateStr}
                                </span>
                                <span className="admin_product_user_call_duration">
                                  {durationStr}
                                </span>
                                {c.is_test ? (
                                  <span className="admin_product_user_call_badge admin_product_user_call_badge--test">
                                    Test
                                  </span>
                                ) : null}
                                {c.matched_tool_name ? (
                                  <span className="admin_product_user_call_badge admin_product_user_call_badge--tool">
                                    {c.matched_tool_name}
                                  </span>
                                ) : null}
                              </div>
                              <div className="admin_product_user_call_caller">
                                {callerLabel}
                              </div>
                              {c.call_summary ? (
                                <div className="admin_product_user_call_summary">
                                  {c.call_summary}
                                </div>
                              ) : null}
                            </button>
                          );
                        })}
                      </div>
                    )}
                  </div>
                </div>
              </>
            ) : productDetailOpen && activeView === "kis" && selectedAi ? (
              <>
                <div className="admin_detail_header">
                  <button
                    type="button"
                    className="admin_detail_back"
                    onClick={() => setSelectedAiId(null)}>
                    &larr;
                  </button>
                  <span className="admin_detail_heading">
                    <strong>
                      {selectedAi.ai_name ||
                        selectedAi.display_name ||
                        "Assistenz"}
                    </strong>
                    <br />
                    <span className="admin_detail_heading_sub">
                      {selectedAi.user_display_name ||
                        selectedAi.user_email ||
                        (selectedAi.user_id
                          ? "User #" + selectedAi.user_id
                          : "ohne Owner")}
                    </span>
                  </span>
                  <div className="admin_detail_header_actions">
                    <button
                      type="button"
                      className="admin_btn admin_detail_header_btn"
                      onClick={() => {}}>
                      KI testen
                    </button>
                  </div>
                </div>
                <div
                  ref={productBodyRef}
                  className="admin_detail_body"
                  style={{ padding: "1rem", flexDirection: "column", gap: 0 }}>
                  <div
                    style={{
                      display: "grid",
                      gridTemplateColumns: "120px 1fr",
                      rowGap: "10px",
                      columnGap: "12px",
                      fontSize: "13px",
                    }}>
                    <span
                      style={{ color: "var(--admin-text-secondary, #717171)" }}>
                      KI-ID
                    </span>
                    <span
                      style={{
                        fontFamily: "monospace",
                        color: "var(--admin-text, #222)",
                      }}>
                      #{selectedAi.id}
                    </span>

                    <span
                      style={{ color: "var(--admin-text-secondary, #717171)" }}>
                      KI-Name
                    </span>
                    <span style={{ color: "var(--admin-text, #222)" }}>
                      {selectedAi.ai_name || "–"}
                    </span>

                    <span
                      style={{ color: "var(--admin-text-secondary, #717171)" }}>
                      Anzeigename
                    </span>
                    <span style={{ color: "var(--admin-text, #222)" }}>
                      {selectedAi.display_name || "–"}
                    </span>

                    <span
                      style={{ color: "var(--admin-text-secondary, #717171)" }}>
                      Rolle
                    </span>
                    <span style={{ color: "var(--admin-text, #222)" }}>
                      {selectedAi.ai_role || "–"}
                    </span>

                    <span
                      style={{ color: "var(--admin-text-secondary, #717171)" }}>
                      Agent
                    </span>
                    <span style={{ color: "var(--admin-text, #222)" }}>
                      {selectedAi.industry_label ||
                        selectedAi.industry_slug ||
                        "–"}
                      {selectedAi.industry &&
                      selectedAi.industry !== selectedAi.industry_slug
                        ? ` (${selectedAi.industry})`
                        : ""}
                    </span>

                    <span
                      style={{ color: "var(--admin-text-secondary, #717171)" }}>
                      Owner
                    </span>
                    <span style={{ color: "var(--admin-text, #222)" }}>
                      {selectedAi.user_display_name || "–"}
                      {selectedAi.user_email ? (
                        <>
                          <br />
                          <span
                            style={{
                              color: "var(--admin-text-secondary, #717171)",
                              fontSize: "12px",
                            }}>
                            {selectedAi.user_email}
                          </span>
                        </>
                      ) : null}
                    </span>
                  </div>
                </div>
              </>
            ) : productDetailOpen &&
              activeView === "anrufe" &&
              callDetail &&
              callDetail.call ? (
              (() => {
                const c = callDetail.call;
                const messages = callDetail.messages || [];
                const audio = callDetail.audio || {};
                const startedAt = c.started_at ? new Date(c.started_at) : null;
                const dateStr = startedAt
                  ? startedAt.toLocaleString("de-DE")
                  : "–";
                const durationStr = c.duration_seconds
                  ? `${Math.floor(c.duration_seconds / 60)}:${String(c.duration_seconds % 60).padStart(2, "0")}`
                  : "–";
                // Kontakt-Overlay (per-User): zeigt den im Adressbuch DES
                // Anruf-Owners hinterlegten Namen, niemals zwischen Usern
                // gemischt. Backend JOIN: users_contacts.user_id = c.user_id
                // AND phone_e164 = c.caller_phone.
                const callerName =
                  c.relationship_person_name ||
                  c.contact_name ||
                  c.caller_phone ||
                  (c.is_test ? "Test" : "Unbekannt");
                const providerLabel =
                  c.provider_display_name ||
                  c.provider_email ||
                  `#${c.user_id || "?"}`;
                const matchedMsgId = c.matched_at_message_id || null;
                const tokenParam = adminToken
                  ? `?token=${encodeURIComponent(adminToken)}`
                  : "";
                const audioUrl = (() => {
                  if (callAudioType === "combined" && audio.has_combined_audio)
                    return `${apiBase}/api/admin/calls/${c.id}/audio/combined${tokenParam}`;
                  if (callAudioType === "user" && audio.has_user_audio)
                    return `${apiBase}/api/admin/calls/${c.id}/audio/user${tokenParam}`;
                  if (
                    callAudioType === "assistant" &&
                    audio.has_assistant_audio
                  )
                    return `${apiBase}/api/admin/calls/${c.id}/audio/assistant${tokenParam}`;
                  return null;
                })();
                return (
                  <>
                    <div className="admin_detail_header">
                      <button
                        type="button"
                        className="admin_detail_back"
                        onClick={() => setSelectedCallId(null)}>
                        &larr;
                      </button>
                      <span className="admin_detail_heading">
                        <strong>{callerName}</strong>
                        <br />
                        <span className="admin_detail_heading_sub">
                          {dateStr} · {durationStr} · {providerLabel}
                        </span>
                      </span>
                    </div>
                    <div
                      ref={productBodyRef}
                      className="admin_detail_body"
                      style={{
                        padding: "16px",
                        flexDirection: "column",
                        gap: 0,
                      }}>
                      {c.fatal_error && (
                        <div
                          style={{
                            marginBottom: "16px",
                            padding: "12px 14px",
                            border: "1px solid #c13515",
                            borderRadius: "8px",
                            background: "rgba(193,53,21,0.08)",
                          }}>
                          <div
                            style={{
                              display: "flex",
                              alignItems: "center",
                              gap: "8px",
                              marginBottom: "6px",
                            }}>
                            <span
                              style={{
                                display: "inline-block",
                                padding: "2px 8px",
                                borderRadius: "10px",
                                background: "#c13515",
                                color: "#fff",
                                fontSize: "11px",
                                fontWeight: 700,
                                letterSpacing: "0.4px",
                              }}>
                              FATAL ERROR
                            </span>
                            <span
                              style={{
                                fontSize: "12px",
                                fontFamily: "monospace",
                                color: "#c13515",
                              }}>
                              {c.fatal_error}
                            </span>
                          </div>
                          <div
                            style={{
                              fontSize: "12px",
                              color: "var(--admin-text, #222)",
                              lineHeight: 1.5,
                              whiteSpace: "pre-wrap",
                            }}>
                            {c.fatal_error === "shared_did_unmatched"
                              ? "Anruf landete auf der SHARED-Twilio-Nummer, aber das ForwardedFrom-Routing fand kein passendes User-Mapping. Der Webhook hat den Anruf mit einer Ansage und Hangup beendet (kein KI-Stream)."
                              : "Der Anruf wurde nicht an die KI durchgestellt und in einen Failsafe-Pfad geleitet."}
                          </div>
                          {c.call_summary && (
                            <div
                              style={{
                                marginTop: "8px",
                                padding: "6px 8px",
                                fontSize: "11px",
                                fontFamily: "monospace",
                                background: "var(--admin-bg-input, #fff)",
                                border: "1px solid var(--admin-border, #ddd)",
                                borderRadius: "6px",
                                color: "var(--admin-text-secondary, #717171)",
                                whiteSpace: "pre-wrap",
                                wordBreak: "break-word",
                              }}>
                              {c.call_summary}
                            </div>
                          )}
                        </div>
                      )}
                      {/* Mapping-Card */}
                      <div
                        style={{
                          marginBottom: "16px",
                          padding: "12px",
                          border: "1px solid var(--admin-border, #ddd)",
                          borderRadius: "8px",
                          background: c.matched_tool_name
                            ? "var(--admin-accent-soft, rgba(255,90,95,0.08))"
                            : "var(--admin-bg-card, #fafafa)",
                        }}>
                        <div
                          style={{
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "space-between",
                            marginBottom: "6px",
                          }}>
                          <span
                            style={{
                              fontSize: "11px",
                              fontWeight: 700,
                              textTransform: "uppercase",
                              color: "var(--admin-text-secondary, #717171)",
                              letterSpacing: "0.4px",
                            }}>
                            Gemapptes Tool (Intent)
                          </span>
                          {c.is_test ? (
                            <span
                              style={{
                                fontSize: "10px",
                                padding: "2px 6px",
                                borderRadius: "8px",
                                background: "var(--admin-bg-input, #fff)",
                                border: "1px solid var(--admin-border, #ddd)",
                              }}>
                              TEST
                            </span>
                          ) : null}
                        </div>
                        {c.matched_tool_name ? (
                          <>
                            <div
                              style={{
                                fontSize: "14px",
                                fontWeight: 700,
                                color: "var(--admin-accent, #FF5A5F)",
                                marginBottom: "6px",
                                fontFamily: "monospace",
                              }}>
                              {c.matched_tool_name}
                            </div>
                            {c.matched_reason && (
                              <div
                                style={{
                                  fontSize: "12px",
                                  color: "var(--admin-text, #222)",
                                  lineHeight: 1.5,
                                  marginBottom: "8px",
                                  whiteSpace: "pre-wrap",
                                }}>
                                {c.matched_reason}
                              </div>
                            )}
                            {c.matched_args &&
                              Object.keys(c.matched_args).length > 0 && (
                                <div
                                  style={{
                                    fontSize: "11px",
                                    fontFamily: "monospace",
                                    padding: "6px 8px",
                                    background: "var(--admin-bg-input, #fff)",
                                    border:
                                      "1px solid var(--admin-border, #ddd)",
                                    borderRadius: "6px",
                                    color: "var(--admin-text, #222)",
                                    overflowX: "auto",
                                  }}>
                                  {JSON.stringify(c.matched_args, null, 2)}
                                </div>
                              )}
                          </>
                        ) : (
                          <div
                            style={{
                              fontSize: "12px",
                              color: "var(--admin-text-secondary, #717171)",
                              fontStyle: "italic",
                            }}>
                            Kein Tool gemappt – die KI hat in diesem Anruf keine
                            Modul-Funktion ausgeloest.
                          </div>
                        )}
                        {c.call_summary && (
                          <div
                            style={{
                              marginTop: "8px",
                              fontSize: "12px",
                              color: "var(--admin-text-secondary, #717171)",
                            }}>
                            <strong
                              style={{ color: "var(--admin-text, #222)" }}>
                              Summary:
                            </strong>{" "}
                            {c.call_summary}
                          </div>
                        )}
                      </div>

                      {/* Audio-Player */}
                      <div
                        style={{
                          marginBottom: "16px",
                          padding: "12px",
                          border: "1px solid var(--admin-border, #ddd)",
                          borderRadius: "8px",
                          background: "var(--admin-bg-card, #fafafa)",
                        }}>
                        <div
                          style={{
                            display: "flex",
                            gap: "6px",
                            marginBottom: "8px",
                          }}>
                          {audio.has_combined_audio && (
                            <button
                              type="button"
                              onClick={() => setCallAudioType("combined")}
                              style={{
                                padding: "4px 10px",
                                fontSize: "11px",
                                borderRadius: "6px",
                                border:
                                  "1px solid " +
                                  (callAudioType === "combined"
                                    ? "var(--admin-accent, #FF5A5F)"
                                    : "var(--admin-border, #ddd)"),
                                background:
                                  callAudioType === "combined"
                                    ? "var(--admin-accent-soft, rgba(255,90,95,0.12))"
                                    : "transparent",
                                color:
                                  callAudioType === "combined"
                                    ? "var(--admin-accent, #FF5A5F)"
                                    : "var(--admin-text, #222)",
                                cursor: "pointer",
                                fontWeight: 600,
                              }}>
                              Kombiniert
                            </button>
                          )}
                          {audio.has_user_audio && (
                            <button
                              type="button"
                              onClick={() => setCallAudioType("user")}
                              style={{
                                padding: "4px 10px",
                                fontSize: "11px",
                                borderRadius: "6px",
                                border:
                                  "1px solid " +
                                  (callAudioType === "user"
                                    ? "var(--admin-accent, #FF5A5F)"
                                    : "var(--admin-border, #ddd)"),
                                background:
                                  callAudioType === "user"
                                    ? "var(--admin-accent-soft, rgba(255,90,95,0.12))"
                                    : "transparent",
                                color:
                                  callAudioType === "user"
                                    ? "var(--admin-accent, #FF5A5F)"
                                    : "var(--admin-text, #222)",
                                cursor: "pointer",
                                fontWeight: 600,
                              }}>
                              Anrufer
                            </button>
                          )}
                          {audio.has_assistant_audio && (
                            <button
                              type="button"
                              onClick={() => setCallAudioType("assistant")}
                              style={{
                                padding: "4px 10px",
                                fontSize: "11px",
                                borderRadius: "6px",
                                border:
                                  "1px solid " +
                                  (callAudioType === "assistant"
                                    ? "var(--admin-accent, #FF5A5F)"
                                    : "var(--admin-border, #ddd)"),
                                background:
                                  callAudioType === "assistant"
                                    ? "var(--admin-accent-soft, rgba(255,90,95,0.12))"
                                    : "transparent",
                                color:
                                  callAudioType === "assistant"
                                    ? "var(--admin-accent, #FF5A5F)"
                                    : "var(--admin-text, #222)",
                                cursor: "pointer",
                                fontWeight: 600,
                              }}>
                              KI
                            </button>
                          )}
                        </div>
                        {audioUrl ? (
                          <audio
                            key={audioUrl}
                            controls
                            style={{ width: "100%" }}
                            src={audioUrl}
                          />
                        ) : (
                          <div
                            style={{
                              fontSize: "12px",
                              color: "var(--admin-text-secondary, #717171)",
                              fontStyle: "italic",
                            }}>
                            Keine Audio-Aufnahme verfuegbar.
                          </div>
                        )}
                      </div>

                      {/* Transkript */}
                      <div style={{ marginBottom: "16px" }}>
                        <h3
                          style={{
                            fontSize: "13px",
                            fontWeight: 700,
                            marginBottom: "8px",
                            color: "var(--admin-text, #222)",
                          }}>
                          Transkript ({messages.length})
                        </h3>
                        {messages.length === 0 ? (
                          <p
                            style={{
                              fontSize: "12px",
                              color: "var(--admin-text-secondary, #717171)",
                            }}>
                            Keine Nachrichten.
                          </p>
                        ) : (
                          <div
                            style={{
                              display: "flex",
                              flexDirection: "column",
                              gap: "6px",
                            }}>
                            {messages.map((m) => {
                              const isMatched =
                                matchedMsgId && m.id === matchedMsgId;
                              const isUser = m.role === "user";
                              const bubbleClass = isMatched
                                ? ""
                                : isUser
                                  ? "admin_anruf_bubble--user"
                                  : "admin_anruf_bubble--assistant";
                              return (
                                <div
                                  key={m.id}
                                  className={bubbleClass}
                                  style={{
                                    alignSelf: isUser
                                      ? "flex-start"
                                      : "flex-end",
                                    maxWidth: "85%",
                                    padding: "8px 10px",
                                    borderRadius: isUser
                                      ? "12px 12px 12px 2px"
                                      : "12px 12px 2px 12px",
                                    border:
                                      "1px solid " +
                                      (isMatched
                                        ? "var(--admin-accent, #FF5A5F)"
                                        : isUser
                                          ? "var(--admin-border, #ddd)"
                                          : "transparent"),
                                    ...(isMatched && {
                                      background:
                                        "var(--admin-accent-soft, rgba(255,90,95,0.12))",
                                    }),
                                    position: "relative",
                                  }}>
                                  <div
                                    style={{
                                      display: "flex",
                                      justifyContent: "space-between",
                                      alignItems: "center",
                                      marginBottom: "3px",
                                      gap: "8px",
                                    }}>
                                    <span
                                      style={{
                                        fontSize: "10px",
                                        fontWeight: 700,
                                        textTransform: "uppercase",
                                        letterSpacing: "0.4px",
                                        color: isUser
                                          ? "#222222"
                                          : "var(--admin-text-secondary, #717171)",
                                      }}>
                                      {isUser ? "Anrufer" : "KI"}
                                      {isMatched ? (
                                        <span
                                          style={{
                                            marginLeft: "6px",
                                            padding: "1px 6px",
                                            borderRadius: "8px",
                                            background:
                                              "var(--admin-accent, #FF5A5F)",
                                            color: "#fff",
                                            fontSize: "9px",
                                          }}>
                                          ↳ Tool ausgeloest
                                        </span>
                                      ) : null}
                                    </span>
                                    <span
                                      style={{
                                        fontSize: "10px",
                                        color:
                                          "var(--admin-text-secondary, #717171)",
                                      }}>
                                      {m.created_at
                                        ? new Date(
                                            m.created_at,
                                          ).toLocaleTimeString("de-DE", {
                                            hour: "2-digit",
                                            minute: "2-digit",
                                            second: "2-digit",
                                          })
                                        : ""}
                                    </span>
                                  </div>
                                  <div
                                    style={{
                                      fontSize: "13px",
                                      color: isUser
                                        ? "#222222"
                                        : "var(--admin-text, #222)",
                                      lineHeight: 1.5,
                                      whiteSpace: "pre-wrap",
                                      wordBreak: "break-word",
                                    }}>
                                    {m.content}
                                  </div>
                                </div>
                              );
                            })}
                          </div>
                        )}
                      </div>
                    </div>
                  </>
                );
              })()
            ) : productDetailOpen &&
              activeView === "anrufe" &&
              callDetailLoading ? (
              <div
                className="admin_detail_body"
                style={{
                  padding: "20px",
                  color: "var(--admin-text-secondary, #717171)",
                }}
              />
            ) : productDetailOpen &&
              activeView === "aicoworker" &&
              aicoworkerDetail ? (
              <>
                <div className="admin_detail_header">
                  <button
                    type="button"
                    className="admin_detail_back"
                    onClick={() => {
                      flushProductPendingSave();
                      setSelectedAicoworkerId(null);
                    }}>
                    &larr;
                  </button>
                  <span className="admin_detail_heading">
                    <strong>{aicoworkerDetail.label}</strong>
                    <br />
                    <span className="admin_detail_heading_sub">
                      {aicoworkerDetail.slug}
                      {!aicoworkerDetail.is_active ? " (inaktiv)" : ""}
                    </span>
                  </span>
                  <div className="admin_detail_header_actions">
                    {productAutoSaveRingActive && (
                      <AutoSaveProgressRing
                        key={productAutoSaveRingKey}
                        durationMs={AUTO_SAVE_MS}
                      />
                    )}
                  </div>
                </div>
                <div
                  ref={productBodyRef}
                  className="admin_detail_body"
                  style={{ padding: "20px", flexDirection: "column", gap: 0 }}>
                  <div
                    style={{
                      display: "flex",
                      justifyContent: "flex-end",
                      marginBottom: "16px",
                    }}>
                    <label
                      style={{
                        display: "flex",
                        alignItems: "center",
                        gap: "6px",
                        fontSize: "13px",
                        cursor: "pointer",
                      }}>
                      <input
                        type="checkbox"
                        checked={!!aicoworkerDetail.is_active}
                        onChange={(e) =>
                          setAicoworkerDetail((prev) => ({
                            ...prev,
                            is_active: e.target.checked ? 1 : 0,
                          }))
                        }
                      />
                      Aktiv
                    </label>
                  </div>
                  <div style={{ marginBottom: "16px" }}>
                    <label
                      style={{
                        display: "block",
                        fontWeight: 600,
                        marginBottom: "6px",
                        fontSize: "13px",
                      }}>
                      Label
                    </label>
                    <input
                      type="text"
                      value={aicoworkerDetail.label || ""}
                      onChange={(e) =>
                        setAicoworkerDetail((prev) => ({
                          ...prev,
                          label: e.target.value,
                        }))
                      }
                      style={{
                        width: "100%",
                        padding: "8px 10px",
                        border: "1px solid var(--admin-border, #ddd)",
                        borderRadius: "8px",
                        fontSize: "13px",
                        boxSizing: "border-box",
                        background: "var(--admin-bg-input, #fff)",
                        color: "var(--admin-text, #222)",
                      }}
                    />
                  </div>
                  <div style={{ marginBottom: "16px" }}>
                    <label
                      style={{
                        display: "block",
                        fontWeight: 600,
                        marginBottom: "6px",
                        fontSize: "13px",
                      }}>
                      Beschreibung
                    </label>
                    <input
                      type="text"
                      value={aicoworkerDetail.description || ""}
                      onChange={(e) =>
                        setAicoworkerDetail((prev) => ({
                          ...prev,
                          description: e.target.value,
                        }))
                      }
                      style={{
                        width: "100%",
                        padding: "8px 10px",
                        border: "1px solid var(--admin-border, #ddd)",
                        borderRadius: "8px",
                        fontSize: "13px",
                        boxSizing: "border-box",
                        background: "var(--admin-bg-input, #fff)",
                        color: "var(--admin-text, #222)",
                      }}
                      placeholder="Kurze Beschreibung des Agenten"
                    />
                  </div>
                  <div style={{ marginBottom: "16px" }}>
                    <label
                      style={{
                        display: "block",
                        fontWeight: 600,
                        marginBottom: "6px",
                        fontSize: "13px",
                      }}>
                      Icon
                    </label>
                    <div
                      style={{
                        display: "flex",
                        flexWrap: "wrap",
                        gap: "6px",
                        padding: "8px",
                        border: "1px solid var(--admin-border, #ddd)",
                        borderRadius: "8px",
                        background: "var(--admin-bg-input, #fff)",
                      }}>
                      <button
                        type="button"
                        onClick={() =>
                          setAicoworkerDetail((prev) => ({ ...prev, icon: "" }))
                        }
                        title="Kein Icon"
                        style={{
                          width: "32px",
                          height: "32px",
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "center",
                          border:
                            "1px solid " +
                            (!aicoworkerDetail.icon
                              ? "var(--admin-accent, #FF5A5F)"
                              : "var(--admin-border, #ddd)"),
                          background: !aicoworkerDetail.icon
                            ? "var(--admin-accent-soft, rgba(255,90,95,0.12))"
                            : "transparent",
                          color: "var(--admin-text-secondary, #717171)",
                          borderRadius: "6px",
                          cursor: "pointer",
                          padding: 0,
                          fontSize: "11px",
                        }}>
                        –
                      </button>
                      {MODULE_ICONS.map((it) => {
                        const active = aicoworkerDetail.icon === it.key;
                        return (
                          <button
                            key={it.key}
                            type="button"
                            onClick={() =>
                              setAicoworkerDetail((prev) => ({
                                ...prev,
                                icon: it.key,
                              }))
                            }
                            title={it.key}
                            style={{
                              width: "32px",
                              height: "32px",
                              display: "flex",
                              alignItems: "center",
                              justifyContent: "center",
                              border:
                                "1px solid " +
                                (active
                                  ? "var(--admin-accent, #FF5A5F)"
                                  : "var(--admin-border, #ddd)"),
                              background: active
                                ? "var(--admin-accent-soft, rgba(255,90,95,0.12))"
                                : "transparent",
                              color: active
                                ? "var(--admin-accent, #FF5A5F)"
                                : "var(--admin-text, #222)",
                              borderRadius: "6px",
                              cursor: "pointer",
                              padding: 0,
                            }}>
                            <ModuleIconSvg name={it.key} size={22} />
                          </button>
                        );
                      })}
                    </div>
                  </div>
                  <div style={{ marginBottom: "16px" }}>
                    <label
                      style={{
                        display: "block",
                        fontWeight: 600,
                        marginBottom: "6px",
                        fontSize: "13px",
                      }}>
                      Systemprompt
                    </label>
                    <textarea
                      value={aicoworkerDetail.system_prompt || ""}
                      onChange={(e) =>
                        setAicoworkerDetail((prev) => ({
                          ...prev,
                          system_prompt: e.target.value,
                        }))
                      }
                      style={{
                        width: "100%",
                        minHeight: "180px",
                        padding: "10px",
                        border: "1px solid var(--admin-border, #ddd)",
                        borderRadius: "8px",
                        fontSize: "13px",
                        lineHeight: "1.5",
                        boxSizing: "border-box",
                        resize: "vertical",
                        fontFamily: "inherit",
                        background: "var(--admin-bg-input, #fff)",
                        color: "var(--admin-text, #222)",
                      }}
                      placeholder="Systemprompt für diesen Agenten..."
                    />
                  </div>
                  <div
                    style={{
                      borderTop: "1px solid var(--admin-border, #ddd)",
                      paddingTop: "16px",
                      marginBottom: "16px",
                      marginTop: "8px",
                    }}>
                    <label
                      style={{
                        display: "block",
                        fontWeight: 600,
                        marginBottom: "10px",
                        fontSize: "14px",
                      }}>
                      Zugewiesene Module
                    </label>
                    <p
                      style={{
                        fontSize: "12px",
                        color: "var(--admin-text-secondary, #717171)",
                        margin: "0 0 12px 0",
                      }}>
                      Klicke auf ein Modul, um es diesem Agenten
                      zuzuweisen oder zu entfernen. Datenquelle und weitere
                      Einstellungen werden im Tab "Module" festgelegt.
                    </p>
                    {(() => {
                      const registryIds = new Set(moduleList.map((s) => s.id));
                      const orphanAssignments = (
                        aicoworkerDetail.modules || []
                      ).filter((s) => !registryIds.has(s.module_id));
                      const buttons = [
                        ...moduleList.map((svc) => {
                          const assigned = (
                            aicoworkerDetail.modules || []
                          ).find((s) => s.module_id === svc.id);
                          return {
                            key: "registry-" + svc.id,
                            label: svc.label,
                            module_key: svc.module_key,
                            icon: svc.icon,
                            assigned,
                            isActive: !!assigned,
                            isDisabled: svc.is_active === 0,
                            isOrphan: false,
                            onAdd: () =>
                              addAicoworkerModule(aicoworkerDetail.id, svc.id),
                          };
                        }),
                        ...orphanAssignments.map((svc) => ({
                          key: "orphan-" + (svc.module_id || svc.module_key),
                          label: svc.label || svc.module_key,
                          module_key: svc.module_key,
                          icon: null,
                          assigned: svc,
                          isActive: true,
                          isDisabled: false,
                          isOrphan: true,
                          onAdd: null,
                        })),
                      ];
                      if (buttons.length === 0) {
                        return (
                          <p
                            style={{
                              fontSize: "13px",
                              color: "var(--admin-text-secondary, #717171)",
                              margin: "8px 0",
                            }}>
                            Keine Module vorhanden. Lege im Tab "Module" Module
                            an.
                          </p>
                        );
                      }
                      return (
                        <div
                          style={{
                            display: "flex",
                            flexWrap: "wrap",
                            gap: "8px",
                          }}>
                          {buttons.map((btn) => (
                            <button
                              key={btn.key}
                              type="button"
                              disabled={btn.isDisabled}
                              onClick={() => {
                                if (btn.isDisabled) return;
                                if (btn.isActive) {
                                  removeAicoworkerModule(
                                    aicoworkerDetail.id,
                                    btn.assigned.id,
                                  );
                                } else if (btn.onAdd) {
                                  btn.onAdd();
                                }
                              }}
                              title={
                                btn.isDisabled
                                  ? btn.module_key +
                                    " (aktuell nicht verfügbar)"
                                  : btn.isOrphan
                                    ? btn.module_key +
                                      " (nicht in Modul-Registry – nur entfernen möglich)"
                                    : btn.module_key
                              }
                              style={{
                                display: "inline-flex",
                                alignItems: "center",
                                gap: "8px",
                                padding: "8px 14px",
                                border:
                                  "1px " +
                                  (btn.isOrphan ? "dashed" : "solid") +
                                  " " +
                                  (btn.isDisabled
                                    ? "var(--admin-border, #ddd)"
                                    : btn.isActive
                                      ? "var(--admin-accent, #FF5A5F)"
                                      : "var(--admin-border, #ddd)"),
                                background: btn.isDisabled
                                  ? "var(--admin-bg-card, #f5f5f5)"
                                  : btn.isActive
                                    ? "var(--admin-accent-soft, rgba(255,90,95,0.12))"
                                    : "var(--admin-bg-input, #fff)",
                                color: btn.isDisabled
                                  ? "var(--admin-text-secondary, #999)"
                                  : btn.isActive
                                    ? "var(--admin-accent, #FF5A5F)"
                                    : "var(--admin-text, #222)",
                                borderRadius: "999px",
                                cursor: btn.isDisabled
                                  ? "not-allowed"
                                  : "pointer",
                                fontSize: "13px",
                                fontWeight: 600,
                                opacity: btn.isDisabled ? 0.55 : 1,
                                transition:
                                  "background 0.15s, border-color 0.15s, color 0.15s",
                              }}>
                              {btn.icon ? (
                                <ModuleIconSvg name={btn.icon} size={16} />
                              ) : null}
                              <span>{btn.label}</span>
                            </button>
                          ))}
                        </div>
                      );
                    })()}
                  </div>
                </div>
              </>
            ) : productDetailOpen && activeView === "module" && moduleDetail ? (
              <>
                <div className="admin_detail_header">
                  <button
                    type="button"
                    className="admin_detail_back"
                    onClick={() => {
                      flushProductPendingSave();
                      setSelectedModuleId(null);
                    }}>
                    &larr;
                  </button>
                  <span className="admin_detail_heading">
                    <strong>{moduleDetail.label}</strong>
                    <br />
                    <span className="admin_detail_heading_sub">
                      {moduleDetail.tools_count != null
                        ? moduleDetail.tools_count +
                          " Tool" +
                          (Number(moduleDetail.tools_count) === 1 ? "" : "s")
                        : moduleDetail.module_key || ""}
                    </span>
                  </span>
                  <div className="admin_detail_header_actions">
                    {(() => {
                      const acList = (aicoworkers || []).filter(
                        (ac) =>
                          Array.isArray(ac.modules) &&
                          ac.modules.some((s) => s.module_id === moduleDetail.id),
                      );
                      if (acList.length === 0) {
                        return (
                          <span
                            style={{
                              fontSize: "11px",
                              color: "var(--admin-text-secondary, #717171)",
                              whiteSpace: "nowrap",
                              maxWidth: "140px",
                              overflow: "hidden",
                              textOverflow: "ellipsis",
                            }}>
                            Keinem Agenten zugeordnet
                          </span>
                        );
                      }
                      const selectedAc =
                        acList.find((a) => a.id === moduleHeaderToolsAicoworkerId) ||
                        acList[0];
                      const mapping = selectedAc.modules.find(
                        (s) => s.module_id === moduleDetail.id,
                      );
                      const toolsOn =
                        !mapping ||
                        mapping.telefonagent_tools_enabled == null ||
                        Number(mapping.telefonagent_tools_enabled) !== 0;
                      return (
                        <>
                          <span
                            style={{
                              fontSize: "11px",
                              fontWeight: 600,
                              color: "var(--admin-text-secondary, #717171)",
                              whiteSpace: "nowrap",
                            }}>
                            Telefon-Tools
                          </span>
                          {acList.length > 1 ? (
                            <select
                              value={selectedAc.id}
                              onChange={(e) =>
                                setModuleHeaderToolsAicoworkerId(
                                  Number(e.target.value),
                                )
                              }
                              style={{
                                maxWidth: "160px",
                                fontSize: "12px",
                                padding: "4px 8px",
                                borderRadius: "6px",
                                border: "1px solid var(--admin-border, #ddd)",
                                background: "var(--admin-bg-input, #fff)",
                                color: "var(--admin-text, #222)",
                              }}>
                              {acList.map((ac) => (
                                <option key={ac.id} value={ac.id}>
                                  {ac.label || ac.slug}
                                </option>
                              ))}
                            </select>
                          ) : null}
                          <button
                            type="button"
                            className={
                              "admin_toggle" + (toolsOn ? " admin_toggle--on" : "")
                            }
                            onClick={() => {
                              if (!mapping) return;
                              updateAicoworkerModule(selectedAc.id, mapping.id, {
                                telefonagent_tools_enabled: toolsOn ? 0 : 1,
                              });
                            }}
                            aria-label="Telefon-Tools fuer KI-Telefonagent"
                            title="Registry-Tools fuer den KI-Telefonagent (Realtime)">
                            <span className="admin_toggle_knob" />
                          </button>
                        </>
                      );
                    })()}
                    {productAutoSaveRingActive && (
                      <AutoSaveProgressRing
                        key={productAutoSaveRingKey}
                        durationMs={AUTO_SAVE_MS}
                      />
                    )}
                  </div>
                </div>
                <div
                  ref={productBodyRef}
                  className="admin_detail_body"
                  style={{ padding: "20px", flexDirection: "column", gap: 0 }}>
                  <div style={{ marginBottom: "14px" }}>
                    <label
                      style={{
                        display: "block",
                        fontWeight: 600,
                        marginBottom: "4px",
                        fontSize: "13px",
                      }}>
                      Label
                    </label>
                    <input
                      type="text"
                      value={moduleDetail.label || ""}
                      onChange={(e) =>
                        setModuleDetail((prev) => ({
                          ...prev,
                          label: e.target.value,
                        }))
                      }
                      style={{
                        width: "100%",
                        padding: "8px 10px",
                        border: "1px solid var(--admin-border, #ddd)",
                        borderRadius: "8px",
                        fontSize: "13px",
                        boxSizing: "border-box",
                        background: "var(--admin-bg-input, #fff)",
                        color: "var(--admin-text, #222)",
                      }}
                    />
                  </div>
                  <div style={{ marginBottom: "14px" }}>
                    <label
                      style={{
                        display: "block",
                        fontWeight: 600,
                        marginBottom: "4px",
                        fontSize: "13px",
                      }}>
                      Beschreibung
                    </label>
                    <input
                      type="text"
                      value={moduleDetail.description || ""}
                      onChange={(e) =>
                        setModuleDetail((prev) => ({
                          ...prev,
                          description: e.target.value,
                        }))
                      }
                      placeholder="Interne Beschreibung des Moduls"
                      style={{
                        width: "100%",
                        padding: "8px 10px",
                        border: "1px solid var(--admin-border, #ddd)",
                        borderRadius: "8px",
                        fontSize: "13px",
                        boxSizing: "border-box",
                        background: "var(--admin-bg-input, #fff)",
                        color: "var(--admin-text, #222)",
                      }}
                    />
                  </div>
                  <div style={{ marginBottom: "14px" }}>
                    <label
                      style={{
                        display: "block",
                        fontWeight: 600,
                        marginBottom: "6px",
                        fontSize: "13px",
                      }}>
                      Icon
                    </label>
                    <div
                      style={{
                        display: "flex",
                        flexWrap: "wrap",
                        gap: "6px",
                        padding: "8px",
                        border: "1px solid var(--admin-border, #ddd)",
                        borderRadius: "8px",
                        background: "var(--admin-bg-input, #fff)",
                      }}>
                      <button
                        type="button"
                        onClick={() =>
                          setModuleDetail((prev) => ({ ...prev, icon: "" }))
                        }
                        title="Kein Icon"
                        style={{
                          width: "32px",
                          height: "32px",
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "center",
                          border:
                            "1px solid " +
                            (!moduleDetail.icon
                              ? "var(--admin-accent, #FF5A5F)"
                              : "var(--admin-border, #ddd)"),
                          background: !moduleDetail.icon
                            ? "var(--admin-accent-soft, rgba(255,90,95,0.12))"
                            : "transparent",
                          color: "var(--admin-text-secondary, #717171)",
                          borderRadius: "6px",
                          cursor: "pointer",
                          padding: 0,
                          fontSize: "11px",
                        }}>
                        –
                      </button>
                      {MODULE_ICONS.map((it) => {
                        const active = moduleDetail.icon === it.key;
                        return (
                          <button
                            key={it.key}
                            type="button"
                            onClick={() =>
                              setModuleDetail((prev) => ({
                                ...prev,
                                icon: it.key,
                              }))
                            }
                            title={it.key}
                            style={{
                              width: "32px",
                              height: "32px",
                              display: "flex",
                              alignItems: "center",
                              justifyContent: "center",
                              border:
                                "1px solid " +
                                (active
                                  ? "var(--admin-accent, #FF5A5F)"
                                  : "var(--admin-border, #ddd)"),
                              background: active
                                ? "var(--admin-accent-soft, rgba(255,90,95,0.12))"
                                : "transparent",
                              color: active
                                ? "var(--admin-accent, #FF5A5F)"
                                : "var(--admin-text, #222)",
                              borderRadius: "6px",
                              cursor: "pointer",
                              padding: 0,
                            }}>
                            <ModuleIconSvg name={it.key} size={22} />
                          </button>
                        );
                      })}
                    </div>
                  </div>
                  {(() => {
                    const usedBy = (aicoworkers || []).filter(
                      (m) =>
                        Array.isArray(m.modules) &&
                        m.modules.some((s) => s.module_id === moduleDetail.id),
                    );
                    return (
                      <div
                        style={{
                          marginBottom: "14px",
                          padding: "10px 12px",
                          border: "1px solid var(--admin-border, #ddd)",
                          borderRadius: "8px",
                          background: "var(--admin-bg-card, #fafafa)",
                        }}>
                        <div
                          style={{
                            fontSize: "11px",
                            fontWeight: 700,
                            textTransform: "uppercase",
                            color: "var(--admin-text-secondary, #717171)",
                            letterSpacing: "0.4px",
                            marginBottom: "6px",
                          }}>
                          Verwendet von Agenten
                        </div>
                        {usedBy.length === 0 ? (
                          <div
                            style={{
                              fontSize: "12px",
                              color: "var(--admin-text-secondary, #717171)",
                              fontStyle: "italic",
                            }}>
                            Aktuell keinem Agenten zugewiesen. Im Tab
                            "Agenten" einem Agenten zuweisen, damit
                            die KI dieses Modul nutzen kann.
                          </div>
                        ) : (
                          <div
                            style={{
                              display: "flex",
                              flexWrap: "wrap",
                              gap: "6px",
                            }}>
                            {usedBy.map((m) => (
                              <span
                                key={m.id}
                                style={{
                                  padding: "3px 10px",
                                  borderRadius: "12px",
                                  background: "var(--admin-bg-input, #fff)",
                                  border: "1px solid var(--admin-border, #ddd)",
                                  color: "var(--admin-text, #222)",
                                  fontSize: "11px",
                                  fontWeight: 600,
                                }}>
                                {m.label || m.slug}
                                {!m.is_active ? " (inaktiv)" : ""}
                              </span>
                            ))}
                          </div>
                        )}
                      </div>
                    );
                  })()}
                  <div
                    style={{
                      borderTop: "1px solid var(--admin-border, #ddd)",
                      paddingTop: "16px",
                      marginTop: "8px",
                      marginBottom: "14px",
                    }}>
                    <div
                      style={{
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "space-between",
                        marginBottom: "10px",
                      }}>
                      <h3
                        style={{
                          fontSize: "14px",
                          fontWeight: 700,
                          margin: 0,
                        }}>
                        Tools ({moduleTools.length})
                      </h3>
                      <button
                        type="button"
                        onClick={() =>
                          createModuleTool(moduleDetail.id, {
                            tool_name:
                              "neues_tool_" + Date.now().toString(36).slice(-4),
                            tool_description:
                              "TODO: Anweisung schreiben (Titel + wann nutzen?).",
                            data_source_type: "sql",
                          })
                        }
                        style={{
                          padding: "5px 10px",
                          background: "var(--admin-accent, #FF5A5F)",
                          color: "#fff",
                          border: "none",
                          borderRadius: "6px",
                          cursor: "pointer",
                          fontSize: "12px",
                          fontWeight: 600,
                        }}>
                        + Tool
                      </button>
                    </div>
                    <p
                      style={{
                        margin: "0 0 12px",
                        fontSize: "11px",
                        color: "var(--admin-text-secondary, #717171)",
                        lineHeight: 1.4,
                      }}>
                      Ein Modul kann mehrere Tools haben (z. B. „Tische
                      abfragen" + „Reservierung anlegen"). Jedes Tool ist eine
                      eigene Function, die die KI aufrufen kann.
                    </p>
                    {!moduleToolsLoading && moduleTools.length === 0 && (
                      <p
                        style={{
                          fontSize: "12px",
                          color: "var(--admin-text-secondary, #717171)",
                          fontStyle: "italic",
                          padding: "12px",
                          border: "1px dashed var(--admin-border, #ddd)",
                          borderRadius: "8px",
                          textAlign: "center",
                        }}>
                        Noch keine Tools angelegt. Klick auf „+ Tool".
                      </p>
                    )}
                    <div
                      style={{
                        display: "flex",
                        flexDirection: "column",
                        gap: "8px",
                      }}>
                      {moduleTools.map((tool) => {
                        const isExpanded = moduleToolEditId === tool.id;
                        return (
                          <div
                            key={tool.id}
                            style={{
                              border: "1px solid var(--admin-border, #ddd)",
                              borderRadius: "8px",
                              background: "var(--admin-bg-card, #fafafa)",
                            }}>
                            <button
                              type="button"
                              onClick={() =>
                                setModuleToolEditId(isExpanded ? null : tool.id)
                              }
                              style={{
                                width: "100%",
                                display: "flex",
                                alignItems: "center",
                                gap: "10px",
                                padding: "10px 12px",
                                background: "transparent",
                                border: "none",
                                cursor: "pointer",
                                textAlign: "left",
                              }}>
                              <span
                                style={{
                                  width: "10px",
                                  color: "var(--admin-text-secondary, #717171)",
                                }}>
                                {isExpanded ? "▾" : "▸"}
                              </span>
                              <span
                                style={{
                                  fontFamily: "monospace",
                                  fontSize: "12px",
                                  fontWeight: 600,
                                  color: "var(--admin-text, #222)",
                                  flex: 1,
                                  overflow: "hidden",
                                  textOverflow: "ellipsis",
                                  whiteSpace: "nowrap",
                                }}>
                                {tool.tool_name}
                              </span>
                              <span
                                style={{
                                  fontSize: "10px",
                                  color: tool.is_active
                                    ? "var(--cn-semantic-success, #008a05)"
                                    : "var(--admin-text-secondary, #717171)",
                                  textTransform: "uppercase",
                                  letterSpacing: "0.4px",
                                  fontWeight: 700,
                                }}>
                                {tool.is_active ? "aktiv" : "inaktiv"}
                              </span>
                              <span
                                style={{
                                  fontSize: "10px",
                                  color: "var(--admin-text-secondary, #717171)",
                                  textTransform: "uppercase",
                                  letterSpacing: "0.4px",
                                }}>
                                {tool.data_source_type}
                              </span>
                            </button>
                            {isExpanded && (
                              <div
                                style={{
                                  padding: "0 12px 12px 12px",
                                  borderTop:
                                    "1px solid var(--admin-border, #ddd)",
                                }}>
                                <div
                                  style={{
                                    marginTop: "10px",
                                    marginBottom: "10px",
                                  }}>
                                  <label
                                    style={{
                                      display: "block",
                                      fontWeight: 600,
                                      marginBottom: "4px",
                                      fontSize: "12px",
                                    }}>
                                    Tool-Name (function name)
                                  </label>
                                  <input
                                    type="text"
                                    value={tool.tool_name || ""}
                                    onChange={(e) => {
                                      const v = e.target.value;
                                      setModuleTools((prev) =>
                                        prev.map((t) =>
                                          t.id === tool.id
                                            ? { ...t, tool_name: v }
                                            : t,
                                        ),
                                      );
                                    }}
                                    onBlur={(e) =>
                                      saveModuleTool(moduleDetail.id, tool.id, {
                                        tool_name: e.target.value,
                                      })
                                    }
                                    style={{
                                      width: "100%",
                                      padding: "6px 8px",
                                      border:
                                        "1px solid var(--admin-border, #ddd)",
                                      borderRadius: "6px",
                                      fontSize: "12px",
                                      boxSizing: "border-box",
                                      fontFamily: "monospace",
                                      background: "var(--admin-bg-input, #fff)",
                                      color: "var(--admin-text, #222)",
                                    }}
                                  />
                                </div>
                                <div style={{ marginBottom: "10px" }}>
                                  <label
                                    style={{
                                      display: "block",
                                      fontWeight: 600,
                                      marginBottom: "4px",
                                      fontSize: "12px",
                                    }}>
                                    Anweisung: Titel + wann nutzen?
                                  </label>
                                  <textarea
                                    value={tool.tool_description || ""}
                                    onChange={(e) => {
                                      const v = e.target.value;
                                      setModuleTools((prev) =>
                                        prev.map((t) =>
                                          t.id === tool.id
                                            ? { ...t, tool_description: v }
                                            : t,
                                        ),
                                      );
                                    }}
                                    onBlur={(e) =>
                                      saveModuleTool(moduleDetail.id, tool.id, {
                                        tool_description: e.target.value,
                                      })
                                    }
                                    placeholder={
                                      'TISCH-VERFUEGBARKEIT PRUEFEN. Wann nutzen? Wenn der Gast einen Tisch fuer einen bestimmten Zeitpunkt sucht ("Habt ihr heute Abend einen Tisch fuer 4?").'
                                    }
                                    style={{
                                      width: "100%",
                                      minHeight: "80px",
                                      padding: "6px 8px",
                                      border:
                                        "1px solid var(--admin-border, #ddd)",
                                      borderRadius: "6px",
                                      fontSize: "12px",
                                      lineHeight: "1.5",
                                      boxSizing: "border-box",
                                      resize: "vertical",
                                      fontFamily: "inherit",
                                      background: "var(--admin-bg-input, #fff)",
                                      color: "var(--admin-text, #222)",
                                    }}
                                  />
                                </div>
                                <div
                                  style={{
                                    display: "flex",
                                    alignItems: "center",
                                    gap: "12px",
                                    marginBottom: "10px",
                                  }}>
                                  <label
                                    style={{
                                      fontSize: "12px",
                                      display: "flex",
                                      alignItems: "center",
                                      gap: "6px",
                                    }}>
                                    <input
                                      type="checkbox"
                                      checked={!!tool.is_active}
                                      onChange={(e) => {
                                        const v = e.target.checked ? 1 : 0;
                                        setModuleTools((prev) =>
                                          prev.map((t) =>
                                            t.id === tool.id
                                              ? { ...t, is_active: v }
                                              : t,
                                          ),
                                        );
                                        saveModuleTool(
                                          moduleDetail.id,
                                          tool.id,
                                          { is_active: v },
                                        );
                                      }}
                                    />
                                    Aktiv
                                  </label>
                                  <label
                                    style={{
                                      fontSize: "12px",
                                      display: "flex",
                                      alignItems: "center",
                                      gap: "6px",
                                    }}>
                                    <span>Datenquelle:</span>
                                    <select
                                      value={tool.data_source_type || "sql"}
                                      onChange={(e) => {
                                        const raw = e.target.value;
                                        const v =
                                          raw === "qdrant" ||
                                          raw === "sql_write"
                                            ? raw
                                            : "sql";
                                        setModuleTools((prev) =>
                                          prev.map((t) =>
                                            t.id === tool.id
                                              ? { ...t, data_source_type: v }
                                              : t,
                                          ),
                                        );
                                        saveModuleTool(
                                          moduleDetail.id,
                                          tool.id,
                                          { data_source_type: v },
                                        );
                                      }}
                                      style={{
                                        padding: "4px 6px",
                                        border:
                                          "1px solid var(--admin-border, #ddd)",
                                        borderRadius: "6px",
                                        fontSize: "12px",
                                        background:
                                          "var(--admin-bg-input, #fff)",
                                        color: "var(--admin-text, #222)",
                                      }}>
                                      <option value="sql">
                                        SQL (Read / SELECT)
                                      </option>
                                      <option value="sql_write">
                                        SQL Write (INSERT/UPDATE/DELETE)
                                      </option>
                                      <option value="qdrant">
                                        Qdrant (Katalog-Suche)
                                      </option>
                                    </select>
                                  </label>
                                  <button
                                    type="button"
                                    onClick={() => {
                                      if (
                                        window.confirm(
                                          'Tool "' +
                                            tool.tool_name +
                                            '" wirklich loeschen?',
                                        )
                                      )
                                        deleteModuleTool(
                                          moduleDetail.id,
                                          tool.id,
                                        );
                                    }}
                                    style={{
                                      marginLeft: "auto",
                                      padding: "4px 10px",
                                      background: "transparent",
                                      border: "1px solid #c13515",
                                      borderRadius: "6px",
                                      cursor: "pointer",
                                      fontSize: "11px",
                                      color: "#c13515",
                                    }}>
                                    Loeschen
                                  </button>
                                </div>
                              </div>
                            )}
                          </div>
                        );
                      })}
                    </div>
                  </div>
                  <ModuleTestsBlock
                    moduleId={moduleDetail.id}
                    tests={moduleTests}
                    loading={moduleTestsLoading}
                    editId={moduleTestEditId}
                    setEditId={setModuleTestEditId}
                    onCreate={createModuleTest}
                    onSave={saveModuleTest}
                    onDelete={deleteModuleTest}
                  />
                  <div
                    style={{
                      borderTop: "1px solid var(--admin-border, #ddd)",
                      paddingTop: "16px",
                      marginBottom: "14px",
                    }}>
                    <div
                      style={{
                        display: "flex",
                        alignItems: "center",
                        gap: "8px",
                        marginBottom: "14px",
                      }}>
                      <label style={{ fontWeight: 600, fontSize: "13px" }}>
                        Modul aktiv:
                      </label>
                      <input
                        type="checkbox"
                        checked={!!moduleDetail.is_active}
                        onChange={(e) =>
                          setModuleDetail((prev) => ({
                            ...prev,
                            is_active: e.target.checked ? 1 : 0,
                          }))
                        }
                      />
                    </div>
                    <button
                      type="button"
                      onClick={() => {
                        if (
                          window.confirm(
                            'Modul "' +
                              moduleDetail.label +
                              '" wirklich loeschen? Alle Tools dieses Moduls werden ebenfalls geloescht.',
                          )
                        )
                          deleteModule(moduleDetail.id);
                      }}
                      style={{
                        padding: "8px 14px",
                        background: "transparent",
                        border: "1px solid #c13515",
                        borderRadius: "8px",
                        cursor: "pointer",
                        fontWeight: 600,
                        fontSize: "13px",
                        color: "#c13515",
                      }}>
                      Modul loeschen
                    </button>
                  </div>
                </div>
              </>
            ) : productDetailOpen &&
            activeView === "tests" &&
            selectedManualTestId != null &&
            manualTestDetail ? (
              <>
                <div className="admin_detail_header">
                  <button
                    type="button"
                    className="admin_detail_back"
                    onClick={() => setSelectedManualTestId(null)}>
                    &larr;
                  </button>
                  <span className="admin_detail_heading">
                    <strong>
                      KI #{manualTestDetail.ai_id} {manualTestDetail.ai_name || ""}
                    </strong>
                    <br />
                    <span className="admin_detail_heading_sub">
                      {manualTestDetail.industry_slug || "?"}
                      {manualTestDetail.duration_ms
                        ? " · " + Math.round(manualTestDetail.duration_ms / 100) / 10 + "s"
                        : ""}
                    </span>
                  </span>
                  <div className="admin_detail_header_actions">
                    <span
                      style={{
                        padding: "4px 10px", borderRadius: "12px",
                        fontSize: "11px", fontWeight: 700, textTransform: "uppercase",
                        background: manualTestDetail.status === "open"
                          ? "rgba(0, 138, 5, 0.15)"
                          : "var(--admin-bg-input, #fff)",
                        color: manualTestDetail.status === "open"
                          ? "var(--cn-semantic-success, #008a05)"
                          : "var(--admin-text-secondary, #717171)",
                      }}>
                      {manualTestDetail.status === "open" ? "OFFEN" : (manualTestDetail.ended_by || "ENDED")}
                    </span>
                    {manualTestDetail.status === "open" && (
                      <button
                        type="button"
                        className="admin_btn admin_detail_header_btn"
                        onClick={() => endManualTest(manualTestDetail.id, "admin")}
                        style={{ borderColor: "#c13515", color: "#c13515" }}>
                        Auflegen
                      </button>
                    )}
                  </div>
                </div>
                <div
                  ref={productBodyRef}
                  className="admin_detail_body"
                  style={{ padding: 0, flexDirection: "column", gap: 0, position: "relative", overflow: "hidden" }}>
                  {manualConnecting && (
                    <div style={{
                      position: "absolute", inset: 0, zIndex: 5,
                      display: "flex", flexDirection: "column",
                      alignItems: "center", justifyContent: "center", gap: "12px",
                      background: "rgba(255,255,255,0.85)",
                      backdropFilter: "blur(2px)",
                      color: "var(--admin-text-secondary, #717171)",
                      fontSize: "13px", fontWeight: 500,
                    }}>
                      <div style={{
                        width: "32px", height: "32px",
                        border: "3px solid var(--cn-border, #ebebeb)",
                        borderTopColor: "var(--admin-accent, #FF5A5F)",
                        borderRadius: "50%",
                        animation: "admin_spin 0.9s linear infinite",
                      }} />
                      <span>Verbindung wird hergestellt…</span>
                    </div>
                  )}

                  <div style={{
                    display: "flex", flexDirection: "column", gap: "8px",
                    flex: 1, minHeight: 0, overflowY: "auto",
                    padding: "16px 16px 24px",
                  }}>
                    {renderAdminTranscriptMerged(manualTestDetail.transcript)}
                    {manualSending && (
                      <div style={{
                        alignSelf: "flex-start",
                        padding: "8px 12px",
                        color: "var(--admin-text-secondary, #717171)",
                        fontSize: "12px", fontStyle: "italic",
                      }}>KI tippt…</div>
                    )}
                  </div>

                  {manualTestDetail.status === "open" && (
                    <form
                      onSubmit={(e) => {
                        e.preventDefault();
                        if (!manualSending && manualInputDraft.trim()) {
                          sendManualMessage(manualTestDetail.id, manualInputDraft.trim());
                          setManualInputDraft("");
                        }
                      }}
                      style={{
                        display: "flex", gap: "8px",
                        flexShrink: 0,
                        padding: "10px 16px",
                        background: "var(--admin-bg-page, #fff)",
                        borderTop: "1px solid var(--cn-border, #ebebeb)",
                      }}>
                      <textarea
                        value={manualInputDraft}
                        onChange={(e) => setManualInputDraft(e.target.value)}
                        onKeyDown={(e) => {
                          if (e.key === "Enter" && !e.shiftKey) {
                            e.preventDefault();
                            if (!manualSending && manualInputDraft.trim()) {
                              sendManualMessage(manualTestDetail.id, manualInputDraft.trim());
                              setManualInputDraft("");
                            }
                          }
                        }}
                        placeholder="Nachricht an die KI…"
                        rows={2}
                        disabled={manualSending || manualConnecting}
                        style={{
                          flex: 1, minWidth: 0,
                          padding: "8px 10px",
                          border: "1px solid var(--admin-border, #ddd)",
                          borderRadius: "8px",
                          background: "var(--admin-bg-input, #fff)",
                          color: "var(--admin-text, #222)",
                          fontSize: "13px",
                          fontFamily: "inherit",
                          resize: "vertical",
                        }}
                      />
                      <button
                        type="submit"
                        disabled={manualSending || manualConnecting || !manualInputDraft.trim()}
                        style={{
                          padding: "8px 14px",
                          background: manualSending || manualConnecting || !manualInputDraft.trim()
                            ? "var(--admin-border, #ddd)"
                            : "var(--admin-accent, #FF5A5F)",
                          color: "#fff",
                          border: "none",
                          borderRadius: "8px",
                          cursor: manualSending || manualConnecting || !manualInputDraft.trim()
                            ? "not-allowed" : "pointer",
                          fontWeight: 600,
                          fontSize: "13px",
                          alignSelf: "flex-end",
                        }}>
                        {manualSending ? "..." : "Senden"}
                      </button>
                    </form>
                  )}
                  {manualTestDetail.status === "ended" && (
                    <div style={{
                      flexShrink: 0,
                      margin: "10px 16px",
                      padding: "10px 12px",
                      border: "1px solid var(--cn-border, #ebebeb)",
                      borderRadius: "8px",
                      background: "var(--admin-bg-card, #fafafa)",
                      fontSize: "12px",
                      color: "var(--admin-text-secondary, #717171)",
                      textAlign: "center",
                    }}>
                      Konversation beendet ({manualTestDetail.ended_by || "ended"})
                    </div>
                  )}
                </div>
              </>
            ) : productDetailOpen &&
            activeView === "tests" &&
            selectedTestRunId != null &&
            testRunDetail ? (
              <>
                <div className="admin_detail_header">
                  <button
                    type="button"
                    className="admin_detail_back"
                    onClick={() => setSelectedTestRunId(null)}>
                    &larr;
                  </button>
                  <span className="admin_detail_heading">
                    <strong>
                      {testRunDetail.scenario_name ||
                        testRunDetail.scenario_key}
                    </strong>
                    <br />
                    <span className="admin_detail_heading_sub">
                      KI #{testRunDetail.ai_id} {testRunDetail.ai_name || ""} (
                      {testRunDetail.industry_slug || "?"})
                      {testRunDetail.duration_ms
                        ? " · " +
                          Math.round(testRunDetail.duration_ms / 100) / 10 +
                          "s"
                        : ""}
                    </span>
                  </span>
                  <div className="admin_detail_header_actions">
                    <span
                      style={{
                        padding: "4px 10px",
                        borderRadius: "12px",
                        fontSize: "11px",
                        fontWeight: 700,
                        textTransform: "uppercase",
                        background:
                          testRunDetail.status === "passed"
                            ? "rgba(0, 138, 5, 0.15)"
                            : testRunDetail.status === "failed"
                              ? "rgba(193, 53, 21, 0.15)"
                              : testRunDetail.status === "error"
                                ? "rgba(193, 53, 21, 0.15)"
                                : "var(--admin-bg-input, #fff)",
                        color:
                          testRunDetail.status === "passed"
                            ? "var(--cn-semantic-success, #008a05)"
                            : testRunDetail.status === "failed"
                              ? "var(--cn-semantic-error, #c13515)"
                              : testRunDetail.status === "error"
                                ? "var(--cn-semantic-error, #c13515)"
                                : "var(--admin-text-secondary, #717171)",
                      }}>
                      {testRunDetail.status}
                    </span>
                    <button
                      type="button"
                      className="admin_btn admin_detail_header_btn"
                      onClick={() =>
                        startTestRun(
                          testRunDetail.ai_id,
                          testRunDetail.scenario_key,
                        )
                      }
                      disabled={
                        testStarting || testRunDetail.status === "running"
                      }>
                      Re-run
                    </button>
                  </div>
                </div>
                <div
                  ref={productBodyRef}
                  className="admin_detail_body"
                  style={{ padding: "16px", flexDirection: "column", gap: 0 }}>
                  {testRunDetail.summary && (
                    <div
                      style={{
                        marginBottom: "14px",
                        padding: "10px 12px",
                        border: "1px solid var(--cn-border, #ebebeb)",
                        borderRadius: "8px",
                        background: "var(--admin-bg-card, #fafafa)",
                        fontSize: "13px",
                        color: "var(--admin-text, #222)",
                      }}>
                      {testRunDetail.summary}
                    </div>
                  )}
                  {testRunDetail.error_message && (
                    <pre
                      style={{
                        marginBottom: "14px",
                        padding: "10px 12px",
                        border: "1px solid #c13515",
                        borderRadius: "8px",
                        background: "rgba(193,53,21,0.08)",
                        fontSize: "11px",
                        color: "var(--admin-text, #222)",
                        whiteSpace: "pre-wrap",
                        overflow: "auto",
                      }}>
                      {testRunDetail.error_message}
                    </pre>
                  )}

                  <h3
                    style={{
                      fontSize: "13px",
                      fontWeight: 700,
                      textTransform: "uppercase",
                      letterSpacing: "0.4px",
                      color: "var(--admin-text-secondary, #717171)",
                      margin: "0 0 8px",
                    }}>
                    Konversation
                  </h3>
                  <div
                    style={{
                      display: "flex",
                      flexDirection: "column",
                      gap: "8px",
                      marginBottom: "20px",
                    }}>
                    {renderAdminTranscriptMerged(testRunDetail.transcript)}
                    {testRunDetail.status === "running" && (
                      <div
                        style={{
                          alignSelf: "flex-start",
                          padding: "8px 12px",
                          color: "var(--admin-text-secondary, #717171)",
                          fontSize: "12px",
                          fontStyle: "italic",
                        }}>
                        läuft … (Polling alle 1.5s)
                      </div>
                    )}
                  </div>

                  <h3
                    style={{
                      fontSize: "13px",
                      fontWeight: 700,
                      textTransform: "uppercase",
                      letterSpacing: "0.4px",
                      color: "var(--admin-text-secondary, #717171)",
                      margin: "0 0 8px",
                    }}>
                    Assertions (
                    {Array.isArray(testRunDetail.assertions)
                      ? testRunDetail.assertions.filter((a) => a.passed).length
                      : 0}{" "}
                    /{" "}
                    {Array.isArray(testRunDetail.assertions)
                      ? testRunDetail.assertions.length
                      : 0}
                    )
                  </h3>
                  <div
                    style={{
                      display: "flex",
                      flexDirection: "column",
                      gap: "6px",
                      marginBottom: "20px",
                    }}>
                    {(Array.isArray(testRunDetail.assertions)
                      ? testRunDetail.assertions
                      : []
                    ).map((a, i) => (
                      <div
                        key={i}
                        style={{
                          padding: "8px 12px",
                          borderRadius: "8px",
                          border:
                            "1px solid " +
                            (a.passed
                              ? "rgba(0,138,5,0.4)"
                              : "rgba(193,53,21,0.5)"),
                          background: a.passed
                            ? "rgba(0,138,5,0.08)"
                            : "rgba(193,53,21,0.08)",
                        }}>
                        <div
                          style={{
                            display: "flex",
                            alignItems: "center",
                            gap: "8px",
                            marginBottom: "4px",
                          }}>
                          <span
                            style={{
                              fontWeight: 700,
                              color: a.passed
                                ? "var(--cn-semantic-success, #008a05)"
                                : "var(--cn-semantic-error, #c13515)",
                            }}>
                            {a.passed ? "✓" : "✗"}
                          </span>
                          <span
                            style={{
                              fontSize: "12px",
                              fontWeight: 600,
                              color: "var(--admin-text, #222)",
                            }}>
                            Turn {a.turn} · {a.description}
                          </span>
                        </div>
                        {!a.passed && (
                          <div
                            style={{
                              fontSize: "11px",
                              fontFamily: "monospace",
                              color: "var(--admin-text-secondary, #717171)",
                              whiteSpace: "pre-wrap",
                            }}>
                            <div>
                              <span style={{ fontWeight: 700 }}>expected:</span>{" "}
                              {JSON.stringify(a.expected)}
                            </div>
                            <div>
                              <span style={{ fontWeight: 700 }}>actual:</span>{" "}
                              {JSON.stringify(a.actual)}
                            </div>
                          </div>
                        )}
                      </div>
                    ))}
                    {(!testRunDetail.assertions ||
                      testRunDetail.assertions.length === 0) &&
                      testRunDetail.status !== "running" && (
                        <div
                          style={{
                            fontSize: "12px",
                            color: "var(--admin-text-secondary, #717171)",
                            fontStyle: "italic",
                          }}>
                          Keine Assertions.
                        </div>
                      )}
                  </div>

                  <h3
                    style={{
                      fontSize: "13px",
                      fontWeight: 700,
                      textTransform: "uppercase",
                      letterSpacing: "0.4px",
                      color: "var(--admin-text-secondary, #717171)",
                      margin: "0 0 8px",
                    }}>
                    Tool-Calls (
                    {Array.isArray(testRunDetail.tool_calls_log)
                      ? testRunDetail.tool_calls_log.length
                      : 0}
                    )
                  </h3>
                  <div
                    style={{
                      display: "flex",
                      flexDirection: "column",
                      gap: "6px",
                      marginBottom: "20px",
                    }}>
                    {(Array.isArray(testRunDetail.tool_calls_log)
                      ? testRunDetail.tool_calls_log
                      : []
                    ).map((tc, i) => (
                      <div
                        key={i}
                        style={{
                          padding: "8px 12px",
                          borderRadius: "8px",
                          border: "1px solid var(--cn-border, #ebebeb)",
                          background: "var(--admin-bg-card, #fafafa)",
                        }}>
                        <div
                          style={{
                            fontSize: "12px",
                            fontFamily: "monospace",
                            fontWeight: 700,
                            color: "var(--admin-text, #222)",
                            marginBottom: "4px",
                          }}>
                          Turn {tc.turn} · {tc.tool_name} · {tc.ms}ms
                          {tc.error ? " · ERROR" : ""}
                        </div>
                        <div
                          style={{
                            fontSize: "11px",
                            fontFamily: "monospace",
                            color: "var(--admin-text-secondary, #717171)",
                            whiteSpace: "pre-wrap",
                          }}>
                          args: {JSON.stringify(tc.args)}
                        </div>
                        <div
                          style={{
                            fontSize: "11px",
                            fontFamily: "monospace",
                            color: "var(--admin-text-secondary, #717171)",
                            whiteSpace: "pre-wrap",
                            marginTop: "2px",
                          }}>
                          result:{" "}
                          {(tc.result || "").length > 300
                            ? (tc.result || "").slice(0, 300) + "…"
                            : tc.result || ""}
                        </div>
                      </div>
                    ))}
                  </div>

                  <button
                    type="button"
                    onClick={() => {
                      if (
                        window.confirm(
                          "Run #" + testRunDetail.id + " wirklich loeschen?",
                        )
                      )
                        deleteTestRun(testRunDetail.id);
                    }}
                    style={{
                      padding: "8px 14px",
                      background: "transparent",
                      border: "1px solid #c13515",
                      borderRadius: "8px",
                      cursor: "pointer",
                      fontWeight: 600,
                      fontSize: "13px",
                      color: "#c13515",
                      alignSelf: "flex-start",
                    }}>
                    Run loeschen
                  </button>
                </div>
              </>
            ) : userStoriesFormSidebarOpen ? (
              <>
                <div className="admin_detail_header">
                  {isMobile ? (
                    <button
                      type="button"
                      className="admin_detail_back"
                      onClick={closeUserStoryCreatePanel}
                      aria-label="Schließen">
                      &larr;
                    </button>
                  ) : null}
                  <span className="admin_detail_heading">
                    <strong>
                      {selectedUserStoryId != null
                        ? "User Story bearbeiten"
                        : "Neue User Story"}
                    </strong>
                    <br />
                    <span className="admin_detail_heading_sub">
                      Titel und Beschreibung; optional Bewertungen 1–10.
                    </span>
                  </span>
                </div>
                <div
                  className="admin_detail_body"
                  style={{
                    padding: "1rem",
                    flexDirection: "column",
                    gap: "12px",
                  }}>
                  <label className="admin_product_user_credentials_label">
                    <span>Titel</span>
                    <input
                      type="text"
                      className="admin_input"
                      value={userStoryTitleDraft}
                      onChange={(e) => setUserStoryTitleDraft(e.target.value)}
                      autoComplete="off"
                      maxLength={255}
                    />
                  </label>
                  <label className="admin_product_user_credentials_label">
                    <span>Beschreibung</span>
                    <textarea
                      className="admin_textarea"
                      value={userStoryDescriptionDraft}
                      onChange={(e) =>
                        setUserStoryDescriptionDraft(e.target.value)
                      }
                      rows={8}
                      placeholder="Beschreibung / Kontext"
                    />
                  </label>
                  {userStoryCreateError ? (
                    <p className="admin_error">{userStoryCreateError}</p>
                  ) : null}
                  <div
                    style={{
                      display: "flex",
                      gap: "8px",
                      flexWrap: "wrap",
                      marginTop: "4px",
                    }}>
                    <button
                      type="button"
                      className="admin_btn admin_btn--primary"
                      disabled={
                        userStoryCreateSaving ||
                        userStoryUpdateSaving
                      }
                      onClick={saveUserStoryHandler}>
                      {userStoryCreateSaving || userStoryUpdateSaving
                        ? "Speichern…"
                        : "Speichern"}
                    </button>
                    <button
                      type="button"
                      className="admin_btn"
                      disabled={
                        userStoryCreateSaving ||
                        userStoryUpdateSaving
                      }
                      onClick={closeUserStoryCreatePanel}>
                      Abbrechen
                    </button>
                  </div>
                  {selectedUserStoryId != null ? (
                    <div
                      style={{
                        marginTop: "12px",
                        paddingTop: "12px",
                        borderTop:
                          "1px solid var(--admin-border, #ddd)",
                        display: "flex",
                        flexDirection: "column",
                        gap: "10px",
                      }}>
                      <h3
                        className="admin_product_user_section_title"
                        style={{ margin: 0 }}>
                        Bewertungen
                      </h3>
                      {userStoryRatings.map((r) => {
                        const memberLabel = (() => {
                          const name = String(
                            r.member_display_name || "",
                          ).trim();
                          const mail = String(r.member_email || "").trim();
                          if (name && mail) return `${name} (${mail})`;
                          if (name) return name;
                          if (mail) return mail;
                          return null;
                        })();
                        return (
                        <div
                          key={r.id}
                          style={{
                            fontSize: "12px",
                            padding: "8px 10px",
                            borderRadius: "8px",
                            border:
                              "1px solid var(--admin-border, #ddd)",
                            background: "var(--admin-bg-input, #fff)",
                          }}>
                          <div
                            style={{
                              display: "flex",
                              justifyContent: "space-between",
                              gap: "8px",
                              marginBottom: "4px",
                            }}>
                            <strong>Score: {r.score}/10</strong>
                            {r.created_at ? (
                              <span
                                style={{
                                  color:
                                    "var(--admin-text-secondary, #717171)",
                                  fontSize: "10px",
                                }}>
                                {new Date(
                                  r.created_at,
                                ).toLocaleString("de-DE")}
                              </span>
                            ) : null}
                          </div>
                          {memberLabel ? (
                            <div
                              style={{
                                fontSize: "11px",
                                color: "var(--admin-text-secondary, #717171)",
                                marginBottom: "4px",
                              }}>
                              Von: {memberLabel}
                            </div>
                          ) : null}
                          {r.comment ? (
                            <div style={{ color: "var(--admin-text, #222)" }}>
                              {r.comment}
                            </div>
                          ) : null}
                          {adminUser &&
                          r.created_by != null &&
                          Number(r.created_by) === Number(adminUser.id) ? (
                          <div style={{ marginTop: "6px" }}>
                            <button
                              type="button"
                              className="admin_btn"
                              style={{
                                fontSize: "12px",
                                padding: "4px 8px",
                              }}
                              disabled={
                                userStoryRatingDeleteId === r.id
                              }
                              onClick={() => deleteUserStoryRating(r.id)}>
                              {userStoryRatingDeleteId === r.id
                                ? "Löschen…"
                                : "Bewertung entfernen"}
                            </button>
                          </div>
                          ) : null}
                        </div>
                        );
                      })}
                      <div
                        style={{
                          display: "grid",
                          gap: "8px",
                        }}>
                        <label className="admin_product_user_credentials_label">
                          <span>Neue Bewertung: Score 1–10</span>
                          <input
                            type="number"
                            min={1}
                            max={10}
                            step={1}
                            className="admin_input"
                            value={userStoryRatingNewScore}
                            onChange={(e) =>
                              setUserStoryRatingNewScore(e.target.value)
                            }
                          />
                        </label>
                        <label className="admin_product_user_credentials_label">
                          <span>Notiz (optional)</span>
                          <input
                            type="text"
                            className="admin_input"
                            value={userStoryRatingNewComment}
                            onChange={(e) =>
                              setUserStoryRatingNewComment(e.target.value)
                            }
                            placeholder="Kurznotiz"
                          />
                        </label>
                        <button
                          type="button"
                          className="admin_btn admin_btn--primary"
                          style={{ alignSelf: "flex-start" }}
                          disabled={userStoryRatingAddSaving}
                          onClick={addUserStoryRating}>
                          {userStoryRatingAddSaving
                            ? "Hinzufügen…"
                            : "Bewertung hinzufügen"}
                        </button>
                      </div>
                      <div style={{ marginTop: "8px" }}>
                        <button
                          type="button"
                          className="admin_btn"
                          style={{
                            border: "1px solid #c13515",
                            color: "#c13515",
                            background: "transparent",
                          }}
                          disabled={userStoryDeleteSaving}
                          onClick={deleteUserStoryAction}>
                          {userStoryDeleteSaving
                            ? "Löschen…"
                            : "User Story löschen"}
                        </button>
                      </div>
                    </div>
                  ) : null}
                </div>
              </>
            ) : userStoriesSidebarOpen ? (
              <>
                <div className="admin_detail_header">
                  <span className="admin_detail_heading">
                    <strong>User Story</strong>
                    <br />
                    <span className="admin_detail_heading_sub">
                      Wähle links eine Story oder lege eine neue an.
                    </span>
                  </span>
                </div>
                <div className="admin_detail_body admin_detail_body--empty">
                  <div className="admin_detail_empty_state">
                    <span>Noch kein Eintrag ausgewählt</span>
                  </div>
                </div>
              </>
            ) : agendasFormSidebarOpen ? (
              <>
                <div className="admin_detail_header">
                  {isMobile ? (
                    <button
                      type="button"
                      className="admin_detail_back"
                      onClick={closeAgendaCreatePanel}
                      aria-label="Schließen">
                      &larr;
                    </button>
                  ) : null}
                  <span className="admin_detail_heading">
                    <strong>
                      {selectedAdminAgendaId != null ? "Agenda bearbeiten" : "Neue Agenda"}
                    </strong>
                    <br />
                    <span className="admin_detail_heading_sub">
                      Name und Datum speichern.
                    </span>
                  </span>
                </div>
                <div
                  className="admin_detail_body"
                  style={{
                    padding: "1rem",
                    flexDirection: "column",
                    gap: "12px",
                  }}>
                  <label className="admin_product_user_credentials_label">
                    <span>Name</span>
                    <input
                      type="text"
                      className="admin_input"
                      value={agendaNameDraft}
                      onChange={(e) => setAgendaNameDraft(e.target.value)}
                      autoComplete="off"
                      maxLength={255}
                    />
                  </label>
                  <label className="admin_product_user_credentials_label">
                    <span>Datum</span>
                    <input
                      type="date"
                      className="admin_input"
                      value={agendaDateDraft}
                      onChange={(e) => setAgendaDateDraft(e.target.value)}
                    />
                  </label>
                  <label className="admin_product_user_credentials_label">
                    <span>Notizen</span>
                    <textarea
                      className="admin_textarea"
                      value={agendaNotesDraft}
                      onChange={(e) => setAgendaNotesDraft(e.target.value)}
                      rows={6}
                      placeholder="Notizen zur Agenda"
                    />
                  </label>
                  <label className="admin_product_user_credentials_label">
                    <span>Marketing: PR</span>
                    <input
                      type="text"
                      className="admin_input"
                      value={agendaPrSloganDraft}
                      onChange={(e) => setAgendaPrSloganDraft(e.target.value)}
                      placeholder="Slogan für PR / Außenkommunikation"
                      autoComplete="off"
                    />
                  </label>
                  <label className="admin_product_user_credentials_label">
                    <span>Sales</span>
                    <input
                      type="text"
                      className="admin_input"
                      value={agendaSalesFocusDraft}
                      onChange={(e) => setAgendaSalesFocusDraft(e.target.value)}
                      placeholder="Sales-Schwerpunkt für diese Agenda"
                      autoComplete="off"
                    />
                  </label>
                  <label className="admin_product_user_credentials_label">
                    <span>People</span>
                    <input
                      type="text"
                      className="admin_input"
                      value={agendaPeopleFocusDraft}
                      onChange={(e) => setAgendaPeopleFocusDraft(e.target.value)}
                      placeholder="People-Schwerpunkt für diese Agenda"
                      autoComplete="off"
                    />
                  </label>
                  {agendaCreateError ? (
                    <p className="admin_error">{agendaCreateError}</p>
                  ) : null}
                  <div
                    style={{
                      display: "flex",
                      gap: "8px",
                      flexWrap: "wrap",
                      marginTop: "4px",
                    }}>
                    <button
                      type="button"
                      className="admin_btn admin_btn--primary"
                      disabled={agendaCreateSaving || agendaUpdateSaving}
                      onClick={
                        selectedAdminAgendaId != null
                          ? saveAdminAgendaUpdate
                          : saveAdminAgenda
                      }>
                      {agendaCreateSaving || agendaUpdateSaving
                        ? "Speichern…"
                        : "Speichern"}
                    </button>
                    <button
                      type="button"
                      className="admin_btn"
                      disabled={agendaCreateSaving || agendaUpdateSaving}
                      onClick={closeAgendaCreatePanel}>
                      Abbrechen
                    </button>
                  </div>
                </div>
              </>
            ) : agendasSidebarOpen ? (
              <>
                <div className="admin_detail_header">
                  <span className="admin_detail_heading">
                    <strong>Agenda auswählen</strong>
                    <br />
                    <span className="admin_detail_heading_sub">
                      Klicke links auf eine Agenda oder erstelle eine neue.
                    </span>
                  </span>
                </div>
                <div className="admin_detail_body admin_detail_body--empty">
                  <div className="admin_detail_empty_state">
                    <span>Noch kein Eintrag ausgewählt</span>
                  </div>
                </div>
              </>
            ) : membersFormSidebarOpen ? (
              <>
                <div className="admin_detail_header">
                  {isMobile ? (
                    <button
                      type="button"
                      className="admin_detail_back"
                      onClick={closeMemberPanel}
                      aria-label="Schließen">
                      &larr;
                    </button>
                  ) : null}
                  <span className="admin_detail_heading">
                    <strong>{selectedMemberId != null ? "Member bearbeiten" : "Neuer Member"}</strong>
                    <br />
                    <span className="admin_detail_heading_sub">
                      Zugang und Bereichsrechte pflegen.
                    </span>
                  </span>
                </div>
                <div
                  className="admin_detail_body"
                  style={{ padding: "1rem", flexDirection: "column", gap: "12px" }}>
                  <label className="admin_product_user_credentials_label">
                    <span>Email</span>
                    <input
                      type="email"
                      className="admin_input"
                      value={memberEmailDraft}
                      onChange={(e) => setMemberEmailDraft(e.target.value)}
                    />
                  </label>
                  <label className="admin_product_user_credentials_label">
                    <span>Name</span>
                    <input
                      type="text"
                      className="admin_input"
                      value={memberDisplayNameDraft}
                      onChange={(e) => setMemberDisplayNameDraft(e.target.value)}
                    />
                  </label>
                  <label className="admin_product_user_credentials_label">
                    <span>Eingeladen von</span>
                    <select
                      className="admin_input"
                      value={memberInvitedFromDraft}
                      onChange={(e) => setMemberInvitedFromDraft(e.target.value)}>
                      {membersList.length === 0 ? (
                        <option value="1">Admin (#1)</option>
                      ) : (
                        membersList
                          .slice()
                          .sort(
                            (a, b) =>
                              (Number(a.id) || 0) - (Number(b.id) || 0),
                          )
                          .map((mm) => (
                            <option key={mm.id} value={String(mm.id)}>
                              {(mm.display_name || "").trim() || mm.email}
                              {(mm.display_name || "").trim() && mm.email
                                ? ` (${mm.email})`
                                : ""}
                            </option>
                          ))
                      )}
                    </select>
                  </label>
                  <label className="admin_product_user_credentials_label">
                    <span>Mobilnummer (E.164)</span>
                    <input
                      type="tel"
                      inputMode="tel"
                      autoComplete="tel"
                      placeholder="+4915112345678"
                      className="admin_input"
                      value={memberPasswordDraft}
                      onChange={(e) => setMemberPasswordDraft(e.target.value)}
                    />
                  </label>
                  <div className="admin_product_user_credentials_label">
                    <span>Bereiche</span>
                    <div style={{ display: "flex", gap: "10px", flexWrap: "wrap" }}>
                      {[
                        ["product", "Product"],
                        ["marketing", "Marketing"],
                        ["sales", "Sales"],
                        ["admin", "Admin"],
                        ["people", "People"],
                        ["finance", "Finance"],
                        ["ai_agents", "AI Agents"],
                        ["design", "Design"],
                      ].map(([key, label]) => (
                        <label key={key} style={{ display: "inline-flex", alignItems: "center", gap: "4px", fontSize: "12px" }}>
                          <input
                            type="checkbox"
                            checked={!!memberAccessDraft[key]}
                            onChange={(e) =>
                              setMemberAccessDraft((prev) => ({ ...prev, [key]: e.target.checked }))
                            }
                          />
                          <span>{label}</span>
                        </label>
                      ))}
                    </div>
                  </div>
                  {memberFormError ? <p className="admin_error">{memberFormError}</p> : null}
                  <div style={{ display: "flex", gap: "8px", flexWrap: "wrap", marginTop: "4px" }}>
                    <button
                      type="button"
                      className="admin_btn admin_btn--primary"
                      disabled={memberSaving}
                      onClick={saveMemberForm}>
                      {memberSaving ? "Speichern…" : "Speichern"}
                    </button>
                    <button
                      type="button"
                      className="admin_btn"
                      disabled={memberSaving}
                      onClick={closeMemberPanel}>
                      Abbrechen
                    </button>
                  </div>
                </div>
              </>
            ) : membersSidebarOpen ? (
              <>
                <div className="admin_detail_header">
                  <span className="admin_detail_heading">
                    <strong>Member auswählen</strong>
                    <br />
                    <span className="admin_detail_heading_sub">
                      Klicke links auf einen Member oder erstelle einen neuen.
                    </span>
                  </span>
                </div>
                <div className="admin_detail_body admin_detail_body--empty">
                  <div className="admin_detail_empty_state">
                    <span>Noch kein Member ausgewählt</span>
                  </div>
                </div>
              </>
            ) : salesCustomersFormSidebarOpen ? (
              <>
                <div className="admin_detail_header">
                  {isMobile ? (
                    <button
                      type="button"
                      className="admin_detail_back"
                      onClick={closeSalesCustomerPanel}
                      aria-label="Schließen">
                      &larr;
                    </button>
                  ) : null}
                  <span className="admin_detail_heading">
                    <strong>
                      {selectedSalesCustomerId != null
                        ? salesCustomerCompanyDraft || "Lead"
                        : "Neuer Lead"}
                    </strong>
                  </span>
                </div>
                <div
                  className="admin_detail_body"
                  style={{ padding: "1rem", flexDirection: "column", gap: "12px" }}>
                  <label className="admin_product_user_credentials_label">
                    <span>Firma</span>
                    <input
                      type="text"
                      className="admin_input"
                      value={salesCustomerCompanyDraft}
                      onChange={(e) => setSalesCustomerCompanyDraft(e.target.value)}
                      autoComplete="organization"
                    />
                  </label>
                  <label className="admin_product_user_credentials_label">
                    <span>Ansprechpartner</span>
                    <input
                      type="text"
                      className="admin_input"
                      value={salesCustomerContactDraft}
                      onChange={(e) => setSalesCustomerContactDraft(e.target.value)}
                      autoComplete="name"
                    />
                  </label>
                  <label className="admin_product_user_credentials_label">
                    <span>E-Mail</span>
                    <input
                      type="email"
                      className="admin_input"
                      value={salesCustomerEmailDraft}
                      onChange={(e) => setSalesCustomerEmailDraft(e.target.value)}
                      autoComplete="email"
                    />
                  </label>
                  <label className="admin_product_user_credentials_label">
                    <span>Telephonio-Nutzer verknüpfen</span>
                    <select
                      className="admin_input"
                      disabled={
                        !String(salesCustomerEmailDraft).trim() ||
                        salesCustomerLinkUsersLoading
                      }
                      value={
                        salesCustomerUserIdDraft != null &&
                        Number.isFinite(Number(salesCustomerUserIdDraft))
                          ? String(salesCustomerUserIdDraft)
                          : ""
                      }
                      onChange={(e) => {
                        const v = e.target.value;
                        setSalesCustomerUserIdDraft(v === "" ? null : parseInt(v, 10));
                      }}>
                      <option value="">Keine Verknüpfung</option>
                      {selectedSalesCustomerId != null &&
                      salesCustomerUserIdDraft != null &&
                      !salesCustomerLinkUsers.some(
                        (u) => Number(u.id) === Number(salesCustomerUserIdDraft),
                      )
                        ? (() => {
                            const r = salesCustomers.find(
                              (c) => c.id === selectedSalesCustomerId,
                            );
                            if (
                              !r ||
                              r.user_id == null ||
                              Number(r.user_id) !== Number(salesCustomerUserIdDraft)
                            ) {
                              return null;
                            }
                            return (
                              <option key="persisted-link" value={String(r.user_id)}>
                                {String(r.linked_user_display_name || "Nutzer")} ·{" "}
                                {String(r.linked_user_email || "")}
                              </option>
                            );
                          })()
                        : null}
                      {salesCustomerLinkUsers.map((u) => (
                        <option key={u.id} value={String(u.id)}>
                          {(u.display_name && String(u.display_name).trim()) || "Nutzer"} · {u.email}{" "}
                          ({abbrevTelephonioPlan(u.plan) || "—"})
                        </option>
                      ))}
                    </select>
                    {salesCustomerLinkUsersLoading ? (
                      <span
                        style={{
                          fontSize: "12px",
                          color: "var(--admin-text-secondary, #717171)",
                        }}>
                        Suche Telephonio-Konten…
                      </span>
                    ) : String(salesCustomerEmailDraft).trim() &&
                      !salesCustomerLinkUsers.length &&
                      salesCustomerUserIdDraft == null ? (
                      <span
                        style={{
                          fontSize: "12px",
                          color: "var(--admin-text-secondary, #717171)",
                        }}>
                        Kein Telephonio-Nutzer mit dieser E-Mail.
                      </span>
                    ) : (
                      <span
                        style={{
                          fontSize: "12px",
                          color: "var(--admin-text-secondary, #717171)",
                          lineHeight: 1.35,
                        }}>
                        Gleiche E-Mail wie beim Telephonio-Login. Optional fest zuordnen; den Plan ändert nur
                        der Kunde im Produkt.
                      </span>
                    )}
                  </label>
                  <div className="admin_product_user_credentials_label">
                    <span>Telephonio-Abo</span>
                    <div
                      className="admin_input"
                      tabIndex={0}
                      style={{
                        cursor: "default",
                        background: "var(--admin-bg-page, #f5f5f5)",
                        borderColor: "var(--admin-border, #ddd)",
                        color: "var(--cn-text-primary, #222)",
                      }}>
                      {(() => {
                        const picked = salesCustomerLinkUsers.find(
                          (u) => Number(u.id) === Number(salesCustomerUserIdDraft),
                        );
                        if (picked && normalizeTelephonioPlanKey(picked.plan)) {
                          return labelTelephonioPlan(picked.plan);
                        }
                        const row = salesCustomers.find(
                          (x) => x.id === selectedSalesCustomerId,
                        );
                        if (
                          salesCustomerUserIdDraft != null &&
                          row &&
                          row.user_id != null &&
                          Number(row.user_id) === Number(salesCustomerUserIdDraft) &&
                          normalizeTelephonioPlanKey(row.platform_plan)
                        ) {
                          return labelTelephonioPlan(row.platform_plan);
                        }
                        if (
                          !salesCustomerUserIdDraft &&
                          normalizeTelephonioPlanKey(row?.platform_plan)
                        ) {
                          return (
                            <>
                              {labelTelephonioPlan(row.platform_plan)}
                              <span
                                style={{
                                  display: "block",
                                  marginTop: "6px",
                                  fontSize: "11px",
                                  color: "var(--admin-text-secondary, #717171)",
                                  fontWeight: 400,
                                }}>
                                Hinweis über E-Mail – zur festen Zuordnung Nutzer oben verknüpfen.
                              </span>
                            </>
                          );
                        }
                        if (String(salesCustomerEmailDraft || row?.email || "").trim()) {
                          if (salesCustomerUserIdDraft != null) return "—";
                          return row
                            ? "Kein Telephonio-Konto zu dieser E-Mail"
                            : "Kein Telephonio-Nutzer mit dieser E-Mail";
                        }
                        return "—";
                      })()}
                    </div>
                  </div>
                  <label className="admin_product_user_credentials_label">
                    <span>Telefon</span>
                    <input
                      type="tel"
                      className="admin_input"
                      value={salesCustomerPhoneDraft}
                      onChange={(e) => setSalesCustomerPhoneDraft(e.target.value)}
                      autoComplete="tel"
                    />
                  </label>
                  {selectedSalesCustomerId != null ? (
                    <div className="admin_sales_call_inline">
                      {renderCallControl(
                        selectedSalesCustomerId,
                        salesCustomerPhoneDraft,
                      )}
                      {voice.error && voice.status === "error" ? (
                        <span className="admin_sales_call_inline_error">
                          Anruf nicht möglich: {voice.error}
                        </span>
                      ) : null}
                    </div>
                  ) : null}
                  <label className="admin_product_user_credentials_label">
                    <span>Notizen</span>
                    <textarea
                      className="admin_textarea"
                      value={salesCustomerNotesDraft}
                      onChange={(e) => setSalesCustomerNotesDraft(e.target.value)}
                      rows={6}
                      placeholder="Interne Notizen"
                    />
                  </label>
                  {salesCustomerFormError ? (
                    <p className="admin_error">{salesCustomerFormError}</p>
                  ) : null}
                  <div style={{ display: "flex", gap: "8px", flexWrap: "wrap", marginTop: "4px" }}>
                    <button
                      type="button"
                      className="admin_btn admin_btn--primary"
                      disabled={salesCustomerSaving}
                      onClick={saveSalesCustomer}>
                      {salesCustomerSaving ? "Speichern…" : "Speichern"}
                    </button>
                    <button
                      type="button"
                      className="admin_btn"
                      disabled={salesCustomerSaving}
                      onClick={closeSalesCustomerPanel}>
                      Abbrechen
                    </button>
                  </div>
                </div>
              </>
            ) : salesCustomersSidebarOpen ? (
              <>
                <div className="admin_detail_header">
                  <span className="admin_detail_heading">
                    <strong>Lead auswählen</strong>
                    <br />
                    <span className="admin_detail_heading_sub">
                      Klicke links auf einen Eintrag oder lege einen neuen an.
                    </span>
                  </span>
                </div>
                <div className="admin_detail_body admin_detail_body--empty">
                  <div className="admin_detail_empty_state">
                    <span>Noch kein Eintrag ausgewählt</span>
                  </div>
                </div>
              </>
            ) : marketingChannelsFormSidebarOpen ? (
              <>
                <div className="admin_detail_header">
                  {isMobile ? (
                    <button
                      type="button"
                      className="admin_detail_back"
                      onClick={closeMarketingChannelPanel}
                      aria-label="Schließen">
                      &larr;
                    </button>
                  ) : null}
                  <span className="admin_detail_heading">
                    <strong>
                      {selectedMarketingChannelId != null
                        ? "Kanal bearbeiten"
                        : "Neuer Kanal"}
                    </strong>
                    <br />
                    <span className="admin_detail_heading_sub">
                      Name und Beschreibung für den Marketing-Kanal.
                    </span>
                  </span>
                </div>
                <div
                  className="admin_detail_body"
                  style={{ padding: "1rem", flexDirection: "column", gap: "12px" }}>
                  <label className="admin_product_user_credentials_label">
                    <span>Name</span>
                    <input
                      type="text"
                      className="admin_input"
                      value={marketingChannelNameDraft}
                      onChange={(e) => setMarketingChannelNameDraft(e.target.value)}
                      autoComplete="off"
                    />
                  </label>
                  <label className="admin_product_user_credentials_label">
                    <span>Beschreibung</span>
                    <textarea
                      className="admin_textarea"
                      value={marketingChannelDescriptionDraft}
                      onChange={(e) =>
                        setMarketingChannelDescriptionDraft(e.target.value)
                      }
                      rows={6}
                      placeholder="Optional"
                    />
                  </label>
                  {marketingChannelFormError ? (
                    <p className="admin_error">{marketingChannelFormError}</p>
                  ) : null}
                  <div style={{ display: "flex", gap: "8px", flexWrap: "wrap", marginTop: "4px" }}>
                    <button
                      type="button"
                      className="admin_btn admin_btn--primary"
                      disabled={marketingChannelSaving}
                      onClick={saveMarketingChannel}>
                      {marketingChannelSaving ? "Speichern…" : "Speichern"}
                    </button>
                    <button
                      type="button"
                      className="admin_btn"
                      disabled={marketingChannelSaving}
                      onClick={closeMarketingChannelPanel}>
                      Abbrechen
                    </button>
                  </div>
                </div>
              </>
            ) : marketingChannelsSidebarOpen ? (
              <>
                <div className="admin_detail_header">
                  <span className="admin_detail_heading">
                    <strong>Kanal auswählen</strong>
                    <br />
                    <span className="admin_detail_heading_sub">
                      Klicke links auf einen Kanal oder lege einen neuen an.
                    </span>
                  </span>
                </div>
                <div className="admin_detail_body admin_detail_body--empty">
                  <div className="admin_detail_empty_state">
                    <span>Noch kein Kanal ausgewählt</span>
                  </div>
                </div>
              </>
            ) : marketingTargetgroupsFormSidebarOpen ? (
              <>
                <div className="admin_detail_header">
                  {isMobile ? (
                    <button
                      type="button"
                      className="admin_detail_back"
                      onClick={closeMarketingTargetgroupPanel}
                      aria-label="Schließen">
                      &larr;
                    </button>
                  ) : null}
                  <span className="admin_detail_heading">
                    <strong>
                      {selectedMarketingTargetgroupId != null
                        ? "Zielgruppe bearbeiten"
                        : "Neue Zielgruppe"}
                    </strong>
                    <br />
                    <span className="admin_detail_heading_sub">
                      Name und Beschreibung für die Marketing-Zielgruppe.
                    </span>
                  </span>
                </div>
                <div
                  className="admin_detail_body"
                  style={{ padding: "1rem", flexDirection: "column", gap: "12px" }}>
                  <label className="admin_product_user_credentials_label">
                    <span>Name</span>
                    <input
                      type="text"
                      className="admin_input"
                      value={marketingTargetgroupNameDraft}
                      onChange={(e) => setMarketingTargetgroupNameDraft(e.target.value)}
                      autoComplete="off"
                    />
                  </label>
                  <label className="admin_product_user_credentials_label">
                    <span>Beschreibung</span>
                    <textarea
                      className="admin_textarea"
                      value={marketingTargetgroupDescriptionDraft}
                      onChange={(e) =>
                        setMarketingTargetgroupDescriptionDraft(e.target.value)
                      }
                      rows={6}
                      placeholder="Optional"
                    />
                  </label>
                  {marketingTargetgroupFormError ? (
                    <p className="admin_error">{marketingTargetgroupFormError}</p>
                  ) : null}
                  <div style={{ display: "flex", gap: "8px", flexWrap: "wrap", marginTop: "4px" }}>
                    <button
                      type="button"
                      className="admin_btn admin_btn--primary"
                      disabled={marketingTargetgroupSaving}
                      onClick={saveMarketingTargetgroup}>
                      {marketingTargetgroupSaving ? "Speichern…" : "Speichern"}
                    </button>
                    <button
                      type="button"
                      className="admin_btn"
                      disabled={marketingTargetgroupSaving}
                      onClick={closeMarketingTargetgroupPanel}>
                      Abbrechen
                    </button>
                  </div>
                </div>
              </>
            ) : marketingTargetgroupsSidebarOpen ? (
              <>
                <div className="admin_detail_header">
                  <span className="admin_detail_heading">
                    <strong>Zielgruppe auswählen</strong>
                    <br />
                    <span className="admin_detail_heading_sub">
                      Klicke links auf eine Zielgruppe oder lege eine neue an.
                    </span>
                  </span>
                </div>
                <div className="admin_detail_body admin_detail_body--empty">
                  <div className="admin_detail_empty_state">
                    <span>Noch keine Zielgruppe ausgewählt</span>
                  </div>
                </div>
              </>
            ) : marketingPostsDetailSidebarOpen ? (
              <>
                <div className="admin_detail_header">
                  {isMobile ? (
                    <button
                      type="button"
                      className="admin_detail_back"
                      onClick={closeMembersPostPanel}
                      aria-label="Schließen">
                      &larr;
                    </button>
                  ) : null}
                  <span className="admin_detail_heading">
                    <strong>Post-Anfrage</strong>
                    <br />
                    <span className="admin_detail_heading_sub">
                      Kanal und Kontakt prüfen, dann als bestätigt markieren.
                    </span>
                  </span>
                </div>
                <div
                  className="admin_detail_body"
                  style={{ padding: "1rem", flexDirection: "column", gap: "12px" }}>
                  {(() => {
                    const sel = membersPosts.find((x) => x.id === selectedMembersPostId) || null;
                    if (!sel) {
                      return (
                        <p className="admin_product_empty" style={{ margin: 0 }}>
                          Eintrag nicht gefunden.
                        </p>
                      );
                    }
                    const st = String(sel.status || "").toLowerCase();
                    const isConfirmed = st === "confirmed";
                    const previewSrc =
                      sel.content_file_key != null && String(sel.content_file_key).trim()
                        ? salesMediaUrl(sel.content_file_key)
                        : "";
                    const kind = String(sel.content_kind || "").toLowerCase();
                    return (
                      <>
                        <div className="admin_product_user_credentials_label">
                          <span>Status</span>
                          <div>
                            <span
                              className={
                                "admin_members_post_badge " +
                                (isConfirmed
                                  ? "admin_members_post_badge--confirmed"
                                  : "admin_members_post_badge--pending")
                              }>
                              {isConfirmed ? "Bestätigt" : "Zur Prüfung"}
                            </span>
                          </div>
                        </div>
                        <div className="admin_product_user_credentials_label">
                          <span>Kanal</span>
                          <div style={{ fontSize: "14px", fontWeight: 600 }}>
                            {membersPostChannelLabel(sel.channel)}
                          </div>
                        </div>
                        <div className="admin_product_user_credentials_label">
                          <span>Angefragt von</span>
                          <div style={{ fontSize: "13px", lineHeight: 1.4 }}>
                            {formatMembersPostActorLabel(
                              sel,
                              "requested_by",
                              "requested_by_display_name",
                              "requested_by_email",
                            )}
                          </div>
                        </div>
                        <label className="admin_product_user_credentials_label">
                          <span>Kontakt</span>
                          <input
                            type="text"
                            className="admin_input"
                            readOnly
                            value={String(sel.contact_value || "")}
                          />
                        </label>
                        <div className="admin_product_user_credentials_label">
                          <span>Content</span>
                          <div style={{ fontSize: "13px", fontWeight: 600 }}>
                            {String(sel.content_title || "").trim() || "(ohne Titel)"}
                          </div>
                          <div style={{ fontSize: "11px", color: "var(--admin-text-secondary, #717171)" }}>
                            ID {sel.content_id}
                            {kind ? ` · ${kind === "video" ? "Video" : "Bild"}` : ""}
                          </div>
                          {previewSrc ? (
                            kind === "video" ? (
                              <video
                                className="admin_sales_media_preview"
                                src={previewSrc}
                                controls
                                playsInline
                              />
                            ) : (
                              <img className="admin_sales_media_preview" src={previewSrc} alt="" />
                            )
                          ) : null}
                        </div>
                        <div className="admin_product_user_credentials_label">
                          <span>Angefragt am</span>
                          <div style={{ fontSize: "13px" }}>
                            {formatMembersPostTs(sel.created_at)}
                          </div>
                        </div>
                        {isConfirmed && sel.confirmed_at ? (
                          <div className="admin_product_user_credentials_label">
                            <span>Bestätigt am</span>
                            <div style={{ fontSize: "13px" }}>
                              {formatMembersPostTs(sel.confirmed_at)}
                            </div>
                          </div>
                        ) : null}
                        {isConfirmed ? (
                          <div className="admin_product_user_credentials_label">
                            <span>Bestätigt von</span>
                            <div style={{ fontSize: "13px", lineHeight: 1.4 }}>
                              {formatMembersPostActorLabel(
                                sel,
                                "confirmed_by",
                                "confirmed_by_display_name",
                                "confirmed_by_email",
                              )}
                            </div>
                          </div>
                        ) : null}
                        {membersPostConfirmError ? (
                          <p className="admin_error">{membersPostConfirmError}</p>
                        ) : null}
                        {!isConfirmed ? (
                          <div style={{ display: "flex", gap: "8px", flexWrap: "wrap", marginTop: "4px" }}>
                            <button
                              type="button"
                              className="admin_btn admin_btn--primary"
                              disabled={membersPostConfirmSaving}
                              onClick={confirmMarketingMembersPost}>
                              {membersPostConfirmSaving ? "Speichern…" : "Als bestätigt markieren"}
                            </button>
                          </div>
                        ) : null}
                      </>
                    );
                  })()}
                </div>
              </>
            ) : marketingPostsSidebarOpen ? (
              <>
                <div className="admin_detail_header">
                  <span className="admin_detail_heading">
                    <strong>Post auswählen</strong>
                    <br />
                    <span className="admin_detail_heading_sub">
                      Klicke links auf eine Anfrage.
                    </span>
                  </span>
                </div>
                <div className="admin_detail_body admin_detail_body--empty">
                  <div className="admin_detail_empty_state">
                    <span>Noch keine Anfrage ausgewählt</span>
                  </div>
                </div>
              </>
            ) : marketingCommunityFormSidebarOpen ? (
              <>
                <div className="admin_detail_header">
                  {isMobile ? (
                    <button
                      type="button"
                      className="admin_detail_back"
                      onClick={closeMarketingCommunityContentPanel}
                      aria-label="Schließen">
                      &larr;
                    </button>
                  ) : null}
                  <span className="admin_detail_heading">
                    <strong>
                      {selectedMarketingCommunityContentId != null
                        ? "Community-Content bearbeiten"
                        : "Neuer Community-Content"}
                    </strong>
                    <br />
                    <span className="admin_detail_heading_sub">
                      Bild/Video hochladen und Metadaten pflegen.
                    </span>
                  </span>
                </div>
                <div
                  className="admin_detail_body"
                  style={{ padding: "1rem", flexDirection: "column", gap: "12px" }}>
                  <div className="admin_product_user_credentials_label">
                    <span>Typ</span>
                    <div style={{ display: "flex", gap: "12px", flexWrap: "wrap" }}>
                      <label style={{ display: "inline-flex", alignItems: "center", gap: "6px", fontSize: "13px" }}>
                        <input
                          type="radio"
                          name="marketing_community_kind"
                          checked={marketingCommunityKindDraft === "image"}
                          onChange={() => setMarketingCommunityKindDraft("image")}
                        />
                        <span>Bild</span>
                      </label>
                      <label style={{ display: "inline-flex", alignItems: "center", gap: "6px", fontSize: "13px" }}>
                        <input
                          type="radio"
                          name="marketing_community_kind"
                          checked={marketingCommunityKindDraft === "video"}
                          onChange={() => setMarketingCommunityKindDraft("video")}
                        />
                        <span>Video</span>
                      </label>
                    </div>
                  </div>
                  <label className="admin_product_user_credentials_label">
                    <span>Titel</span>
                    <input
                      type="text"
                      className="admin_input"
                      value={marketingCommunityTitleDraft}
                      onChange={(e) => setMarketingCommunityTitleDraft(e.target.value)}
                    />
                  </label>
                  <label className="admin_product_user_credentials_label">
                    <span>Beschreibung</span>
                    <textarea
                      className="admin_textarea"
                      value={marketingCommunityDescriptionDraft}
                      onChange={(e) => setMarketingCommunityDescriptionDraft(e.target.value)}
                      rows={5}
                    />
                  </label>
                  <label className="admin_product_user_credentials_label">
                    <span>Zielgruppe</span>
                    <select
                      className="admin_input"
                      value={marketingCommunityTargetgroupDraft}
                      onChange={(e) =>
                        setMarketingCommunityTargetgroupDraft(e.target.value)
                      }
                      disabled={marketingTargetgroupsLoading}>
                      <option value="">Keine</option>
                      {marketingTargetgroups.map((tg) => (
                        <option key={tg.id} value={String(tg.id)}>
                          {tg.name || "(ohne Name)"}
                        </option>
                      ))}
                    </select>
                  </label>
                  <label className="admin_product_user_credentials_label">
                    <span>Verdienst in €</span>
                    <input
                      type="number"
                      inputMode="numeric"
                      min={0}
                      step={1}
                      className="admin_input"
                      value={marketingCommunityEarningsEurDraft}
                      onChange={(e) => setMarketingCommunityEarningsEurDraft(e.target.value)}
                    />
                  </label>
                  <label className="admin_product_user_credentials_label">
                    <span>Datei{selectedMarketingCommunityContentId != null ? " (optional)" : ""}</span>
                    <input
                      type="file"
                      accept="image/*,video/*"
                      onChange={(e) =>
                        setMarketingCommunityFileDraft(
                          e.target.files && e.target.files[0] ? e.target.files[0] : null,
                        )
                      }
                    />
                  </label>
                  {selectedMarketingCommunityContentId != null ? (
                    <div style={{ display: "flex", flexDirection: "column", gap: "8px" }}>
                      <div style={{ fontSize: "12px", color: "var(--admin-text-secondary, #717171)" }}>
                        Aktuelle Datei:{" "}
                        {(() => {
                          const row = salesContents.find(
                            (x) => x.id === selectedMarketingCommunityContentId,
                          );
                          return row && row.file_key ? row.file_key : "–";
                        })()}
                      </div>
                      {(() => {
                        const row = salesContents.find(
                          (x) => x.id === selectedMarketingCommunityContentId,
                        );
                        const src = row && row.file_key ? salesMediaUrl(row.file_key) : "";
                        if (!src) return null;
                        return row.kind === "video" ? (
                          <video className="admin_sales_media_preview" src={src} controls playsInline />
                        ) : (
                          <img className="admin_sales_media_preview" src={src} alt="" />
                        );
                      })()}
                    </div>
                  ) : null}
                  {marketingCommunityFormError ? (
                    <p className="admin_error">{marketingCommunityFormError}</p>
                  ) : null}
                  <div style={{ display: "flex", gap: "8px", flexWrap: "wrap", marginTop: "4px" }}>
                    <button
                      type="button"
                      className="admin_btn admin_btn--primary"
                      disabled={marketingCommunitySaving}
                      onClick={saveMarketingCommunityContent}>
                      {marketingCommunitySaving ? "Speichern…" : "Speichern"}
                    </button>
                    <button
                      type="button"
                      className="admin_btn"
                      disabled={marketingCommunitySaving}
                      onClick={closeMarketingCommunityContentPanel}>
                      Abbrechen
                    </button>
                  </div>
                </div>
              </>
            ) : marketingCommunitySidebarOpen ? (
              <>
                <div className="admin_detail_header">
                  <span className="admin_detail_heading">
                    <strong>Community-Content auswählen</strong>
                    <br />
                    <span className="admin_detail_heading_sub">
                      Klicke links auf eine Karte oder lege neuen Content an.
                    </span>
                  </span>
                </div>
                <div className="admin_detail_body admin_detail_body--empty">
                  <div className="admin_detail_empty_state">
                    <span>Noch kein Eintrag ausgewählt</span>
                  </div>
                </div>
              </>
            ) : marketingChannelContentFormSidebarOpen ? (
              <>
                <div className="admin_detail_header">
                  {isMobile ? (
                    <button
                      type="button"
                      className="admin_detail_back"
                      onClick={closeMarketingChannelContentPanel}
                      aria-label="Schließen">
                      &larr;
                    </button>
                  ) : null}
                  <span className="admin_detail_heading">
                    <strong>
                      {selectedMarketingChannelContentId != null
                        ? "Channel-Content bearbeiten"
                        : "Neuer Channel-Content"}
                    </strong>
                    <br />
                    <span className="admin_detail_heading_sub">
                      Bild/Video pro Kanal hochladen und Metadaten pflegen.
                    </span>
                  </span>
                </div>
                <div
                  className="admin_detail_body"
                  style={{ padding: "1rem", flexDirection: "column", gap: "12px" }}>
                  <div className="admin_product_user_credentials_label">
                    <span>Typ</span>
                    <div style={{ display: "flex", gap: "12px", flexWrap: "wrap" }}>
                      <label style={{ display: "inline-flex", alignItems: "center", gap: "6px", fontSize: "13px" }}>
                        <input
                          type="radio"
                          name="marketing_channel_content_kind"
                          checked={marketingChannelContentKindDraft === "image"}
                          onChange={() => setMarketingChannelContentKindDraft("image")}
                        />
                        <span>Bild</span>
                      </label>
                      <label style={{ display: "inline-flex", alignItems: "center", gap: "6px", fontSize: "13px" }}>
                        <input
                          type="radio"
                          name="marketing_channel_content_kind"
                          checked={marketingChannelContentKindDraft === "video"}
                          onChange={() => setMarketingChannelContentKindDraft("video")}
                        />
                        <span>Video</span>
                      </label>
                    </div>
                  </div>
                  <label className="admin_product_user_credentials_label">
                    <span>Titel</span>
                    <input
                      type="text"
                      className="admin_input"
                      value={marketingChannelContentTitleDraft}
                      onChange={(e) => setMarketingChannelContentTitleDraft(e.target.value)}
                    />
                  </label>
                  <label className="admin_product_user_credentials_label">
                    <span>Beschreibung</span>
                    <textarea
                      className="admin_textarea"
                      value={marketingChannelContentDescriptionDraft}
                      onChange={(e) =>
                        setMarketingChannelContentDescriptionDraft(e.target.value)
                      }
                      rows={5}
                    />
                  </label>
                  <label className="admin_product_user_credentials_label">
                    <span>Kanal</span>
                    <select
                      className="admin_input"
                      value={marketingChannelContentChannelDraft}
                      onChange={(e) =>
                        setMarketingChannelContentChannelDraft(e.target.value)
                      }
                      disabled={marketingChannelsLoading}>
                      <option value="">Keiner</option>
                      {marketingChannels.map((ch) => (
                        <option key={ch.id} value={String(ch.id)}>
                          {ch.name || "(ohne Name)"}
                        </option>
                      ))}
                    </select>
                  </label>
                  <label className="admin_product_user_credentials_label">
                    <span>Verdienst in €</span>
                    <input
                      type="number"
                      inputMode="numeric"
                      min={0}
                      step={1}
                      className="admin_input"
                      value={marketingChannelContentEarningsEurDraft}
                      onChange={(e) =>
                        setMarketingChannelContentEarningsEurDraft(e.target.value)
                      }
                    />
                  </label>
                  <label className="admin_product_user_credentials_label">
                    <span>
                      Datei
                      {selectedMarketingChannelContentId != null ? " (optional)" : ""}
                    </span>
                    <input
                      type="file"
                      accept="image/*,video/*"
                      onChange={(e) =>
                        setMarketingChannelContentFileDraft(
                          e.target.files && e.target.files[0] ? e.target.files[0] : null,
                        )
                      }
                    />
                  </label>
                  {selectedMarketingChannelContentId != null ? (
                    <div style={{ display: "flex", flexDirection: "column", gap: "8px" }}>
                      <div style={{ fontSize: "12px", color: "var(--admin-text-secondary, #717171)" }}>
                        Aktuelle Datei:{" "}
                        {(() => {
                          const row = channelContents.find(
                            (x) => x.id === selectedMarketingChannelContentId,
                          );
                          return row && row.file_key ? row.file_key : "–";
                        })()}
                      </div>
                      {(() => {
                        const row = channelContents.find(
                          (x) => x.id === selectedMarketingChannelContentId,
                        );
                        const src = row && row.file_key ? salesMediaUrl(row.file_key) : "";
                        if (!src) return null;
                        return row.kind === "video" ? (
                          <video className="admin_sales_media_preview" src={src} controls playsInline />
                        ) : (
                          <img className="admin_sales_media_preview" src={src} alt="" />
                        );
                      })()}
                    </div>
                  ) : null}
                  {marketingChannelContentFormError ? (
                    <p className="admin_error">{marketingChannelContentFormError}</p>
                  ) : null}
                  <div style={{ display: "flex", gap: "8px", flexWrap: "wrap", marginTop: "4px" }}>
                    <button
                      type="button"
                      className="admin_btn admin_btn--primary"
                      disabled={marketingChannelContentSaving}
                      onClick={saveMarketingChannelContent}>
                      {marketingChannelContentSaving ? "Speichern…" : "Speichern"}
                    </button>
                    <button
                      type="button"
                      className="admin_btn"
                      disabled={marketingChannelContentSaving}
                      onClick={closeMarketingChannelContentPanel}>
                      Abbrechen
                    </button>
                  </div>
                </div>
              </>
            ) : marketingChannelContentSidebarOpen ? (
              <>
                <div className="admin_detail_header">
                  <span className="admin_detail_heading">
                    <strong>Channel-Content auswählen</strong>
                    <br />
                    <span className="admin_detail_heading_sub">
                      Klicke links auf eine Karte oder lege neuen Channel-Content an.
                    </span>
                  </span>
                </div>
                <div className="admin_detail_body admin_detail_body--empty">
                  <div className="admin_detail_empty_state">
                    <span>Noch kein Eintrag ausgewählt</span>
                  </div>
                </div>
              </>
            ) : salesContentsFormSidebarOpen ? (
              <>
                <div className="admin_detail_header admin_detail_header--sales_akquise">
                  {isMobile ? (
                    <button
                      type="button"
                      className="admin_detail_back"
                      onClick={closeSalesContentPanel}
                      aria-label="Schließen">
                      &larr;
                    </button>
                  ) : null}
                  <span className="admin_detail_heading" style={{ flex: 1, minWidth: 0, margin: 0 }}>
                    <strong
                      style={{
                        display: "block",
                        fontSize: isMobile ? "15px" : "16px",
                        lineHeight: 1.25,
                        overflow: "hidden",
                        textOverflow: "ellipsis",
                        whiteSpace: "nowrap",
                      }}
                      title={(() => {
                        const row =
                          selectedSalesContentId != null
                            ? salesContents.find((x) => x.id === selectedSalesContentId)
                            : null;
                        return row ? String(row.title || "").trim() || "(ohne Titel)" : "(ohne Titel)";
                      })()}>
                      {(() => {
                        const row =
                          selectedSalesContentId != null
                            ? salesContents.find((x) => x.id === selectedSalesContentId)
                            : null;
                        return row ? String(row.title || "").trim() || "(ohne Titel)" : "(ohne Titel)";
                      })()}
                    </strong>
                  </span>
                  <div className="admin_detail_header_actions">
                    <button
                      type="button"
                      className="admin_btn admin_detail_header_btn"
                      onClick={downloadSalesAkquiseContent}>
                      Herunterladen
                    </button>
                    <button
                      type="button"
                      className="admin_btn admin_detail_header_btn"
                      onClick={shareSalesAkquiseContent}>
                      Teilen
                    </button>
                  </div>
                </div>
                <div className="admin_detail_body admin_detail_body--sales_akquise">
                  {(() => {
                    const row =
                      selectedSalesContentId != null
                        ? salesContents.find((x) => x.id === selectedSalesContentId)
                        : null;
                    if (!row) return null;
                    const cid = row.id;
                    const src = row.file_key ? salesMediaUrl(row.file_key) : "";
                    const desc = String(row.description || "").trim();
                    const titleStr = String(row.title || "").trim();
                    const social = salesAkquiseSocialByContentId[cid] || {};
                    const awaitingMarketing = salesAkquiseAwaitingMarketingByContentId[cid] || {};
                    const earningsEurNum = Number(row.earnings_eur);
                    const earningsShow =
                      Number.isFinite(earningsEurNum) && earningsEurNum >= 0
                        ? new Intl.NumberFormat("de-DE", { maximumFractionDigits: 0 }).format(
                            earningsEurNum,
                          ) + "\u00a0€"
                        : "–";
                    const tgIdRaw = row.targetgroup_id;
                    const tgId =
                      tgIdRaw != null && Number.isFinite(Number(tgIdRaw))
                        ? Number(tgIdRaw)
                        : null;
                    const targetgroupNameFromApi = String(
                      row.targetgroup_name != null ? row.targetgroup_name : "",
                    ).trim();
                    const targetgroupNameFromList =
                      tgId != null
                        ? String(
                            marketingTargetgroups.find((t) => Number(t.id) === tgId)?.name ||
                              "",
                          ).trim()
                        : "";
                    const targetgroupLabel =
                      tgId != null ? targetgroupNameFromApi || targetgroupNameFromList : "";
                    const targetgroupDisplay =
                      tgId != null && targetgroupLabel ? targetgroupLabel : null;
                    const waAwaitingMarketing = !!awaitingMarketing.whatsapp;
                    const waSaving = salesAkquiseConfirmSavingKey === `${cid}:whatsapp`;
                    const isVideoMedium = row.kind === "video";
                    return (
                      <div className="admin_sales_akquise_detail">
                        <div className="admin_sales_akquise_detail_hero">
                          <div className="admin_sales_akquise_detail_thumb_wrap">
                            {src ? (
                              row.kind === "video" ? (
                                <video
                                  className="admin_sales_akquise_detail_thumb_media"
                                  src={src}
                                  controls
                                  playsInline
                                  preload="metadata"
                                />
                              ) : (
                                <img
                                  className="admin_sales_akquise_detail_thumb_media"
                                  src={src}
                                  alt=""
                                  loading="lazy"
                                />
                              )
                            ) : (
                              <div className="admin_sales_akquise_detail_thumb_placeholder">
                                Kein Medium
                              </div>
                            )}
                          </div>
                          <div className="admin_sales_akquise_detail_cta">
                            <div className="admin_sales_akquise_detail_cta_copy">
                              <p className="admin_sales_akquise_detail_cta_title">
                                {titleStr || "(ohne Titel)"}
                              </p>
                              {desc ? (
                                <p className="admin_sales_akquise_detail_cta_desc">{desc}</p>
                              ) : null}
                              {targetgroupDisplay ? (
                                <span
                                  className="admin_sales_akquise_detail_targetgroup"
                                  title="Zielgruppe">
                                  {targetgroupDisplay}
                                </span>
                              ) : null}
                            </div>
                          </div>
                        </div>
                        <hr className="admin_sales_akquise_detail_rule" />
                        {salesAkquisePostError ? (
                          <p className="admin_error" style={{ margin: "0 0 8px" }}>
                            {salesAkquisePostError}
                          </p>
                        ) : null}
                        {salesAkquisePostInfo ? (
                          <p
                            style={{
                              margin: "0 0 8px",
                              fontSize: "13px",
                              color: "var(--cn-semantic-success, #008a05)",
                            }}>
                            {salesAkquisePostInfo}
                          </p>
                        ) : null}
                        <p className="admin_sales_akquise_detail_hint">
                          <span className="admin_sales_akquise_detail_hint_earn">
                            Verdiene{" "}
                            <strong className="admin_sales_akquise_detail_hint_amount">
                              {earningsShow}
                            </strong>
                          </span>
                        </p>
                        <div className="admin_sales_akquise_steps">
                          <div className="admin_sales_akquise_step">
                            <span className="admin_sales_akquise_step_num" aria-hidden="true">
                              1
                            </span>
                            <div className="admin_sales_akquise_step_body">
                              <p className="admin_sales_akquise_step_title">
                                {isVideoMedium ? "Video runterladen" : "Bild runterladen"}
                              </p>
                              <button
                                type="button"
                                className="admin_btn admin_btn--primary admin_sales_akquise_step_btn"
                                onClick={() => downloadSalesAkquiseContent()}>
                                {isVideoMedium ? "Video herunterladen" : "Bild herunterladen"}
                              </button>
                            </div>
                          </div>
                          <div className="admin_sales_akquise_step">
                            <span className="admin_sales_akquise_step_num" aria-hidden="true">
                              2
                            </span>
                            <div className="admin_sales_akquise_step_body">
                              <p className="admin_sales_akquise_step_title">
                                Auf WhatsApp-Status teilen
                              </p>
                              <p className="admin_sales_akquise_step_hint">
                                {isVideoMedium
                                  ? "Lade das Video in deinen WhatsApp-Status hoch."
                                  : "Lade das Bild in deinen WhatsApp-Status hoch."}
                              </p>
                            </div>
                          </div>
                          <div className="admin_sales_akquise_step">
                            <span className="admin_sales_akquise_step_num" aria-hidden="true">
                              3
                            </span>
                            <div className="admin_sales_akquise_step_body">
                              <p className="admin_sales_akquise_step_title">
                                Dein WhatsApp-Profil auf öffentlich schalten
                              </p>
                              <p className="admin_sales_akquise_step_hint">
                                In WhatsApp unter Profil bzw. Datenschutz sicherstellen, dass dein
                                Profil öffentlich sichtbar ist.
                              </p>
                            </div>
                          </div>
                          <div className="admin_sales_akquise_step">
                            <span className="admin_sales_akquise_step_num" aria-hidden="true">
                              4
                            </span>
                            <div className="admin_sales_akquise_step_body">
                              <p className="admin_sales_akquise_step_title">
                                Deine Telnummer hier eintragen und eine Überprüfung anfordern
                              </p>
                              <label className="admin_product_user_credentials_label">
                                <span>WhatsApp-Rufnummer</span>
                                <input
                                  type="text"
                                  className="admin_input"
                                  inputMode="tel"
                                  autoComplete="off"
                                  placeholder="+49 …"
                                  value={social.whatsapp ?? ""}
                                  onChange={(e) =>
                                    patchAkquiseSocialField(cid, "whatsapp", e.target.value)
                                  }
                                  disabled={waAwaitingMarketing}
                                />
                              </label>
                              <button
                                type="button"
                                className={
                                  "admin_btn admin_sales_akquise_confirm_btn" +
                                  (waAwaitingMarketing ? "" : " admin_btn--primary")
                                }
                                disabled={waAwaitingMarketing || waSaving}
                                title={
                                  waAwaitingMarketing
                                    ? "Marketing prüft diese Anfrage unter Marketing › Posts."
                                    : undefined
                                }
                                onClick={() => confirmAkquiseSocialRow(cid, "whatsapp")}>
                                {waAwaitingMarketing
                                  ? "In Prüfung"
                                  : waSaving
                                    ? "…"
                                    : "Bestätigen"}
                              </button>
                            </div>
                          </div>
                        </div>
                      </div>
                    );
                  })()}
                </div>
              </>
            ) : salesContentsSidebarOpen ? (
              <>
                <div className="admin_detail_header">
                  <span className="admin_detail_heading">
                    <strong>Content auswählen</strong>
                    <br />
                    <span className="admin_detail_heading_sub">
                      Klicke links auf eine Karte.
                    </span>
                  </span>
                </div>
                <div className="admin_detail_body admin_detail_body--empty">
                  <div className="admin_detail_empty_state">
                    <span>Noch kein Content ausgewählt</span>
                  </div>
                </div>
              </>
            ) : salesTrainingsSidebarOpen &&
              activeView === "sales_training" &&
              selectedSalesTrainingId != null ? (
              (() => {
                const row = salesTrainings.find((x) => x.id === selectedSalesTrainingId);
                const src = row && row.file_key ? salesMediaUrl(row.file_key) : "";
                const trainingQuestions = [
                  {
                    id: "q1",
                    text: "Frage 1: Was ist der wichtigste Schritt im ersten Kundenkontakt?",
                    answers: [
                      "Antwort A – Direkt Preis nennen",
                      "Antwort B – Bedarf erfragen und zuhören",
                      "Antwort C – Über das Unternehmen sprechen",
                    ],
                  },
                  {
                    id: "q2",
                    text: "Frage 2: Wie reagierst du auf einen Einwand?",
                    answers: [
                      "Antwort A – Einwand wegdiskutieren",
                      "Antwort B – Einwand wiederholen und nachfragen",
                      "Antwort C – Sofort Rabatt anbieten",
                    ],
                  },
                  {
                    id: "q3",
                    text: "Frage 3: Wann ist der beste Zeitpunkt für den Abschluss?",
                    answers: [
                      "Antwort A – Direkt am Anfang des Gesprächs",
                      "Antwort B – Nachdem alle Fragen geklärt sind",
                      "Antwort C – Nie aktiv vorschlagen",
                    ],
                  },
                ];
                return (
                  <>
                    <div className="admin_detail_header">
                      {isMobile ? (
                        <button
                          type="button"
                          className="admin_detail_back"
                          onClick={closeSalesTrainingPanel}
                          aria-label="Schließen">
                          &larr;
                        </button>
                      ) : null}
                      <span className="admin_detail_heading">
                        <strong>{(row && row.title) || "Schulung"}</strong>
                        <br />
                        <span className="admin_detail_heading_sub">
                          Schau dir das Material an und beantworte die Fragen.
                        </span>
                      </span>
                    </div>
                    <div
                      className="admin_detail_body"
                      style={{
                        padding: 0,
                        flexDirection: "column",
                        gap: 0,
                        overflow: "auto",
                      }}>
                      <div className="admin_sales_training_media">
                        {src ? (
                          row && row.kind === "video" ? (
                            <video
                              className="admin_sales_training_media_el"
                              src={src}
                              controls
                              playsInline
                            />
                          ) : (
                            <img
                              className="admin_sales_training_media_el"
                              src={src}
                              alt={(row && row.title) || ""}
                            />
                          )
                        ) : (
                          <div className="admin_sales_training_media_empty">
                            Kein Medium vorhanden.
                          </div>
                        )}
                      </div>
                      <div className="admin_sales_training_quiz">
                        {trainingQuestions.map((q) => (
                          <div className="admin_sales_training_question" key={q.id}>
                            <div className="admin_sales_training_question_text">{q.text}</div>
                            <div className="admin_sales_training_answers">
                              {q.answers.map((a, i) => (
                                <button
                                  key={i}
                                  type="button"
                                  className="admin_sales_training_answer"
                                  onClick={() => {}}>
                                  {a}
                                </button>
                              ))}
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                  </>
                );
              })()
            ) : salesTrainingsFormSidebarOpen && activeView === "people_schulungen" ? (
              <>
                <div className="admin_detail_header">
                  {isMobile ? (
                    <button
                      type="button"
                      className="admin_detail_back"
                      onClick={closeSalesTrainingPanel}
                      aria-label="Schließen">
                      &larr;
                    </button>
                  ) : null}
                  <span className="admin_detail_heading">
                    <strong>
                      {selectedSalesTrainingId != null ? "Schulung bearbeiten" : "Neue Schulung"}
                    </strong>
                    <br />
                    <span className="admin_detail_heading_sub">
                      Bild/Video hochladen und Metadaten pflegen.
                    </span>
                  </span>
                </div>
                <div
                  className="admin_detail_body"
                  style={{ padding: "1rem", flexDirection: "column", gap: "12px" }}>
                  <div className="admin_product_user_credentials_label">
                    <span>Typ</span>
                    <div style={{ display: "flex", gap: "12px", flexWrap: "wrap" }}>
                      <label style={{ display: "inline-flex", alignItems: "center", gap: "6px", fontSize: "13px" }}>
                        <input
                          type="radio"
                          name="sales_training_kind"
                          checked={salesTrainingKindDraft === "image"}
                          onChange={() => setSalesTrainingKindDraft("image")}
                        />
                        <span>Bild</span>
                      </label>
                      <label style={{ display: "inline-flex", alignItems: "center", gap: "6px", fontSize: "13px" }}>
                        <input
                          type="radio"
                          name="sales_training_kind"
                          checked={salesTrainingKindDraft === "video"}
                          onChange={() => setSalesTrainingKindDraft("video")}
                        />
                        <span>Video</span>
                      </label>
                    </div>
                  </div>
                  <label className="admin_product_user_credentials_label">
                    <span>Titel</span>
                    <input
                      type="text"
                      className="admin_input"
                      value={salesTrainingTitleDraft}
                      onChange={(e) => setSalesTrainingTitleDraft(e.target.value)}
                    />
                  </label>
                  <label className="admin_product_user_credentials_label">
                    <span>Beschreibung</span>
                    <textarea
                      className="admin_textarea"
                      value={salesTrainingDescriptionDraft}
                      onChange={(e) => setSalesTrainingDescriptionDraft(e.target.value)}
                      rows={5}
                    />
                  </label>
                  <label className="admin_product_user_credentials_label">
                    <span>Verdienst (€)</span>
                    <input
                      type="number"
                      className="admin_input"
                      min={0}
                      step={1}
                      value={salesTrainingEarningsEurDraft}
                      onChange={(e) =>
                        setSalesTrainingEarningsEurDraft(e.target.value)
                      }
                      placeholder="0"
                    />
                  </label>
                  <label className="admin_product_user_credentials_label">
                    <span>Datei{selectedSalesTrainingId != null ? " (optional)" : ""}</span>
                    <input
                      type="file"
                      accept="image/*,video/*"
                      onChange={(e) => setSalesTrainingFileDraft(e.target.files && e.target.files[0] ? e.target.files[0] : null)}
                    />
                  </label>
                  {selectedSalesTrainingId != null ? (
                    <div style={{ display: "flex", flexDirection: "column", gap: "8px" }}>
                      <div style={{ fontSize: "12px", color: "var(--admin-text-secondary, #717171)" }}>
                        Aktuelle Datei:{" "}
                        {(() => {
                          const row = salesTrainings.find((x) => x.id === selectedSalesTrainingId);
                          return row && row.file_key ? row.file_key : "–";
                        })()}
                      </div>
                      {(() => {
                        const row = salesTrainings.find((x) => x.id === selectedSalesTrainingId);
                        const src = row && row.file_key ? salesMediaUrl(row.file_key) : "";
                        if (!src) return null;
                        return row.kind === "video" ? (
                          <video className="admin_sales_media_preview" src={src} controls playsInline />
                        ) : (
                          <img className="admin_sales_media_preview" src={src} alt="" />
                        );
                      })()}
                    </div>
                  ) : null}
                  {salesTrainingFormError ? <p className="admin_error">{salesTrainingFormError}</p> : null}
                  <div style={{ display: "flex", gap: "8px", flexWrap: "wrap", marginTop: "4px" }}>
                    <button
                      type="button"
                      className="admin_btn admin_btn--primary"
                      disabled={salesTrainingSaving}
                      onClick={saveSalesTraining}>
                      {salesTrainingSaving ? "Speichern…" : "Speichern"}
                    </button>
                    <button
                      type="button"
                      className="admin_btn"
                      disabled={salesTrainingSaving}
                      onClick={closeSalesTrainingPanel}>
                      Abbrechen
                    </button>
                  </div>
                </div>
              </>
            ) : salesTrainingsSidebarOpen ? (
              <>
                <div className="admin_detail_header">
                  <span className="admin_detail_heading">
                    <strong>
                      {activeView === "people_schulungen"
                        ? "Schulungen"
                        : "Schulung auswählen"}
                    </strong>
                    <br />
                    <span className="admin_detail_heading_sub">
                      {activeView === "people_schulungen"
                        ? "Wähle links eine Karte oder lege über „Neue Schulung“ einen Eintrag an."
                        : "Klicke links auf eine Karte oder lege neue Schulung an."}
                    </span>
                  </span>
                </div>
                <div className="admin_detail_body admin_detail_body--empty">
                  <div className="admin_detail_empty_state">
                    <span>Noch keine Schulung ausgewählt</span>
                  </div>
                </div>
              </>
            ) : salesRevenueSidebarOpen ? (
              salesRevenueDetailKind ? (
                <>
                  <div className="admin_detail_header">
                    <span className="admin_detail_heading">
                      <strong>
                        {salesRevenueDetailKind === "career_stage"
                          ? "Karriere"
                          : "Verdienst"}
                      </strong>
                      <br />
                      <span className="admin_detail_heading_sub">
                        {salesRevenueDetailKind === "career_stage"
                          ? "Detailansicht – Inhalt folgt später."
                          : `Summe: ${revenueTotalDisplay}.`}
                      </span>
                    </span>
                  </div>
                  <div
                    className="admin_detail_body"
                    style={{
                      padding: "1rem",
                      flexDirection: "column",
                      gap: "12px",
                    }}>
                    {salesRevenueDetailKind === "career_stage" ? (
                      <p className="admin_product_empty" style={{ margin: 0 }}>
                        Noch kein Inhalt.
                      </p>
                    ) : (
                      <p style={{ margin: 0, fontSize: "14px", lineHeight: 1.45 }}>
                        Summe der Beträge in der Liste links; unter „Posts“ siehst du die einzelnen
                        Buchungen.
                      </p>
                    )}
                    <button
                      type="button"
                      className="admin_btn"
                      onClick={() => setSalesRevenueDetailKind(null)}>
                      Schließen
                    </button>
                  </div>
                </>
              ) : (
                <>
                  <div className="admin_detail_header">
                    <span className="admin_detail_heading">
                      <strong>Einnahmen</strong>
                      <br />
                      <span className="admin_detail_heading_sub">
                        Wähle links „Verdienst“ oder „Karriere“ für die Detailansicht.
                      </span>
                    </span>
                  </div>
                  <div className="admin_detail_body admin_detail_body--empty">
                    <div className="admin_detail_empty_state">
                      <span>Keine Auswahl</span>
                    </div>
                  </div>
                </>
              )
            ) : salesTeamFormSidebarOpen ? (
              <>
                <div className="admin_detail_header">
                  {isMobile ? (
                    <button
                      type="button"
                      className="admin_detail_back"
                      onClick={closeTeamInviteCreatePanel}
                      aria-label="Schließen">
                      &larr;
                    </button>
                  ) : null}
                  <span className="admin_detail_heading">
                    <strong>Person einladen</strong>
                    <br />
                    <span className="admin_detail_heading_sub">
                      Mobilnummer (Pflicht) + optional E-Mail. Per SMS wird der 6-stellige Code
                      verschickt. Den Einladungslink finden Sie auch im Server-Log unter
                      <code> member_invite</code> mit <code>invite_url</code>.
                    </span>
                  </span>
                </div>
                <div
                  className="admin_detail_body"
                  style={{
                    padding: "1rem",
                    flexDirection: "column",
                    gap: "12px",
                  }}>
                  <label className="admin_product_user_credentials_label">
                    <span>Mobilnummer (E.164)</span>
                    <input
                      type="tel"
                      className="admin_input"
                      value={teamInvitePhoneDraft}
                      onChange={(e) => setTeamInvitePhoneDraft(e.target.value)}
                      autoComplete="tel"
                      inputMode="tel"
                      placeholder="+4915112345678"
                    />
                  </label>
                  <label className="admin_product_user_credentials_label">
                    <span>E-Mail (optional, fuer Einladungs-E-Mail)</span>
                    <input
                      type="email"
                      className="admin_input"
                      value={teamInviteEmailDraft}
                      onChange={(e) => setTeamInviteEmailDraft(e.target.value)}
                      autoComplete="email"
                    />
                  </label>
                  {teamInviteFormError ? <p className="admin_error">{teamInviteFormError}</p> : null}
                  <div
                    style={{
                      display: "flex",
                      gap: "8px",
                      flexWrap: "wrap",
                      marginTop: "4px",
                    }}>
                    <button
                      type="button"
                      className="admin_btn admin_btn--primary"
                      disabled={teamInviteSaving}
                      onClick={submitTeamInvite}>
                      {teamInviteSaving ? "Wird erstellt…" : "Einladung senden"}
                    </button>
                    <button
                      type="button"
                      className="admin_btn"
                      disabled={teamInviteSaving}
                      onClick={closeTeamInviteCreatePanel}>
                      Abbrechen
                    </button>
                  </div>
                </div>
              </>
            ) : salesTeamMemberSidebarOpen ? (
              (() => {
                const m = teamRoster.find((x) => x.id === selectedTeamMemberId);
                if (!m) {
                  return (
                    <>
                      <div className="admin_detail_header">
                        {isMobile ? (
                          <button
                            type="button"
                            className="admin_detail_back"
                            onClick={closeTeamMemberChat}
                            aria-label="Zurück">
                            &larr;
                          </button>
                        ) : null}
                        <span className="admin_detail_heading">
                          <strong>Team-Mitglied</strong>
                        </span>
                      </div>
                      <div className="admin_detail_body admin_detail_body--empty">
                        <div className="admin_detail_empty_state">
                          <span>Person nicht gefunden.</span>
                        </div>
                      </div>
                    </>
                  );
                }
                const picSrc = memberProfilePicSrc(
                  m,
                  apiBase,
                  adminToken,
                  profilePicVersion,
                );
                const isMe =
                  adminUser && Number(adminUser.id) === Number(m.id);
                const customerCount = Number(m.customer_count || 0);
                const postCount = Number(m.post_count || 0);
                const careerLevel = m.career_level || "Neu";
                const accessAreas = ADMIN_AREAS.filter((a) => {
                  const flag = areaAccessField(a.id);
                  return flag && !!m[flag];
                });
                const inviterLabel =
                  m.inviter_display_name || m.inviter_email
                    ? [m.inviter_display_name, m.inviter_email]
                        .filter(Boolean)
                        .join(" · ")
                    : null;

                // ---- Chat-Panel (Default): Nachrichten + Composer ----
                if (!teamMemberDetailOpen) {
                  const peerLabel = m.display_name || m.email || `Member ${m.id}`;
                  const myId = adminUser ? Number(adminUser.id) : null;
                  // Tagesueberschrift einfuegen wenn neuer Tag beginnt.
                  const renderItems = [];
                  let lastDayKey = "";
                  for (const msg of teamChatMessages) {
                    const ts = Number(msg.created_at || 0);
                    const d = new Date(ts * 1000);
                    const dayKey = isNaN(d.getTime())
                      ? "?"
                      : `${d.getFullYear()}-${d.getMonth()}-${d.getDate()}`;
                    if (dayKey !== lastDayKey) {
                      renderItems.push({
                        kind: "day",
                        key: `day-${dayKey}-${msg.id}`,
                        label: formatChatDayLabel(ts),
                      });
                      lastDayKey = dayKey;
                    }
                    renderItems.push({ kind: "msg", key: `msg-${msg.id}`, msg });
                  }
                  return (
                    <>
                      <div className="admin_detail_header admin_chat_header">
                        {isMobile ? (
                          <button
                            type="button"
                            className="admin_detail_back"
                            onClick={closeTeamMemberChat}
                            aria-label="Zurück zur Liste">
                            &larr;
                          </button>
                        ) : null}
                        <button
                          type="button"
                          className="admin_chat_header_button"
                          onClick={openTeamMemberDetail}
                          aria-label="Profil oeffnen">
                          {picSrc ? (
                            <img
                              src={picSrc}
                              alt=""
                              className="admin_chat_header_avatar admin_chat_header_avatar--img"
                            />
                          ) : (
                            <div
                              className="admin_chat_header_avatar admin_chat_header_avatar--initials"
                              aria-hidden="true">
                              {memberInitials(m)}
                            </div>
                          )}
                          <span className="admin_chat_header_name">
                            {peerLabel}
                            {isMe ? (
                              <span className="admin_team_member_self_badge"> Du</span>
                            ) : null}
                          </span>
                        </button>
                      </div>
                      <div
                        className="admin_chat_panel"
                        ref={teamChatScrollRef}>
                        {teamChatError ? (
                          <p
                            className="admin_error"
                            style={{ margin: "8px 12px" }}>
                            {teamChatError}
                          </p>
                        ) : null}
                        {!teamChatLoading &&
                          !teamChatError &&
                          teamChatMessages.length === 0 ? (
                          <p
                            className="admin_product_empty"
                            style={{ padding: "24px 16px", textAlign: "center" }}>
                            Noch keine Nachrichten. Schreibe die erste.
                          </p>
                        ) : null}
                        <div className="admin_chat_messages">
                          {renderItems.map((it) => {
                            if (it.kind === "day") {
                              return (
                                <div
                                  key={it.key}
                                  className="admin_chat_day_separator">
                                  <span>{it.label}</span>
                                </div>
                              );
                            }
                            const isMine =
                              myId != null &&
                              Number(it.msg.sender_id) === myId;
                            return (
                              <div
                                key={it.key}
                                className={
                                  "admin_chat_msg" +
                                  (isMine
                                    ? " admin_chat_msg--me"
                                    : " admin_chat_msg--them")
                                }>
                                <div className="admin_chat_msg_bubble">
                                  <span className="admin_chat_msg_text">
                                    {String(it.msg.content || "")}
                                  </span>
                                  <span className="admin_chat_msg_time">
                                    {formatChatBubbleTime(it.msg.created_at)}
                                  </span>
                                </div>
                              </div>
                            );
                          })}
                        </div>
                      </div>
                      <div className="admin_chat_composer">
                        <textarea
                          className="admin_input admin_chat_composer_input"
                          placeholder="Nachricht schreiben…"
                          rows={1}
                          value={teamChatDraft}
                          onChange={(e) => setTeamChatDraft(e.target.value)}
                          onKeyDown={(e) => {
                            if (e.key === "Enter" && !e.shiftKey) {
                              e.preventDefault();
                              sendTeamChat();
                            }
                          }}
                          disabled={teamChatSending}
                          aria-label="Nachricht"
                        />
                        <button
                          type="button"
                          className="admin_btn admin_btn--primary admin_chat_composer_send"
                          disabled={
                            teamChatSending || !teamChatDraft.trim()
                          }
                          onClick={sendTeamChat}
                          aria-label="Senden">
                          {teamChatSending ? "…" : "Senden"}
                        </button>
                      </div>
                    </>
                  );
                }

                // ---- Detail-Panel (oeffnet vom Chat-Header) ----
                return (
                  <>
                    <div className="admin_detail_header">
                      <button
                        type="button"
                        className="admin_detail_back"
                        onClick={closeTeamMemberDetail}
                        aria-label="Zurück zum Chat">
                        &larr;
                      </button>
                      <span className="admin_detail_heading">
                        <strong>Team-Mitglied</strong>
                        <br />
                        <span className="admin_detail_heading_sub">
                          Stammdaten, Berechtigungen und Aktivität.
                        </span>
                      </span>
                    </div>
                    <div
                      className="admin_detail_body"
                      style={{
                        padding: "20px 16px 24px",
                        flexDirection: "column",
                        gap: "16px",
                        alignItems: "stretch",
                      }}>
                      <div
                        style={{
                          display: "flex",
                          flexDirection: "column",
                          alignItems: "center",
                          gap: "10px",
                        }}>
                        {picSrc ? (
                          <img
                            src={picSrc}
                            alt=""
                            className="admin_team_member_detail_avatar admin_team_member_detail_avatar--img"
                          />
                        ) : (
                          <div
                            className="admin_team_member_detail_avatar admin_team_member_detail_avatar--initials"
                            aria-hidden="true">
                            {memberInitials(m)}
                          </div>
                        )}
                        <div
                          style={{
                            textAlign: "center",
                            display: "flex",
                            flexDirection: "column",
                            gap: "4px",
                          }}>
                          <span
                            style={{
                              fontSize: "18px",
                              fontWeight: 700,
                              color: "var(--admin-text, var(--cn-text-primary))",
                            }}>
                            {m.display_name || m.email || "(ohne Name)"}
                            {isMe ? (
                              <span className="admin_team_member_self_badge"> Du</span>
                            ) : null}
                          </span>
                          {m.email ? (
                            <span
                              style={{
                                fontSize: "13px",
                                color:
                                  "var(--admin-text-secondary, var(--cn-text-secondary))",
                              }}>
                              {m.email}
                            </span>
                          ) : null}
                        </div>
                      </div>

                      <div
                        style={{
                          display: "grid",
                          gridTemplateColumns: "repeat(3, minmax(0, 1fr))",
                          gap: "8px",
                        }}>
                        {[
                          {
                            label: customerCount === 1 ? "Kunde" : "Kunden",
                            value: customerCount,
                          },
                          {
                            label: postCount === 1 ? "Post" : "Posts",
                            value: postCount,
                          },
                          { label: "Stufe", value: careerLevel },
                        ].map((stat) => (
                          <div
                            key={stat.label}
                            style={{
                              border:
                                "1px solid var(--admin-border, var(--cn-border))",
                              borderRadius: "10px",
                              padding: "10px 12px",
                              display: "flex",
                              flexDirection: "column",
                              alignItems: "center",
                              gap: "2px",
                              background: "var(--admin-bg-card, var(--cn-bg-card))",
                            }}>
                            <span
                              style={{
                                fontSize: "16px",
                                fontWeight: 700,
                                color:
                                  "var(--admin-text, var(--cn-text-primary))",
                              }}>
                              {stat.value}
                            </span>
                            <span
                              style={{
                                fontSize: "11px",
                                color:
                                  "var(--admin-text-secondary, var(--cn-text-secondary))",
                                letterSpacing: "0.02em",
                              }}>
                              {stat.label}
                            </span>
                          </div>
                        ))}
                      </div>

                      <div>
                        <div
                          style={{
                            fontSize: "11px",
                            fontWeight: 700,
                            letterSpacing: "0.04em",
                            textTransform: "uppercase",
                            color:
                              "var(--admin-text-secondary, var(--cn-text-secondary))",
                            marginBottom: "6px",
                          }}>
                          Berechtigungen
                        </div>
                        {accessAreas.length === 0 ? (
                          <span
                            style={{
                              fontSize: "13px",
                              color:
                                "var(--admin-text-secondary, var(--cn-text-secondary))",
                            }}>
                            Keine Bereichs-Freigaben.
                          </span>
                        ) : (
                          <div
                            style={{
                              display: "flex",
                              flexWrap: "wrap",
                              gap: "6px",
                            }}>
                            {accessAreas.map((a) => (
                              <span
                                key={a.id}
                                style={{
                                  fontSize: "11px",
                                  fontWeight: 700,
                                  letterSpacing: "0.02em",
                                  padding: "3px 8px",
                                  borderRadius: "999px",
                                  border:
                                    "1px solid var(--admin-border, var(--cn-border))",
                                  background:
                                    "var(--admin-bg-input, var(--cn-bg-card))",
                                  color:
                                    "var(--admin-text, var(--cn-text-primary))",
                                }}>
                                {a.label}
                              </span>
                            ))}
                          </div>
                        )}
                      </div>

                      {inviterLabel ? (
                        <div>
                          <div
                            style={{
                              fontSize: "11px",
                              fontWeight: 700,
                              letterSpacing: "0.04em",
                              textTransform: "uppercase",
                              color:
                                "var(--admin-text-secondary, var(--cn-text-secondary))",
                              marginBottom: "6px",
                            }}>
                            Eingeladen von
                          </div>
                          <span
                            style={{
                              fontSize: "13px",
                              color:
                                "var(--admin-text, var(--cn-text-primary))",
                            }}>
                            {inviterLabel}
                          </span>
                        </div>
                      ) : null}
                    </div>
                  </>
                );
              })()
            ) : salesTeamSidebarOpen ? (
              <>
                <div className="admin_detail_header">
                  <span className="admin_detail_heading">
                    <strong>Team</strong>
                    <br />
                    <span className="admin_detail_heading_sub">
                      Klicke auf „Person einladen“ oder nutze die Mitte-Aktion auf dem Smartphone.
                      Eingeladene erscheinen links; offene Einladungen kannst du widerrufen.
                    </span>
                  </span>
                </div>
                <div className="admin_detail_body admin_detail_body--empty">
                  <div className="admin_detail_empty_state">
                    <span>Noch keine Einladung in Bearbeitung</span>
                  </div>
                </div>
              </>
            ) : aiAgentsDetailOpen &&
              activeView === "ai_agents_services" &&
              selectedMockAiService ? (
              <>
                <div className="admin_detail_header agent_detail_header">
                  {isMobile ? (
                    <button
                      type="button"
                      className="admin_detail_back"
                      onClick={() => setSelectedAiAgentServiceId(null)}
                      aria-label="Zurück">
                      &larr;
                    </button>
                  ) : null}
                  <div className="agent_detail_header_top">
                    <span className="admin_detail_heading">
                      <strong>
                        {selectedMockAiService.icon} {selectedMockAiService.name}
                      </strong>
                      <br />
                      <span className="admin_detail_heading_sub">
                        {selectedMockAiService.slug} · {selectedMockAiService.version}
                        {hasAiServiceDraft ? (
                          <span className="agent_draft_inline_badge">DRAFT aktiv</span>
                        ) : null}
                      </span>
                    </span>
                    <div className="agent_detail_header_actions">
                      {!hasAiServiceDraft ? (
                        <button
                          type="button"
                          className="admin_btn admin_btn--primary"
                          onClick={() => {
                            const sid = selectedAiAgentServiceId;
                            const pub = selectedServicePublished;
                            if (!sid || !pub) return;
                            setAiAgentServiceDraftById((prev) => ({
                              ...prev,
                              [sid]: {
                                brain: { ...pub.brain },
                                contract: JSON.parse(JSON.stringify(pub.contract)),
                                tools: [...pub.tools],
                                knowledge: JSON.parse(
                                  JSON.stringify(pub.knowledge || []),
                                ),
                                versions: JSON.parse(
                                  JSON.stringify(pub.versions || []),
                                ),
                                endpoints: { ...pub.endpoints },
                                consumer_ids: [...pub.consumer_ids],
                              },
                            }));
                          }}>
                          Draft anlegen
                        </button>
                      ) : (
                        <>
                          <button
                            type="button"
                            className="admin_btn"
                            onClick={() => {
                              const sid = selectedAiAgentServiceId;
                              if (!sid) return;
                              setAiAgentServiceDraftById((prev) => {
                                const n = { ...prev };
                                delete n[sid];
                                return n;
                              });
                            }}>
                            Verwerfen
                          </button>
                          <button
                            type="button"
                            className="admin_btn admin_btn--primary"
                            onClick={() => {
                              setAiAgentServicePromoteStatus("beta");
                              setAiAgentServicePromoteOpen(true);
                            }}>
                            Promote…
                          </button>
                        </>
                      )}
                    </div>
                  </div>
                  {hasAiServiceDraft ? (
                    <p className="agent_draft_hint">
                      Änderungen werden im Entwurf gehalten (Mockup, nur im Tab
                      gespeichert). Übersicht zeigt weiter Live-Kennzahlen.
                    </p>
                  ) : null}
                </div>
                <div className="admin_detail_body agent_detail_body">
                  <details className="agent_accordion" open>
                    <summary className="agent_accordion_summary">Übersicht (Live)</summary>
                    <div className="agent_accordion_body">
                      <div className="agent_kpi_row">
                        <div className="agent_kpi_card">
                          <span className="agent_kpi_card_label">Calls / 24h</span>
                          <span className="agent_kpi_card_value">
                            {selectedServiceLive.stats.calls_24h != null
                              ? selectedServiceLive.stats.calls_24h.toLocaleString("de-DE")
                              : "–"}
                          </span>
                        </div>
                        <div className="agent_kpi_card">
                          <span className="agent_kpi_card_label">Success</span>
                          <span className="agent_kpi_card_value">
                            {selectedServiceLive.stats.success != null
                              ? `${(selectedServiceLive.stats.success * 100).toFixed(1)}%`
                              : "–"}
                          </span>
                        </div>
                        <div className="agent_kpi_card">
                          <span className="agent_kpi_card_label">P95 Latenz</span>
                          <span className="agent_kpi_card_value">
                            {selectedServiceLive.stats.p95_ms != null
                              ? `${(selectedServiceLive.stats.p95_ms / 1000).toFixed(1)}s`
                              : "–"}
                          </span>
                        </div>
                      </div>
                      <p className="agent_section_label">Letzte Runs</p>
                      <ul className="agent_mini_run_list">
                        {selectedServiceLive.recent_runs &&
                        selectedServiceLive.recent_runs.length > 0 ? (
                          selectedServiceLive.recent_runs.map((rr) => (
                            <li key={rr.id}>
                              <AgentMockRunIcon status={rr.status} /> {rr.at} · {rr.caller}{" "}
                              ·{" "}
                              {rr.latency_ms != null
                                ? `${(rr.latency_ms / 1000).toFixed(2)}s`
                                : "–"}{" "}
                              · {rr.tokens != null ? `${rr.tokens} tok` : "–"}
                            </li>
                          ))
                        ) : (
                          <li>Keine Einträge.</li>
                        )}
                      </ul>
                    </div>
                  </details>
                  <details className="agent_accordion" open>
                    <summary className="agent_accordion_summary">Contract</summary>
                    <div className="agent_accordion_body">
                      {hasAiServiceDraft ? (
                        <>
                          <label className="agent_field_label">Input-Schema (JSON)</label>
                          <textarea
                            className="admin_input agent_textarea agent_textarea--code"
                            rows={8}
                            value={JSON.stringify(
                              selectedMockAiService.contract.input_schema,
                              null,
                              2,
                            )}
                            onChange={(e) => {
                              const sid = selectedAiAgentServiceId;
                              let parsed;
                              try {
                                parsed = JSON.parse(e.target.value);
                              } catch {
                                return;
                              }
                              setAiAgentServiceDraftById((prev) => {
                                const d = prev[sid];
                                if (!d) return prev;
                                return {
                                  ...prev,
                                  [sid]: {
                                    ...d,
                                    contract: {
                                      ...d.contract,
                                      input_schema: parsed,
                                    },
                                  },
                                };
                              });
                            }}
                          />
                          <label className="agent_field_label">Output-Schema (JSON)</label>
                          <textarea
                            className="admin_input agent_textarea agent_textarea--code"
                            rows={8}
                            value={JSON.stringify(
                              selectedMockAiService.contract.output_schema,
                              null,
                              2,
                            )}
                            onChange={(e) => {
                              const sid = selectedAiAgentServiceId;
                              let parsed;
                              try {
                                parsed = JSON.parse(e.target.value);
                              } catch {
                                return;
                              }
                              setAiAgentServiceDraftById((prev) => {
                                const d = prev[sid];
                                if (!d) return prev;
                                return {
                                  ...prev,
                                  [sid]: {
                                    ...d,
                                    contract: {
                                      ...d.contract,
                                      output_schema: parsed,
                                    },
                                  },
                                };
                              });
                            }}
                          />
                          <label className="agent_field_label">Beispiel request/response (JSON)</label>
                          <textarea
                            className="admin_input agent_textarea agent_textarea--code"
                            rows={6}
                            value={JSON.stringify(
                              {
                                request: selectedMockAiService.contract.example_request,
                                response: selectedMockAiService.contract.example_response,
                              },
                              null,
                              2,
                            )}
                            onChange={(e) => {
                              const sid = selectedAiAgentServiceId;
                              let parsed;
                              try {
                                parsed = JSON.parse(e.target.value);
                              } catch {
                                return;
                              }
                              const req = parsed.request;
                              const res = parsed.response;
                              if (req === undefined || res === undefined) return;
                              setAiAgentServiceDraftById((prev) => {
                                const d = prev[sid];
                                if (!d) return prev;
                                return {
                                  ...prev,
                                  [sid]: {
                                    ...d,
                                    contract: {
                                      ...d.contract,
                                      example_request: req,
                                      example_response: res,
                                    },
                                  },
                                };
                              });
                            }}
                          />
                        </>
                      ) : (
                        <>
                          <p className="agent_section_label">Input-Schema</p>
                          <pre className="agent_json_pre">
                            {JSON.stringify(selectedMockAiService.contract.input_schema, null, 2)}
                          </pre>
                          <p className="agent_section_label">Output-Schema</p>
                          <pre className="agent_json_pre">
                            {JSON.stringify(selectedMockAiService.contract.output_schema, null, 2)}
                          </pre>
                          <p className="agent_section_label">Beispiel</p>
                          <pre className="agent_json_pre">
                            {JSON.stringify(
                              {
                                request: selectedMockAiService.contract.example_request,
                                response: selectedMockAiService.contract.example_response,
                              },
                              null,
                              2,
                            )}
                          </pre>
                        </>
                      )}
                    </div>
                  </details>
                  <details className="agent_accordion">
                    <summary className="agent_accordion_summary">Brain</summary>
                    <div className="agent_accordion_body">
                      <label className="agent_field_label">Modell</label>
                      {hasAiServiceDraft ? (
                        <select
                          className="admin_input"
                          value={selectedMockAiService.brain.model}
                          onChange={(e) => {
                            const sid = selectedAiAgentServiceId;
                            setAiAgentServiceDraftById((prev) => {
                              const d = prev[sid];
                              if (!d) return prev;
                              return {
                                ...prev,
                                [sid]: {
                                  ...d,
                                  brain: { ...d.brain, model: e.target.value },
                                },
                              };
                            });
                          }}>
                          <option value="claude-opus-4.6">claude-opus-4.6</option>
                          <option value="claude-sonnet-4.5">claude-sonnet-4.5</option>
                          <option value="claude-haiku-4.5">claude-haiku-4.5</option>
                        </select>
                      ) : (
                        <input
                          type="text"
                          className="admin_input"
                          readOnly
                          value={selectedMockAiService.brain.model}
                        />
                      )}
                      <label className="agent_field_label">Thinking-Budget</label>
                      <input
                        type="number"
                        className="admin_input"
                        readOnly={!hasAiServiceDraft}
                        value={String(selectedMockAiService.brain.thinking_budget)}
                        onChange={(e) => {
                          const sid = selectedAiAgentServiceId;
                          const n = parseInt(e.target.value, 10);
                          if (!Number.isFinite(n)) return;
                          setAiAgentServiceDraftById((prev) => {
                            const d = prev[sid];
                            if (!d) return prev;
                            return {
                              ...prev,
                              [sid]: {
                                ...d,
                                brain: { ...d.brain, thinking_budget: n },
                              },
                            };
                          });
                        }}
                      />
                      <label className="agent_field_label">Sysprompt</label>
                      <textarea
                        className="admin_input agent_textarea"
                        readOnly={!hasAiServiceDraft}
                        rows={5}
                        value={selectedMockAiService.brain.sysprompt}
                        onChange={(e) => {
                          const sid = selectedAiAgentServiceId;
                          setAiAgentServiceDraftById((prev) => {
                            const d = prev[sid];
                            if (!d) return prev;
                            return {
                              ...prev,
                              [sid]: {
                                ...d,
                                brain: { ...d.brain, sysprompt: e.target.value },
                              },
                            };
                          });
                        }}
                      />
                    </div>
                  </details>
                  <details className="agent_accordion">
                    <summary className="agent_accordion_summary">Tools</summary>
                    <div className="agent_accordion_body">
                      {hasAiServiceDraft ? (
                        <textarea
                          className="admin_input agent_textarea"
                          rows={4}
                          value={selectedMockAiService.tools.join("\n")}
                          onChange={(e) => {
                            const sid = selectedAiAgentServiceId;
                            const tools = e.target.value
                              .split(/[\n,]+/)
                              .map((t) => t.trim())
                              .filter(Boolean);
                            setAiAgentServiceDraftById((prev) => {
                              const d = prev[sid];
                              if (!d) return prev;
                              return { ...prev, [sid]: { ...d, tools } };
                            });
                          }}
                          placeholder="Ein Tool pro Zeile"
                        />
                      ) : (
                        <ul className="agent_tool_list">
                          {selectedMockAiService.tools.map((t) => (
                            <li key={t}>
                              <code>{t}</code>
                            </li>
                          ))}
                        </ul>
                      )}
                    </div>
                  </details>
                  <details className="agent_accordion">
                    <summary className="agent_accordion_summary">Knowledge</summary>
                    <div className="agent_accordion_body">
                      {hasAiServiceDraft ? (
                        <textarea
                          className="admin_input agent_textarea agent_textarea--code"
                          rows={8}
                          value={JSON.stringify(selectedMockAiService.knowledge, null, 2)}
                          onChange={(e) => {
                            const sid = selectedAiAgentServiceId;
                            let parsed;
                            try {
                              parsed = JSON.parse(e.target.value);
                            } catch {
                              return;
                            }
                            if (!Array.isArray(parsed)) return;
                            setAiAgentServiceDraftById((prev) => {
                              const d = prev[sid];
                              if (!d) return prev;
                              return { ...prev, [sid]: { ...d, knowledge: parsed } };
                            });
                          }}
                        />
                      ) : selectedMockAiService.knowledge.length === 0 ? (
                        <p className="admin_product_empty">Keine Quellen.</p>
                      ) : (
                        <table className="agent_data_table">
                          <thead>
                            <tr>
                              <th>Quelle</th>
                              <th>Chunks</th>
                              <th>Index</th>
                            </tr>
                          </thead>
                          <tbody>
                            {selectedMockAiService.knowledge.map((k) => (
                              <tr key={k.name}>
                                <td>{k.name}</td>
                                <td>{k.chunks.toLocaleString("de-DE")}</td>
                                <td>{k.last_index}</td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      )}
                    </div>
                  </details>
                  <details className="agent_accordion">
                    <summary className="agent_accordion_summary">Versionen (Live)</summary>
                    <div className="agent_accordion_body">
                      <p className="agent_section_note">
                        Nach Promote erscheint hier eine neue Zeile. Im Draft nur
                        lesen.
                      </p>
                      {selectedMockAiService.versions.map((v, vi) => (
                        <div key={`${v.v}-${vi}`} className="agent_version_block">
                          <div className="agent_version_head">
                            <span className="agent_version_id">{v.v}</span>
                            <AgentMockStatusPill status={v.status} />
                            <span className="agent_version_traffic">{v.traffic}% Traffic</span>
                          </div>
                          <div className="agent_traffic_bar">
                            <div
                              className="agent_traffic_bar_fill"
                              style={{ width: `${Math.min(100, v.traffic)}%` }}
                            />
                          </div>
                          <p className="agent_version_changelog">{v.changelog}</p>
                        </div>
                      ))}
                    </div>
                  </details>
                  <details className="agent_accordion">
                    <summary className="agent_accordion_summary">Endpoints</summary>
                    <div className="agent_accordion_body">
                      {["mcp", "a2a", "rest"].map((k) => {
                        const url = selectedMockAiService.endpoints[k];
                        if (url == null && !hasAiServiceDraft) {
                          return (
                            <div key={k} className="agent_endpoint_card agent_endpoint_card--disabled">
                              <span className="agent_endpoint_label">{k.toUpperCase()}</span>
                              <span>—</span>
                            </div>
                          );
                        }
                        if (hasAiServiceDraft) {
                          return (
                            <div key={k} className="agent_endpoint_card">
                              <span className="agent_endpoint_label">{k.toUpperCase()}</span>
                              <input
                                type="text"
                                className="admin_input"
                                value={url || ""}
                                placeholder="URL oder leer"
                                onChange={(e) => {
                                  const sid = selectedAiAgentServiceId;
                                  const val =
                                    e.target.value.trim() === ""
                                      ? null
                                      : e.target.value.trim();
                                  setAiAgentServiceDraftById((prev) => {
                                    const d = prev[sid];
                                    if (!d) return prev;
                                    return {
                                      ...prev,
                                      [sid]: {
                                        ...d,
                                        endpoints: { ...d.endpoints, [k]: val },
                                      },
                                    };
                                  });
                                }}
                              />
                              {url ? (
                                <button
                                  type="button"
                                  className="admin_btn"
                                  onClick={() => agentMockCopyToClipboard(url)}>
                                  Kopieren
                                </button>
                              ) : null}
                            </div>
                          );
                        }
                        return (
                          <div key={k} className="agent_endpoint_card">
                            <span className="agent_endpoint_label">{k.toUpperCase()}</span>
                            <code className="agent_endpoint_url">{url}</code>
                            <button
                              type="button"
                              className="admin_btn"
                              onClick={() => agentMockCopyToClipboard(url)}>
                              Kopieren
                            </button>
                          </div>
                        );
                      })}
                    </div>
                  </details>
                  <details className="agent_accordion">
                    <summary className="agent_accordion_summary">Konsumenten</summary>
                    <div className="agent_accordion_body">
                      {hasAiServiceDraft ? (
                        <>
                          <label className="agent_field_label">
                            Consumer-IDs (Komma)
                          </label>
                          <textarea
                            className="admin_input agent_textarea"
                            rows={3}
                            value={selectedMockAiService.consumer_ids.join(", ")}
                            onChange={(e) => {
                              const sid = selectedAiAgentServiceId;
                              const ids = e.target.value
                                .split(",")
                                .map((x) => x.trim())
                                .filter(Boolean);
                              setAiAgentServiceDraftById((prev) => {
                                const d = prev[sid];
                                if (!d) return prev;
                                return {
                                  ...prev,
                                  [sid]: { ...d, consumer_ids: ids },
                                };
                              });
                            }}
                          />
                        </>
                      ) : (
                        <ul className="agent_consumer_badge_list">
                          {selectedMockAiService.consumer_ids.map((cid) => {
                            const con = MOCK_AI_CONSUMERS.find((c) => c.id === cid);
                            return (
                              <li key={cid}>
                                {con ? `${con.icon} ${con.name}` : cid}
                              </li>
                            );
                          })}
                        </ul>
                      )}
                    </div>
                  </details>
                </div>
              </>
            ) : aiAgentsDetailOpen &&
              activeView === "ai_agents_consumers" &&
              selectedMockAiConsumer ? (
              <>
                <div className="admin_detail_header">
                  {isMobile ? (
                    <button
                      type="button"
                      className="admin_detail_back"
                      onClick={() => setSelectedAiAgentConsumerId(null)}
                      aria-label="Zurück">
                      &larr;
                    </button>
                  ) : null}
                  <span className="admin_detail_heading">
                    <strong>
                      {selectedMockAiConsumer.icon} {selectedMockAiConsumer.name}
                    </strong>
                    <br />
                    <span className="admin_detail_heading_sub">
                      {selectedMockAiConsumer.slug}
                    </span>
                  </span>
                </div>
                <div className="admin_detail_body agent_detail_body">
                  <details className="agent_accordion" open>
                    <summary className="agent_accordion_summary">Übersicht</summary>
                    <div className="agent_accordion_body">
                      <p>
                        Kind: <strong>{selectedMockAiConsumer.kind}</strong>
                      </p>
                      <p>
                        Quota: {selectedMockAiConsumer.quota_per_day.toLocaleString("de-DE")} / Tag
                      </p>
                      <p>
                        Erlaubte Services: {selectedMockAiConsumer.services_count}
                      </p>
                    </div>
                  </details>
                  <details className="agent_accordion" open>
                    <summary className="agent_accordion_summary">API-Keys</summary>
                    <div className="agent_accordion_body">
                      <ul className="agent_key_list">
                        {selectedMockAiConsumer.keys.map((k) => (
                          <li key={k.label}>
                            <span>{k.label}</span> <code>{k.masked}</code>
                          </li>
                        ))}
                      </ul>
                    </div>
                  </details>
                  <details className="agent_accordion">
                    <summary className="agent_accordion_summary">Erlaubte Services</summary>
                    <div className="agent_accordion_body">
                      <ul>
                        {selectedMockAiConsumer.allowed_service_ids.map((sid) => {
                          const sv = MOCK_AI_SERVICES.find((s) => s.id === sid);
                          return <li key={sid}>{sv ? sv.name : sid}</li>;
                        })}
                      </ul>
                    </div>
                  </details>
                  <details className="agent_accordion">
                    <summary className="agent_accordion_summary">Audit-Log</summary>
                    <div className="agent_accordion_body">
                      {selectedMockAiConsumer.audit_log.length === 0 ? (
                        <p>Keine Einträge.</p>
                      ) : (
                        <ul className="agent_audit_list">
                          {selectedMockAiConsumer.audit_log.map((a, idx) => (
                            <li key={idx}>
                              <strong>{a.at}</strong> {a.action}: {a.detail}
                            </li>
                          ))}
                        </ul>
                      )}
                    </div>
                  </details>
                </div>
              </>
            ) : aiAgentsDetailOpen &&
              activeView === "ai_agents_runs" &&
              selectedMockAiRun ? (
              <>
                <div className="admin_detail_header">
                  {isMobile ? (
                    <button
                      type="button"
                      className="admin_detail_back"
                      onClick={() => setSelectedAiAgentRunId(null)}
                      aria-label="Zurück">
                      &larr;
                    </button>
                  ) : null}
                  <span className="admin_detail_heading">
                    <strong>Run {selectedMockAiRun.id}</strong>
                    <br />
                    <span className="admin_detail_heading_sub">
                      {selectedMockAiRun.service_id} · {selectedMockAiRun.version}
                    </span>
                  </span>
                </div>
                <div className="admin_detail_body agent_detail_body">
                  <p className="agent_run_meta_line">
                    {selectedMockAiRun.at_label} · Caller {selectedMockAiRun.caller_id} ·{" "}
                    {selectedMockAiRun.cost_eur != null
                      ? `${selectedMockAiRun.cost_eur.toFixed(3)} €`
                      : "–"}
                  </p>
                  <div className="agent_trace_timeline">
                    <AgentMockTraceSteps nodes={selectedMockAiRun.trace} />
                  </div>
                </div>
              </>
            ) : aiAgentsDetailOpen &&
              activeView === "ai_agents_evals" &&
              selectedMockAiEval ? (
              <>
                <div className="admin_detail_header">
                  {isMobile ? (
                    <button
                      type="button"
                      className="admin_detail_back"
                      onClick={() => setSelectedAiAgentEvalId(null)}
                      aria-label="Zurück">
                      &larr;
                    </button>
                  ) : null}
                  <span className="admin_detail_heading">
                    <strong>{selectedMockAiEval.name}</strong>
                    <br />
                    <span className="admin_detail_heading_sub">
                      {selectedMockAiEval.service_id}
                    </span>
                  </span>
                </div>
                <div className="admin_detail_body agent_detail_body">
                  <details className="agent_accordion" open>
                    <summary className="agent_accordion_summary">Übersicht</summary>
                    <div className="agent_accordion_body">
                      <p>
                        {selectedMockAiEval.current_v}: <strong>{selectedMockAiEval.current_pct}%</strong> vs{" "}
                        {selectedMockAiEval.prev_v}: {selectedMockAiEval.prev_pct}%
                      </p>
                      <AgentMockSparkline values={selectedMockAiEval.sparkline} />
                    </div>
                  </details>
                  <details className="agent_accordion">
                    <summary className="agent_accordion_summary">Datapoints</summary>
                    <div className="agent_accordion_body">
                      {(selectedMockAiEval.datapoint_examples || []).length === 0 ? (
                        <p className="admin_product_empty">Keine Beispiele.</p>
                      ) : (
                        <ul>
                          {(selectedMockAiEval.datapoint_examples || []).map((d) => (
                            <li key={d.id}>
                              {d.label}{" "}
                              {d.pass ? (
                                <span className="agent_pass_ok">PASS</span>
                              ) : (
                                <span className="agent_pass_fail">FAIL</span>
                              )}
                            </li>
                          ))}
                        </ul>
                      )}
                    </div>
                  </details>
                  <details className="agent_accordion">
                    <summary className="agent_accordion_summary">Run-Historie</summary>
                    <div className="agent_accordion_body">
                      <table className="agent_data_table">
                        <thead>
                          <tr>
                            <th>Zeit</th>
                            <th>Score</th>
                            <th>Version</th>
                          </tr>
                        </thead>
                        <tbody>
                          {selectedMockAiEval.run_history.map((rh, i) => (
                            <tr key={i}>
                              <td>{rh.at}</td>
                              <td>{rh.score}%</td>
                              <td>{rh.version}</td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  </details>
                  <details className="agent_accordion">
                    <summary className="agent_accordion_summary">Diff Versionen</summary>
                    <div className="agent_accordion_body">
                      <p>{selectedMockAiEval.diff_note}</p>
                    </div>
                  </details>
                </div>
              </>
            ) : aiAgentsDetailOpen &&
              activeView === "ai_agents_governance" &&
              aiAgentGovernanceMode === "cost" &&
              selectedMockAiCost ? (
              <>
                <div className="admin_detail_header">
                  {isMobile ? (
                    <button
                      type="button"
                      className="admin_detail_back"
                      onClick={() => setSelectedAiAgentCostId(null)}
                      aria-label="Zurück">
                      &larr;
                    </button>
                  ) : null}
                  <span className="admin_detail_heading">
                    <strong>
                      {MOCK_AI_SERVICES.find((s) => s.id === selectedMockAiCost.service_id)
                        ?.name || selectedMockAiCost.service_id}
                    </strong>
                    <br />
                    <span className="admin_detail_heading_sub">Kosten-Detail</span>
                  </span>
                </div>
                <div className="admin_detail_body agent_detail_body">
                  <p>
                    Calls 24h: {selectedMockAiCost.calls_24h.toLocaleString("de-DE")}
                  </p>
                  <p>
                    Tokens 24h: {selectedMockAiCost.tokens_24h.toLocaleString("de-DE")}
                  </p>
                  <p>
                    EUR 24h: {selectedMockAiCost.eur_24h.toFixed(2)} € / Budget{" "}
                    {selectedMockAiCost.budget_eur_day} €
                  </p>
                  <div className="agent_budget_bar_wrap">
                    <div className="agent_budget_bar">
                      <div
                        className="agent_budget_bar_fill"
                        style={{
                          width: `${Math.min(
                            100,
                            (selectedMockAiCost.eur_24h /
                              Math.max(selectedMockAiCost.budget_eur_day, 0.01)) *
                              100,
                          )}%`,
                        }}
                      />
                    </div>
                  </div>
                </div>
              </>
            ) : aiAgentsDetailOpen &&
              activeView === "ai_agents_governance" &&
              aiAgentGovernanceMode === "approvals" &&
              selectedMockAiApproval ? (
              <>
                <div className="admin_detail_header">
                  {isMobile ? (
                    <button
                      type="button"
                      className="admin_detail_back"
                      onClick={() => setSelectedAiAgentApprovalId(null)}
                      aria-label="Zurück">
                      &larr;
                    </button>
                  ) : null}
                  <span className="admin_detail_heading">
                    <strong>Freigabe {selectedMockAiApproval.run_id}</strong>
                    <br />
                    <span className="admin_detail_heading_sub">
                      risk {selectedMockAiApproval.risk_score}
                    </span>
                  </span>
                </div>
                <div className="admin_detail_body agent_detail_body">
                  <p>{selectedMockAiApproval.summary}</p>
                  <label className="agent_field_label">Notiz</label>
                  <textarea
                    className="admin_input agent_textarea"
                    rows={3}
                    placeholder={selectedMockAiApproval.justification_placeholder}
                    readOnly
                  />
                  <p className="agent_hint">
                    Mockup: Freigabe über Liste oder hier später mit Backend.
                  </p>
                </div>
              </>
            ) : aiAgentsDetailOpen &&
              activeView === "ai_agents_tools" &&
              selectedMockAiTool ? (
              <>
                <div className="admin_detail_header">
                  {isMobile ? (
                    <button
                      type="button"
                      className="admin_detail_back"
                      onClick={() => setSelectedAiAgentToolId(null)}
                      aria-label="Zurück">
                      &larr;
                    </button>
                  ) : null}
                  <span className="admin_detail_heading">
                    <strong>🔧 {selectedMockAiTool.name}</strong>
                    <br />
                    <span className="admin_detail_heading_sub">
                      {selectedMockAiTool.slug} · {selectedMockAiTool.version} ·{" "}
                      {selectedMockAiTool.category}
                    </span>
                  </span>
                </div>
                <div className="admin_detail_body agent_detail_body">
                  <details className="agent_accordion" open>
                    <summary className="agent_accordion_summary">Übersicht</summary>
                    <div className="agent_accordion_body">
                      <p>{selectedMockAiTool.description}</p>
                      <p>
                        Status:{" "}
                        <AgentMockStatusPill status={selectedMockAiTool.status} />
                      </p>
                      <p>
                        Calls 24h:{" "}
                        {selectedMockAiTool.calls_24h.toLocaleString("de-DE")} ·{" "}
                        Success: {(selectedMockAiTool.success * 100).toFixed(1)}%
                      </p>
                    </div>
                  </details>
                  <details className="agent_accordion" open>
                    <summary className="agent_accordion_summary">Parameter</summary>
                    <div className="agent_accordion_body">
                      <pre className="agent_trace_step_body">
                        {JSON.stringify(selectedMockAiTool.params, null, 2)}
                      </pre>
                      <p>
                        <strong>Returns:</strong> {selectedMockAiTool.returns}
                      </p>
                    </div>
                  </details>
                  <details className="agent_accordion">
                    <summary className="agent_accordion_summary">Bindung</summary>
                    <div className="agent_accordion_body">
                      <p>
                        Action:{" "}
                        <button
                          type="button"
                          className="admin_btn"
                          style={{ padding: "2px 8px", fontSize: "12px" }}
                          onClick={() => {
                            setSelectedAiAgentActionId(
                              selectedMockAiTool.action_id,
                            );
                            switchView("ai_agents_actions");
                          }}>
                          {selectedMockAiTool.action_id}
                        </button>
                      </p>
                      <p>
                        Genutzt von Agents:{" "}
                        {selectedMockAiTool.used_by_agent_ids.join(", ") || "–"}
                      </p>
                    </div>
                  </details>
                </div>
              </>
            ) : aiAgentsDetailOpen &&
              activeView === "ai_agents_actions" &&
              selectedMockAiAction ? (
              <>
                <div className="admin_detail_header">
                  {isMobile ? (
                    <button
                      type="button"
                      className="admin_detail_back"
                      onClick={() => setSelectedAiAgentActionId(null)}
                      aria-label="Zurück">
                      &larr;
                    </button>
                  ) : null}
                  <span className="admin_detail_heading">
                    <strong>⚡ {selectedMockAiAction.name}</strong>
                    <br />
                    <span className="admin_detail_heading_sub">
                      {selectedMockAiAction.kind} · Owner{" "}
                      {selectedMockAiAction.owner}
                    </span>
                  </span>
                </div>
                <div className="admin_detail_body agent_detail_body">
                  <details className="agent_accordion" open>
                    <summary className="agent_accordion_summary">Ausführung</summary>
                    <div className="agent_accordion_body">
                      <p>
                        Ziel:{" "}
                        <code className="agent_inline_code">
                          {selectedMockAiAction.target}
                        </code>
                      </p>
                      <p>
                        Timeout: {(selectedMockAiAction.timeout_ms / 1000).toFixed(1)}s ·{" "}
                        Retries: {selectedMockAiAction.retries} · Sandbox:{" "}
                        {selectedMockAiAction.sandbox ? "ja" : "nein"}
                      </p>
                      <p>
                        Status:{" "}
                        <AgentMockStatusPill
                          status={selectedMockAiAction.status}
                        />{" "}
                        · Success {selectedMockAiAction.success_pct}% · letzter
                        Lauf {selectedMockAiAction.last_run}
                      </p>
                    </div>
                  </details>
                  <details className="agent_accordion" open>
                    <summary className="agent_accordion_summary">Verwendung</summary>
                    <div className="agent_accordion_body">
                      <p>
                        Exposed über Tools:{" "}
                        {selectedMockAiAction.exposed_via_tool_count}
                      </p>
                      {selectedMockAiAction.data_store_ids.length > 0 ? (
                        <p>
                          Schreibt/Liest Datenspeicher:{" "}
                          {selectedMockAiAction.data_store_ids.map((id, idx) => (
                            <span key={id}>
                              {idx > 0 ? ", " : null}
                              <button
                                type="button"
                                className="admin_btn"
                                style={{ padding: "2px 8px", fontSize: "12px" }}
                                onClick={() => {
                                  setSelectedAiAgentDataId(id);
                                  switchView("ai_agents_data");
                                }}>
                                {id}
                              </button>
                            </span>
                          ))}
                        </p>
                      ) : (
                        <p>Kein direkter Datenspeicher.</p>
                      )}
                    </div>
                  </details>
                  <details className="agent_accordion">
                    <summary className="agent_accordion_summary">Policy</summary>
                    <div className="agent_accordion_body">
                      <p>{selectedMockAiAction.policy}</p>
                    </div>
                  </details>
                </div>
              </>
            ) : aiAgentsDetailOpen &&
              activeView === "ai_agents_data" &&
              selectedMockAiData ? (
              <>
                <div className="admin_detail_header">
                  {isMobile ? (
                    <button
                      type="button"
                      className="admin_detail_back"
                      onClick={() => setSelectedAiAgentDataId(null)}
                      aria-label="Zurück">
                      &larr;
                    </button>
                  ) : null}
                  <span className="admin_detail_heading">
                    <strong>{selectedMockAiData.name}</strong>
                    <br />
                    <span className="admin_detail_heading_sub">
                      {selectedMockAiData.kind} · {selectedMockAiData.scope}
                    </span>
                  </span>
                </div>
                <div className="admin_detail_body agent_detail_body">
                  <details className="agent_accordion" open>
                    <summary className="agent_accordion_summary">Übersicht</summary>
                    <div className="agent_accordion_body">
                      <p>
                        Ort:{" "}
                        <code className="agent_inline_code">
                          {selectedMockAiData.location}
                        </code>
                      </p>
                      <p>
                        Größe: {selectedMockAiData.size_label} ·{" "}
                        {selectedMockAiData.rows_label}
                      </p>
                      <p>
                        Letzter Index: {selectedMockAiData.last_index} ·{" "}
                        Retention:{" "}
                        {selectedMockAiData.retention_days === 0
                          ? "kein Persist"
                          : `${selectedMockAiData.retention_days} Tage`}
                      </p>
                    </div>
                  </details>
                  <details className="agent_accordion" open>
                    <summary className="agent_accordion_summary">Genutzt von</summary>
                    <div className="agent_accordion_body">
                      {selectedMockAiData.used_by_action_ids.length === 0 ? (
                        <p>Keine Action.</p>
                      ) : (
                        <ul>
                          {selectedMockAiData.used_by_action_ids.map((id) => (
                            <li key={id}>
                              <button
                                type="button"
                                className="admin_btn"
                                style={{ padding: "2px 8px", fontSize: "12px" }}
                                onClick={() => {
                                  setSelectedAiAgentActionId(id);
                                  switchView("ai_agents_actions");
                                }}>
                                {id}
                              </button>
                            </li>
                          ))}
                        </ul>
                      )}
                    </div>
                  </details>
                  <details className="agent_accordion">
                    <summary className="agent_accordion_summary">Policy</summary>
                    <div className="agent_accordion_body">
                      <p>{selectedMockAiData.policy}</p>
                    </div>
                  </details>
                </div>
              </>
            ) : aiAgentsDetailOpen &&
              activeView === "ai_agents_governance" &&
              aiAgentGovernanceMode === "incidents" &&
              selectedMockAiIncident ? (
              <>
                <div className="admin_detail_header">
                  {isMobile ? (
                    <button
                      type="button"
                      className="admin_detail_back"
                      onClick={() => setSelectedAiAgentIncidentId(null)}
                      aria-label="Zurück">
                      &larr;
                    </button>
                  ) : null}
                  <span className="admin_detail_heading">
                    <strong>{selectedMockAiIncident.title}</strong>
                    <br />
                    <span className="admin_detail_heading_sub">
                      {selectedMockAiIncident.severity} · {selectedMockAiIncident.opened_at}
                    </span>
                  </span>
                </div>
                <div className="admin_detail_body agent_detail_body">
                  <p>{selectedMockAiIncident.description}</p>
                  <p>
                    Betroffene Calls: {selectedMockAiIncident.affected_calls} · Owner:{" "}
                    {selectedMockAiIncident.owner}
                  </p>
                </div>
              </>
            ) : workspaceShellRightOpen ? (
              <>
                <div className="admin_detail_header">
                  <span className="admin_detail_heading">
                    <strong>
                      {WORKSPACE_SHELL_META[activeView].detailTitle}
                    </strong>
                    <br />
                    <span className="admin_detail_heading_sub">
                      {WORKSPACE_SHELL_META[activeView].detailSub}
                    </span>
                  </span>
                </div>
                <div className="admin_detail_body admin_detail_body--empty">
                  <div className="admin_detail_empty_state">
                    <span>Inhalt folgt.</span>
                  </div>
                </div>
              </>
            ) : designPatternsHasSelection ? (
              (() => {
                const p = DESIGN_PATTERN_PLACEHOLDERS.find(
                  (x) => x.id === selectedDesignPatternId,
                );
                return (
                  <>
                    <div className="admin_detail_header">
                      {isMobile ? (
                        <button
                          type="button"
                          className="admin_detail_back"
                          onClick={() => setSelectedDesignPatternId(null)}
                          aria-label="Zurück">
                          &larr;
                        </button>
                      ) : null}
                      <span className="admin_detail_heading">
                        <strong>{p ? p.title : "Pattern"}</strong>
                        <br />
                        <span className="admin_detail_heading_sub">
                          Platzhalter — Detail folgt.
                        </span>
                      </span>
                    </div>
                    <div className="admin_detail_body">
                      <p style={{ padding: "1rem", margin: 0 }}>
                        Inhalt folgt.
                      </p>
                    </div>
                  </>
                );
              })()
            ) : designPatternsSidebarOpen ? (
              <>
                <div className="admin_detail_header">
                  <span className="admin_detail_heading">
                    <strong>Pattern auswählen</strong>
                    <br />
                    <span className="admin_detail_heading_sub">
                      Klicke links auf ein Pattern.
                    </span>
                  </span>
                </div>
                <div className="admin_detail_body admin_detail_body--empty">
                  <div className="admin_detail_empty_state">
                    <span>Kein Pattern ausgewählt</span>
                  </div>
                </div>
              </>
            ) : productDetailOpen || aiAgentsDetailOpen ? (
              <>
                <div className="admin_detail_header">
                  <span className="admin_detail_heading">
                    <strong>{emptyPlaceholderTitle}</strong>
                    <br />
                    <span className="admin_detail_heading_sub">
                      {emptyPlaceholderSub}
                    </span>
                  </span>
                </div>
                <div className="admin_detail_body admin_detail_body--empty">
                  <div className="admin_detail_empty_state">
                    <svg
                      width="40"
                      height="40"
                      viewBox="0 0 24 24"
                      fill="none"
                      stroke="currentColor"
                      strokeWidth="1.5"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      aria-hidden="true">
                      <circle cx="11" cy="11" r="7"></circle>
                      <line x1="21" y1="21" x2="16.65" y2="16.65"></line>
                    </svg>
                    <span>Kein Eintrag ausgewählt</span>
                  </div>
                </div>
              </>
            ) : null}
          </div>
        );
      })()}

      {/* Mobile bottom nav – view tabs */}
      {!areaPickerOpen ? (
        <div className="admin_bottom_nav">
          <div className="admin_bottom_nav_inner">
            {views.map((view) => (
              <button
                key={view.id}
                type="button"
                className={
                  "admin_bottom_nav_item" +
                  (activeView === view.id
                    ? " admin_bottom_nav_item--active"
                    : "")
                }
                onClick={() => switchView(view.id)}>
                <span className="admin_bottom_nav_icon">
                  <ViewNavIcon viewId={view.id} size={22} />
                </span>
                <span className="admin_bottom_nav_label">{view.label}</span>
              </button>
            ))}
          </div>
        </div>
      ) : null}

      {aiAgentServicePromoteOpen ? (
        <div
          className="agent_modal_overlay"
          role="presentation"
          onClick={() => setAiAgentServicePromoteOpen(false)}>
          <div
            className="agent_modal"
            role="dialog"
            aria-modal="true"
            aria-labelledby="agent_promote_title"
            onClick={(e) => e.stopPropagation()}>
            <h3 id="agent_promote_title" className="agent_modal_title">
              Promote
            </h3>
            <p className="agent_modal_desc">
              Der Entwurf wird als neue Version übernommen und erhält den gewählten
              Status. Mockup: gilt nur bis zum Neuladen der Seite.
            </p>
            <label className="agent_field_label">Ziel-Status</label>
            <select
              className="admin_input"
              value={aiAgentServicePromoteStatus}
              onChange={(e) =>
                setAiAgentServicePromoteStatus(e.target.value)
              }>
              <option value="draft">draft</option>
              <option value="beta">beta</option>
              <option value="stable">stable</option>
            </select>
            <div className="agent_modal_actions">
              <button
                type="button"
                className="admin_btn"
                onClick={() => setAiAgentServicePromoteOpen(false)}>
                Abbrechen
              </button>
              <button
                type="button"
                className="admin_btn admin_btn--primary"
                onClick={() => {
                  const sid = selectedAiAgentServiceId;
                  const pub = selectedServicePublished;
                  const draft = aiAgentServiceDraftById[sid];
                  if (!sid || !pub || !draft) {
                    setAiAgentServicePromoteOpen(false);
                    return;
                  }
                  const merged = mergeAiAgentDraft(pub, draft);
                  const newVer = bumpAgentServiceVersion(pub.version);
                  const st = aiAgentServicePromoteStatus;
                  const changelog = `Promote als ${st}`;
                  const newRow = {
                    v: newVer,
                    status: st,
                    traffic: st === "stable" ? 100 : st === "beta" ? 50 : 10,
                    changelog,
                  };
                  const restVers =
                    st === "stable"
                      ? (merged.versions || []).map((vr) => ({
                          ...vr,
                          traffic: 0,
                        }))
                      : [...(merged.versions || [])];
                  const nextVersions = [newRow, ...restVers];
                  setAiAgentServicePublishedById((prev) => ({
                    ...prev,
                    [sid]: {
                      brain: merged.brain,
                      contract: merged.contract,
                      tools: merged.tools,
                      knowledge: merged.knowledge,
                      versions: nextVersions,
                      endpoints: merged.endpoints,
                      consumer_ids: merged.consumer_ids,
                      version: newVer,
                      status: st,
                    },
                  }));
                  setAiAgentServiceDraftById((prev) => {
                    const n = { ...prev };
                    delete n[sid];
                    return n;
                  });
                  setAiAgentServicePromoteOpen(false);
                }}>
                Promote ausführen
              </button>
            </div>
          </div>
        </div>
      ) : null}
    </div>
  );
}
